// DispatchDeck - Maintenance Module
// Fleet maintenance: meters tracking, PM scheduling, work orders

import {
  db,
  auth,
  storage,
  functions,
  httpsCallable,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  onSnapshot,
  Timestamp,
  ref,
  uploadBytes,
  getDownloadURL,
  limit,
  arrayUnion,
  arrayRemove
} from '../firebase-config.js?v=1868';

// runTransaction is not re-exported from firebase-config.js — import it directly
// so this module doesn't force a firebase-config cache-buster rebump. Used for
// the zero-floored deployedQty decrement (TASK-1255).
import { runTransaction, serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-9775 — HEIC → JPEG before a receipt photo is sent (same ?v= as every other importer).
import { prepareUploadFile } from '../utils/uploadPrep.js?v=1';
import '../utils/recordDate.js?v=9923'; // TASK-9923 DVIR history days = company calendar

// TASK-1265 — the standalone decrementDeployedQtyFloored helper was removed; the
// floored decrement now lives inside the dismiss handler's single idempotent
// transaction (gated on the return-inspection status) so a double-dismiss can't
// re-run it. `runTransaction` (imported above) is still used there.

// Default PM checklist items — used when tenant has no custom list
const DEFAULT_PM_CHECKLIST = [
  'Oil Change', 'Oil Filter', 'Fuel Filter', 'Air Filter', 'Coolant Filter',
  'Grease', 'DEF', 'Coolant Check', 'Belt Inspection', 'Tire Check'
];

// ============================================
// MAINTENANCE STATE
// ============================================
// TASK-9339 — the client-side PM defaults. `truck` here is a BUILT-IN default, not a
// saved tenant setting; the modal labels it as such until the tenant saves a value.
// functions/src/index.js (_pmCalculateStatus caller) merges the same object — keep in step.
// TASK-9659 — the built-ins live in shared/pmStatus.js (window.PmStatus, imported
// by app.js before this view); never a second hand-typed copy here.
const BUILTIN_PM_INTERVALS = Object.freeze({ ...window.PmStatus.BUILTIN_INTERVALS });

const MaintenanceState = {
  tasks: [],
  _rawTasks: [], // wo-legacy-orders — the snapshot as read; `tasks` = these through _woNormalize (office view only)
  people: [],
  trucks: [],
  trailers: [],
  assets: [],
  metersLog: [],
  vehicleHolds: [],
  returnInspections: [],
  pretrips: [],
  fluidsLog: [],
  readingsStripOpen: false, // TASK-9898 — Fleet Status "readings look mistyped" list expanded
  pmSettings: {
    // pmIntervals map: slug → miles, _default → catch-all, _hours → hours interval
    pmIntervals: { ...BUILTIN_PM_INTERVALS },
    // TASK-9339 — keys present in the tenant's SAVED settings.pmIntervals (vs merged-in built-ins)
    savedIntervalKeys: [],
    // PM checklist items — customizable per tenant
    pmChecklist: null // null = use defaults, array = custom list
  },
  unsubscribers: [],
  currentTab: 'fleet', // fleet, tasks, holds
  currentAssetType: 'vehicles', // vehicles, trailers, assets
  currentFilter: 'open',
  truckFilter: null, // legacy; TASK-9897 folded the fleet-card drill into woFilter.vehicles
  // TASK-9897 — the Work Orders filter state. NOT trailerFilter (that object is the
  // trailer GRID's {search,type,status}). One predicate (woMatches) reads this.
  woFilter: null,
  woSort: 'newest',
  woMoreOpen: false,
  tenantId: null,
  selectedDVIRTruckId: null,
  selectedDVIRStartDate: null,
  selectedDVIREndDate: null,
  selectedHistoryTruckId: null,
  pendingReceipts: [],
  // Mechanic signature state
  mechSigCanvas: null,
  mechSigCtx: null,
  mechSigDrawing: false,
  mechSigHasDrawn: false,
  // Fleet filter state (trucks)
  fleetFilter: {
    search: '',
    type: 'all',
    status: 'all', // all, attention, ok
    expiry: 'all', // all | reg (registration expiring ≤30d/overdue) | inspection (annual inspection expiring ≤30d/overdue)
    sort: 'unit' // 'unit' (alpha, default) | 'pmdue' (closest PM due first)
  },
  // Trailer filter state
  trailerFilter: {
    search: '',
    type: 'all',
    status: 'all' // all, attention, ok
  },
  // Asset filter state
  assetFilter: {
    search: '',
    category: 'all'
  },
  // Dispatched truck IDs for today (from jobDays)
  dispatchedTruckIds: new Set(),
  // Pretrip lookup: { "truckId_YYYY-MM-DD": true } for DVIR status
  pretripTruckDates: {},
  // Vehicle inventory tracking
  inventoryLogs: [],
  inventoryPurchases: [],
  currentInventoryTruckId: null,
  currentInventoryVehicleType: 'truck', // 'truck' or 'trailer'
  currentInventoryTab: 'submit', // submit, purchase, history
  fleetInventoryLimit: 100, // TASK-9335 — fleet view renders newest N, "Show more" adds 100
  // TASK-4773 — which of the two driver paths the task modal is currently on:
  // 'issue' (a problem → status:'open') or 'done' (work already finished →
  // status:'pending_review'). Reset on every showNewTask(); driver modal only.
  driverTaskMode: 'issue'
};

// ============================================
// DRIVER MODE HELPERS
// ============================================
function isDriverPinMode() {
  return !!window.AppState?.driverModePin;
}

function getDriverPersonId() {
  return window.AppState?.driverPersonId || null;
}

function getDriverPersonName() {
  return window.AppState?.driverPersonName || null;
}

// ============================================
// INITIALIZE MAINTENANCE VIEW
// ============================================
function initMaintenance(container, tenantId) {
  console.log('[Maintenance] Initializing for tenant:', tenantId);

  // Guard against null tenant
  if (!tenantId) {
    console.error('[Maintenance] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load maintenance. Please try logging out and back in.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  MaintenanceState.tenantId = tenantId;
  // TASK-9658 S1 — mount generation: an async record lookup that resolves after
  // this view was torn down / re-mounted must not open a modal.
  MaintenanceState._mountGen = (MaintenanceState._mountGen || 0) + 1;
  _parkMaintOpenHint(); // TASK-9671 S1 — before any snapshot can drain

  // Reset filter state so stale filters don't carry over between navigations
  MaintenanceState.currentAssetType = 'vehicles';
  MaintenanceState.currentFilter = 'open';
  MaintenanceState.truckFilter = null;
  MaintenanceState._trucksLoaded = false;
  MaintenanceState._trailersLoaded = false;
  MaintenanceState._woAllAssets = null;
  MaintenanceState._assetsLoaded = false; // wo-legacy-orders — imported orders resolve by name only once all three fleets are in
  MaintenanceState._tasksLoaded = false;
  MaintenanceState._tasksError = false; // TASK-9405 — a failed subscription is NOT "no tasks"
  MaintenanceState._holdsLoaded = false;
  MaintenanceState._fluidsLoaded = false;
  MaintenanceState.fleetFilter = { search: '', type: 'all', status: 'all', expiry: 'all', sort: getStoredFleetSort() };
  MaintenanceState.trailerFilter = { search: '', type: 'all', status: 'all' };
  MaintenanceState.assetFilter = { search: '', category: 'all' };
  // TASK-9897 — work-order filters: sort from localStorage (tenant+user), filters
  // from sessionStorage (tenant+user, this browser session only), status tab NOT
  // restored (it was reset to 'open' above).
  woLoadState();

  // Render the maintenance UI (driver mode gets simplified view)
  if (isDriverPinMode()) {
    container.innerHTML = getDriverMaintenanceHTML();
    setupModals();
    subscribeToDriverData(tenantId);
  } else {
    container.innerHTML = getMaintenanceHTML();
    // TASK-9897 — the vehicle dropdown closes on an outside click; bound once.
    if (!MaintenanceState._woDocClickBound) {
      document.addEventListener('click', woOnDocClick);
      MaintenanceState._woDocClickBound = true;
    }
    // TASK-9657 — paint the Approvals count (full view only; never the driver view).
    window.ApprovalsBadge?.paint();

    // Populate dynamic filter dropdowns (uses helpers from roster.js exposed on window)
    if (window.populateTypeDropdown) {
      window.populateTypeDropdown('filter-fleet-type', 'truckTypes', true);
      window.populateTypeDropdown('filter-trailer-type', 'trailerTypes', true);
    }

    // Set up modal handlers
    setupModals();

    // Subscribe to real-time data
    subscribeToData(tenantId);

    // On mobile: show all fleet sections as collapsible, closed by default
    if (isMobileView()) {
      setupMobileCollapsible();
    }
  }

  // TASK-9658 S1 — record deep link: drained by the snapshot callbacks once the
  // list lands; this fallback resolves it (by-id read → open or "not found")
  // if a subscription never delivers.
  if (_recordFocusFallbackTimer) { clearTimeout(_recordFocusFallbackTimer); _recordFocusFallbackTimer = null; }
  if (window.RecordLinks?.hasPendingFocus?.('maintenance')) {
    _recordFocusFallbackTimer = setTimeout(() => { _recordFocusFallbackTimer = null; drainMaintenanceRecordFocus(true); }, 8000);
  }

  // Return cleanup function
  return () => {
    if (_recordFocusFallbackTimer) { clearTimeout(_recordFocusFallbackTimer); _recordFocusFallbackTimer = null; }
    MaintenanceState._mountGen = (MaintenanceState._mountGen || 0) + 1; // TASK-9658 — orphan in-flight lookups
    MaintenanceState.unsubscribers.forEach(unsub => unsub());
    MaintenanceState.unsubscribers = [];
    // TASK-4884 — stop the Inspections listener and drop any open modal.
    window.FacilityInspections?.closeFacilityModal();
    window.FacilityInspections?.unmount();
    ['task-description','task-notes','pm-notes']
      .forEach(id => window.SmartAssistant?.unregister(id));
    // TASK-9122 — navigating away with the modal open would otherwise leave a
    // visualViewport listener holding a detached .modal-content.
    detachKeyboardInset();
  };
}

// ============================================
// DRIVER MODE — SIMPLIFIED HTML TEMPLATE
// ============================================
function getDriverMaintenanceHTML() {
  const driverName = getDriverPersonName() || 'Driver';
  return `
    <div class="maintenance-view driver-maintenance-view">
      <!-- Header -->
      <div style="text-align:center;margin-bottom:var(--spacing-sm);">
        <h1 style="margin:0;font-size:1.5rem;font-weight:700;">Maintenance</h1>
        <p class="maintenance-subtitle">${escapeHtml(driverName)} — Log maintenance & track your tasks</p>
      </div>
      <div class="maintenance-header">
        <div class="maintenance-header-actions" style="justify-content:center;width:100%;display:flex;gap:8px;flex-wrap:wrap;">
          ${
            // TASK-774 Gap B — a driver who reached Maintenance ONLY because a task
            // was assigned to them (driverHasAssignedMaintenance, no full
            // allowMaintenance grant) gets a SCOPED surface: their assigned to-do
            // list, but NOT Log Maintenance (that's the full grant's affordance).
            // Show Log Maintenance when the driver has the full grant, OR for any
            // non-PIN context (admin/demo full view never lands here, but keep the
            // button for the grant + non-driver paths). Hide ONLY for the
            // assigned-but-grant-less PIN driver.
            // TASK-9122 — the two paths are now the ENTRY POINT, not a toggle
            // inside the form. Chris 2026-09-10: "there is a submit and a 'log
            // work i did'... i would get rid of the redundancy." Both buttons
            // stay inside this ONE ternary on purpose — split them and the
            // Gap-B driver silently regains a control they were denied.
            (window.AppState?.driverModePin && !window.AppState?.driverAllowMaintenance)
              ? ''
              : `<button id="btn-new-task" class="btn btn-primary" style="min-height:44px;"
                  onclick="window.MaintenanceModule.showNewTask('issue')">
            <span class="material-icons">${DRIVER_TASK_MODES.issue.icon}</span> ${DRIVER_TASK_MODES.issue.label}
          </button>
          <button id="btn-log-work-done" class="btn btn-secondary" style="min-height:44px;"
                  onclick="window.MaintenanceModule.showNewTask('done')">
            <span class="material-icons">${DRIVER_TASK_MODES.done.icon}</span> ${DRIVER_TASK_MODES.done.label}
          </button>`
          }
        </div>
      </div>

      <!-- Task Filters -->
      <div class="maintenance-filter-bar" style="display:flex;gap:6px;justify-content:center;margin-bottom:var(--spacing-md);">
        <button class="filter-chip active" data-filter="open" onclick="window.MaintenanceModule.filterTasks('open')">
          Open <span class="tab-count count-zero" id="driver-count-open">0</span>
        </button>
        <button class="filter-chip" data-filter="in_progress" onclick="window.MaintenanceModule.filterTasks('in_progress')">
          In Progress <span class="tab-count count-zero" id="driver-count-progress">0</span>
        </button>
        <button class="filter-chip" data-filter="pending_review" onclick="window.MaintenanceModule.filterTasks('pending_review')">
          Pending Review <span class="tab-count count-zero" id="driver-count-review">0</span>
        </button>
        <button class="filter-chip" data-filter="completed" onclick="window.MaintenanceModule.filterTasks('completed')">
          Completed <span class="tab-count count-zero" id="driver-count-done">0</span>
        </button>
      </div>

      <!-- Task List -->
      <div id="maintenance-list" class="maintenance-list"></div>

      <!-- Modals reused from full view -->
      ${getDriverTaskModalHTML()}
      ${getCompletionModalHTML()}
    </div>
  `;
}

// TASK-4773 — the driver modal used to be defect-shaped only ("Report Issue",
// "What's the issue?"), and saveTask stamped status:'open' on every create. So a
// driver who greased a truck and logged it left a work order somebody in the
// office had to go close. Chris: "weird we say 'report issue' when someone is
// just greasing a truck... generally no one reads anything" — which is why the
// two paths are two BUTTONS the driver taps, not a line of sub-copy under one.
//
// DRIVER_TASK_MODES is the single source of truth for every string that differs
// between the two paths, so a future edit can't rename the button and leave the
// label behind (the partial-rename trap: showNewTask() re-sets the title at open
// time, and this table is what it reads).
const DRIVER_TASK_MODES = {
  issue: {
    label: 'Report a problem',
    icon: 'report_problem',
    descLabel: "What's wrong? *",
    descPlaceholder: 'Describe the problem...',
    submit: 'Submit'
  },
  done: {
    label: 'Log work I did',
    icon: 'task_alt',
    descLabel: 'What did you do? *',
    descPlaceholder: 'Greased the fifth wheel, swapped a tire...',
    submit: 'Log It'
  }
};

function getDriverTaskModalHTML() {
  return `
    <div class="modal hidden modal-kb-aware" id="modal-task">
      <div class="modal-content modal-fullscreen">
        <div class="modal-header">
          <h3 id="modal-task-title">Log Maintenance</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('task')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <!-- TASK-9122 — the scrollable-form trio, NOT a plain form. Same structure
             as #form-job (dispatch.js:1771) and #form-assignment (dispatch.js:2350,
             which got it 2026-09-08 for this exact class of bug on a laptop).
             A plain form inside .modal-content gets a scrollbar from ONE rule —
             .modal-content form:not(.modal-form-scrollable) (styles.css ~:9839) —
             which lives only in the 768px-and-under block. Desktop had no such
             rule, so anything past 90vh was unreachable; and on a phone that rule
             makes BOTH the form and .modal-body scrollers while .form-actions is
             pinned sticky to the bottom of a 100dvh box. The trio gives exactly
             one scroller (.modal-form-scroll) and one pinned footer, at every
             width. Form padding is zeroed because .modal-content form
             (styles.css:5336) still applies spacing-lg and .modal-form-scroll
             brings its own — without that the content is double-padded. -->
        <form id="form-task" class="modal-form-scrollable" novalidate onsubmit="window.MaintenanceModule.saveTask(event)" style="display:flex;flex-direction:column;overflow:hidden;flex:1;min-height:0;padding:0;">
          <input type="hidden" id="task-id">
          <div class="modal-form-scroll" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:var(--spacing-lg);">

          <div class="form-row">
            <div class="form-group">
              <label for="task-asset-type">Asset Type *</label>
              <select id="task-asset-type" required onchange="window.MaintenanceModule.updateTaskAssetDropdown()">
                <option value="truck">Vehicle</option>
                <option value="trailer">Trailer</option>
                <option value="asset">Asset</option>
              </select>
            </div>
            <div class="form-group">
              <label for="task-asset">Vehicle *</label>
              <select id="task-asset" required>
                <option value="">Select vehicle</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="task-category">Category *</label>
            <select id="task-category" required onchange="window.MaintenanceModule.onTaskCategoryChange()">
              <option value="Repair">Repair</option>
              <option value="Tire">Tire</option>
              <option value="DVIR Defect">DVIR Defect</option>
              <option value="PM Service">PM Service</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div class="form-group">
            <label for="task-description" id="task-description-label">${DRIVER_TASK_MODES.issue.descLabel}</label>
            <textarea id="task-description" rows="3" required placeholder="${DRIVER_TASK_MODES.issue.descPlaceholder}"></textarea>
          </div>

          <!-- Priority is a triage signal for work that still needs doing. On the
               "work I did" path it is hidden AND forced to normal in saveTask —
               a completed log must never mint a critical safety hold (dispatch.js
               safety-hold board + pretrip.js DVIR block both key off
               priority==='critical' && status!=='completed'). -->
          <div class="form-group" id="task-priority-group">
            <label for="task-priority">Priority</label>
            <select id="task-priority">
              <option value="normal" selected>Normal</option>
              <option value="high">High</option>
              <option value="critical">Critical — Needs immediate attention</option>
            </select>
          </div>

          <div class="form-group">
            <label for="task-notes">Notes</label>
            <textarea id="task-notes" rows="2" placeholder="Additional details..."></textarea>
          </div>

          <!-- Hidden fields for driver mode (auto-set) -->
          <input type="hidden" id="task-assigned" value="">
          <div id="task-meters-row" class="form-row" style="display:none;">
            <div class="form-group">
              <label for="task-miles">Current Mileage</label>
              <input type="number" id="task-miles" placeholder="e.g. 125000" min="0" step="1">
            </div>
            <div class="form-group">
              <label for="task-hours">Current Hours</label>
              <input type="number" id="task-hours" placeholder="e.g. 4500" min="0" step="0.1">
            </div>
          </div>

          </div>
          <div class="modal-form-footer" style="display:flex;gap:var(--spacing-sm);justify-content:flex-end;flex-shrink:0;">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('task')">Cancel</button>
            <button type="submit" id="task-submit-btn" class="btn btn-primary">${DRIVER_TASK_MODES.issue.submit}</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

// TASK-4773 — flip the driver modal between the two paths. No-ops safely on the
// ADMIN modal, which carries none of these ids (the reduced-copy trap that cost
// five months on TASK-4779 runs in both directions).
//
// TASK-9122 — the two in-form toggle BUTTONS are gone; the choice is now made on
// the list screen (getDriverMaintenanceHTML) and passed to showNewTask(mode),
// which calls this. Everything else about it is unchanged, and this stays the
// single source of truth for every string + the priority suppression. The modal
// TITLE now carries the path, because with the toggle gone that heading is the
// only thing telling a driver which of the two they are filling in.
function setDriverTaskMode(mode) {
  const m = DRIVER_TASK_MODES[mode] ? mode : 'issue';
  MaintenanceState.driverTaskMode = m;

  const title = document.getElementById('modal-task-title');
  if (title && isDriverPinMode()) title.textContent = DRIVER_TASK_MODES[m].label;

  const descLabel = document.getElementById('task-description-label');
  if (descLabel) descLabel.textContent = DRIVER_TASK_MODES[m].descLabel;
  const descEl = document.getElementById('task-description');
  if (descEl) descEl.placeholder = DRIVER_TASK_MODES[m].descPlaceholder;

  const submitBtn = document.getElementById('task-submit-btn');
  if (submitBtn) submitBtn.textContent = DRIVER_TASK_MODES[m].submit;

  // Hidden AND reset — the value is forced in saveTask too, but a stale
  // 'critical' left in the DOM is exactly the kind of thing that survives a
  // refactor of the write path.
  const prioGroup = document.getElementById('task-priority-group');
  const prioEl = document.getElementById('task-priority');
  if (prioGroup) prioGroup.style.display = (m === 'done') ? 'none' : '';
  if (prioEl && m === 'done') prioEl.value = 'normal';

  clearTaskFormError(document.getElementById('form-task'));
}

function getCompletionModalHTML() {
  return `
    <div class="modal hidden" id="modal-complete">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Complete Work Order</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('complete')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-complete" novalidate onsubmit="window.MaintenanceModule.driverConfirmCompletion(event)" style="display:flex;flex-direction:column;overflow:hidden;flex:1;min-height:0;">
          <input type="hidden" id="complete-task-id">
          <div class="modal-body">
            <div class="form-group">
              <label for="completion-notes">What was done?</label>
              <textarea id="completion-notes" rows="3" placeholder="Describe the work completed..."></textarea>
            </div>
            <!-- TASK-9775 — receipts for this work order. The photo goes to the
                 office's receipt inbox (same reader), linked to this work order +
                 its truck, and waits there for the office to check. The driver
                 sees only their own receipts, never the work order's costs. -->
            <div class="form-group" id="wo-receipt-section">
              <label>Receipts</label>
              <div id="wo-receipt-list"></div>
              <!-- No capture= attribute: a receipt photographed earlier at the parts
                   counter must be pickable from the photo library (COD Domain). -->
              <input type="file" id="wo-receipt-input" accept="image/*" style="display:none;"
                     onchange="window.MaintenanceModule.addWoReceipt(this)">
              <button type="button" id="btn-wo-receipt-add" class="btn btn-secondary" style="min-height:48px;width:100%;"
                      onclick="document.getElementById('wo-receipt-input').click()">
                <span class="material-icons">photo_camera</span> Add receipt
              </button>
              <div class="form-hint" style="color:var(--text-secondary);">Bought parts for this job? Take a picture. The office checks it.</div>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('complete')">Cancel</button>
            <button type="submit" id="btn-wo-complete" class="btn btn-primary">Mark Complete</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

// ============================================
// DRIVER MODE — DATA SUBSCRIPTION
// ============================================
function subscribeToDriverData(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;
  const driverPersonId = getDriverPersonId();

  // Subscribe to maintenance tasks (all — we filter client-side to driver's tasks)
  const maintenanceQuery = query(
    collection(db, 'tenants', tenant, 'maintenance'),
    orderBy('createdAt', 'desc')
  );

  const unsubMaintenance = onSnapshot(maintenanceQuery, snapshot => {
    // wo-legacy-followups — the driver list reads imported orders in the current
    // shape too (status Open/Closed/Done -> its tab, `by` -> createdBy, unit by
    // name once every fleet list is in), so the SAME "assigned to / logged by
    // me" rule decides them as it does a normal order.
    MaintenanceState._rawTasks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    _woRenormalizeTasks();
    MaintenanceState._tasksLoaded = true;
    MaintenanceState._tasksError = false;
    renderDriverTasksList(MaintenanceState.currentFilter);
    updateDriverCounts();
    drainMaintenanceRecordFocus(); // TASK-9658 S1
  }, err => {
    console.error('[Maintenance-Driver] Tasks subscription error:', err);
    MaintenanceState._tasksLoaded = true;
    MaintenanceState._tasksError = true;
    renderDriverTasksList(MaintenanceState.currentFilter);
    window.showToast?.('Error loading maintenance tasks', 'error');
    drainMaintenanceRecordFocus(); // TASK-9658 S1 — by-id read decides
  });
  MaintenanceState.unsubscribers.push(unsubMaintenance);

  // Load trucks for the vehicle dropdown
  const trucksQuery = query(collection(db, 'tenants', tenant, 'trucks'), orderBy('unit'));
  const unsubTrucks = onSnapshot(trucksQuery, snapshot => {
    MaintenanceState.trucks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    MaintenanceState._trucksLoaded = true;
    updateTaskAssetDropdown();
    _driverFleetChanged(); // wo-legacy-followups
  }, err => {
    console.error('[Maintenance-Driver] Trucks subscription error:', err);
  });
  MaintenanceState.unsubscribers.push(unsubTrucks);

  // Load trailers for the vehicle dropdown
  const trailersQuery = query(collection(db, 'tenants', tenant, 'trailers'), orderBy('unit'));
  const unsubTrailers = onSnapshot(trailersQuery, snapshot => {
    MaintenanceState.trailers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    MaintenanceState._trailersLoaded = true;
    updateTaskAssetDropdown();
    _driverFleetChanged(); // wo-legacy-followups
  }, err => {
    console.error('[Maintenance-Driver] Trailers subscription error:', err);
  });
  MaintenanceState.unsubscribers.push(unsubTrailers);

  // Load assets for the asset dropdown
  getDocs(collection(db, 'tenants', tenant, 'assets')).then(snap => {
    MaintenanceState.assets = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    MaintenanceState._assetsLoaded = true;
    _driverFleetChanged(); // wo-legacy-followups
  }).catch(err => {
    // Not loaded = imported orders keep their typed unit name (never a guess on a partial fleet).
    console.error('[Maintenance-Driver] Assets load error:', err);
  });

  // Load people for display names
  getDocs(collection(db, 'tenants', tenant, 'people')).then(snap => {
    MaintenanceState.people = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }).catch(err => {
    console.error('[Maintenance-Driver] People load error:', err);
  });
}

// wo-legacy-followups — a fleet list landed: imported orders re-resolve by name
// (only once trucks, trailers AND equipment are all in — _woNormalize) and the
// driver list repaints. A no-op until the task snapshot itself has landed.
function _driverFleetChanged() {
  if (!MaintenanceState._tasksLoaded) return;
  _woRenormalizeTasks();
  renderDriverTasksList(MaintenanceState.currentFilter);
  updateDriverCounts();
}

// ============================================
// DRIVER MODE — RENDER TASK LIST
// ============================================
function renderDriverTasksList(filter = 'open') {
  const container = document.getElementById('maintenance-list');
  if (!container) return;

  const driverPersonId = getDriverPersonId();
  const driverName = getDriverPersonName();

  // Filter to tasks relevant to this driver: assigned to them, or created by them
  let tasks = MaintenanceState.tasks.filter(t => {
    if (woTab(t) !== filter) return false; // wo-legacy-followups — the office list's tab rule (Closed/Done -> completed)
    return t.assignedTo === driverPersonId ||
           t.createdByPersonId === driverPersonId ||
           t.createdBy === driverName;
  });

  if (tasks.length === 0) {
    if (!MaintenanceState._tasksLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading tasks...</div>
        </div>
      `;
      return;
    }
    const filterLabel = { open: 'open', in_progress: 'in progress', pending_review: 'pending review', completed: 'completed' }[filter];
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔧</div>
        <div class="empty-state-title">No ${filterLabel} tasks</div>
        <div class="empty-state-text">${
          filter === 'open' ? 'Tap "Log Maintenance" to report a problem.'
          : filter === 'pending_review' ? 'Work you log as done waits here until the office signs off.'
          : 'Tasks will appear here as they are updated.'
        }</div>
      </div>
    `;
    return;
  }

  container.innerHTML = tasks.map(task => {
    const date = task.createdAt?.toDate?.() || new Date();
    const dateStr = formatDate(date);
    const priorityClass = task.priority || 'normal';
    const isFromDvir = task.source === 'pretrip';
    const isTrailerTask = task.assetType === 'trailer' || !!task.trailerId;
    const assetIcon = isTrailerTask ? 'rv_hookup' : 'local_shipping';
    let assetUnit;
    if (isTrailerTask) {
      const trailerObj = task.trailerId && MaintenanceState.trailers.find(t => t.id === task.trailerId);
      assetUnit = trailerObj && window.getUnitDisplay ? window.getUnitDisplay(trailerObj) : (task.trailerUnit || '?');
    } else {
      const truckObj = task.truckId && MaintenanceState.trucks.find(t => t.id === task.truckId);
      assetUnit = truckObj && window.getUnitDisplay ? window.getUnitDisplay(truckObj) : (task.truckUnit || '?');
    }

    return `
      ${/* TASK-9287 -- THREE POSITIONS, TWO ESCAPERS. 'priority' is a free-text field
             on the task document and a Firestore doc id is chosen by the caller on a
             setDoc, so both are attacker-controlled from a driver-PIN session.
             class= and data-id= are HTML ATTRIBUTE VALUES -> escapeHtml (this file's
             local wrapper delegates to window.escapeHtml, the regex escaper covering
             the full &<>"' set, so it IS attribute-safe here -- unlike app.js's
             DOM-based local variant, which leaves the quote unescaped).
             The onclick argument is a JS STRING INSIDE an attribute: the HTML parser
             decodes &#39; back to an apostrophe BEFORE the handler is compiled as
             JavaScript, so no html escaper protects it -- that position needs
             escJsAttr and only escJsAttr. Proof in tests/task9287-maintenance-card-xss.spec.js. */''}
      <div class="maintenance-card priority-${escapeHtml(priorityClass)}" data-id="${escapeHtml(task.id)}">
        <div class="maintenance-card-priority">
          <span class="priority-indicator"></span>
        </div>
        <div class="maintenance-card-info">
          <div class="maintenance-card-header">
            <span class="maintenance-card-truck"><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 2px;">${assetIcon}</span>${escapeHtml(assetUnit)}</span>
            <span class="maintenance-card-category">${escapeHtml(task.category || 'General')}</span>
            ${isFromDvir ? '<span class="source-badge">DVIR</span>' : ''}
          </div>
          <div class="maintenance-card-description">${escapeHtml(task.description || 'No description')}</div>
          <div class="maintenance-card-meta">
            <span><span class="material-icons">event</span> ${dateStr}</span>
            ${task.status === 'completed' && task.completedBy
              ? `<span class="completed-by"><span class="material-icons">check_circle</span> ${escapeHtml(task.completedBy)}</span>`
              : task.status === 'pending_review' && task.driverCompletedBy
                ? `<span class="completed-by"><span class="material-icons">hourglass_top</span> Awaiting admin review</span>`
                : ''}
          </div>
        </div>
        <div class="maintenance-card-actions">
          ${task.status === 'open' ? `
            <button class="btn btn-sm btn-secondary" onclick="event.stopPropagation(); window.MaintenanceModule.updateStatus('${escJsAttr(task.id)}', 'in_progress')">
              Start Work
            </button>
          ` : ''}
          ${task.status === 'in_progress' ? `
            <button class="btn btn-sm btn-primary" onclick="event.stopPropagation(); window.MaintenanceModule.updateStatus('${escJsAttr(task.id)}', 'completed')">
              Complete
            </button>
          ` : ''}
          ${task.status === 'pending_review' ? `
            <span style="color:var(--brand-blue);font-size:0.8rem;font-weight:500;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">schedule</span> Pending</span>
          ` : ''}
          ${/* TASK-9361 -- the driver HTML never mounts #modal-task-detail, so the
                 admin "Print Work Order" button (inside showTaskDetail) is unreachable
                 here and calling showTaskDetail from this card would throw. The icon
                 goes straight to printWorkOrder, which reads isDriverPinMode() and
                 leaves costs + vendor off the sheet. */''}
          <button type="button" class="btn-icon-xs" onclick="event.stopPropagation(); window.MaintenanceModule.printWorkOrder('${escJsAttr(task.id)}')" title="Print work order" aria-label="Print work order">
            <span class="material-icons">print</span>
          </button>
        </div>
      </div>
    `;
  }).join('');
}

function updateDriverCounts() {
  const driverPersonId = getDriverPersonId();
  const driverName = getDriverPersonName();

  const myTasks = MaintenanceState.tasks.filter(t =>
    t.assignedTo === driverPersonId ||
    t.createdByPersonId === driverPersonId ||
    t.createdBy === driverName
  );

  // wo-legacy-followups — counted by the same tab rule the list filters on.
  const openCount = myTasks.filter(t => woTab(t) === 'open').length;
  const progressCount = myTasks.filter(t => woTab(t) === 'in_progress').length;
  const reviewCount = myTasks.filter(t => woTab(t) === 'pending_review').length;
  const doneCount = myTasks.filter(t => woTab(t) === 'completed').length;

  const openEl = document.getElementById('driver-count-open');
  const progressEl = document.getElementById('driver-count-progress');
  const reviewEl = document.getElementById('driver-count-review');
  const doneEl = document.getElementById('driver-count-done');

  if (openEl) { openEl.textContent = openCount; openEl.classList.toggle('count-zero', openCount === 0); }
  if (progressEl) { progressEl.textContent = progressCount; progressEl.classList.toggle('count-zero', progressCount === 0); }
  if (reviewEl) { reviewEl.textContent = reviewCount; reviewEl.classList.toggle('count-zero', reviewCount === 0); }
  if (doneEl) { doneEl.textContent = doneCount; doneEl.classList.toggle('count-zero', doneCount === 0); }
}

// ============================================
// DRIVER MODE — COMPLETION FLOW
// ============================================
function showDriverCompletionModal(taskId) {
  document.getElementById('complete-task-id').value = taskId;
  document.getElementById('completion-notes').value = '';
  document.getElementById('modal-complete').classList.remove('hidden');
  openWoReceipts(taskId);
}

// ============================================
// DRIVER MODE — RECEIPTS ON A WORK ORDER (TASK-9775)
// ============================================
// Three driver callables (ingest / save / list DriverWorkOrderReceipt). The
// server checks the driver session, that this work order is theirs, the daily
// caps and the photo's real bytes; this side only shows what comes back.
const WoReceipts = { taskId: null, rows: [], busy: 0, gen: 0 };

function _woDriverCreds() {
  const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
  return {
    tenantId: tenant,
    personId: getDriverPersonId(),
    driverToken: window.AppState?.driverAccessToken || '',
    driverPin: typeof window.AppState?.driverPin === 'string' ? window.AppState.driverPin : '',
  };
}

function _woReceiptErr(err) {
  const code = String(err?.code || '');
  if (code.includes('internal') || !err?.message) return "Couldn't save that receipt — try again";
  return err.message;
}

async function openWoReceipts(taskId) {
  const gen = ++WoReceipts.gen;
  WoReceipts.taskId = taskId;
  WoReceipts.rows = [];
  WoReceipts.busy = 0;
  renderWoReceipts();
  try {
    const res = await httpsCallable(functions, 'listDriverWorkOrderReceipts')({ ..._woDriverCreds(), maintenanceTaskId: taskId });
    if (gen !== WoReceipts.gen) return;
    // Merge, don't replace: a receipt added (or a correction typed) while this
    // list was loading must survive it (COD Code Reviewer).
    const have = new Set(WoReceipts.rows.filter((r) => r.receiptId).map((r) => r.receiptId));
    const listed = (res.data?.receipts || []).filter((r) => !have.has(r.receiptId)).map((r) => ({ ...r, dirty: false }));
    WoReceipts.rows = [...listed, ...WoReceipts.rows];
    renderWoReceipts();
  } catch (err) {
    // A failed list is not "no receipts": say so; Add receipt still works.
    if (gen !== WoReceipts.gen) return;
    console.warn('[Maintenance-Driver] receipts list failed:', err?.code || err?.message);
    renderWoReceipts("Couldn't load the receipts you already added");
  }
}

function renderWoReceipts(note) {
  const box = document.getElementById('wo-receipt-list');
  if (!box) return;
  const inputStyle = 'width:100%;min-height:48px;font-size:16px;';
  const rows = WoReceipts.rows.map((r, i) => {
    if (r.pending) {
      return `<div class="wo-receipt-row wo-receipt-pending" style="border:1px solid var(--border-light);border-radius:10px;padding:10px 12px;margin-bottom:8px;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;vertical-align:-4px;">hourglass_top</span> Reading receipt…
        </div>`;
    }
    const status = r.reviewed ? 'Checked by the office' : (r.readStatus === 'read' ? 'Check the store and amount' : 'Type the store and amount');
    const ro = r.editable ? '' : ' disabled';
    return `<div class="wo-receipt-row" data-receipt-id="${escapeHtml(r.receiptId)}" style="border:1px solid var(--border-light);border-radius:10px;padding:10px 12px;margin-bottom:8px;">
        <div style="font-size:0.85rem;font-weight:600;color:var(--text-primary);margin-bottom:6px;">
          <span class="material-icons" style="font-size:18px;vertical-align:-4px;color:${r.reviewed ? '#059669' : 'var(--brand-blue)'};">${r.reviewed ? 'check_circle' : 'receipt_long'}</span> ${escapeHtml(status)}
        </div>
        <div style="display:grid;grid-template-columns:2fr 1fr;gap:8px;">
          <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);">Store / shop
            <input type="text" class="form-control wo-receipt-vendor" maxlength="200" value="${escapeHtml(r.vendor || '')}" placeholder="Who you paid" style="${inputStyle}"${ro}
                   oninput="window.MaintenanceModule.editWoReceipt(${i}, 'vendor', this.value)">
          </label>
          <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);">Amount
            <input type="number" class="form-control wo-receipt-total" step="0.01" min="0.01" max="100000" inputmode="decimal" value="${r.total != null ? escapeHtml(r.total) : ''}" placeholder="0.00" style="${inputStyle}"${ro}
                   oninput="window.MaintenanceModule.editWoReceipt(${i}, 'total', this.value)">
          </label>
        </div>
      </div>`;
  }).join('');
  box.innerHTML = rows + (note ? `<div class="wo-receipt-note" style="font-size:0.85rem;color:#B91C1C;margin-bottom:8px;">${escapeHtml(note)}</div>` : '');
  const add = document.getElementById('btn-wo-receipt-add');
  if (add) add.disabled = WoReceipts.busy > 0;
}

function editWoReceipt(i, field, value) {
  const r = WoReceipts.rows[i];
  if (!r || !r.editable || (field !== 'vendor' && field !== 'total')) return;
  r[field] = value;
  r.dirty = true;
}

// Shrink a camera photo to ~2000px JPEG so it fits one callable request.
async function _woShrinkPhoto(file) {
  try {
    if (typeof createImageBitmap !== 'function') return file;
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 2.5 * 1024 * 1024 && file.type === 'image/jpeg') { bmp.close?.(); return file; }
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close?.();
    const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.85));
    return blob ? new File([blob], 'receipt.jpg', { type: 'image/jpeg' }) : file;
  } catch (_) {
    return file;
  }
}

async function addWoReceipt(input) {
  let file = input.files?.[0];
  input.value = '';
  if (!file || !WoReceipts.taskId) return;
  const gen = WoReceipts.gen;
  const taskId = WoReceipts.taskId;
  const placeholder = { pending: true };
  WoReceipts.rows.push(placeholder);
  WoReceipts.busy++;
  renderWoReceipts();
  let note = '';
  try {
    // HEIC → JPEG first (the converter every other photo upload uses).
    const prepared = await prepareUploadFile(file);
    file = await _woShrinkPhoto(prepared.file);
    if (file.size > 7 * 1024 * 1024) throw Object.assign(new Error('That photo is too big — take it again a little farther away'), { code: 'invalid-argument' });
    const imageBase64 = await window.FileReadUtil.readAsBase64(file);
    const res = await httpsCallable(functions, 'ingestDriverWorkOrderReceipt')({
      ..._woDriverCreds(), maintenanceTaskId: taskId, imageBase64, mimeType: file.type || 'image/jpeg',
    });
    if (gen !== WoReceipts.gen) return;
    const d = res.data || {};
    if (d.duplicate) {
      note = d.receipt ? 'You already added that receipt' : 'The office already has that receipt';
    } else if (d.receipt) {
      WoReceipts.rows.splice(WoReceipts.rows.indexOf(placeholder), 0, { ...d.receipt, dirty: false });
      if (d.readError) note = d.readError;
      window.showToast?.('Receipt added', 'success');
    }
  } catch (err) {
    if (gen !== WoReceipts.gen) return;
    note = err?.userMessage || _woReceiptErr(err);
  } finally {
    if (gen === WoReceipts.gen) {
      const at = WoReceipts.rows.indexOf(placeholder);
      if (at >= 0) WoReceipts.rows.splice(at, 1);
      WoReceipts.busy = Math.max(0, WoReceipts.busy - 1);
      renderWoReceipts(note);
    }
  }
}

// Saves the driver's corrections (store / amount). A receipt problem NEVER
// blocks closing the repair (COD Domain: the work-order record matters more,
// 49 CFR 396.3) — the receipt is already filed; a correction that won't save
// is reported and the office checks the photo. Returns how many failed.
async function saveWoReceiptEdits() {
  const dirty = WoReceipts.rows.filter((r) => r.dirty && r.editable && r.receiptId);
  let failed = 0;
  for (const r of dirty) {
    try {
      const res = await httpsCallable(functions, 'saveDriverWorkOrderReceipt')({
        ..._woDriverCreds(), receiptId: r.receiptId, vendor: String(r.vendor || ''), total: r.total,
      });
      Object.assign(r, res.data?.receipt || {}, { dirty: false });
    } catch (err) {
      failed++;
      console.warn('[Maintenance-Driver] receipt correction not saved:', err?.code || err?.message);
    }
  }
  return failed;
}

async function driverConfirmCompletion(e) {
  e.preventDefault();
  const taskId = document.getElementById('complete-task-id').value;
  const completionNotes = document.getElementById('completion-notes').value.trim();

  // A receipt still reading keeps going on the server (the row is filed before
  // the read), so completion never waits on it.
  const btn = document.getElementById('btn-wo-complete');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  const receiptFails = await saveWoReceiptEdits();
  if (btn) { btn.disabled = false; btn.textContent = 'Mark Complete'; }
  if (receiptFails > 0) window.showToast?.("A receipt change didn't save — the office will check the photo", 'info');

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    const updateData = {
      status: 'pending_review',
      driverCompletedAt: Timestamp.now(),
      driverCompletedBy: getDriverPersonName(),
      driverCompletedByPersonId: getDriverPersonId(),
      updatedAt: Timestamp.now()
    };

    if (completionNotes) {
      updateData.completionNotes = completionNotes;
    }

    const _auditBefore = { ...(MaintenanceState.tasks.find(t => t.id === taskId) || { id: taskId }) };
    await updateDoc(doc(db, 'tenants', tenant, 'maintenance', taskId), updateData);
    closeModal('complete');
    filterTasks('pending_review');
    window.showToast?.('Submitted for review', 'success');
    // TASK-9766 (c)
    await auditTaskStatus(_auditBefore, _auditBefore.status || null, 'pending_review', 'submit_review',
      { signOffName: updateData.driverCompletedBy });
  } catch (error) {
    console.error('[Maintenance-Driver] Error completing task:', error);
    window.showToast?.('Error completing task', 'error');
  }
}

// ============================================
// HTML TEMPLATE
// ============================================
function getMaintenanceHTML() {
  return `
    <div class="maintenance-view">
      <!-- Header -->
      <div style="text-align:center;margin-bottom:var(--spacing-sm);">
        <h1 style="margin:0;font-size:1.5rem;font-weight:700;">Maintenance</h1>
      </div>
      <div class="maintenance-header">
        <div class="maintenance-header-actions" style="justify-content:center;width:100%;display:flex;gap:8px;flex-wrap:wrap;">
          <!-- TASK-9657 — Claude requests waiting for THIS person's approval. Hidden
               until window.ApprovalsBadge paints a count > 0; tap opens the approval
               list (#/account?claude=requests). The driver view (getDriverMaintenanceHTML)
               has no such button, and the badge never paints for PIN/link sessions. -->
          <button type="button" class="btn btn-secondary dd-approvals-btn" data-approvals-badge hidden style="display:none;">
            <span class="material-icons">pending_actions</span> <span class="dd-approvals-label">Approvals</span> <span class="dd-approvals-count"></span>
          </button>
          <!-- TASK-9898 S4 — New Task is the only primary. PM Settings, Shop Costs
               and Inventory live in one "Fleet tools" menu. -->
          <div class="fleet-tools" style="position:relative;display:inline-block;">
            <button type="button" id="btn-fleet-tools" class="btn btn-secondary btn-neon-settings btn-module-header" style="min-height:44px;"
              aria-haspopup="true" aria-expanded="false" aria-controls="fleet-tools-menu"
              onclick="event.stopPropagation(); window.MaintenanceModule.toggleFleetTools()">
              <span class="material-icons">settings</span> Fleet tools <span class="material-icons" style="font-size:18px;">expand_more</span>
            </button>
            <div id="fleet-tools-menu" class="fleet-tools-menu" role="menu" hidden
              style="position:absolute;top:calc(100% + 4px);left:0;z-index:50;min-width:200px;padding:4px 0;background:var(--bg-primary, #fff);border:1px solid var(--border-light, #E5E7EB);border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.12);">
              <button type="button" role="menuitem" class="fleet-tools-item" style="display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:8px 14px;border:0;background:none;cursor:pointer;text-align:left;color:var(--text-primary);font-size:0.9rem;"
                onclick="window.MaintenanceModule.closeFleetTools(); window.MaintenanceModule.showPMSettings()">
                <span class="material-icons" style="font-size:18px;">tune</span> PM Settings
              </button>
              <button type="button" role="menuitem" class="fleet-tools-item" style="display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:8px 14px;border:0;background:none;cursor:pointer;text-align:left;color:var(--text-primary);font-size:0.9rem;"
                onclick="window.MaintenanceModule.closeFleetTools(); window.MaintenanceModule.showShopCostHistory()" title="View and add shop costs">
                <span class="material-icons" style="font-size:18px;">store</span> Shop Costs
              </button>
              <!-- TASK-9335 — every past count on every truck and trailer. Hidden until
                   any vehicle has inventory on OR any count/purchase exists
                   (updateFleetInventoryButton). -->
              <button type="button" role="menuitem" id="btn-fleet-inventory" class="fleet-tools-item hidden" style="display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:8px 14px;border:0;background:none;cursor:pointer;text-align:left;color:var(--text-primary);font-size:0.9rem;"
                onclick="window.MaintenanceModule.closeFleetTools(); window.MaintenanceModule.showFleetInventory()" title="Inventory counts and purchases across the fleet">
                <span class="material-icons" style="font-size:18px;">inventory_2</span> Inventory
              </button>
              ${/* TASK-10135 — per type / per unit inspection checklists live on Inspections; this opens them there (same gate as that button). */ window.PretripModule?.canManageInspectionTemplates?.() ? `<button type="button" role="menuitem" class="fleet-tools-item" style="display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:8px 14px;border:0;background:none;cursor:pointer;text-align:left;color:var(--text-primary);font-size:0.9rem;"
                onclick="window.MaintenanceModule.closeFleetTools(); window.PretripModule.pointToInspectionTemplates()" title="Opens on Inspections">
                <span class="material-icons" style="font-size:18px;">fact_check</span> Unit Checklists
              </button>` : ''}
            </div>
          </div>
          <button id="btn-new-task" class="btn btn-primary" onclick="window.MaintenanceModule.showNewTask()">
            <span class="material-icons">add</span> New Task
          </button>
        </div>
      </div>

      <!-- Main Tabs -->
      <div class="maintenance-tabs">
        <button class="maintenance-tab active" data-tab="fleet" onclick="window.MaintenanceModule.switchTab('fleet')">
          <span class="material-icons">local_shipping</span>
          Fleet Status
        </button>
        <button class="maintenance-tab" data-tab="tasks" onclick="window.MaintenanceModule.switchTab('tasks')">
          <span class="material-icons">assignment</span>
          Work Orders <span class="tab-count count-zero" id="tab-count-tasks">0</span>
        </button>
        <button class="maintenance-tab" data-tab="holds" onclick="window.MaintenanceModule.switchTab('holds')">
          <span class="material-icons">block</span>
          Vehicle Holds <span class="tab-count count-zero" id="tab-count-holds">0</span>
        </button>
        <button class="maintenance-tab" data-tab="citations" onclick="window.MaintenanceModule.switchTab('citations')">
          <span class="material-icons">gavel</span>
          Citations <span class="tab-count count-zero" id="tab-count-citations">0</span>
        </button>
        <!-- TASK-4884 — fixed shop equipment inspections (window.FacilityInspections). -->
        <button class="maintenance-tab" data-tab="inspections" onclick="window.MaintenanceModule.switchTab('inspections')">
          <span class="material-icons">local_fire_department</span>
          Inspections <span class="tab-count count-zero" id="tab-count-inspections">0</span>
        </button>
        <!-- TASK-10148 — suggested missed downtime (lazy views/maintDowntimeAudit.js); not on a driver login. -->
        ${isDriverPinMode() ? '' : `<button class="maintenance-tab" data-tab="downtime" onclick="window.MaintenanceModule.switchTab('downtime')">
          <span class="material-icons">event_busy</span>
          Downtime Audit
        </button>`}
      </div>

      <!-- Fleet Status Tab -->
      <div class="maintenance-tab-content active" id="tab-fleet">
        <!-- Asset Type Toggle (Vehicles / Trailers) -->
        <div class="fleet-type-toggle">
          <button class="fleet-type-btn active" data-type="vehicles" onclick="window.MaintenanceModule.switchAssetType('vehicles')">
            <span class="material-icons">local_shipping</span>
            Vehicles <span class="fleet-type-count" id="vehicles-toggle-count">–</span>
          </button>
          <button class="fleet-type-btn" data-type="trailers" onclick="window.MaintenanceModule.switchAssetType('trailers')">
            <span class="material-icons">rv_hookup</span>
            Trailers <span class="fleet-type-count" id="trailers-toggle-count">–</span>
          </button>
          <button class="fleet-type-btn" data-type="assets" onclick="window.MaintenanceModule.switchAssetType('assets')">
            <span class="material-icons">handyman</span>
            Assets <span class="fleet-type-count" id="assets-toggle-count">–</span>
          </button>
        </div>

        <!-- Vehicles View -->
        <div id="vehicles-view" class="fleet-asset-view">
          <!-- Fleet Filter Bar -->
          <div class="fleet-filter-bar">
            <div class="fleet-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-fleet" placeholder="Search vehicles..." autocomplete="off" oninput="window.MaintenanceModule.filterFleet()">
            </div>
            <select id="filter-fleet-type" onchange="window.MaintenanceModule.filterFleet()">
            </select>
            <select id="filter-fleet-expiry" title="Expiring = within 30 days or overdue. Vehicles with no date on file are not shown — set expiry dates in Roster to track them." onchange="window.MaintenanceModule.filterFleet()">
              <option value="all">All Documents</option>
              <option value="reg">Registration expiring</option>
              <option value="inspection">Annual inspection expiring</option>
            </select>
            <select id="sort-fleet" title="Sort vehicles" onchange="window.MaintenanceModule.filterFleet()">
              <option value="unit"${getStoredFleetSort() === 'unit' ? ' selected' : ''}>Sort: Unit</option>
              <option value="pmdue"${getStoredFleetSort() === 'pmdue' ? ' selected' : ''}>Sort: Closest PM Due</option>
            </select>
          </div>

          <!-- TASK-9898 S4: count tiles (whole fleet, filters ignored); tap = filter.
               Replaces the Status select. -->
          <div id="fleet-status-tiles" class="fleet-status-tiles" role="group" aria-label="Filter vehicles by status"
            style="display:grid;grid-template-columns:repeat(auto-fit,minmax(104px,1fr));gap:8px;margin:0 0 var(--spacing-md, 12px);"></div>

          <!-- TASK-9898: fleet-wide readings to check + default-interval line -->
          <div id="fleet-readings-strip" style="display:none;"></div>
          <div id="fleet-default-interval-line" style="display:none;"></div>

          <!-- Needs Attention Section -->
          <div id="fleet-attention-section" class="fleet-attention-section hidden">
            <div class="fleet-section-header">
              <span class="material-icons">warning</span>
              <h3>Needs Attention</h3>
              <span class="fleet-section-count" id="attention-count">0</span>
            </div>
            <div class="fleet-grid fleet-grid-attention" id="fleet-grid-attention"></div>
          </div>

          <!-- All Vehicles Section -->
          <div id="fleet-all-section">
            <div class="fleet-section-header fleet-section-header-all" id="fleet-all-header">
              <span class="material-icons">local_shipping</span>
              <h3>All Vehicles</h3>
              <span class="fleet-section-count" id="all-count">0</span>
            </div>
            <div class="fleet-grid" id="fleet-grid"></div>
          </div>
        </div>

        <!-- Trailers View -->
        <div id="trailers-view" class="fleet-asset-view" style="display:none;">
          <!-- Trailer Filter Bar -->
          <div class="fleet-filter-bar">
            <div class="fleet-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-trailers" placeholder="Search trailers..." autocomplete="off" oninput="window.MaintenanceModule.filterTrailers()">
            </div>
            <select id="filter-trailer-type" onchange="window.MaintenanceModule.filterTrailers()">
            </select>
            <select id="filter-trailer-status" onchange="window.MaintenanceModule.filterTrailers()">
              <option value="all">All Status</option>
              <option value="attention">Needs Attention</option>
              <option value="ok">OK</option>
              <option value="hold">On Hold</option>
            </select>
          </div>

          <!-- Trailer Grid -->
          <div class="fleet-grid" id="trailer-grid"></div>
        </div>

        <!-- Assets View -->
        <div id="assets-view" class="fleet-asset-view" style="display:none;">
          <!-- Asset Filter Bar -->
          <div class="fleet-filter-bar">
            <div class="fleet-search">
              <span class="material-icons">search</span>
              <input type="search" id="search-assets" placeholder="Search assets..." autocomplete="off" oninput="window.MaintenanceModule.filterAssets()">
            </div>
            <select id="filter-asset-category" onchange="window.MaintenanceModule.filterAssets()">
              <option value="all">All Categories</option>
              <option value="drill_pipe">Drill Pipe</option>
              <option value="equipment">Equipment</option>
              <option value="tool">Tool</option>
              <option value="other">Other</option>
            </select>
          </div>
          <!-- Asset Grid -->
          <div class="fleet-grid" id="asset-grid"></div>
        </div>
      </div>

      <!-- Work Orders Tab -->
      <div class="maintenance-tab-content" id="tab-tasks">
        <!-- Filter Tabs -->
        <div class="maintenance-filters">
          <button class="maintenance-filter active" data-filter="open" onclick="window.MaintenanceModule.filterTasks('open')">
            <span class="material-icons">pending</span>
            Open <span class="filter-count count-zero" id="count-open">0</span>
          </button>
          <button class="maintenance-filter" data-filter="in_progress" onclick="window.MaintenanceModule.filterTasks('in_progress')">
            <span class="material-icons">build</span>
            In Progress <span class="filter-count count-zero" id="count-in-progress">0</span>
          </button>
          <button class="maintenance-filter" data-filter="pending_review" onclick="window.MaintenanceModule.filterTasks('pending_review')">
            <span class="material-icons">rate_review</span>
            Needs Review <span class="filter-count count-zero" id="count-pending-review">0</span>
          </button>
          <button class="maintenance-filter" data-filter="completed" onclick="window.MaintenanceModule.filterTasks('completed')">
            <span class="material-icons">check_circle</span>
            Completed <span class="filter-count count-zero" id="count-completed">0</span>
          </button>
        </div>

        <!-- TASK-9897 — work-order filter bar. ONE DOM for desktop and phone: on a
             phone #wo-filter-panel becomes a bottom sheet (CSS), so no control is
             duplicated and no two controls share an id (that killed billing's
             mobile filter). New chips use .wo-chip, never .filter-chip /
             .maintenance-filter — filterTasks toggles .active on those
             document-wide, the driver PIN bar included. The dynamic option lists
             are filled by woRenderFilterChrome from the docs on screen. -->
        <div class="fleet-filter-bar wo-filter-bar" id="wo-filter-bar">
          <div class="fleet-search">
            <span class="material-icons">search</span>
            <input type="search" id="wo-search" placeholder="Search work orders..." autocomplete="off"
              aria-label="Search work orders"
              oninput="window.MaintenanceModule.woSetFilter('search', this.value)">
          </div>
          <button type="button" class="btn btn-secondary wo-filters-toggle" id="wo-filters-toggle"
            onclick="window.MaintenanceModule.woOpenSheet()">
            <span class="material-icons">tune</span> <span id="wo-filters-toggle-label">Filters</span>
          </button>
          <div class="wo-sheet-backdrop" id="wo-sheet-backdrop" onclick="window.MaintenanceModule.woCloseSheet()"></div>
          <div class="wo-filter-panel" id="wo-filter-panel" role="group" aria-label="Work order filters">
            <div class="wo-sheet-head">
              <strong>Filter work orders</strong>
              <button type="button" class="btn btn-icon" aria-label="Close filters" onclick="window.MaintenanceModule.woCloseSheet()">
                <span class="material-icons">close</span>
              </button>
            </div>
            <details class="wo-vehicle-picker" id="wo-vehicle-picker">
              <summary id="wo-vehicle-summary">All vehicles</summary>
              <div class="wo-vehicle-list" id="wo-vehicle-list"></div>
            </details>
            <select id="wo-type" aria-label="Type" onchange="window.MaintenanceModule.woSetFilter('type', this.value)"><option value="">All types</option></select>
            <select id="wo-priority" aria-label="Priority" onchange="window.MaintenanceModule.woSetFilter('priority', this.value)"><option value="">All priorities</option></select>
            <select id="wo-sort" aria-label="Sort" onchange="window.MaintenanceModule.woSetSort(this.value)">
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="cost">Cost high → low</option>
              <option value="vehicle">Vehicle A → Z</option>
            </select>
            <button type="button" class="btn btn-secondary wo-more-toggle" id="wo-more-toggle" aria-expanded="false"
              onclick="window.MaintenanceModule.woToggleMore()">
              <span class="material-icons">filter_list</span> More filters
            </button>
            <div class="wo-more" id="wo-more">
              <label class="wo-field"><span>Created from</span>
                <input type="date" id="wo-created-from" onchange="window.MaintenanceModule.woSetFilter('createdFrom', this.value)"></label>
              <label class="wo-field"><span>Created to</span>
                <input type="date" id="wo-created-to" onchange="window.MaintenanceModule.woSetFilter('createdTo', this.value)"></label>
              <label class="wo-field wo-completed-only"><span>Completed from</span>
                <input type="date" id="wo-completed-from" onchange="window.MaintenanceModule.woSetFilter('completedFrom', this.value)"></label>
              <label class="wo-field wo-completed-only"><span>Completed to</span>
                <input type="date" id="wo-completed-to" onchange="window.MaintenanceModule.woSetFilter('completedTo', this.value)"></label>
              <label class="wo-field"><span>Reported by</span>
                <select id="wo-reported-by" onchange="window.MaintenanceModule.woSetFilter('reportedBy', this.value)"><option value="">Anyone</option></select></label>
              <label class="wo-field"><span>Completed by</span>
                <select id="wo-completed-by" onchange="window.MaintenanceModule.woSetFilter('completedBy', this.value)"><option value="">Anyone</option></select></label>
              <label class="wo-field"><span>Assigned to</span>
                <select id="wo-assigned-to" onchange="window.MaintenanceModule.woSetFilter('assignedTo', this.value)"><option value="">Anyone</option></select></label>
              <label class="wo-field"><span>Cost</span>
                <select id="wo-cost" onchange="window.MaintenanceModule.woSetFilter('cost', this.value)">
                  <option value="">Any cost</option>
                  <option value="has">Has cost</option>
                  <option value="none">No cost entered</option>
                </select></label>
            </div>
            <div class="wo-sheet-foot">
              <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.woClearFilters()">Clear filters</button>
              <button type="button" class="btn btn-primary" id="wo-sheet-done" onclick="window.MaintenanceModule.woCloseSheet()">Show results</button>
            </div>
          </div>
        </div>
        <!-- Active filters: a chip per filter + Clear, the filtered-state line, and the
             hidden-critical warning. Status tab counts above stay UNFILTERED. -->
        <div class="wo-active-filters hidden" id="wo-active-filters"></div>

        <!-- Tasks List -->
        <div class="maintenance-list" id="maintenance-list">
          <div class="loading-state"><div class="loading-spinner"></div><p>Loading maintenance...</p></div>
        </div>
      </div>

      <!-- Vehicle Holds Tab -->
      <div class="maintenance-tab-content" id="tab-holds">
        <div class="holds-header">
          <p class="holds-description">Vehicle holds mark trucks or trailers as unavailable for dispatch during specific date ranges.</p>
          <button class="btn btn-primary" onclick="window.MaintenanceModule.showNewHold()">
            <span class="material-icons">add</span> New Hold
          </button>
        </div>
        <div class="holds-list" id="holds-list">
          <div style="text-align:center;padding:var(--spacing-xl);">
            <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
            <div style="color:var(--text-secondary);font-size:0.875rem;">Loading holds...</div>
          </div>
        </div>
      </div>

      <!-- Citations Tab -->
      <div class="maintenance-tab-content" id="tab-citations">
        <div class="holds-header" style="margin-bottom:16px;">
          <p class="holds-description">DOT Vehicle Exam Reports from roadside inspections. Track violations, Out of Service orders, and 15-day repair deadlines.</p>
        </div>
        <!-- Summary Cards -->
        <div id="citations-summary" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:20px;"></div>
        <!-- Citations List -->
        <div id="citations-list">
          <div class="empty-state">
            <div class="empty-state-icon">✓</div>
            <div class="empty-state-title">No exam reports</div>
            <div class="empty-state-text">Vehicle exam reports uploaded by drivers will appear here.</div>
          </div>
        </div>
      </div>

      <!-- TASK-4884 — Inspections Tab (fixed shop equipment). Rendered by
           window.FacilityInspections (public/src/facilityInspections.js) on
           first visit; rules gate it by the maintenance MODULE. -->
      <div class="maintenance-tab-content" id="tab-inspections">
        <div id="facility-inspections-host"></div>
      </div>
      <div class="maintenance-tab-content" id="tab-downtime"><div id="downtime-audit-host"></div></div>

    </div>

    <!-- Update Meters Modal -->
    <!-- TASK-9898 S3 — Vehicle panel (tap a card). FIRST of the module's modals
         so every modal it opens (History, DVIRs, Log PM ...) stacks over it. -->
    <div class="modal hidden" id="modal-vehicle-panel">
      <div class="modal-content modal-fullscreen" style="max-width:560px;width:100%;">
        <div class="modal-header">
          <h3 id="vehicle-panel-title" style="min-width:0;overflow-wrap:anywhere;">Vehicle</h3>
          <button class="btn-icon" style="min-width:44px;min-height:44px;" aria-label="Close" onclick="window.MaintenanceModule.closeModal('vehicle-panel')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="vehicle-panel-body"></div>
      </div>
    </div>

    <div class="modal hidden" id="modal-meters">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3 id="modal-meters-title">Update Meters</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('meters')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-meters" novalidate onsubmit="window.MaintenanceModule.saveMeters(event)">
          <input type="hidden" id="meters-truck-id">

          <div class="meters-current" id="meters-current">
            <!-- Filled dynamically -->
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="meters-miles">Current Miles</label>
              <input type="number" id="meters-miles" step="0.1" min="0" placeholder="45230.5">
            </div>
            <div class="form-group">
              <label for="meters-hours">Current Hours</label>
              <input type="number" id="meters-hours" step="0.1" min="0" placeholder="1234.5">
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('meters')">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Meters</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Log PM Service Modal -->
    <div class="modal hidden" id="modal-pm">
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="modal-pm-title">Log PM Service</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('pm')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-pm" novalidate onsubmit="window.MaintenanceModule.savePMService(event)">
          <input type="hidden" id="pm-truck-id">

          <div class="pm-info" id="pm-info">
            <!-- Filled dynamically -->
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="pm-miles">Miles at PM</label>
              <input type="number" id="pm-miles" step="0.1" min="0">
            </div>
            <div class="form-group">
              <label for="pm-hours">Hours at PM</label>
              <input type="number" id="pm-hours" step="0.1" min="0">
            </div>
          </div>

          <div class="form-group">
            <label>PM Items Completed</label>
            <div class="pm-checklist" id="pm-checklist">
              <!-- Dynamically populated by showLogPM() -->
            </div>
          </div>

          <div class="form-group">
            <label for="pm-notes">Notes</label>
            <textarea id="pm-notes" rows="2" placeholder="Additional notes..."></textarea>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('pm')">Cancel</button>
            <button type="submit" class="btn btn-primary">Log PM Service</button>
          </div>
        </form>
      </div>
    </div>

    <!-- PM Settings Modal (dynamically populated) -->
    <div class="modal hidden" id="modal-pm-settings">
      <div class="modal-content" style="max-width:480px;">
        <div class="modal-header">
          <h3>PM Schedule</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closePMSettings()" style="color:var(--text-secondary);">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-pm-settings" novalidate onsubmit="window.MaintenanceModule.savePMSettings(event)">
          <div style="padding:var(--spacing-lg);max-height:65vh;max-height:65dvh;overflow-y:auto;">

            <!-- Configured type overrides -->
            <div id="pm-type-list" style="display:flex;flex-direction:column;gap:8px;">
              <!-- Dynamically populated by showPMSettings() -->
            </div>

            <!-- Add type dropdown -->
            <div id="pm-add-type-row" style="margin-top:12px;">
              <button type="button" class="btn btn-small btn-secondary" onclick="window.MaintenanceModule.showPMTypeDropdown()" id="btn-pm-add-type" style="border-radius:8px;font-size:0.8rem;">
                <span class="material-icons" style="font-size:16px;">add</span> Add Vehicle Type
              </button>
              <div id="pm-type-dropdown-row" style="display:none;margin-top:8px;display:none;align-items:center;gap:8px;">
                <select id="pm-type-selector" style="flex:1;padding:8px 12px;border:1px solid var(--border-light);border-radius:8px;font-size:0.85rem;">
                </select>
                <button type="button" class="btn btn-small btn-primary" onclick="window.MaintenanceModule.addPMTypeOverride()" style="border-radius:8px;white-space:nowrap;">Add</button>
              </div>
            </div>

            <!-- Baselines -->
            <div style="margin-top:var(--spacing-lg);padding-top:var(--spacing-md);border-top:1px solid var(--border-light);">
              <label style="font-size:0.75rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:block;">Baselines</label>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 16px;">
                <div class="form-group" style="margin-bottom:0;">
                  <label for="pm-miles-default" style="font-size:0.85rem;">Default (miles)</label>
                  <input type="number" id="pm-miles-default" min="1000" step="500" value="5000">
                  <span class="form-hint" style="font-size:0.7rem;">Catch-all for types without overrides</span>
                </div>
                <div class="form-group" style="margin-bottom:0;">
                  <label for="pm-hours-setting" style="font-size:0.85rem;">All Vehicles (hours)</label>
                  <input type="number" id="pm-hours-setting" min="50" step="25" value="250">
                </div>
              </div>
            </div>

            <!-- PM Checklist Items -->
            <div style="margin-top:var(--spacing-lg);padding-top:var(--spacing-md);border-top:1px solid var(--border-light);">
              <label style="font-size:0.75rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:block;">PM Checklist Items</label>
              <div id="pm-checklist-settings" style="display:flex;flex-direction:column;gap:4px;">
                <!-- Dynamically populated -->
              </div>
              <div style="display:flex;gap:8px;margin-top:8px;">
                <input type="text" id="pm-checklist-new-item" placeholder="New item name..." style="flex:1;padding:8px 12px;border:1px solid var(--border-light);border-radius:8px;font-size:0.85rem;">
                <button type="button" class="btn btn-small btn-primary" onclick="window.MaintenanceModule.addPMChecklistItem()" style="border-radius:8px;white-space:nowrap;padding:8px 12px;">
                  <span class="material-icons" style="font-size:16px;">add</span> Add
                </button>
              </div>
            </div>
          </div>

          <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closePMSettings()" style="border-radius:8px;">Cancel</button>
            <button type="submit" class="btn btn-primary" style="border-radius:8px;">Save Settings</button>
          </div>
        </form>
      </div>
    </div>

    <!-- New/Edit Task Modal -->
    <div class="modal hidden" id="modal-task">
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="modal-task-title">New Maintenance Task</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('task')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-task" novalidate onsubmit="window.MaintenanceModule.saveTask(event)" style="display:flex;flex-direction:column;overflow:hidden;flex:1;min-height:0;">
          <input type="hidden" id="task-id">
          <div class="modal-body">

          <div class="form-row">
            <div class="form-group">
              <label for="task-asset-type">Asset Type *</label>
              <select id="task-asset-type" required onchange="window.MaintenanceModule.updateTaskAssetDropdown()">
                <option value="truck">Vehicle</option>
                <option value="trailer">Trailer</option>
                <option value="asset">Asset</option>
              </select>
            </div>
            <div class="form-group">
              <label for="task-asset">Vehicle *</label>
              <select id="task-asset" required>
                <option value="">Select vehicle</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="task-category">Category *</label>
            <select id="task-category" required onchange="window.MaintenanceModule.onTaskCategoryChange()">
              <option value="PM Service">PM Service</option>
              <option value="Repair">Repair</option>
              <option value="DVIR Defect">DVIR Defect</option>
              <option value="Pretrip Note">Pretrip Note</option>
              <option value="Tire">Tire</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div class="form-group">
            <label for="task-description">Description *</label>
            <textarea id="task-description" rows="3" required placeholder="Describe the maintenance needed..."></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="task-priority">Priority</label>
              <select id="task-priority">
                <option value="low">Low</option>
                <option value="normal" selected>Normal</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div class="form-group">
              <label for="task-assigned">Assigned To</label>
              <select id="task-assigned">
                <option value="">Unassigned</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="task-vendor">Vendor / Service Company</label>
            <input type="text" id="task-vendor" placeholder="e.g., Bakken Fleet Services" maxlength="200">
          </div>

          <div id="task-meters-row" class="form-row" style="display:none;">
            <div class="form-group">
              <label for="task-miles">Current Mileage</label>
              <input type="number" id="task-miles" placeholder="e.g. 125000" min="0" step="1">
            </div>
            <div class="form-group">
              <label for="task-hours">Current Hours</label>
              <input type="number" id="task-hours" placeholder="e.g. 4500" min="0" step="0.1">
            </div>
          </div>

          <div class="form-group">
            <label for="task-notes">Notes</label>
            <textarea id="task-notes" rows="2" placeholder="Additional notes..."></textarea>
          </div>

          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('task')">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Task</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Task Detail Modal -->
    <div class="modal hidden" id="modal-task-detail">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Task Details</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('task-detail')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="task-detail-body">
          <!-- Populated dynamically -->
        </div>
      </div>
    </div>

    <!-- DVIR History Modal -->
    <div class="modal hidden" id="modal-dvir-history">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-dvir-title">DVIR History</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('dvir-history')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="dvir-history-nav">
            <div class="dvir-day-buttons" id="dvir-day-buttons">
              <!-- Day buttons populated dynamically -->
            </div>
            <div class="dvir-date-range">
              <input type="date" id="dvir-start-date" onchange="window.MaintenanceModule.applyDVIRDateRange()">
              <span>to</span>
              <input type="date" id="dvir-end-date" onchange="window.MaintenanceModule.applyDVIRDateRange()">
            </div>
            <button class="btn btn-sm btn-primary" onclick="window.MaintenanceModule.showBulkPdfModal()">
              <span class="material-icons">print</span> Bulk Print
            </button>
          </div>
          <div class="dvir-history-list" id="dvir-history-list">
            <!-- DVIR items populated dynamically -->
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk PDF Modal -->
    <div class="modal hidden" id="modal-bulk-pdf">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Bulk Print DVIRs</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('bulk-pdf')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <p class="form-hint">Select a date range to generate PDFs for all DVIRs in that period.</p>
          <div class="form-row">
            <div class="form-group">
              <label for="bulk-pdf-start">Start Date</label>
              <input type="date" id="bulk-pdf-start" required>
            </div>
            <div class="form-group">
              <label for="bulk-pdf-end">End Date</label>
              <input type="date" id="bulk-pdf-end" required>
            </div>
          </div>
          <div id="bulk-pdf-preview" class="bulk-pdf-preview"></div>
          <div id="bulk-pdf-progress" class="bulk-pdf-progress hidden">
            <div class="progress-bar"><div class="progress-fill" id="bulk-pdf-progress-fill"></div></div>
            <div class="progress-text" id="bulk-pdf-progress-text">Preparing...</div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('bulk-pdf')">Cancel</button>
          <button type="button" class="btn btn-primary" id="btn-bulk-pdf-generate" onclick="window.MaintenanceModule.generateBulkPdfs()">
            <span class="material-icons">picture_as_pdf</span> Generate PDFs
          </button>
        </div>
      </div>
    </div>

    <!-- Photo Lightbox Modal -->
    <div class="modal hidden" id="modal-dvir-photos">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-photos-title">DVIR Photos</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('dvir-photos')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="dvir-photos-grid" id="dvir-photos-grid">
            <!-- Photos populated dynamically -->
          </div>
        </div>
      </div>
    </div>

    <!-- Work Order Completion Modal -->
    <div class="modal hidden" id="modal-complete-task">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Finalize Work Order</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('complete-task')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-complete-task" novalidate onsubmit="window.MaintenanceModule.confirmCompletion(event)">
          <input type="hidden" id="complete-task-id">

          <p class="form-hint" style="margin-bottom: var(--spacing-md);">Sign off on the completed work. You'll be able to add costs & receipts next.</p>

          <div class="form-group">
            <label for="completed-by-name">Completed By *</label>
            <select id="completed-by-name" class="form-control" required>
              <option value="">Select person...</option>
            </select>
          </div>

          <div class="form-group">
            <label for="completion-notes">Notes (Optional)</label>
            <textarea id="completion-notes" rows="2" placeholder="Any notes about the completed work..."></textarea>
          </div>

          <div id="mechanic-sig-section" class="mechanic-signature-section hidden">
            <label><span class="material-icons" style="font-size:16px;vertical-align:middle;color:var(--danger);">draw</span> Reviewer Signature *</label>
            <p class="form-hint" style="margin:0 0 8px;font-size:0.8rem;">Required for safety sign-off per DOT regulations</p>
            <div class="signature-canvas-wrap">
              <canvas id="mechanic-sig-canvas"></canvas>
              <button type="button" class="btn-clear-sig" onclick="window.MaintenanceModule.clearMechanicSig()" title="Clear">
                <span class="material-icons" style="font-size:16px;">restart_alt</span>
              </button>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('complete-task')">Cancel</button>
            <button type="submit" class="btn btn-primary"><span class="material-icons">paid</span> Finalize & Add Costs</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Vehicle Hold Modal -->
    <div class="modal hidden" id="modal-hold">
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="modal-hold-title">New Vehicle Hold</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('hold')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-hold" novalidate onsubmit="window.MaintenanceModule.saveHold(event)">
          <input type="hidden" id="hold-id">

          <div class="form-row">
            <div class="form-group">
              <label for="hold-asset-type">Asset Type *</label>
              <select id="hold-asset-type" required onchange="window.MaintenanceModule.updateHoldAssetDropdown()">
                <option value="truck">Vehicle</option>
                <option value="trailer">Trailer</option>
              </select>
            </div>
            <div class="form-group">
              <label for="hold-asset">Vehicle *</label>
              <select id="hold-asset" required>
                <option value="">Select vehicle</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="hold-start">Start Date *</label>
              <input type="date" id="hold-start" required>
            </div>
            <div class="form-group">
              <label for="hold-end">End Date *</label>
              <input type="date" id="hold-end" required>
            </div>
          </div>

          <div class="form-group">
            <label for="hold-reason">Reason *</label>
            <select id="hold-reason" required>
              <option value="Maintenance">Maintenance/Repair</option>
              <option value="Inspection">DOT Inspection</option>
              <option value="Accident">Accident/Damage</option>
              <option value="Parts">Waiting for Parts</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div class="form-group">
            <label for="hold-notes">Notes</label>
            <textarea id="hold-notes" rows="2" placeholder="Additional details..."></textarea>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('hold')">Cancel</button>
            <button type="submit" class="btn btn-primary">Create Hold</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add Costs Modal -->
    <div class="modal hidden" id="modal-costs">
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="modal-costs-title">Add Costs</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('costs')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-costs" novalidate onsubmit="window.MaintenanceModule.saveCosts(event)">
          <input type="hidden" id="costs-task-id">

          <div class="costs-task-info" id="costs-task-info">
            <!-- Filled dynamically -->
          </div>

          <div class="form-section">
            <label class="cost-tax-toggle">
              <input type="checkbox" id="costs-third-party-cb" onchange="window.MaintenanceModule.toggleVendorField()"> Work performed by a 3rd party vendor
            </label>
            <div class="form-group hidden" id="costs-vendor-group" style="margin-top:8px;">
              <label for="costs-vendor-name">Vendor / Service Company</label>
              <input type="text" id="costs-vendor-name" placeholder="e.g., Bakken Fleet Services" maxlength="200">
            </div>
          </div>

          <div class="form-section cost-section-collapsible">
            <h4 class="cost-section-toggle" onclick="window.MaintenanceModule.toggleCostSection('labor')">
              <span class="material-icons cost-section-chevron" id="labor-section-chevron">expand_more</span>
              Labor
              <span class="cost-section-badge hidden" id="labor-section-badge">$0</span>
            </h4>
            <div class="cost-section-body hidden" id="costs-labor-body">
              <div class="form-row">
                <div class="form-group">
                  <label for="costs-labor-hours">Hours</label>
                  <input type="number" id="costs-labor-hours" step="0.25" min="0" placeholder="0" oninput="window.MaintenanceModule.calculateCostTotals()">
                </div>
                <div class="form-group">
                  <label for="costs-labor-rate">Rate ($/hr)</label>
                  <input type="number" id="costs-labor-rate" step="0.01" min="0" placeholder="75.00" value="75" oninput="window.MaintenanceModule.calculateCostTotals()">
                </div>
                <div class="form-group">
                  <label>Labor Total</label>
                  <div class="cost-display" id="costs-labor-total">$0.00</div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-section cost-section-collapsible">
            <h4 class="cost-section-toggle" onclick="window.MaintenanceModule.toggleCostSection('parts')">
              <span class="material-icons cost-section-chevron" id="parts-section-chevron">expand_more</span>
              Parts
              <span class="cost-section-badge hidden" id="parts-section-badge">$0</span>
            </h4>
            <div class="cost-section-body hidden" id="costs-parts-body">
              <div class="form-group">
                <label for="costs-parts-total">Parts Cost</label>
                <input type="number" id="costs-parts-total" step="0.01" min="0" placeholder="0.00" oninput="window.MaintenanceModule.calculateCostTotals()">
              </div>
              <div class="cost-tax-row">
                <label class="cost-tax-toggle">
                  <input type="checkbox" id="costs-parts-tax-cb" onchange="window.MaintenanceModule.togglePartsTax()"> Add Tax
                </label>
                <div class="cost-tax-rate hidden" id="costs-parts-tax-group">
                  <input type="number" id="costs-parts-tax-rate" step="0.01" min="0" max="25" placeholder="0" value="" oninput="window.MaintenanceModule.calculateCostTotals()">
                  <span>%</span>
                </div>
                <div class="cost-tax-amount hidden" id="costs-parts-tax-amount">Tax: $0.00</div>
              </div>
            </div>
          </div>

          <div class="form-section cost-section-collapsible">
            <h4 class="cost-section-toggle" onclick="window.MaintenanceModule.toggleCostSection('shop')">
              <span class="material-icons cost-section-chevron" id="shop-section-chevron">expand_more</span>
              Shop Costs
              <span class="cost-section-badge hidden" id="shop-section-badge">$0</span>
            </h4>
            <div class="cost-section-body hidden" id="costs-shop-body">
              <div class="form-group">
                <label for="costs-shop-total">Shop Cost</label>
                <input type="number" id="costs-shop-total" step="0.01" min="0" placeholder="0.00" oninput="window.MaintenanceModule.calculateCostTotals()">
              </div>
              <div class="cost-tax-row">
                <label class="cost-tax-toggle">
                  <input type="checkbox" id="costs-shop-tax-cb" onchange="window.MaintenanceModule.toggleShopTax()"> Add Tax
                </label>
                <div class="cost-tax-rate hidden" id="costs-shop-tax-group">
                  <input type="number" id="costs-shop-tax-rate" step="0.01" min="0" max="25" placeholder="0" value="" oninput="window.MaintenanceModule.calculateCostTotals()">
                  <span>%</span>
                </div>
                <div class="cost-tax-amount hidden" id="costs-shop-tax-amount">Tax: $0.00</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h4>Receipts</h4>
            <div class="receipt-upload-area" id="receipt-upload-area">
              <input type="file" id="receipt-file-input" accept="image/*,.pdf" multiple style="display:none" onchange="window.MaintenanceModule.handleReceiptUpload(event)">
              <div class="receipt-upload-buttons">
                <button type="button" class="btn btn-secondary" onclick="document.getElementById('receipt-file-input').click()">
                  <span class="material-icons">upload_file</span> Upload
                </button>
                <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.captureReceipt()">
                  <span class="material-icons">photo_camera</span> Photo
                </button>
                <button type="button" class="btn btn-ai-scan ai-entry" onclick="window.MaintenanceModule.aiScanReceipt()">
                  <span class="material-icons">auto_awesome</span> Smart Scan
                </button>
              </div>
              <div class="receipt-list" id="receipt-list">
                <!-- Uploaded receipts will appear here -->
              </div>
            </div>
          </div>

          <div class="costs-grand-total">
            <span>Grand Total:</span>
            <span class="grand-total-amount" id="costs-grand-total">$0.00</span>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('costs')">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Costs</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Vehicle Info Modal -->
    <div class="modal hidden" id="modal-vehicle-info">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-vehicle-info-title">Vehicle Information</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('vehicle-info')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="vehicle-info-body">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Fluids Log Modal -->
    <div class="modal hidden" id="modal-fluids">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-fluids-title">Fluids Log</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('fluids')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <form id="form-fluids" novalidate onsubmit="window.MaintenanceModule.addFluidEntry(event)">
            <input type="hidden" id="fluids-truck-id">

            <div class="form-row">
              <div class="form-group">
                <label for="fluid-type">Fluid Type *</label>
                <select id="fluid-type" required onchange="window.MaintenanceModule.toggleOilFields()">
                  <option value="Oil">Oil</option>
                  <option value="Coolant">Coolant</option>
                  <option value="DEF">DEF</option>
                  <option value="Hydraulic">Hydraulic</option>
                  <option value="Transmission">Transmission</option>
                  <option value="Power Steering">Power Steering</option>
                  <option value="Brake Fluid">Brake Fluid</option>
                  <option value="Windshield">Windshield Washer</option>
                </select>
              </div>
              <div class="form-group">
                <label for="fluid-amount">Amount *</label>
                <input type="number" id="fluid-amount" step="0.25" min="0" required placeholder="1.5">
              </div>
              <div class="form-group">
                <label for="fluid-unit">Unit</label>
                <select id="fluid-unit">
                  <option value="quarts">Quarts</option>
                  <option value="gallons">Gallons</option>
                  <option value="liters">Liters</option>
                </select>
              </div>
            </div>

            <div id="oil-specific-fields">
              <div class="form-row">
                <div class="form-group">
                  <label for="oil-brand">Oil Brand</label>
                  <input type="text" id="oil-brand" placeholder="Shell Rotella">
                </div>
                <div class="form-group">
                  <label for="oil-weight">Oil Weight</label>
                  <select id="oil-weight">
                    <option value="">Select weight</option>
                    <option value="15W-40">15W-40</option>
                    <option value="10W-30">10W-30</option>
                    <option value="5W-30">5W-30</option>
                    <option value="5W-40">5W-40</option>
                    <option value="0W-20">0W-20</option>
                    <option value="SAE 30">SAE 30</option>
                  </select>
                </div>
              </div>
              <div class="form-group">
                <label class="checkbox-label">
                  <input type="checkbox" id="oil-filter-replaced">
                  <span>Oil filter replaced</span>
                </label>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="fluid-mileage">Mileage at Service</label>
                <input type="number" id="fluid-mileage" min="0" placeholder="45000">
              </div>
              <div class="form-group">
                <label for="fluid-hours">Hours at Service</label>
                <input type="number" id="fluid-hours" step="0.1" min="0" placeholder="1234.5">
              </div>
            </div>

            <div class="form-group">
              <label for="fluid-notes">Notes</label>
              <textarea id="fluid-notes" rows="2" placeholder="Additional notes..."></textarea>
            </div>

            <div class="form-actions" style="margin-bottom: var(--spacing-lg);">
              <button type="submit" class="btn btn-primary">
                <span class="material-icons">add</span> Add Entry
              </button>
            </div>
          </form>

          <div class="fluids-history-section">
            <h4>Fluid History</h4>
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
              <input type="checkbox" id="fluids-autopurge-cb" onchange="window.MaintenanceModule.toggleFluidsAutoPurge()">
              <label for="fluids-autopurge-cb" style="cursor:pointer;margin:0;font-size:0.82rem;color:var(--text-secondary);">Auto-purge old logs</label>
              <select id="fluids-retention-select" class="form-control" style="width:auto;padding:2px 8px;font-size:0.8rem;margin-left:auto;display:none;" onchange="window.MaintenanceModule.saveFluidsRetention()">
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">1 year</option>
                <option value="24">2 years</option>
                <option value="0">Forever</option>
              </select>
            </div>
            <div class="fluids-history" id="fluids-history">
              <div style="text-align:center;padding:var(--spacing-xl);">
                <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
                <div style="color:var(--text-secondary);font-size:0.875rem;">Loading...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Work Order History Modal -->
    <div class="modal hidden" id="modal-work-history">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-work-history-title">Work Order History</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('work-history')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="history-filters">
            <div class="form-row">
              <div class="form-group">
                <label for="history-from">From Date</label>
                <input type="date" id="history-from" onchange="window.MaintenanceModule.filterHistory()">
              </div>
              <div class="form-group">
                <label for="history-to">To Date</label>
                <input type="date" id="history-to" onchange="window.MaintenanceModule.filterHistory()">
              </div>
              <div class="form-group">
                <label for="history-category">Category</label>
                <select id="history-category" onchange="window.MaintenanceModule.filterHistory()">
                  <option value="">All Categories</option>
                  <option value="PM Service">PM Service</option>
                  <option value="Repair">Repair</option>
                  <option value="DVIR Defect">DVIR Defect</option>
                  <option value="Pretrip Note">Pretrip Note</option>
                  <option value="Tire">Tire</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <div class="history-summary" id="history-summary">
            <!-- Summary stats -->
          </div>

          <div class="history-list" id="history-list">
            <!-- Work orders list -->
          </div>

          <div class="history-actions">
            <button type="button" class="btn btn-secondary" data-history-truck-only onclick="window.MaintenanceModule.showEmailSummary()">
              <span class="material-icons">email</span> Email Summary
            </button>
            <button type="button" class="btn btn-secondary" data-history-truck-only onclick="window.MaintenanceModule.emailComplianceReport()">
              <span class="material-icons">verified_user</span> Email Compliance Report to Me
            </button>
            <button type="button" class="btn btn-secondary" title="Closed work orders and repair costs. Inspections export from the Inspections tab." onclick="window.MaintenanceModule.exportHistory()">
              <span class="material-icons">download</span> Export Repair History (CSV)
            </button>
          </div>
          <p id="history-truck-only-note" style="display:none;margin:8px 0 0;font-size:0.8rem;color:var(--text-secondary);">Email Summary and Compliance Report are for trucks only.</p>
        </div>
      </div>
    </div>

    <!-- Email Summary Modal -->
    <div class="modal hidden" id="modal-email-summary">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Email Maintenance Summary</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('email-summary')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-email-summary" novalidate onsubmit="window.MaintenanceModule.sendEmailSummary(event)">
          <input type="hidden" id="email-truck-id">

          <div class="form-group">
            <label for="email-recipients">Recipients (comma-separated)</label>
            <input type="text" id="email-recipients" required placeholder="manager@company.com, mechanic@company.com">
          </div>

          <div class="form-group">
            <label for="email-period">Time Period</label>
            <select id="email-period" onchange="window.MaintenanceModule.updateEmailPeriod()">
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="custom">Custom date range</option>
            </select>
          </div>

          <div id="email-custom-dates" style="display: none;">
            <div class="form-row">
              <div class="form-group">
                <label for="email-from-date">From</label>
                <input type="date" id="email-from-date">
              </div>
              <div class="form-group">
                <label for="email-to-date">To</label>
                <input type="date" id="email-to-date">
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('email-summary')">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span class="material-icons">send</span> Send Summary
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Vehicle Inventory Modal -->
    <div class="modal hidden" id="modal-inventory">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-inventory-title">Vehicle Inventory</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('inventory')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <!-- Inventory Tabs -->
          <div class="inventory-tabs">
            <button class="inventory-tab active" data-tab="submit" onclick="window.MaintenanceModule.switchInventoryTab('submit')">
              <span class="material-icons">checklist</span> Count
            </button>
            <button class="inventory-tab" data-tab="purchase" onclick="window.MaintenanceModule.switchInventoryTab('purchase')">
              <span class="material-icons">shopping_cart</span> Add Purchase
            </button>
            <button class="inventory-tab" data-tab="history" onclick="window.MaintenanceModule.switchInventoryTab('history')">
              <span class="material-icons">history</span> History
            </button>
          </div>

          <!-- Submit Tab -->
          <div class="inventory-tab-content active" id="inventory-tab-submit">
            <input type="hidden" id="inventory-truck-id">
            <div class="inventory-checklist" id="inventory-checklist">
              <!-- Populated dynamically -->
            </div>
            <div class="form-group">
              <label for="inventory-submitter">Submitted By *</label>
              <select id="inventory-submitter" required>
                <option value="">Select person...</option>
              </select>
            </div>
            <div class="form-group">
              <label for="inventory-notes">Notes</label>
              <textarea id="inventory-notes" rows="2" placeholder="Any notes about this inventory check..."></textarea>
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-primary" onclick="window.MaintenanceModule.submitInventory()">
                <span class="material-icons">check</span> Save Counts
              </button>
            </div>
          </div>

          <!-- Purchase Tab -->
          <div class="inventory-tab-content hidden" id="inventory-tab-purchase">
            <form id="form-inventory-purchase" novalidate onsubmit="window.MaintenanceModule.addInventoryPurchase(event)">
              <div class="form-group">
                <label for="purchase-item">Item *</label>
                <select id="purchase-item" required>
                  <option value="">Select item...</option>
                </select>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="purchase-quantity">Quantity *</label>
                  <input type="number" id="purchase-quantity" min="1" step="1" required placeholder="1">
                </div>
                <div class="form-group">
                  <label for="purchase-unit-cost">Unit Cost ($)</label>
                  <input type="number" id="purchase-unit-cost" min="0" step="0.01" placeholder="0.00">
                </div>
              </div>
              <div class="form-group">
                <label for="purchase-total" style="display:flex;justify-content:space-between;">
                  <span>Total Cost</span>
                  <span id="purchase-total-display">$0.00</span>
                </label>
              </div>
              <div class="form-group">
                <label for="purchase-by">Purchased By *</label>
                <select id="purchase-by" required>
                  <option value="">Select person...</option>
                </select>
              </div>
              <div class="form-group">
                <label for="purchase-notes">Notes</label>
                <textarea id="purchase-notes" rows="2" placeholder="Receipt #, vendor, etc..."></textarea>
              </div>
              <div class="form-actions">
                <button type="submit" class="btn btn-primary">
                  <span class="material-icons">add</span> Add Purchase
                </button>
              </div>
            </form>
          </div>

          <!-- History Tab -->
          <div class="inventory-tab-content hidden" id="inventory-tab-history">
            <div class="inventory-history-filter">
              <select id="inventory-history-type" onchange="window.MaintenanceModule.filterInventoryHistory()">
                <option value="all">All Activity</option>
                <option value="pending">Pending Review</option>
                <option value="counts">Inventory Counts</option>
                <option value="purchases">Purchases</option>
              </select>
            </div>
            <div class="inventory-history-list" id="inventory-history-list">
              <!-- Populated dynamically -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TASK-9335 — Fleet Inventory Modal (every truck + trailer) -->
    <div class="modal hidden" id="modal-fleet-inventory">
      <div class="modal-content modal-large modal-fullscreen">
        <div class="modal-header">
          <h3>Fleet Inventory</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('fleet-inventory')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="overflow-y:auto;">
          <!-- Every control here acts immediately (re-renders the list), so the
               ids and the wrapper all carry "filter" — the modal close guard
               treats them as chrome, not unsaved work (TASK-9332). -->
          <div class="inventory-history-filter fleet-inventory-filters" style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;">
            <select id="fleet-inv-filter-type" class="control-auto" aria-label="Activity type" onchange="window.MaintenanceModule.renderFleetInventory(true)">
              <option value="all">All Activity</option>
              <option value="pending">Pending Review</option>
              <option value="counts">Inventory Counts</option>
              <option value="purchases">Purchases</option>
            </select>
            <select id="fleet-inv-filter-vehicle" class="control-auto" aria-label="Vehicle" onchange="window.MaintenanceModule.renderFleetInventory(true)">
              <option value="">All vehicles</option>
            </select>
            <select id="fleet-inv-filter-person" class="control-auto" aria-label="Person" onchange="window.MaintenanceModule.renderFleetInventory(true)">
              <option value="">Everyone</option>
            </select>
            <label style="display:flex;align-items:center;gap:4px;">From
              <input type="date" id="fleet-inv-filter-from" class="control-auto" onchange="window.MaintenanceModule.renderFleetInventory(true)">
            </label>
            <label style="display:flex;align-items:center;gap:4px;">To
              <input type="date" id="fleet-inv-filter-to" class="control-auto" onchange="window.MaintenanceModule.renderFleetInventory(true)">
            </label>
            <label style="display:flex;align-items:center;gap:4px;">
              <input type="checkbox" id="fleet-inv-filter-discrep" onchange="window.MaintenanceModule.renderFleetInventory(true)"> Discrepancies only
            </label>
          </div>
          <div class="inventory-history-list" id="fleet-inventory-list" style="max-height:none;overflow:visible;">
            <!-- Populated dynamically -->
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Inventory Purchase Modal -->
    <div class="modal hidden" id="modal-edit-inventory-purchase">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Edit Purchase</h3>
          <button class="btn-icon" onclick="document.getElementById('modal-edit-inventory-purchase').classList.add('hidden')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-edit-inventory-purchase" novalidate onsubmit="window.MaintenanceModule.saveInventoryPurchaseEdit(event)">
          <input type="hidden" id="edit-purchase-id">
          <div class="modal-body" style="padding:20px;">
            <div class="form-group">
              <label>Item</label>
              <div id="edit-purchase-item-name" style="font-weight:600;font-size:1rem;"></div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="edit-purchase-quantity">Quantity *</label>
                <input type="number" id="edit-purchase-quantity" min="1" step="1" required oninput="window.MaintenanceModule.updateEditPurchaseTotal()">
              </div>
              <div class="form-group">
                <label for="edit-purchase-unit-cost">Unit Cost ($) *</label>
                <input type="number" id="edit-purchase-unit-cost" min="0" step="0.01" required oninput="window.MaintenanceModule.updateEditPurchaseTotal()">
              </div>
            </div>
            <div class="form-group">
              <label>Total</label>
              <div id="edit-purchase-total-display" style="font-size:1.1rem;font-weight:600;color:var(--brand-blue);">$0.00</div>
            </div>
            <div class="form-group">
              <label for="edit-purchase-notes">Notes</label>
              <textarea id="edit-purchase-notes" rows="2" placeholder="Optional notes..."></textarea>
            </div>
          </div>
          <div class="form-actions" style="padding:0 20px 20px;">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-edit-inventory-purchase').classList.add('hidden')">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span class="material-icons">save</span> Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Log Asset Hours Modal -->
    <div class="modal hidden" id="modal-asset-hours">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3 id="modal-asset-hours-title">Log Hours</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('asset-hours')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-asset-hours" novalidate onsubmit="window.MaintenanceModule.saveAssetHours(event)">
          <input type="hidden" id="asset-hours-id">
          <div class="meters-current" id="asset-hours-current">
            <!-- Filled dynamically -->
          </div>
          <div class="form-group">
            <label for="asset-hours-new">New Hours Reading *</label>
            <input type="number" id="asset-hours-new" step="0.1" min="0" required placeholder="Enter current hours...">
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" id="asset-hours-pm">
              <span>Mark as PM service completed (resets PM counter)</span>
            </label>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('asset-hours')">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span class="material-icons">save</span> Save Hours
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Asset Location Coming Soon Modal -->
    <div class="modal hidden" id="modal-asset-location">
      <div class="modal-content modal-coming-soon">
        <div class="modal-coming-soon-body">
          <div class="modal-coming-soon-icon">
            <span class="material-icons">place</span>
            <span class="material-icons coming-soon-sparkle">auto_awesome</span>
          </div>
          <h3>Asset Tracking</h3>
          <p>GPS-based asset location tracking is coming soon. You'll be able to see where your equipment is in real-time.</p>
          <button class="btn btn-primary" onclick="window.MaintenanceModule.closeModal('asset-location')">Got It</button>
        </div>
      </div>
    </div>

    <!-- Asset Work History Modal -->
    <div class="modal hidden" id="modal-asset-history">
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="modal-asset-history-title">Asset Work History</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('asset-history')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="asset-history-content">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Standalone Shop Cost Modal -->
    <div class="modal hidden" id="modal-shop-cost">
      <div class="modal-content" style="max-width:520px;">
        <div class="modal-header">
          <h3>Add Shop Cost</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('shop-cost')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-shop-cost" novalidate onsubmit="window.MaintenanceModule.saveShopCost(event)">
          <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
            <div class="form-group">
              <label for="shop-cost-amount">Amount ($) *</label>
              <input type="number" id="shop-cost-amount" step="0.01" min="0" class="form-control" placeholder="0.00" required oninput="window.MaintenanceModule.updateStandaloneShopTotal()">
            </div>
            <div class="cost-tax-row" style="margin-bottom:16px;">
              <label class="cost-tax-toggle">
                <input type="checkbox" id="shop-cost-tax-cb" onchange="window.MaintenanceModule.toggleStandaloneShopTax()"> Add Tax
              </label>
              <div class="cost-tax-rate hidden" id="shop-cost-tax-group">
                <input type="number" id="shop-cost-tax-rate" step="0.01" min="0" max="25" placeholder="0" value="" oninput="window.MaintenanceModule.updateStandaloneShopTotal()">
                <span>%</span>
              </div>
              <div class="cost-tax-amount hidden" id="shop-cost-tax-amount">Tax: $0.00</div>
            </div>
            <div class="form-group">
              <label for="shop-cost-vehicle">Vehicle (optional)</label>
              <select id="shop-cost-vehicle" class="form-control">
                <option value="">Shop / General (no vehicle)</option>
              </select>
            </div>
            <div class="form-group">
              <label for="shop-cost-description">Description *</label>
              <input type="text" id="shop-cost-description" class="form-control" placeholder="e.g. New shop compressor filter" required>
            </div>
            <div class="form-group">
              <label for="shop-cost-date">Date</label>
              <input type="date" id="shop-cost-date" class="form-control">
            </div>

            <div class="form-group">
              <label>Receipt Photo</label>
              <div class="shop-receipt-upload">
                <input type="file" id="shop-cost-receipt-input" accept="image/*,.pdf" style="display:none" onchange="window.MaintenanceModule.handleShopReceipt(event)">
                <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('shop-cost-receipt-input').click()">
                  <span class="material-icons">upload_file</span> Upload
                </button>
                <button type="button" class="btn btn-secondary btn-sm" onclick="window.MaintenanceModule.captureShopReceipt()">
                  <span class="material-icons">photo_camera</span> Photo
                </button>
              </div>
              <div id="shop-cost-receipt-preview" class="shop-receipt-preview"></div>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.showShopCostHistory()">
              <span class="material-icons">history</span> History
            </button>
            <span style="flex:1;"></span>
            <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.closeModal('shop-cost')">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span class="material-icons">save</span> Save
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Shop Cost History Modal -->
    <div class="modal hidden" id="modal-shop-cost-history">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>Shop Cost History</h3>
          <div style="display:flex;align-items:center;gap:8px;">
            <button type="button" class="btn btn-primary btn-sm" onclick="window.MaintenanceModule.addShopCostFromHistory()">
              <span class="material-icons">add</span> Add Shop Cost
            </button>
            <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('shop-cost-history')">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>
        <div class="modal-body" id="shop-cost-history-content" style="padding:20px;max-height:70vh;overflow-y:auto;">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Quick Edit Asset Modal -->
    <div class="modal hidden" id="modal-quick-edit-asset">
      <div class="modal-content" style="max-width:480px;">
        <div class="modal-header">
          <h3 id="quick-edit-asset-title">Edit Asset</h3>
          <button class="btn-icon" onclick="window.MaintenanceModule.closeModal('quick-edit-asset')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form novalidate onsubmit="window.MaintenanceModule.saveQuickEditAsset(event)">
          <div class="modal-body" style="padding:20px;display:flex;flex-direction:column;gap:16px;">
            <input type="hidden" id="quick-edit-asset-id" />

            <div id="quick-edit-asset-hours-group" class="form-group" style="display:none;">
              <label for="quick-edit-asset-hours">Current Hours</label>
              <input type="number" id="quick-edit-asset-hours" class="form-control" min="0" step="0.1" />
            </div>

            <div class="form-group">
              <label for="quick-edit-asset-condition">Condition</label>
              <select id="quick-edit-asset-condition" class="form-control">
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Needs Repair">Needs Repair</option>
                <option value="Out of Service">Out of Service</option>
              </select>
            </div>

            <!-- TASK-1141 — quick-edit notes now go to the per-unit THREAD, not
                 the static notes field (split-brain mitigation: this modal used
                 to overwrite the same field roster's full-edit form writes).
                 The legacy field value renders read-only inside the section. -->
            <div class="form-group">
              <div id="quick-edit-asset-notes-thread"></div>
            </div>
          </div>
          <div class="form-actions" style="display:flex;justify-content:space-between;align-items:center;padding:16px 20px;">
            <button type="button" class="btn btn-secondary" onclick="window._rosterPendingEdit={type:'asset',id:document.getElementById('quick-edit-asset-id').value}; window.MaintenanceModule.closeModal('quick-edit-asset'); window.location.hash='/roster';">
              <span class="material-icons">open_in_new</span> Full Edit in Roster
            </button>
            <button type="submit" class="btn btn-primary">
              <span class="material-icons">save</span> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

// ============================================
// MODAL HANDLERS
// ============================================
function setupModals() {
  // Close modal on backdrop click (but not for form modals to prevent data loss)
  document.querySelectorAll('.modal').forEach(modal => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        // Don't close modals with form inputs on backdrop click (prevents data loss)
        const hasFormInputs = modal.querySelector('input, select, textarea');
        if (hasFormInputs) return;

        modal.classList.add('hidden');
      }
    });
  });
}

// ============================================
// TASK-9658 S1 — RECORD DEEP LINKS (#/maintenance/vehicle/{id}, #/maintenance/task/{id})
// ============================================
// app.js parks AppState.pendingRecordFocus = { view:'maintenance', type, id }
// and mounts this view through navigateToView (module gate already passed).
// We drain it ONCE, after the relevant list has landed, and open the record in
// its normal modal. A record missing from the live list is re-read by id
// (persistentLocalCache can hand us a cache-only first snapshot that predates a
// just-created work order); still nothing → a plain "not found" toast and the
// user stays on Maintenance. The 8s fallback covers a list that never loads
// (subscription error) so a link never just silently does nothing.
let _recordFocusFallbackTimer = null;

// TASK-9671 S1 — a Claude lookup link (#/maintenance?dd_open=vehicle&vehicle={id},
// app.js parks it as AppState.pendingFocus via window.OpenLinks) becomes THIS
// view's record focus, so the record-link open below opens it (ready checks,
// by-id read, not-found toast, fallback). takeHint consumes it once (stale /
// other company = null); module access is re-checked here, failing closed.
function _parkMaintOpenHint() {
  const OL = window.OpenLinks;
  if (!OL || window.AppState?.pendingFocus?.view !== 'maintenance') return;
  const h = OL.takeHint('maintenance');
  if (!h || h.kind !== 'vehicle' || !h.ids.vehicle || window.checkModuleAccess?.('maintenance', true) !== true) return;
  window.AppState.pendingRecordFocus = { view: 'maintenance', type: 'vehicle', id: h.ids.vehicle, at: Date.now() };
}

function _recordFocusReady(focus) {
  if (focus.type === 'task') return !!MaintenanceState._tasksLoaded;
  if (focus.type === 'vehicle') {
    // Driver UI has no vehicle detail — resolves immediately (to "not available").
    if (isDriverPinMode()) return true;
    return !!(MaintenanceState._trucksLoaded && MaintenanceState._trailersLoaded);
  }
  return true;
}

function drainMaintenanceRecordFocus(force = false) {
  const RL = window.RecordLinks;
  if (!RL || !RL.hasPendingFocus('maintenance')) return;
  const parked = window.AppState.pendingRecordFocus;
  if (!force && !_recordFocusReady(parked)) return;
  const focus = RL.takePendingFocus('maintenance');
  if (!focus) return;
  if (_recordFocusFallbackTimer) { clearTimeout(_recordFocusFallbackTimer); _recordFocusFallbackTimer = null; }
  const gen = MaintenanceState._mountGen;
  _openMaintenanceRecord(focus, gen).catch((e) => {
    console.warn('[Maintenance] record deep link failed', e);
    if (gen === MaintenanceState._mountGen) _recordNotFoundToast();
  });
}

function _recordNotFoundToast(msg) {
  window.showToast?.(msg || 'That record wasn’t found — showing Maintenance.', 'info');
}

function _driverOwnsTask(t) {
  const pid = getDriverPersonId();
  const pname = getDriverPersonName();
  if (!t) return false;
  if (pid && (t.assignedTo === pid || t.createdByPersonId === pid)) return true;
  // COD (Code Reviewer): the legacy name match only for records with NO creator
  // id — a same-named driver must not pass on a record that names someone else.
  return !!(pname && !t.createdByPersonId && t.createdBy === pname);
}

async function _fetchMaintDoc(coll, id) {
  const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant, coll, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (e) {
    // permission-denied reads the same as missing to the user: never reveal
    // whether a record the session can't read exists.
    console.warn(`[Maintenance] record lookup ${coll}/${id} failed:`, e?.code || e?.message);
    return null;
  }
}

async function _openMaintenanceRecord(focus, gen) {
  const stale = () => gen !== MaintenanceState._mountGen;

  if (focus.type === 'task') {
    let task = MaintenanceState.tasks.find(t => t.id === focus.id);
    if (!task) {
      task = await _fetchMaintDoc('maintenance', focus.id);
      if (stale()) return;
      // wo-legacy-orders — an imported order reads in the current shape (office AND
      // driver: wo-legacy-followups, so the driver's ownership + tab match the list).
      if (task) task = _woNormalize(task);
      // The next snapshot replaces the array wholesale, so this is only a bridge.
      if (task && !MaintenanceState.tasks.some(t => t.id === task.id)) MaintenanceState.tasks.push(task);
    }
    if (!task) { _recordNotFoundToast(); return; }

    if (isDriverPinMode()) {
      // Same scope the driver list uses — assigned to / logged by THIS driver.
      // A crewmate's work order reads as not found (never confirm it exists).
      if (!_driverOwnsTask(task)) { _recordNotFoundToast(); return; }
      const tab = woTab(task); // wo-legacy-followups — the list's own tab rule
      filterTasks(tab);
      setTimeout(() => {
        if (stale()) return;
        // Compare dataset values — no selector built from a document id.
        const card = Array.from(document.querySelectorAll('#maintenance-list .maintenance-card'))
          .find(el => el.dataset.id === task.id);
        if (!card) return;
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.style.outline = '3px solid var(--brand-blue)';
        card.style.outlineOffset = '2px';
        setTimeout(() => { card.style.outline = ''; card.style.outlineOffset = ''; }, 3000);
      }, 0);
      return;
    }
    showTaskDetail(task.id);
    return;
  }

  if (focus.type === 'vehicle') {
    if (isDriverPinMode()) { _recordNotFoundToast('Vehicle details open from the office login — showing your maintenance list.'); return; }
    let truck = MaintenanceState.trucks.find(t => t.id === focus.id);
    let trailer = truck ? null : MaintenanceState.trailers.find(t => t.id === focus.id);
    if (!truck && !trailer) {
      truck = await _fetchMaintDoc('trucks', focus.id);
      if (!truck) trailer = await _fetchMaintDoc('trailers', focus.id);
      if (stale()) return;
      if (truck && !MaintenanceState.trucks.some(t => t.id === truck.id)) MaintenanceState.trucks.push(truck);
      if (trailer && !MaintenanceState.trailers.some(t => t.id === trailer.id)) MaintenanceState.trailers.push(trailer);
    }
    if (truck) { showVehicleInfo(truck.id); return; }
    if (trailer) {
      // Put the trailer grid behind the modal so closing it lands on the right list.
      try { if (typeof switchAssetType === 'function') switchAssetType('trailers'); } catch (_) {}
      showTrailerInfo(trailer.id);
      return;
    }
    _recordNotFoundToast();
  }
}

// ============================================
// DATA SUBSCRIPTIONS
// ============================================
function subscribeToData(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;

  // Subscribe to maintenance tasks
  const tasksQuery = query(
    collection(db, 'tenants', tenant, 'maintenance'),
    orderBy('createdAt', 'desc')
  );

  const unsubTasks = onSnapshot(tasksQuery, (snapshot) => {
    MaintenanceState._rawTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    _woRenormalizeTasks(); // wo-legacy-orders — imported Open/Closed/Done orders in the current shape
    MaintenanceState._tasksLoaded = true;
    MaintenanceState._tasksError = false;
    renderTasksList(MaintenanceState.currentFilter);
    updateCounts();
    renderFleetGrid(); // Refresh fleet cards to update open task counts
    _maybeOpenPendingTask(); // TASK-9691 — "Open work order" from a DVIR

    // Dispatch event for tutorial system
    window.dispatchEvent(new CustomEvent('maintenance:updated', {
      detail: { count: MaintenanceState.tasks.length }
    }));
    drainMaintenanceRecordFocus(); // TASK-9658 S1
  }, (error) => {
    console.error('[Maintenance] Error loading tasks:', error);
    MaintenanceState._tasksLoaded = true;
    MaintenanceState._tasksError = true;
    renderTasksList(MaintenanceState.currentFilter);
    window.showToast?.('Error loading maintenance tasks', 'error');
    drainMaintenanceRecordFocus(); // TASK-9658 S1 — by-id read decides
  });
  MaintenanceState.unsubscribers.push(unsubTasks);

  // Load people (one-time fetch — doesn't change during a maintenance session)
  getDocs(collection(db, 'tenants', tenant, 'people')).then(snapshot => {
    MaintenanceState.people = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    updateAssignedDropdown();
    renderTasksList(MaintenanceState.currentFilter); // TASK-9897 — vehicle/person lookups changed; repaint filter options + matches
  }).catch(error => {
    console.error('[Maintenance] People load error:', error);
    MaintenanceState.people = [];
  });

  // Subscribe to trucks - no orderBy to avoid index requirement
  const trucksRef = collection(db, 'tenants', tenant, 'trucks');
  const unsubTrucks = onSnapshot(trucksRef, (snapshot) => {
    MaintenanceState.trucks = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => (a.unit || '').localeCompare(b.unit || ''));
    MaintenanceState._trucksLoaded = true;
    _woRenormalizeTasks(); // wo-legacy-orders — an imported order resolves to a unit by name
    console.log('[Maintenance] Trucks updated:', MaintenanceState.trucks.length);
    updateTruckDropdown();
    // TASK-9339 — offer every type the fleet actually carries (was: registered list only)
    window.populateTypeFilterDropdown?.('filter-fleet-type', 'truckTypes', MaintenanceState.trucks);
    renderFleetGrid();
    onFleetInventoryDataChanged(); // TASK-9335
    drainMaintenanceRecordFocus(); // TASK-9658 S1
    renderTasksList(MaintenanceState.currentFilter); // TASK-9897 — vehicle/person lookups changed; repaint filter options + matches
  }, (error) => {
    console.error('[Maintenance] Trucks subscription error:', error);
    MaintenanceState.trucks = [];
  });
  MaintenanceState.unsubscribers.push(unsubTrucks);

  // Load meters log (one-time fetch — optimistic updates handle new entries)
  getDocs(collection(db, 'tenants', tenant, 'metersLog')).then(snapshot => {
    MaintenanceState.metersLog = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        const aTime = a.timestamp?.toDate?.() || new Date(0);
        const bTime = b.timestamp?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
    console.log('[Maintenance] MetersLog loaded:', MaintenanceState.metersLog.length);
    renderFleetGrid();
  }).catch(error => {
    console.error('[Maintenance] Meters log load error:', error);
    MaintenanceState.metersLog = [];
  });

  // Subscribe to trailers (no orderBy to avoid index requirement)
  const trailersRef = collection(db, 'tenants', tenant, 'trailers');
  const unsubTrailers = onSnapshot(trailersRef, (snapshot) => {
    MaintenanceState.trailers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    MaintenanceState._trailersLoaded = true;
    // Sort client-side
    MaintenanceState.trailers.sort((a, b) => (a.unit || '').localeCompare(b.unit || ''));
    _woRenormalizeTasks(); // wo-legacy-orders
    console.log('[Maintenance] Trailers updated:', MaintenanceState.trailers.length);
    updateTrailerDropdown();
    window.populateTypeFilterDropdown?.('filter-trailer-type', 'trailerTypes', MaintenanceState.trailers); // TASK-9339
    renderTrailerGrid();
    onFleetInventoryDataChanged(); // TASK-9335
    drainMaintenanceRecordFocus(); // TASK-9658 S1
    renderTasksList(MaintenanceState.currentFilter); // TASK-9897 — vehicle/person lookups changed; repaint filter options + matches
  }, (error) => {
    console.error('[Maintenance] Trailers subscription error:', error);
    MaintenanceState.trailers = [];
  });
  MaintenanceState.unsubscribers.push(unsubTrailers);

  // Subscribe to vehicle holds (no orderBy to avoid index requirement)
  const holdsRef = collection(db, 'tenants', tenant, 'vehicleHolds');
  const unsubHolds = onSnapshot(holdsRef, (snapshot) => {
    MaintenanceState.vehicleHolds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    MaintenanceState._holdsLoaded = true;
    // Sort client-side by startDate descending
    MaintenanceState.vehicleHolds.sort((a, b) => {
      const aDate = a.startDate?.toDate?.() || new Date(a.startDate || 0);
      const bDate = b.startDate?.toDate?.() || new Date(b.startDate || 0);
      return bDate - aDate;
    });
    renderHoldsList();
    renderFleetGrid(); // Update fleet grid with hold status
    renderTrailerGrid(); // Update trailer grid with hold status
    updateHoldsCount();
    updateAssetTypeCounts();
  }, (error) => {
    console.error('[Maintenance] Vehicle holds subscription error:', error);
    MaintenanceState.vehicleHolds = [];
    MaintenanceState._holdsLoaded = true;
    renderHoldsList();
    window.showToast?.('Error loading vehicle holds', 'error');
  });
  MaintenanceState.unsubscribers.push(unsubHolds);

  // Load return inspections (one-time fetch)
  getDocs(collection(db, 'tenants', tenant, 'returnInspections')).then(snapshot => {
    const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    MaintenanceState.returnInspections = all.filter(ri => ri.status === 'pending');
    MaintenanceState._allReturnInspections = all.filter(ri => ri.status !== 'pending');
    renderFleetGrid();
  }).catch(error => {
    console.error('[Maintenance] Return inspections load error:', error);
    MaintenanceState.returnInspections = [];
    MaintenanceState._allReturnInspections = [];
  });

  // Load pretrips/DVIRs (one-time fetch)
  getDocs(query(collection(db, 'tenants', tenant, 'pretrips'), orderBy('createdAt', 'desc'))).then(snapshot => {
    MaintenanceState.pretrips = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const lookup = {};
    // TASK-9898 S5 — keyed by the TENANT's calendar day, the same day
    // hasDvirToday() asks about (it used the viewer's device zone).
    const tz = _maintTenantTz();
    snapshot.docs.forEach(d => {
      const data = d.data();
      if (!data.truckId || !data.createdAt) return;
      const date = data.createdAt.toDate();
      lookup[data.truckId + '_' + _maintTenantClock(date, tz).ymd] = true;
    });
    MaintenanceState.pretripTruckDates = lookup;
    renderFleetGrid();
    // TASK-9766 (d) COD — the Possible repeat chip reads these DVIRs; the task
    // list may have painted before they arrived.
    renderTasksList(MaintenanceState.currentFilter);
  }).catch(error => {
    console.error('[Maintenance] Pretrips load error:', error);
    MaintenanceState.pretrips = [];
    MaintenanceState.pretripTruckDates = {};
  });

  // Load today's jobDays to know which trucks are dispatched (one-time fetch)
  // TASK-9898 S5 (COD) — the TENANT's today, same day the No DVIR check uses.
  // jobDay.date is anchored to noon, so a device-local window around that
  // calendar day still catches it.
  const [_ty, _tm, _td] = _maintTenantClock(new Date()).ymd.split('-').map(Number);
  const todayStart = new Date(_ty, _tm - 1, _td);
  const todayEnd = new Date(_ty, _tm - 1, _td + 1);
  getDocs(query(
    collection(db, 'tenants', tenant, 'jobDays'),
    where('date', '>=', todayStart),
    where('date', '<', todayEnd)
  )).then(snapshot => {
    const truckIds = new Set();
    snapshot.docs.forEach(d => {
      (d.data().trucks || []).forEach(id => truckIds.add(id));
    });
    MaintenanceState.dispatchedTruckIds = truckIds;
    renderFleetGrid();
  }).catch(error => {
    console.error('[Maintenance] JobDays load error:', error);
  });

  // Live inventory subscriptions (real-time so review queue stays in sync across admins)
  if (window.isVehicleInventoryEnabled?.()) {
    const unsubInvLogs = onSnapshot(collection(db, 'tenants', tenant, 'inventoryLogs'), snapshot => {
      MaintenanceState.inventoryLogs = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.submittedAt?.toDate?.() || new Date(0)) - (a.submittedAt?.toDate?.() || new Date(0)));
      if (MaintenanceState.currentInventoryTab === 'history' && !document.getElementById('modal-inventory')?.classList.contains('hidden')) {
        renderInventoryHistory();
      }
      onFleetInventoryDataChanged(); // TASK-9335 — a driver's PIN count shows up live
    }, () => { MaintenanceState.inventoryLogs = []; });
    MaintenanceState.unsubscribers.push(unsubInvLogs);

    const unsubInvPurchases = onSnapshot(collection(db, 'tenants', tenant, 'inventoryPurchases'), snapshot => {
      MaintenanceState.inventoryPurchases = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.purchasedAt?.toDate?.() || new Date(0)) - (a.purchasedAt?.toDate?.() || new Date(0)));
      if (MaintenanceState.currentInventoryTab === 'history' && !document.getElementById('modal-inventory')?.classList.contains('hidden')) {
        renderInventoryHistory();
      }
      onFleetInventoryDataChanged(); // TASK-9335
    }, () => { MaintenanceState.inventoryPurchases = []; });
    MaintenanceState.unsubscribers.push(unsubInvPurchases);
  }

  // Load assets (one-time fetch)
  getDocs(collection(db, 'tenants', tenant, 'assets')).then(snapshot => {
    // wo-legacy-orders — imported orders resolve against EVERY equipment item
    // (deactivated too), like trucks/trailers here and the connector/partner side.
    MaintenanceState._woAllAssets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    MaintenanceState.assets = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(a => a.active !== false)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    console.log('[Maintenance] Assets loaded:', MaintenanceState.assets.length);
    // wo-legacy-orders — equipment names take part in the by-name resolution (a
    // name a truck AND an equipment item share stops resolving), so re-derive
    // and repaint the truck/trailer cards that count those orders.
    MaintenanceState._assetsLoaded = true;
    _woRenormalizeTasks();
    renderFleetGrid();
    renderTrailerGrid();
    updateAssetTypeCounts();
    // TASK-1227 — mobile shows all three fleet sections at once (they're
    // collapsible, not toggled), so gating this on the desktop toggle state
    // left the asset grid stuck on the empty state it painted before the
    // fetch resolved — while the badge showed the real count. Repaint
    // whenever the data lands; renderAssetGrid() already no-ops if a
    // maintenance modal is open.
    renderAssetGrid();
    renderTasksList(MaintenanceState.currentFilter); // TASK-9897 — vehicle/person lookups changed; repaint filter options + matches
  }).catch(error => {
    console.error('[Maintenance] Assets load error:', error);
    MaintenanceState.assets = [];
    // wo-legacy-orders — no equipment list: imported orders stay unresolved
    // ("not in fleet") rather than attach on a name that might be shared.
    updateAssetTypeCounts();
    renderAssetGrid();
  });

  // Load PM settings from tenant doc
  loadPMSettings(tenant);

  // Register fields with Smart Assistant
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    SA.register('task-description', () => [], 'Maintenance', { formatOnly: true });
    SA.register('task-notes', () => [], 'Maintenance', { formatOnly: true });
    SA.register('pm-notes', () => [], 'Maintenance', { formatOnly: true });
  }
}

async function loadPMSettings(tenant) {
  try {
    const tenantRef = doc(db, 'tenants', tenant);
    const snap = await getDoc(tenantRef);
    // TASK-9339 — start from the built-ins, not from whatever the previous tenant left
    // in state (a removed built-in is stored as null and must not carry across tenants).
    MaintenanceState.pmSettings.pmIntervals = { ...BUILTIN_PM_INTERVALS };
    MaintenanceState.pmSettings.savedIntervalKeys = [];
    if (snap.exists()) {
      const data = snap.data();
      const stored = data?.settings?.pmIntervals;
      if (stored && typeof stored === 'object') {
        MaintenanceState.pmSettings.pmIntervals = {
          ...BUILTIN_PM_INTERVALS,
          ...stored
        };
        MaintenanceState.pmSettings.savedIntervalKeys = Object.keys(stored);
      }
      // Load custom PM checklist if configured
      const checklist = data?.settings?.pmChecklist;
      if (Array.isArray(checklist) && checklist.length > 0) {
        MaintenanceState.pmSettings.pmChecklist = checklist;
      }
    }
  } catch (error) {
    console.error('[Maintenance] Error loading PM settings:', error);
  }
}

// ============================================
// TAB SWITCHING
// ============================================
function switchTab(tab) {
  MaintenanceState.currentTab = tab;

  // Update tab buttons
  document.querySelectorAll('.maintenance-tab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
  });

  // Update tab content
  document.getElementById('tab-fleet').classList.toggle('active', tab === 'fleet');
  document.getElementById('tab-tasks').classList.toggle('active', tab === 'tasks');
  document.getElementById('tab-holds').classList.toggle('active', tab === 'holds');
  document.getElementById('tab-citations')?.classList.toggle('active', tab === 'citations');
  document.getElementById('tab-inspections')?.classList.toggle('active', tab === 'inspections');
  document.getElementById('tab-downtime')?.classList.toggle('active', tab === 'downtime');
  if (tab === 'downtime') _openDowntimeAudit();

  // Lazy-load citations on first tab visit
  if (tab === 'citations' && !MaintenanceState._citationsLoaded) {
    loadCitations();
  }
  // TASK-4884 — fixed-equipment inspections (window.*, never an import: Pitfall #1).
  if (tab === 'inspections') {
    const host = document.getElementById('facility-inspections-host');
    if (host) window.FacilityInspections?.mount(host, MaintenanceState.tenantId);
  }
}

function isMobileView() {
  return window.innerWidth <= 768;
}

function setupMobileCollapsible() {
  if (!isMobileView()) return;

  // Hide desktop toggle buttons
  const toggleBar = document.querySelector('.fleet-type-toggle');
  if (toggleBar) toggleBar.style.display = 'none';

  const views = [
    { id: 'vehicles-view', icon: 'local_shipping', label: 'Vehicles', countId: 'vehicles-toggle-count' },
    { id: 'trailers-view', icon: 'rv_hookup', label: 'Trailers', countId: 'trailers-toggle-count' },
    { id: 'assets-view', icon: 'handyman', label: 'Assets', countId: 'assets-toggle-count' }
  ];

  views.forEach(v => {
    const view = document.getElementById(v.id);
    if (!view || view.dataset.mobileCollapsible) return;
    view.dataset.mobileCollapsible = 'true';
    view.style.display = 'block';

    // Wrap contents in a details/summary
    const details = document.createElement('details');
    details.className = 'fleet-mobile-section';

    const summary = document.createElement('summary');
    summary.className = 'fleet-mobile-section-header';
    const count = document.getElementById(v.countId);
    const countVal = count ? count.textContent : '0';
    summary.innerHTML = `<span class="material-icons fleet-mobile-chevron">expand_more</span><span class="material-icons" style="font-size:20px;">${v.icon}</span> ${v.label} <span class="fleet-mobile-count">${countVal}</span>`;
    details.appendChild(summary);

    // Move all children into details
    while (view.firstChild) {
      details.appendChild(view.firstChild);
    }
    view.appendChild(details);
  });

  // Render all grids so data is ready when user expands
  renderFleetGrid();
  renderTrailerGrid();
  renderAssetGrid();
}

function updateMobileCollapsibleCounts() {
  if (!isMobileView()) return;
  const map = {
    'vehicles-view': 'vehicles-toggle-count',
    'trailers-view': 'trailers-toggle-count',
    'assets-view': 'assets-toggle-count'
  };
  Object.entries(map).forEach(([viewId, countId]) => {
    const view = document.getElementById(viewId);
    const countEl = document.getElementById(countId);
    if (!view || !countEl) return;
    const badge = view.querySelector('.fleet-mobile-count');
    if (badge) badge.textContent = countEl.textContent;
  });
}

function switchAssetType(type) {
  MaintenanceState.currentAssetType = type;

  // Update toggle buttons
  document.querySelectorAll('.fleet-type-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.type === type);
  });

  const vehiclesView = document.getElementById('vehicles-view');
  const trailersView = document.getElementById('trailers-view');
  const assetsView = document.getElementById('assets-view');

  if (isMobileView()) {
    // On mobile: show all views, render all grids
    if (vehiclesView) vehiclesView.style.display = 'block';
    if (trailersView) trailersView.style.display = 'block';
    if (assetsView) assetsView.style.display = 'block';
    renderFleetGrid();
    renderTrailerGrid();
    renderAssetGrid();
    setupMobileCollapsible();
  } else {
    // Desktop: toggle views as before
    if (vehiclesView) vehiclesView.style.display = type === 'vehicles' ? 'block' : 'none';
    if (trailersView) trailersView.style.display = type === 'trailers' ? 'block' : 'none';
    if (assetsView) assetsView.style.display = type === 'assets' ? 'block' : 'none';

    if (type === 'vehicles') {
      renderFleetGrid();
    } else if (type === 'trailers') {
      renderTrailerGrid();
    } else {
      renderAssetGrid();
    }
  }
}

function updateAssetTypeCounts() {
  const vehiclesCount = document.getElementById('vehicles-toggle-count');
  const trailersCount = document.getElementById('trailers-toggle-count');
  const assetsCount = document.getElementById('assets-toggle-count');

  if (vehiclesCount) {
    const activeVehicles = MaintenanceState.trucks.filter(t => t.active !== false).length;
    vehiclesCount.textContent = activeVehicles;
  }

  if (trailersCount) {
    const activeTrailers = MaintenanceState.trailers.filter(t => t.active !== false).length;
    trailersCount.textContent = activeTrailers;
  }

  if (assetsCount) {
    assetsCount.textContent = MaintenanceState.assets.length;
  }

  updateMobileCollapsibleCounts();
}

// ============================================
// FLEET GRID RENDERING
// ============================================
let _fleetGridTimer = null;
function renderFleetGrid() {
  // Debounce: batch rapid-fire subscription callbacks into one render
  if (_fleetGridTimer) clearTimeout(_fleetGridTimer);
  _fleetGridTimer = setTimeout(_doRenderFleetGrid, 50);
}
function _doRenderFleetGrid() {
  _fleetGridTimer = null;
  if (isMaintenanceModalOpen()) return;
  refreshVehiclePanel(); // TASK-9898 S3 — live data reaches an open panel too
  const container = document.getElementById('fleet-grid');
  const attentionContainer = document.getElementById('fleet-grid-attention');
  const attentionSection = document.getElementById('fleet-attention-section');
  const allHeader = document.getElementById('fleet-all-header');
  const attentionCount = document.getElementById('attention-count');
  const allCount = document.getElementById('all-count');

  if (!container) return;

  console.log('[Maintenance] Rendering fleet grid with', MaintenanceState.trucks.length, 'trucks');

  if (MaintenanceState.trucks.length === 0) {
    // TASK-9898 — clear the fleet-level strip + default line too (last truck removed).
    renderFleetReadingsStrip([], new Map());
    renderFleetDefaultIntervalLine([], new Map());
    renderFleetStatusTiles(null);
    if (attentionSection) attentionSection.classList.add('hidden');
    if (allHeader) allHeader.style.display = 'none';
    // Show loading spinner until first snapshot arrives, then show empty state
    if (!MaintenanceState._trucksLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading fleet...</div>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">🚛</div>
          <div class="empty-state-title">No vehicles in fleet</div>
          <div class="empty-state-text">Add trucks in the Roster module first.</div>
        </div>
      `;
    }
    return;
  }

  // Apply filters
  const { search, type, status, expiry } = MaintenanceState.fleetFilter;
  let filtered = MaintenanceState.trucks;

  if (search) {
    const searchLower = search.trim().toLowerCase();
    filtered = filtered.filter(t => {
      const fields = [t.unit, t.name, t.make, t.model].filter(Boolean);
      if (fields.some(f => f.toLowerCase().includes(searchLower))) return true;
      if (t.inventoryItems?.length) {
        return t.inventoryItems.some(item =>
          (item.name || '').toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  }
  if (type !== 'all') {
    const typeLabel = window.getTypeLabel?.(type, 'truckTypes')?.toLowerCase() || '';
    const typeLc = String(type).toLowerCase(); // TASK-9339 — in-use slugs keep their stored case
    filtered = filtered.filter(t => {
      const tt = (t.type || '').toLowerCase();
      return tt === typeLc || tt === typeLabel;
    });
  }
  if (expiry === 'reg') {
    filtered = filtered.filter(t => isVehicleExpiring(t, 'reg'));
  } else if (expiry === 'inspection') {
    filtered = filtered.filter(t => isVehicleExpiring(t, 'inspection'));
  }

  // TASK-9898 — PM position of the WHOLE fleet once per render: the readings
  // strip and the default-interval line are fleet-wide (filters do not hide a
  // typo), and the filtered cards below reuse the same answer.
  const fleetMeters = new Map(MaintenanceState.trucks.map(t => [t.id, getLatestMeters(t.id)]));
  renderFleetReadingsStrip(MaintenanceState.trucks, fleetMeters);
  renderFleetDefaultIntervalLine(MaintenanceState.trucks, fleetMeters);

  // Calculate attention status for each truck — the WHOLE fleet (TASK-9898 S4:
  // the count tiles ignore filters), then keep the filtered ones for the grid.
  const fleetRows = MaintenanceState.trucks.map(truck => {
    const meters = fleetMeters.get(truck.id) || getLatestMeters(truck.id);
    const pmStatus = calculatePMStatus(truck, meters);
    const pmUrgency = calculatePMUrgency(truck, meters);
    const activeHold = getActiveHoldForAsset(truck.id, 'truck');
    // TASK-9899 — the open COUNT is the shared vocabulary (window.MaintStatus,
    // bi.js twin of utils/maintenanceStatus): Closed / Done are finished, the
    // same as get_vehicle_status. The safety issue stays on the BOARD test
    // (status !== 'completed', shared/safetyHold.js): a critical "Closed" WO
    // still holds the unit, so it still shows as one.
    const truckTasks = MaintenanceState.tasks.filter(t =>
      t.truckId === truck.id && t.status !== 'completed'
    );
    const openTasks = truckTasks.filter(t => window.MaintStatus.isMaintOpen(t.status)).length;
    const hasSafetyIssue = truckTasks.some(t => t.priority === 'critical');
    const missingDvirToday = isMissingDvirDispatched(truck.id);
    // TASK-9659 — was `pmStatus.class === 'pm-due'`, a class nothing returns, so
    // due-soon never reached the attention section.
    // TASK-9898 — a reading that only LOOKS mistyped is not attention on its own
    // (it lives in the readings strip), unless no meter compares to the last PM:
    // then the typo may be hiding an overdue PM, so the truck stays.
    const needsAttention = fleetNeedsAttention({ activeHold, hasSafetyIssue, pmStatus, missingDvirToday });

    return { truck, meters, pmStatus, pmUrgency, activeHold, openTasks, hasSafetyIssue, missingDvirToday, needsAttention };
  });
  renderFleetStatusTiles(fleetStatusTileCounts(fleetRows));
  const filteredIds = new Set(filtered.map(t => t.id));
  const trucksWithStatus = fleetRows.filter(r => filteredIds.has(r.truck.id));

  // Filter by status if needed
  let displayTrucks = trucksWithStatus;
  if (status === 'attention') {
    displayTrucks = trucksWithStatus.filter(t => t.needsAttention);
  } else if (status === 'ok') {
    displayTrucks = trucksWithStatus.filter(t => !t.needsAttention);
  } else if (status === 'hold') {
    displayTrucks = trucksWithStatus.filter(t => t.activeHold);
  } else if (status === 'opentask') {
    // TASK-9766 (b) — any task on the truck that is not completed (open, in
    // progress, pending review): the same openTasks count the card shows. A
    // truck's "Needs Attention" deliberately does NOT count plain open tasks;
    // this filter is the way to see them.
    displayTrucks = trucksWithStatus.filter(t => t.openTasks > 0);
  } else if (fleetTileMatch(status)) {
    // TASK-9898 S4 — a count tile (overdue / due soon / no DVIR / readings).
    displayTrucks = trucksWithStatus.filter(fleetTileMatch(status));
  }

  // Separate attention and ok trucks
  const attentionTrucks = displayTrucks.filter(t => t.needsAttention);
  const okTrucks = displayTrucks.filter(t => !t.needsAttention);

  // Sort by closest PM due (overdue -> soonest -> later -> unknown last), secondary alpha by unit.
  // Default 'unit' keeps today's behavior (trucks arrive pre-sorted alpha at the loader).
  if (MaintenanceState.fleetFilter.sort === 'pmdue') {
    const byPmUrgency = (a, b) => {
      const ua = (a.pmUrgency == null || Number.isNaN(a.pmUrgency)) ? Infinity : a.pmUrgency;
      const ub = (b.pmUrgency == null || Number.isNaN(b.pmUrgency)) ? Infinity : b.pmUrgency;
      if (ua !== ub) return ua - ub;
      return (a.truck.unit || '').localeCompare(b.truck.unit || '');
    };
    attentionTrucks.sort(byPmUrgency);
    okTrucks.sort(byPmUrgency);
  }
  // TASK-9898 — Needs Attention is ranked by severity whatever the Sort select
  // says (hold/critical → dispatched with no DVIR → PM overdue → due soon); the
  // Sort choice only orders trucks of the same rank (Array#sort is stable).
  attentionTrucks.sort((a, b) => fleetAttentionRank(a) - fleetAttentionRank(b));

  // Show/hide attention section
  if (attentionSection && attentionContainer) {
    if (attentionTrucks.length > 0 && status !== 'ok') {
      attentionSection.classList.remove('hidden');
      attentionContainer.innerHTML = attentionTrucks.map(t => renderFleetCard(t)).join('');
      if (attentionCount) attentionCount.textContent = attentionTrucks.length;
    } else {
      attentionSection.classList.add('hidden');
    }
  }

  // Show all header only if we have attention section visible
  if (allHeader) {
    allHeader.style.display = (attentionTrucks.length > 0 && status !== 'ok') ? 'flex' : 'none';
  }
  if (allCount) allCount.textContent = status === 'attention' ? 0 : okTrucks.length;

  // Render main grid (ok trucks, or all if filtering by status)
  const mainTrucks = status === 'attention' ? [] : okTrucks;

  if (displayTrucks.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search or filter.</div>
      </div>
    `;
    return;
  }

  if (mainTrucks.length === 0 && status !== 'attention') {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = mainTrucks.map(t => renderFleetCard(t)).join('');
}

function renderFleetCard({ truck, meters, pmStatus, activeHold, openTasks, hasSafetyIssue, missingDvirToday }) {
  const showInspFlag = shouldShowInspectionFlag(truck);
  const daysUntil = truck.inspectionExpiry ? Math.ceil((new Date(truck.inspectionExpiry) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const inspFlagTip = !truck.inspectionExpiry
    ? 'No inspection date entered — add an expiry date in Roster to track compliance.'
    : daysUntil < 0
      ? `Annual inspection expired ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} ago. Update in Roster or create a work order.`
      : `Annual inspection due in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}. Schedule service before it expires.`;
  const unitLabel = String((window.getUnitDisplay ? window.getUnitDisplay(truck) : truck.unit) || truck.unit || '?');
  return `
    <div class="fleet-card ${pmStatus.class} ${activeHold ? 'fleet-card-on-hold' : ''} ${hasSafetyIssue ? 'fleet-card-safety-issue' : ''}" ${_cardOpenAttrs('truck', truck.id)}>
      <div class="fleet-card-header">
        <div class="fleet-card-unit">${escapeHtml(window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || '?'))}</div>
        <div class="fleet-card-type">${escapeHtml((window.getTypeLabel ? window.getTypeLabel(truck.type, 'truckTypes') : truck.type) || 'Truck')}</div>
        ${showInspFlag ? `<span class="fleet-card-insp-flag" data-tooltip="${inspFlagTip}"><span class="material-icons">flag</span></span>` : ''}
        ${/* TASK-9898 — no header DVIR icon: the "No DVIR submitted today" banner below says it once. */''}
        ${activeHold ? '<span class="fleet-card-hold-badge">ON HOLD</span>' : ''}
      </div>

      ${activeHold ? `
        <div class="fleet-card-hold-banner">
          <span class="material-icons">block</span>
          <span>${escapeHtml(activeHold.reason)} until ${activeHold.endDate}</span>
        </div>
      ` : ''}

      <div class="fleet-card-meters">
        <div class="meter-item">
          <span class="material-icons">speed</span>
          <span class="meter-value">${meters.miles !== null ? formatNumber(meters.miles) : '--'}</span>
          <span class="meter-label">${window.AppState?.settings?.distanceUnit === 'km' ? 'km' : 'miles'}</span>
        </div>
        <div class="meter-item">
          <span class="material-icons">schedule</span>
          <span class="meter-value">${meters.hours !== null ? formatNumber(meters.hours) : '--'}</span>
          <span class="meter-label">hours</span>
        </div>
      </div>

      <div class="fleet-card-pm ${pmStatus.class}">
        <span class="material-icons">${pmStatus.icon}</span>
        <span>${escapeHtml(fleetPmHeadline(pmStatus))}</span>
      </div>
      ${renderPMNotes(truck, pmStatus)}

      ${openTasks > 0 ? `
        <div class="fleet-card-tasks clickable" onclick="event.stopPropagation(); window.MaintenanceModule.showTruckTasks('${escJsAttr(truck.id)}')" title="View open tasks">
          <span class="material-icons">assignment</span>
          <span>${openTasks} open task${openTasks > 1 ? 's' : ''}</span>
          <span class="material-icons tasks-arrow">chevron_right</span>
        </div>
      ` : ''}

      ${missingDvirToday ? `
        <div class="fleet-card-dvir-banner" onclick="event.stopPropagation(); window.MaintenanceModule.showDVIRHistory('${escJsAttr(truck.id)}')">
          <span class="material-icons">warning</span>
          <div>
            <div class="fleet-dvir-date">No DVIR submitted today</div>
          </div>
          <span class="material-icons" style="margin-left:auto;font-size:16px;opacity:0.6;">chevron_right</span>
        </div>
      ` : ''}

      ${/* TASK-9898 S3 — two buttons: one contextual primary (Release on a held
           truck, else Log PM) + "⋯". Info, History, DVIRs, Fluids, Meters and
           Hold live in the Vehicle panel (tap the card or "⋯"). */''}
      <div class="fleet-card-actions">
        ${activeHold ? `
          <button class="btn btn-sm btn-secondary" style="min-height:44px;color:var(--action-danger, #DC2626);" onclick="event.stopPropagation(); window.MaintenanceModule.releaseHold('${escJsAttr(activeHold.id)}')">
            <span class="material-icons">lock_open</span> Release
          </button>
        ` : `
          <button class="btn btn-sm btn-primary" style="min-height:44px;" onclick="event.stopPropagation(); window.MaintenanceModule.showLogPM('${escJsAttr(truck.id)}')">
            <span class="material-icons">build</span> Log PM
          </button>
        `}
        ${_cardMoreButton('truck', truck.id, unitLabel)}
      </div>
    </div>
  `;
}

// ============================================
// TASK-9898 S3 — VEHICLE PANEL (tap a card)
// ============================================
// Tap a truck, trailer or asset card → one panel on the Work Order History
// pattern: three plain totals, then one row per section (Info · History · DVIRs
// · Fluids · Meters), actions below. Each row opens the SAME modal the card
// buttons used to open (History is today's modal, Email Summary / Compliance
// Report / Export CSV included), stacked over the panel, so closing it lands
// back here. Card keeps one contextual primary (Log PM / Release / Log Hours)
// + "⋯" (this panel).
//
// Meters → showUpdateMeters (the guarded path: confirmPlausibleMeter asks
// before an implausible reading is saved). The card's old "Miles" button went
// to showCorrectMeters → the unguarded correctMeters endpoint; that button is
// gone from Fleet Status (hardening that endpoint is slice 0, not here).

// Which panel is open: { kind: 'truck'|'trailer'|'asset', id } or null.
let _vehiclePanel = null;

// TASK-10269 — ONE rule for which work orders the panel tiles count and Work
// Order History / equipment history list: finished (shared vocabulary), dated,
// and not dated after today. Returns that order's local day, or '' if it does
// not count. maintVehicleInfo.js gets this same function through _bind.
function _ymd(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
function maintHistoryDay(t) {
  if (!t || !_woIsClosed(t.status)) return '';
  const d = t.completedAt?.toDate?.() || t.createdAt?.toDate?.();
  if (!d || typeof d.getTime !== 'function' || Number.isNaN(d.getTime())) return '';
  const day = _ymd(d);
  return day <= _ymd(new Date()) ? day : '';
}
// TASK-10269 — a typed cost/hours ("$1,250.00" → 1250) via window.Moneyish (shared/moneyish.js); also given to maintVehicleInfo.js.
function maintMoney(v) { return window.Moneyish ? window.Moneyish.parse(v) : (Number(v) || 0); }

function _panelCards(kind, id) {
  const key = kind === 'trailer' ? 'trailerId' : kind === 'asset' ? 'assetId' : 'truckId';
  const mine = MaintenanceState.tasks.filter(t => t[key] === id);
  const done = mine.filter(t => maintHistoryDay(t));
  let labor = 0;
  let cost = 0;
  done.forEach(t => {
    if (t.costs) {
      labor += maintMoney(t.costs.laborHours);
      cost += maintMoney(t.costs.totalCost);
    }
  });
  // Open = not finished (an undated/future finished order is neither open nor counted).
  return { open: mine.filter(t => !_woIsClosed(t.status)).length, done: done.length, labor, cost };
}

function _panelRow({ icon, label, meta, action, warn }) {
  return `
    <button type="button" class="vehicle-panel-row" onclick="${action}"
      style="display:flex;align-items:center;gap:12px;width:100%;min-height:52px;padding:10px 12px;border:0;border-top:1px solid var(--border-light);background:none;cursor:pointer;text-align:left;color:var(--text-primary);font-size:0.95rem;">
      <span class="material-icons" style="font-size:20px;color:${warn ? 'var(--warning, #D97706)' : 'var(--text-secondary)'};">${icon}</span>
      <span style="flex:1;min-width:0;">
        <span style="display:block;font-weight:600;">${escapeHtml(label)}</span>
        ${meta ? `<span style="display:block;font-size:0.8rem;color:${warn ? 'var(--warning, #B45309)' : 'var(--text-secondary)'};overflow-wrap:anywhere;">${escapeHtml(meta)}</span>` : ''}
      </span>
      <span class="material-icons" style="font-size:20px;color:var(--text-secondary);">chevron_right</span>
    </button>`;
}

function _panelVehicle(kind, id) {
  if (kind === 'trailer') return MaintenanceState.trailers.find(t => t.id === id) || null;
  if (kind === 'asset') return MaintenanceState.assets.find(a => a.id === id) || null;
  return MaintenanceState.trucks.find(t => t.id === id) || null;
}

function showVehiclePanel(kind, id) {
  if (!_panelVehicle(kind, id)) { window.showToast?.('Not found', 'error'); return; }
  _vehiclePanel = { kind, id };
  _renderVehiclePanel();
  document.getElementById('modal-vehicle-panel')?.classList.remove('hidden');
}

// Repaint an open panel (a save in a modal stacked over it changed its numbers).
function refreshVehiclePanel() {
  const m = document.getElementById('modal-vehicle-panel');
  if (!_vehiclePanel || !m || m.classList.contains('hidden')) return;
  if (!_panelVehicle(_vehiclePanel.kind, _vehiclePanel.id)) { m.classList.add('hidden'); _vehiclePanel = null; return; }
  _renderVehiclePanel();
}

// A row that leaves Fleet Status (open work orders → Work Orders tab) closes the
// panel first; every other row opens its modal on top of the panel.
function vehiclePanelGo(action) {
  const p = _vehiclePanel;
  if (!p) return;
  const M = window.MaintenanceModule;
  const v = _panelVehicle(p.kind, p.id);
  if (!v) return;
  const unit = (window.getUnitDisplay ? window.getUnitDisplay(v) : v.unit) || v.unit || v.name || '';
  const close = () => closeModal('vehicle-panel');
  switch (action) {
    case 'tasks':
      close();
      if (p.kind === 'trailer') M.showTrailerTasks(p.id);
      else if (p.kind === 'asset') M.showAssetTasks(p.id);
      else M.showTruckTasks(p.id);
      return;
    case 'info': return p.kind === 'trailer' ? M.showTrailerInfo(p.id) : M.showVehicleInfo(p.id);
    case 'history':
      if (p.kind === 'trailer') return M.showTrailerHistory(p.id);
      if (p.kind === 'asset') return M.showAssetHistory(p.id);
      return M.showWorkHistory(p.id);
    case 'dvirs': return M.showDVIRHistory(p.id);
    case 'fluids': return M.showFluidsLog(p.id);
    case 'meters': return M.showUpdateMeters(p.id);
    case 'logpm': return M.showLogPM(p.id);
    case 'hours': return M.showLogAssetHours(p.id);
    case 'edit': return M.showQuickEditAsset(p.id);
    case 'hold': return M.showNewHoldForVehicle(p.id, unit, p.kind);
    case 'release': {
      const hold = getActiveHoldForAsset(p.id, p.kind);
      if (hold) return M.releaseHold(hold.id);
      return undefined;
    }
    default: return undefined;
  }
}

function _renderVehiclePanel() {
  const p = _vehiclePanel;
  const body = document.getElementById('vehicle-panel-body');
  const title = document.getElementById('vehicle-panel-title');
  if (!p || !body) return;
  const v = _panelVehicle(p.kind, p.id);
  if (!v) return;
  const go = (a) => `window.MaintenanceModule.vehiclePanelGo('${escJsAttr(a)}')`;
  const unit = (window.getUnitDisplay ? window.getUnitDisplay(v) : v.unit) || v.unit || v.name || 'Unnamed';
  const typeLabel = p.kind === 'asset'
    ? getAssetCategoryLabel(v.category)
    : ((window.getTypeLabel ? window.getTypeLabel(v.type, p.kind === 'trailer' ? 'trailerTypes' : 'truckTypes') : v.type) || (p.kind === 'trailer' ? 'Trailer' : 'Truck'));
  if (title) title.textContent = `${unit} · ${typeLabel}`;

  const c = _panelCards(p.kind, p.id);
  // TASK-10269 — with work orders counted, each tile opens History (the same
  // orders: hours and cost are sums of that list). 0 orders = plain tile.
  const tile = (val, lbl) => c.done
    ? `<button type="button" class="stat-item stat-item-link" onclick="${go('history')}" aria-label="${window.escAttr(`${lbl} ${val}, view history`)}"><span class="stat-value">${val}</span><span class="stat-label">${lbl}</span><span class="stat-link">View ›</span></button>`
    : `<div class="stat-item"><span class="stat-value">${val}</span><span class="stat-label">${lbl}</span></div>`;
  const hold = p.kind === 'asset' ? null : getActiveHoldForAsset(p.id, p.kind);
  const lines = [];   // status lines above the totals (each fact once)
  const rows = [];
  const actions = [];

  if (hold) lines.push({ icon: 'block', text: `On hold: ${hold.reason || 'hold'} until ${hold.endDate || '—'}`, warn: true });

  if (p.kind === 'truck') {
    const meters = getLatestMeters(p.id);
    const pmStatus = calculatePMStatus(v, meters);
    lines.push({ icon: pmStatus.icon, text: fleetPmHeadline(pmStatus), warn: pmStatus.status !== 'ok' && pmStatus.status != null });
    const dist = window.AppState?.settings?.distanceUnit === 'km' ? 'km' : 'mi';
    const meterText = [
      meters.miles !== null ? `${formatNumber(meters.miles)} ${dist}` : '',
      meters.hours !== null ? `${formatNumber(meters.hours)} hrs` : ''
    ].filter(Boolean).join(' · ') || 'No readings yet';
    const noDvir = isMissingDvirDispatched(p.id);
    if (c.open) rows.push(_panelRow({ icon: 'assignment', label: 'Open work orders', meta: `${c.open} open`, action: go('tasks') }));
    rows.push(_panelRow({ icon: 'info', label: 'Info', meta: [v.year, v.make, v.model].filter(Boolean).join(' '), action: go('info') }));
    rows.push(_panelRow({ icon: 'history', label: 'History', meta: `${c.done} completed work order${c.done === 1 ? '' : 's'}`, action: go('history') }));
    rows.push(_panelRow({ icon: 'description', label: 'DVIRs', meta: noDvir ? 'No DVIR submitted today' : '', warn: noDvir, action: go('dvirs') }));
    rows.push(_panelRow({ icon: 'opacity', label: 'Fluids', meta: '', action: go('fluids') }));
    rows.push(_panelRow({ icon: 'speed', label: 'Meters', meta: `${meterText} · update miles or hours`, action: go('meters') }));
    actions.push(`<button type="button" class="btn btn-primary" style="min-height:44px;" onclick="${go('logpm')}"><span class="material-icons">build</span> Log PM</button>`);
  } else if (p.kind === 'trailer') {
    if (c.open) rows.push(_panelRow({ icon: 'assignment', label: 'Open work orders', meta: `${c.open} open`, action: go('tasks') }));
    rows.push(_panelRow({ icon: 'info', label: 'Info', meta: [v.year, v.make, v.model].filter(Boolean).join(' '), action: go('info') }));
    rows.push(_panelRow({ icon: 'history', label: 'History', meta: `${c.done} completed work order${c.done === 1 ? '' : 's'}`, action: go('history') }));
  } else {
    const hoursOn = !!v.hoursTracking?.enabled;
    const pm = calculateAssetPMStatus(v);
    if (pm) lines.push({ icon: pm.icon, text: pm.text, warn: pm.class !== 'pm-ok' });
    if (c.open) rows.push(_panelRow({ icon: 'assignment', label: 'Open work orders', meta: `${c.open} open`, action: go('tasks') }));
    rows.push(_panelRow({ icon: 'history', label: 'History', meta: `${c.done} completed work order${c.done === 1 ? '' : 's'}`, action: go('history') }));
    if (hoursOn) rows.push(_panelRow({ icon: 'schedule', label: 'Hours', meta: `${formatNumber(v.hoursTracking?.currentHours || 0)} hrs · log hours`, action: go('hours') }));
    rows.push(_panelRow({ icon: 'edit', label: 'Edit', meta: [v.partNumber, v.serialNumber].filter(Boolean).join(' · '), action: go('edit') }));
  }

  if (p.kind !== 'asset') {
    actions.push(hold
      ? `<button type="button" class="btn btn-secondary" style="min-height:44px;color:var(--action-danger, #DC2626);" onclick="${go('release')}"><span class="material-icons">lock_open</span> Release hold</button>`
      : `<button type="button" class="btn btn-secondary" style="min-height:44px;" onclick="${go('hold')}"><span class="material-icons">block</span> Put on hold</button>`);
  }

  body.innerHTML = `
    ${lines.map(l => `
      <div style="display:flex;align-items:center;gap:8px;margin:0 0 8px;font-size:0.9rem;color:${l.warn ? 'var(--warning, #B45309)' : 'var(--text-primary)'};">
        <span class="material-icons" style="font-size:18px;">${escapeHtml(l.icon || 'info')}</span>
        <span style="min-width:0;overflow-wrap:anywhere;">${escapeHtml(l.text || '')}</span>
      </div>`).join('')}
    <div class="history-summary"><div class="history-summary-stats">${tile(c.done, 'Work Orders')}${tile(c.labor.toFixed(1), 'Labor Hours')}${tile('$' + c.cost.toFixed(2), 'Total Cost')}</div></div>
    <div class="vehicle-panel-sections" style="margin-top:12px;border-bottom:1px solid var(--border-light);">${rows.join('')}</div>
    ${actions.length ? `<div class="vehicle-panel-actions" style="display:flex;flex-wrap:wrap;gap:8px;margin-top:16px;">${actions.join('')}</div>` : ''}`;
}

// The card's two buttons: one contextual primary + "⋯" (opens the panel).
// Both stop the click so it does not also open the panel from the card.
function _cardMoreButton(kind, id, unit) {
  return `<button type="button" class="btn btn-sm btn-secondary fleet-card-more" style="flex:0 0 auto;min-width:44px;min-height:44px;"
      aria-label="${window.escAttr(`More for ${unit}`)}" title="More"
      onclick="event.stopPropagation(); window.MaintenanceModule.showVehiclePanel('${escJsAttr(kind)}', '${escJsAttr(id)}')">
      <span class="material-icons" style="font-size:20px;">more_horiz</span>
    </button>`;
}

// Whole-card tap target (mouse / touch). Deliberately NOT role="button": that
// makes every button inside the card presentational to a screen reader. The
// keyboard / screen-reader path is the card's real "⋯" button.
function _cardOpenAttrs(kind, id) {
  return `style="cursor:pointer;"
      onclick="window.MaintenanceModule.showVehiclePanel('${escJsAttr(kind)}', '${escJsAttr(id)}')"`;
}

// ============================================
// TRAILER GRID RENDERING
// ============================================
let _trailerGridTimer = null;
function renderTrailerGrid() {
  if (_trailerGridTimer) clearTimeout(_trailerGridTimer);
  _trailerGridTimer = setTimeout(_doRenderTrailerGrid, 50);
}
function _doRenderTrailerGrid() {
  _trailerGridTimer = null;
  if (isMaintenanceModalOpen()) return;
  refreshVehiclePanel();
  const container = document.getElementById('trailer-grid');
  const countEl = document.getElementById('trailers-count');

  if (!container) return;

  console.log('[Maintenance] Rendering trailer grid with', MaintenanceState.trailers.length, 'trailers');

  if (MaintenanceState.trailers.length === 0) {
    if (!MaintenanceState._trailersLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading...</div>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">🚚</div>
          <div class="empty-state-title">No trailers in fleet</div>
          <div class="empty-state-text">Add trailers in the Roster module first.</div>
        </div>
      `;
    }
    if (countEl) countEl.textContent = '0';
    return;
  }

  // Apply filters
  const { search, type, status } = MaintenanceState.trailerFilter;
  let filtered = MaintenanceState.trailers.filter(t => t.active !== false);

  if (search) {
    const searchLower = search.trim().toLowerCase();
    filtered = filtered.filter(t => {
      const fields = [t.unit, t.name, t.make, t.type].filter(Boolean);
      if (fields.some(f => f.toLowerCase().includes(searchLower))) return true;
      if (t.inventoryItems?.length) {
        return t.inventoryItems.some(item =>
          (item.name || '').toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  }
  if (type !== 'all') {
    const typeLabel = window.getTypeLabel?.(type, 'trailerTypes')?.toLowerCase() || '';
    const typeLc = String(type).toLowerCase(); // TASK-9339
    filtered = filtered.filter(t => {
      const tt = (t.type || '').toLowerCase();
      return tt === typeLc || tt === typeLabel;
    });
  }

  // Calculate attention status for each trailer
  const now = new Date();
  const trailersWithStatus = filtered.map(trailer => {
    const activeHold = getActiveHoldForAsset(trailer.id, 'trailer');
    const trailerTasks = MaintenanceState.tasks.filter(t =>
      t.trailerId === trailer.id && window.MaintStatus.isMaintOpen(t.status) // TASK-9899
    );
    const openTasks = trailerTasks.length;
    // TASK-9899 (COD) — a critical WO the board still holds on (status !==
    // 'completed', e.g. a legacy critical "Closed") keeps the trailer in Needs
    // Attention even though it no longer counts as an open task.
    const hasSafetyIssue = MaintenanceState.tasks.some(t =>
      t.trailerId === trailer.id && t.priority === 'critical' && t.status !== 'completed'
    );

    // Check expiry dates
    const inspExpiry = trailer.inspectionExpiry ? new Date(trailer.inspectionExpiry) : null;
    const regExpiry = _regExpiryOf(trailer) ? new Date(trailer.registrationExpiry) : null;
    const insExpiry = trailer.insuranceExpiry ? new Date(trailer.insuranceExpiry) : null;

    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    const inspStatus = getExpiryStatus(inspExpiry, now, thirtyDaysFromNow);
    const regStatus = trailer.registrationNoExpiry === true ? 'ok' : getExpiryStatus(regExpiry, now, thirtyDaysFromNow);
    const insStatus = getExpiryStatus(insExpiry, now, thirtyDaysFromNow);

    const needsAttention = activeHold || openTasks > 0 || hasSafetyIssue ||
      inspStatus === 'expired' || inspStatus === 'warning' ||
      regStatus === 'expired' || regStatus === 'warning' ||
      insStatus === 'expired' || insStatus === 'warning';

    return {
      trailer,
      activeHold,
      openTasks,
      inspExpiry,
      regExpiry,
      insExpiry,
      inspStatus,
      regStatus,
      insStatus,
      needsAttention
    };
  });

  // Filter by status if needed
  let displayTrailers = trailersWithStatus;
  if (status === 'attention') {
    displayTrailers = trailersWithStatus.filter(t => t.needsAttention);
  } else if (status === 'ok') {
    displayTrailers = trailersWithStatus.filter(t => !t.needsAttention);
  } else if (status === 'hold') {
    displayTrailers = trailersWithStatus.filter(t => t.activeHold);
  }

  if (countEl) countEl.textContent = displayTrailers.length;

  if (displayTrailers.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔍</div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search or filter.</div>
      </div>
    `;
    return;
  }

  container.innerHTML = displayTrailers.map(t => renderTrailerCard(t)).join('');
}

// TASK-9814 follow-up — "Doesn't expire" wins: a stale date left on the doc is never read as expiring/expired.
function _regExpiryOf(v) { return !v || v.registrationNoExpiry === true ? '' : (v.registrationExpiry || ''); }

function getExpiryStatus(expiryDate, now, thirtyDaysFromNow) {
  if (!expiryDate) return 'unknown';
  if (expiryDate < now) return 'expired';
  if (expiryDate < thirtyDaysFromNow) return 'warning';
  return 'ok';
}

function formatExpiryDate(date) {
  if (!date) return 'Not set';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function renderTrailerCard({ trailer, activeHold, openTasks, inspExpiry, regExpiry, insExpiry, inspStatus, regStatus, insStatus, needsAttention }) {
  const typeLabel = (window.getTypeLabel ? window.getTypeLabel(trailer.type, 'trailerTypes') : capitalizeFirst(trailer.type)) || 'Trailer';
  const showInspFlag = shouldShowInspectionFlag(trailer);
  const daysUntil = trailer.inspectionExpiry ? Math.ceil((new Date(trailer.inspectionExpiry) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const inspFlagTip = !trailer.inspectionExpiry
    ? 'No inspection date entered — add an expiry date in Roster to track compliance.'
    : daysUntil < 0
      ? `Annual inspection expired ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} ago. Update in Roster or create a work order.`
      : `Annual inspection due in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}. Schedule service before it expires.`;

  // Get display name - prefer getUnitDisplay helper, fallback to unit, name, then make/model
  let displayName = window.getUnitDisplay ? window.getUnitDisplay(trailer) : trailer.unit;
  if (!displayName || displayName.trim() === '') {
    displayName = trailer.name;
  }
  if (!displayName || displayName.trim() === '') {
    displayName = [trailer.make, trailer.model].filter(Boolean).join(' ');
  }
  if (!displayName || displayName.trim() === '') {
    displayName = 'Unnamed Trailer';
  }

  return `
    <div class="fleet-card fleet-card-trailer ${needsAttention ? 'fleet-card-attention' : ''} ${activeHold ? 'fleet-card-on-hold' : ''}" ${_cardOpenAttrs('trailer', trailer.id)}>
      <div class="fleet-card-header">
        <div class="fleet-card-unit">${escapeHtml(displayName)}</div>
        <div class="fleet-card-type">${escapeHtml(typeLabel)}</div>
        ${showInspFlag ? `<span class="fleet-card-insp-flag" data-tooltip="${inspFlagTip}"><span class="material-icons">flag</span></span>` : ''}
        ${activeHold ? '<span class="fleet-card-hold-badge">ON HOLD</span>' : ''}
      </div>

      ${activeHold ? `
        <div class="fleet-card-hold-banner">
          <span class="material-icons">block</span>
          <span>${escapeHtml(activeHold.reason)} until ${activeHold.endDate}</span>
        </div>
      ` : ''}

      <div class="trailer-card-expiries">
        <div class="trailer-expiry-item expiry-${inspStatus}">
          <span class="material-icons">${inspStatus === 'expired' ? 'error' : inspStatus === 'warning' ? 'warning' : 'verified'}</span>
          <span class="expiry-label">Inspection</span>
          <span class="expiry-date">${formatExpiryDate(inspExpiry)}</span>
        </div>
        <div class="trailer-expiry-item expiry-${regStatus}">
          <span class="material-icons">${regStatus === 'expired' ? 'error' : regStatus === 'warning' ? 'warning' : 'how_to_reg'}</span>
          <span class="expiry-label">Registration</span>
          <span class="expiry-date">${trailer.registrationNoExpiry === true ? 'Doesn’t expire' : formatExpiryDate(regExpiry)}</span>
        </div>
        <div class="trailer-expiry-item expiry-${insStatus}">
          <span class="material-icons">${insStatus === 'expired' ? 'error' : insStatus === 'warning' ? 'warning' : 'shield'}</span>
          <span class="expiry-label">Insurance</span>
          <span class="expiry-date">${formatExpiryDate(insExpiry)}</span>
        </div>
      </div>

      ${openTasks > 0 ? `
        <div class="fleet-card-tasks clickable" onclick="event.stopPropagation(); window.MaintenanceModule.showTrailerTasks('${escJsAttr(trailer.id)}')" title="View open tasks">
          <span class="material-icons">assignment</span>
          <span>${openTasks} open task${openTasks > 1 ? 's' : ''}</span>
          <span class="material-icons tasks-arrow">chevron_right</span>
        </div>
      ` : ''}

      ${/* TASK-9898 S3 — same layout as a truck: Release on a held trailer, else
           Hold (COD Domain: grounding a trailer stays one tap; trailers have no
           PM to log), + "⋯" (Vehicle panel: Info · History · Hold). */''}
      <div class="fleet-card-actions">
        ${activeHold ? `
          <button class="btn btn-sm btn-secondary" style="min-height:44px;color:var(--action-danger, #DC2626);" onclick="event.stopPropagation(); window.MaintenanceModule.releaseHold('${escJsAttr(activeHold.id)}')">
            <span class="material-icons">lock_open</span> Release
          </button>
        ` : `
          <button class="btn btn-sm btn-secondary" style="min-height:44px;" onclick="event.stopPropagation(); window.MaintenanceModule.showNewHoldForVehicle('${escJsAttr(trailer.id)}', '${escJsAttr(displayName)}', 'trailer')" title="Put on hold">
            <span class="material-icons">block</span> Hold
          </button>
        `}
        ${_cardMoreButton('trailer', trailer.id, displayName)}
      </div>
    </div>
  `;
}

function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Check if inspection flag should show for vehicle/trailer
// Days until an ISO date string (negative = past). Returns null for missing/invalid dates.
function daysUntilDate(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
}

// "Expiring" = within EXPIRY_WINDOW_DAYS or already overdue. Matches the 30-day
// inspection-flag default used in roster.js / shouldShowInspectionFlag.
const EXPIRY_WINDOW_DAYS = 30;
function isVehicleExpiring(truck, kind) {
  const isoStr = kind === 'reg' ? _regExpiryOf(truck) : truck.inspectionExpiry;
  const days = daysUntilDate(isoStr);
  if (days === null) return false; // null/missing expiry never matches (TASK-473 guard precedent)
  return days <= EXPIRY_WINDOW_DAYS;
}

function shouldShowInspectionFlag(entity) {
  if (!entity.showInspectionFlag) return false;

  // If flag was dismissed, don't show
  if (entity.inspectionFlagDismissed) return false;

  // Flag if no inspection date is entered at all
  if (!entity.inspectionExpiry) return true;

  const expiryDate = new Date(entity.inspectionExpiry);
  const today = new Date();
  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  const flagDays = entity.inspectionFlagDays || 30;

  return daysUntilExpiry <= flagDays;
}

// TASK-9898 S5 — "No DVIR submitted today" runs on the TENANT's clock: the
// company's settings.timezone (Settings → timezone, default Central), never a
// hard-coded America/Chicago and never the viewer's device zone. An unusable
// stored value falls back to Central instead of throwing.
function _maintTenantTz() {
  const s = window.AppState?.settings || window.AppState?.tenantData?.settings || {};
  const tz = String(s.timezone || window.AppState?.tenantData?.timezone || '').trim();
  if (tz) {
    try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return tz; } catch (_) { /* fall through */ }
  }
  return 'America/Chicago';
}

// { ymd: 'YYYY-MM-DD', hour: 0-23 } for `date` on the tenant's wall clock.
const _maintClockFmt = new Map();
function _maintTenantClock(date, tz) {
  const zone = tz || _maintTenantTz();
  let fmt = _maintClockFmt.get(zone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit'
    });
    _maintClockFmt.set(zone, fmt);
  }
  const parts = {};
  fmt.formatToParts(date).forEach(p => { parts[p.type] = p.value; });
  return { ymd: `${parts.year}-${parts.month}-${parts.day}`, hour: parseInt(parts.hour, 10) % 24 };
}

function hasDvirToday(truckId, now = new Date()) {
  return !!MaintenanceState.pretripTruckDates[truckId + '_' + _maintTenantClock(now).ymd];
}

// Noon on the tenant's clock (was noon America/Chicago for every company).
function isAfterNoonTenant(now = new Date()) {
  return _maintTenantClock(now).hour >= 12;
}

function isMissingDvirDispatched(truckId) {
  if (!MaintenanceState.dispatchedTruckIds.has(truckId)) return false;
  if (!isAfterNoonTenant()) return false;
  return !hasDvirToday(truckId);
}

// ============================================
// ASSET GRID RENDERING
// ============================================
function getAssetCategoryLabel(category) {
  if (window.getTypeLabel) return window.getTypeLabel(category, 'assetCategories') || 'Other';
  const labels = { drill_pipe: 'Drill Pipe', equipment: 'Equipment', tool: 'Tool', other: 'Other' };
  return labels[category] || 'Other';
}

function getAssetCategoryClass(category) {
  const classes = {
    drill_pipe: 'category-drill-pipe',
    equipment: 'category-equipment',
    tool: 'category-tool',
    other: 'category-other'
  };
  return classes[category] || 'category-other';
}

function calculateAssetPMStatus(asset) {
  if (!asset.hoursTracking?.enabled) return null;
  const currentHours = asset.hoursTracking.currentHours || 0;
  const lastPmHours = asset.hoursTracking.lastPmHours || 0;
  const pmInterval = asset.hoursTracking.pmInterval || 250;
  const hoursSincePM = currentHours - lastPmHours;
  const remaining = pmInterval - hoursSincePM;

  if (remaining < 0) {
    return { class: 'pm-overdue', icon: 'error', text: `PM overdue by ${Math.abs(Math.round(remaining))} hrs`, remaining };
  } else if (remaining < pmInterval * 0.1) {
    return { class: 'pm-due-soon', icon: 'warning', text: `PM due in ${Math.round(remaining)} hrs`, remaining };
  }
  return { class: 'pm-ok', icon: 'check_circle', text: `PM OK — ${Math.round(remaining)} hrs remaining`, remaining };
}

function renderAssetGrid() {
  if (isMaintenanceModalOpen()) return;
  refreshVehiclePanel();
  const container = document.getElementById('asset-grid');
  if (!container) return;

  console.log('[Maintenance] Rendering asset grid with', MaintenanceState.assets.length, 'assets');

  if (MaintenanceState.assets.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon"><span class="material-icons" style="font-size:48px;color:var(--text-muted);">handyman</span></div>
        <div class="empty-state-title">No assets yet</div>
        <div class="empty-state-text">Add equipment and tools in the Roster module under the Assets tab.</div>
      </div>
    `;
    return;
  }

  // Apply filters
  const { search, category } = MaintenanceState.assetFilter;
  let filtered = MaintenanceState.assets;

  if (search) {
    const searchLower = search.trim().toLowerCase();
    filtered = filtered.filter(a =>
      (a.name || '').toLowerCase().includes(searchLower) ||
      (a.partNumber || '').toLowerCase().includes(searchLower) ||
      (a.serialNumber || '').toLowerCase().includes(searchLower)
    );
  }
  if (category !== 'all') {
    filtered = filtered.filter(a => a.category === category);
  }

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon"><span class="material-icons" style="font-size:48px;color:var(--text-muted);">search</span></div>
        <div class="empty-state-title">No matches</div>
        <div class="empty-state-text">Try adjusting your search or filter.</div>
      </div>
    `;
    return;
  }

  // Compute open tasks per asset
  container.innerHTML = filtered.map(asset => {
    const openTasks = MaintenanceState.tasks.filter(t =>
      // TASK-9899 — shared vocabulary; (COD) the equipment card has no hold
      // badge, so a critical WO the board still holds on stays counted.
      t.assetId === asset.id && (window.MaintStatus.isMaintOpen(t.status) || (t.priority === 'critical' && t.status !== 'completed'))
    ).length;
    const pmStatus = calculateAssetPMStatus(asset);
    const pendingRI = MaintenanceState.returnInspections.find(ri => ri.assetId === asset.id);
    return renderAssetCard(asset, openTasks, pmStatus, pendingRI);
  }).join('');
}

function renderAssetCard(asset, openTasks, pmStatus, pendingRI) {
  const catLabel = getAssetCategoryLabel(asset.category);
  const catClass = getAssetCategoryClass(asset.category);
  const hoursEnabled = asset.hoursTracking?.enabled;
  const currentHours = asset.hoursTracking?.currentHours || 0;

  // Tracking info line
  let trackingInfo = '';
  if (asset.trackingMode === 'quantity' && asset.quantity != null) {
    trackingInfo += `<span class="asset-tracking-item"><span class="material-icons">inventory_2</span> Qty: ${escapeHtml(String(asset.quantity))}</span>`;
  } else if (asset.trackingMode === 'individual' && asset.serialNumber) {
    trackingInfo += `<span class="asset-tracking-item"><span class="material-icons">qr_code</span> SN: ${escapeHtml(asset.serialNumber)}</span>`;
  }
  if (asset.manufacturer) {
    trackingInfo += `<span class="asset-tracking-item"><span class="material-icons">factory</span> ${escapeHtml(asset.manufacturer)}</span>`;
  }

  return `
    <div class="fleet-card fleet-card-asset" ${_cardOpenAttrs('asset', asset.id)}>
      <div class="fleet-card-header">
        <div class="fleet-card-unit">${escapeHtml(asset.name || 'Unnamed')}</div>
        <span class="asset-category-badge ${catClass}">${escapeHtml(catLabel)}</span>
      </div>

      <div class="asset-card-body">
        ${asset.partNumber ? `<div class="asset-part-number"><span class="material-icons">tag</span> ${escapeHtml(asset.partNumber)}</div>` : ''}

        ${hoursEnabled ? `
          <div class="asset-card-hours">
            <div class="meter-item">
              <span class="material-icons">schedule</span>
              <span class="meter-value">${formatNumber(currentHours)}</span>
              <span class="meter-label">hours</span>
            </div>
          </div>
          ${pmStatus ? `
            <div class="fleet-card-pm ${pmStatus.class}">
              <span class="material-icons">${pmStatus.icon}</span>
              <span>${pmStatus.text}</span>
            </div>
          ` : ''}
        ` : ''}

        ${trackingInfo ? `<div class="asset-tracking-info">${trackingInfo}</div>` : ''}
      </div>

      ${pendingRI ? `
        <div class="fleet-card-warning" style="display:flex;align-items:center;gap:6px;padding:8px 10px;background:#FEF2F2;border:1px solid #FECACA;border-radius:var(--radius-sm);margin:4px 0;color:#DC2626;font-size:0.78rem;font-weight:600;">
          <span class="material-icons" style="font-size:16px;">assignment_return</span>
          <span style="flex:1;">Return inspection overdue</span>
          <button class="btn btn-sm" onclick="event.stopPropagation(); window.MaintenanceModule.dismissReturnInspection('${escJsAttr(pendingRI.id)}')" style="padding:2px 8px;font-size:0.7rem;background:#fff;border:1px solid #FECACA;color:#DC2626;border-radius:4px;" title="Dismiss this warning">
            <span class="material-icons" style="font-size:12px;">close</span> Dismiss
          </button>
        </div>
      ` : ''}

      ${openTasks > 0 ? `
        <div class="fleet-card-tasks clickable" onclick="event.stopPropagation(); window.MaintenanceModule.showAssetTasks('${escJsAttr(asset.id)}')" title="View open tasks">
          <span class="material-icons">assignment</span>
          <span>${openTasks} open task${openTasks > 1 ? 's' : ''}</span>
          <span class="material-icons tasks-arrow">chevron_right</span>
        </div>
      ` : ''}

      <div class="asset-location-tag" onclick="event.stopPropagation(); window.MaintenanceModule.showAssetLocationComingSoon()">
        <span class="material-icons">place</span>
        <span>Location</span>
      </div>

      ${/* TASK-9898 S3 — same layout: Log Hours (hours-tracked) or History, + "⋯"
           (panel: History · Hours · Edit). */''}
      <div class="fleet-card-actions">
        ${hoursEnabled ? `
          <button class="btn btn-sm btn-secondary" style="min-height:44px;" onclick="event.stopPropagation(); window.MaintenanceModule.showLogAssetHours('${escJsAttr(asset.id)}')" title="Log Hours">
            <span class="material-icons">schedule</span> Log Hours
          </button>
        ` : `
          <button class="btn btn-sm btn-secondary" style="min-height:44px;" onclick="event.stopPropagation(); window.MaintenanceModule.showAssetHistory('${escJsAttr(asset.id)}')" title="Work History">
            <span class="material-icons">history</span> History
          </button>
        `}
        ${_cardMoreButton('asset', asset.id, asset.name || 'Unnamed')}
      </div>
    </div>
  `;
}

function filterAssets() {
  const searchEl = document.getElementById('search-assets');
  const categoryEl = document.getElementById('filter-asset-category');
  MaintenanceState.assetFilter.search = searchEl?.value || '';
  MaintenanceState.assetFilter.category = categoryEl?.value || 'all';
  renderAssetGrid();
}

function showLogAssetHours(assetId) {
  const asset = MaintenanceState.assets.find(a => a.id === assetId);
  if (!asset) return;

  document.getElementById('asset-hours-id').value = assetId;
  document.getElementById('modal-asset-hours-title').textContent = `Log Hours — ${asset.name}`;
  document.getElementById('asset-hours-new').value = '';
  document.getElementById('asset-hours-pm').checked = false;

  const currentHours = asset.hoursTracking?.currentHours || 0;
  const pmStatus = calculateAssetPMStatus(asset);
  document.getElementById('asset-hours-current').innerHTML = `
    <div class="meters-current-row">
      <span class="material-icons">schedule</span>
      <strong>Current Hours:</strong> ${formatNumber(currentHours)}
    </div>
    ${pmStatus ? `
      <div class="meters-current-row">
        <span class="material-icons">${pmStatus.icon}</span>
        <span class="${pmStatus.class}">${pmStatus.text}</span>
      </div>
    ` : ''}
  `;

  document.getElementById('modal-asset-hours').classList.remove('hidden');
}

async function saveAssetHours(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const assetId = document.getElementById('asset-hours-id').value;
  const newHours = parseFloat(document.getElementById('asset-hours-new').value);
  const markPM = document.getElementById('asset-hours-pm').checked;

  if (isNaN(newHours) || newHours < 0) {
    window.showToast?.('Please enter a valid hours reading', 'error');
    return;
  }

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    const updateData = {
      'hoursTracking.currentHours': newHours,
      updatedAt: Timestamp.now()
    };

    if (markPM) {
      updateData['hoursTracking.lastPmHours'] = newHours;
    }

    await updateDoc(doc(db, 'tenants', tenant, 'assets', assetId), updateData);
    window.showToast?.('Hours updated', 'success');
    closeModal('asset-hours');
  } catch (error) {
    console.error('[Maintenance] Error saving asset hours:', error);
    window.showToast?.('Error saving hours', 'error');
  }
}

// ============================================
// QUICK EDIT ASSET
// ============================================
function showQuickEditAsset(assetId) {
  const asset = MaintenanceState.assets.find(a => a.id === assetId);
  if (!asset) return;

  document.getElementById('quick-edit-asset-id').value = assetId;
  document.getElementById('quick-edit-asset-title').textContent = asset.name || 'Edit Asset';
  document.getElementById('quick-edit-asset-condition').value = asset.condition || 'Good';

  const hoursGroup = document.getElementById('quick-edit-asset-hours-group');
  if (asset.hoursTracking?.enabled) {
    hoursGroup.style.display = '';
    document.getElementById('quick-edit-asset-hours').value = asset.hoursTracking?.currentHours || 0;
  } else {
    hoursGroup.style.display = 'none';
  }

  document.getElementById('modal-quick-edit-asset').classList.remove('hidden');

  // TASK-1141 — notes here are a per-unit THREAD now (split-brain mitigation:
  // the old textarea overwrote the same static field roster's form writes).
  window.EquipmentNotes?.mount('quick-edit-asset-notes-thread', {
    kind: 'asset',
    id: assetId,
    unitLabel: asset.name || '',
    legacyNote: asset.notes || '',
  });
}

async function saveQuickEditAsset(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const assetId = document.getElementById('quick-edit-asset-id').value;
  if (!assetId) return;

  const asset = MaintenanceState.assets.find(a => a.id === assetId);
  const condition = document.getElementById('quick-edit-asset-condition').value;

  // TASK-1141 — this modal no longer writes the static `notes` field (its old
  // write forked from roster's full-edit form — the split-brain). Notes go to
  // the per-unit thread subcollection via window.EquipmentNotes.
  const updates = {
    condition,
    updatedAt: Timestamp.now()
  };

  if (asset?.hoursTracking?.enabled) {
    const hours = parseFloat(document.getElementById('quick-edit-asset-hours').value);
    if (!isNaN(hours) && hours >= 0) {
      updates['hoursTracking.currentHours'] = hours;
    }
  }

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    await updateDoc(doc(db, 'tenants', tenant, 'assets', assetId), updates);
    window.showToast?.('Asset updated', 'success');
    closeModal('quick-edit-asset');
  } catch (error) {
    console.error('[Maintenance] Error saving quick edit:', error);
    window.showToast?.('Error saving asset', 'error');
  }
}

function showAssetTasks(assetId) {
  // TASK-9897 — one Vehicle filter; replaces any earlier drill instead of AND-ing.
  woDrillToVehicle('asset:' + assetId);
}

function showAssetHistory(assetId) {
  const asset = MaintenanceState.assets.find(a => a.id === assetId);
  if (!asset) {
    window.showToast?.('Asset not found', 'error');
    return;
  }

  // TASK-10269 — the same rule as the panel tiles (maintHistoryDay).
  const completedTasks = MaintenanceState.tasks.filter(t =>
    t.assetId === assetId && maintHistoryDay(t)
  ).sort((a, b) => {
    const aDate = a.completedAt?.toDate?.() || new Date(0);
    const bDate = b.completedAt?.toDate?.() || new Date(0);
    return bDate - aDate;
  });

  document.getElementById('modal-asset-history-title').textContent = `${asset.name} — Work History`;

  // Get completed return inspections for this asset
  const completedReturns = (MaintenanceState.returnInspections || [])
    .concat((MaintenanceState._allReturnInspections || []))  // Include non-pending ones if loaded
    .filter(ri => ri.assetId === assetId && (ri.status === 'completed' || ri.status === 'dismissed'));

  // Merge work orders and return inspections into a single timeline
  const timelineItems = [];

  completedTasks.forEach(task => {
    const date = task.completedAt?.toDate?.() || task.createdAt?.toDate?.(); // TASK-10269: the maintHistoryDay date (always set here)
    timelineItems.push({ type: 'task', date, data: task });
  });

  completedReturns.forEach(ri => {
    const date = ri.completedAt?.toDate?.() || ri.dismissedAt?.toDate?.() || ri.createdAt?.toDate?.() || new Date();
    timelineItems.push({ type: 'return', date, data: ri });
  });

  timelineItems.sort((a, b) => b.date - a.date);

  let html = '';
  if (timelineItems.length === 0) {
    html = `
      <div class="empty-state">
        <div class="empty-state-icon"><span class="material-icons" style="font-size:48px;color:var(--text-muted);">history</span></div>
        <div class="empty-state-title">No history yet</div>
        <div class="empty-state-text">Completed work orders and return inspections will appear here.</div>
      </div>
    `;
  } else {
    html = `<div class="work-history-list">` + timelineItems.map(item => {
      const dateStr = item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

      if (item.type === 'return') {
        const ri = item.data;
        const qtyText = ri.returnedQty ? `${ri.returnedQty} returned` : '';
        const receiverText = ri.receiver ? `by ${ri.receiver}` : '';
        const yardText = ri.toYardName ? `→ ${ri.toYardName}` : '';
        const statusLabel = ri.status === 'dismissed' ? 'Dismissed' : 'Inspected';
        return `
          <div class="work-history-item" style="border-left:3px solid #3B82F6;">
            <div class="work-history-date">${dateStr}</div>
            <div class="work-history-info">
              <div class="work-history-desc">${escapeHtml([qtyText, receiverText, yardText].filter(Boolean).join(' ') || 'Return recorded')}</div>
              <div class="work-history-meta">
                <span class="maintenance-card-category" style="background:#DBEAFE;color:#1E40AF;">Return Inspection</span>
                <span style="font-size:0.7rem;color:var(--text-secondary);">${statusLabel}</span>
              </div>
              ${ri.condition ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(ri.condition)}</div>` : ''}
            </div>
          </div>
        `;
      }

      // Work order
      const task = item.data;
      const costNum = maintMoney(task.costs?.totalCost); // TASK-10269: legacy / "$1,250.00" text costs
      const hasCosts = costNum > 0;
      return `
        <div class="work-history-item" onclick="window.MaintenanceModule.showTaskDetail('${escJsAttr(task.id)}')">
          <div class="work-history-date">${dateStr}</div>
          <div class="work-history-info">
            <div class="work-history-desc">${escapeHtml(task.description || 'No description')}</div>
            <div class="work-history-meta">
              <span class="maintenance-card-category">${escapeHtml(task.category || 'General')}</span>
              ${hasCosts ? `<span class="cost-badge">$${costNum.toFixed(2)}</span>` : ''}
            </div>
          </div>
          <span class="material-icons" style="color:var(--text-muted);">chevron_right</span>
        </div>
      `;
    }).join('') + `</div>`;
  }

  document.getElementById('asset-history-content').innerHTML = html;
  document.getElementById('modal-asset-history').classList.remove('hidden');
}

function showAssetLocationComingSoon() {
  document.getElementById('modal-asset-location').classList.remove('hidden');
}

async function dismissReturnInspection(riId) {
  const ri = MaintenanceState.returnInspections.find(r => r.id === riId);
  if (!ri) return;

  const asset = MaintenanceState.assets.find(a => a.id === ri.assetId);
  const isQty = asset?.trackingMode === 'quantity';
  const expectedQty = ri.expectedQty || (isQty ? (asset?.deployedQty || 0) : 1);

  // Confirm with option to update inventory
  const confirmed = await window.showConfirm(
    'Dismiss Return Inspection',
    `Dismiss the return inspection for ${ri.assetName || 'this asset'}?${isQty ? `\n\nExpected return: ${expectedQty} units. Inventory will be updated as if all were returned.` : '\n\nThe asset will be marked as available.'}`,
    { confirmText: 'Dismiss & Update Inventory' }
  );
  if (!confirmed) return;

  try {
    const tenantId = MaintenanceState.tenantId;

    // TASK-1265 — atomic + idempotent dismiss: fold the inventory decrement + the
    // status→'dismissed' flip into ONE transaction, gated on the RI not already
    // terminal (completed OR dismissed). Prevents a double-dismiss from double-
    // decrementing deployedQty (same root cause + fix as the return-flow path).
    const riRef = doc(db, 'tenants', tenantId, 'returnInspections', riId);
    const assetRef = asset ? doc(db, 'tenants', tenantId, 'assets', asset.id) : null;
    const txResult = await runTransaction(db, async (tx) => {
      const riSnap = await tx.get(riRef);
      const st = riSnap.exists() ? riSnap.data().status : null;
      if (st === 'completed' || st === 'dismissed') return { alreadyDone: true };
      let committed = null;
      if (asset) {
        if (isQty) {
          const aSnap = await tx.get(assetRef);
          const current = (aSnap.exists() ? aSnap.data().deployedQty : 0) || 0;
          committed = Math.max(0, current - expectedQty);
          if (current - expectedQty < 0) console.warn(`[Rental] deployedQty floor hit on asset ${asset.id}: ${current} - ${expectedQty} < 0 -> clamped to 0.`);
          tx.update(assetRef, { deployedQty: committed, updatedAt: Timestamp.now() });
        } else {
          tx.update(assetRef, { deploymentStatus: 'available', deployedToJobId: null, updatedAt: Timestamp.now() });
        }
      }
      tx.update(riRef, {
        status: 'dismissed', returnedQty: isQty ? expectedQty : 1,
        dismissedBy: window.AppState?.user?.email || 'unknown', dismissedAt: Timestamp.now(),
      });
      return { alreadyDone: false, committedDeployedQty: committed };
    });

    if (txResult.alreadyDone) {
      window.showToast?.('This return inspection was already handled.', 'info');
      return;
    }
    if (asset && isQty) asset.deployedQty = txResult.committedDeployedQty;

    // Log transaction
    await addDoc(collection(db, 'tenants', tenantId, 'assetTransactions'), {
      assetId: ri.assetId, assetName: ri.assetName || '',
      type: 'return', direction: 'return', quantity: isQty ? expectedQty : 1,
      jobId: ri.jobId || null,
      note: 'Returned via maintenance dismiss (no formal inspection)',
      issuedBy: window.AppState?.user?.email || 'unknown',
      createdAt: Timestamp.now()
    });

    window.showToast?.('Return inspection dismissed — inventory updated', 'success');
  } catch (err) {
    console.error('[Maintenance] Dismiss return inspection error:', err);
    window.showToast?.('Error dismissing inspection', 'error');
  }
}

// =============================================================================
// TASK-9659 — PM math is ONE module: functions/src/shared/pmStatus.js, mirrored
// to the browser as window.PmStatus (public/src/shared/pm-status.js, generated
// by scripts/build-shared.cjs). The connector's get_pm_due / get_vehicle_status
// and the in-app assistant run the same function, so this grid, Claude and the
// bot cannot disagree. Do NOT re-implement interval / since-last-PM / due math
// here: it used to live here three times over and one typo read "PM OK, 88,017
// hrs left". The functions below only adapt its answer to the card's shape.
// =============================================================================

// The tenant's settings.pmIntervals AS SAVED (a built-in the tenant never saved
// is a default, not a setting). pmIntervals in state is the merged map + any
// unsaved draft from the PM Settings modal; savedIntervalKeys says what is saved.
function _pmSavedIntervals() {
  const pm = MaintenanceState.pmSettings.pmIntervals || {};
  const out = {};
  (MaintenanceState.pmSettings.savedIntervalKeys || []).forEach(k => {
    if (Object.prototype.hasOwnProperty.call(pm, k)) out[k] = pm[k];
  });
  return out;
}

function _pmVehicleById(vehicleId) {
  return MaintenanceState.trucks.find(t => t.id === vehicleId)
    || (MaintenanceState.trailers || []).find(t => t.id === vehicleId)
    || { id: vehicleId };
}

/** Full PM position of one vehicle (shared/pmStatus.pmStatusFor). */
function getPMState(vehicle) {
  // TASK-9898 follow-up — the company zone dates the suspect-reading review clock.
  return window.PmStatus.pmStatusFor(vehicle, MaintenanceState.metersLog, _pmSavedIntervals(), { timeZone: _maintTenantTz() });
}

// Latest BELIEVABLE miles / hours (a reading that looks mistyped is not shown as
// current, and is not pre-filled into the next form) + the last PM baseline.
// `pm` carries the full shared answer so callers don't compute it twice.
function getLatestMeters(truckId) {
  const s = getPMState(MaintenanceState.trucks.find(t => t.id === truckId) || { id: truckId });
  return {
    miles: s.current.miles,
    hours: s.current.hours,
    lastPM: s.lastPm ? {
      miles: s.lastPm.miles,
      hours: s.lastPm.hours,
      date: s.lastPm.atMs ? new Date(s.lastPm.atMs) : undefined
    } : null,
    pm: s
  };
}

const _PM_BADGE = {
  overdue: { class: 'pm-overdue', icon: 'warning' },
  due_soon: { class: 'pm-due-soon', icon: 'schedule' },
  // A reading that looks mistyped is never "OK": amber, with the fix on the card.
  check_reading: { class: 'pm-due-soon pm-check', icon: 'error_outline' },
  ok: { class: 'pm-ok', icon: 'check_circle' }
};

function calculatePMStatus(truck, meters) {
  const s = (meters && meters.pm) || getPMState(truck);
  const b = _PM_BADGE[s.status]
    || { class: 'pm-unknown', icon: s.reason === 'no meter readings recorded' ? 'help_outline' : 'info' };
  return { class: b.class, icon: b.icon, text: s.label, status: s.status, pm: s };
}

// Sort score (lower = closer to due, negative = overdue): the shared answer's
// urgency — the more urgent meter as a fraction of its interval, only meters
// that compare. No comparable meter = Infinity (sorts last). Same number the
// connector's get_pm_due orders by, so the badge and the sort agree.
function calculatePMUrgency(truck, meters) {
  const s = (meters && meters.pm) || getPMState(truck);
  return Number.isFinite(s.urgency) ? s.urgency : Infinity;
}

// TASK-9659 — the shared plausibility rule on every meter this screen writes
// (Update Meters, Log PM, a task's meter). A reading that does not fit the
// vehicle's history asks first; saved anyway, it carries meterConfirmed: true so
// the PM math believes it (a replaced meter) instead of flagging it.
async function confirmPlausibleMeter(vehicleId, miles, hours) {
  const P = window.PmStatus;
  if (!P) return { proceed: true, confirmed: false };
  const v = _pmVehicleById(vehicleId);
  const r = P.pmCheckReading(P.pmRowsForVehicle(MaintenanceState.metersLog, v), { miles, hours, atMs: Date.now() });
  if (r.ok) return { proceed: true, confirmed: false };
  const label = window.getUnitDisplay ? window.getUnitDisplay(v) : (v.unit || '');
  const msg = `${r.problems.map(p => P.pmProblemText(p, label)).join(' ')} Check the number. Save it only if it is right (for example, the meter was replaced).`;
  const yes = await window.showConfirm('Check this reading', msg, { confirmText: 'Save anyway', cancelText: 'Fix the number', safeDefault: true, icon: 'error_outline' });
  return { proceed: !!yes, confirmed: !!yes };
}

// TASK-9659 — write a metersLog row. firestore.rules lets only an admin or a
// Maintenance holder create one carrying meterConfirmed; anyone else's "Save
// anyway" is saved as a plain row (the card then shows it flagged for the office)
// instead of failing the whole save. Mutates `entry` so optimistic copies match.
async function addMeterRow(tenant, entry) {
  const col = collection(db, 'tenants', tenant, 'metersLog');
  // TASK-9898 follow-up — `createdAt` (SERVER time) starts a mistyped-looking
  // reading's 7-day review clock (shared/pmStatus). The local copy gets the
  // device time so this session's math has a clock until the next load.
  // TASK-9913 — a confirmed row carries who (own uid) + when (server time); rules require both.
  const write = () => addDoc(col, {
    ...entry,
    createdAt: serverTimestamp(),
    ...(entry.meterConfirmed ? { meterConfirmedAt: serverTimestamp(), meterConfirmedBy: auth.currentUser?.uid || null } : {})
  });
  entry.createdAt = Timestamp.now();
  try {
    return await write();
  } catch (err) {
    if (!entry.meterConfirmed || !/permission/i.test(String((err && (err.code || err.message)) || ''))) throw err;
    delete entry.meterConfirmed;
    const ref = await write();
    window.showToast?.('Saved. Only Maintenance can mark a reading as right, so the office will see it flagged.', 'warning');
    return ref;
  }
}

// TASK-9659 — the fix action on a card whose reading looks mistyped.
//  - 'fix'     a newest reading is wrong: log the right one (Update Meters).
//  - 'relogpm' the last PM's reading is wrong: log the PM again with the real meter.
//  - 'right'   the reading IS right (a replaced meter): mark it confirmed.
async function fixSuspectReading(truckId, action, rowId) {
  if (action === 'relogpm') { showLogPM(truckId); return; }
  if (action === 'fix') { showUpdateMeters(truckId); return; }
  if (action !== 'right' || !rowId) return;
  const row = MaintenanceState.metersLog.find(m => m.id === rowId);
  if (!row) return;
  const v = _pmVehicleById(truckId);
  const label = window.getUnitDisplay ? window.getUnitDisplay(v) : (v.unit || '');
  const what = [row.miles != null && row.miles !== '' ? `${formatNumber(row.miles)} mi` : '', row.hours != null && row.hours !== '' ? `${formatNumber(row.hours)} hrs` : ''].filter(Boolean).join(' / ');
  const yes = await window.showConfirm('Mark this reading as right?',
    `${label}: ${what}. PM will count from this reading from now on (use this when the meter was replaced). Readings before it are no longer used.`,
    { confirmText: 'It is right' });
  if (!yes) return;
  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    await updateDoc(doc(db, 'tenants', tenant, 'metersLog', rowId), {
      meterConfirmed: true,
      // TASK-9913 — firestore.rules require server time + the caller's own uid.
      meterConfirmedAt: serverTimestamp(),
      meterConfirmedBy: auth.currentUser?.uid || null
    });
    row.meterConfirmed = true;
    renderFleetGrid();
    window.showToast?.('Reading marked as right', 'success');
  } catch (err) {
    console.error('[Maintenance] confirm reading failed:', err);
    window.showToast?.('Could not update the reading', 'error');
  }
}

// TASK-9898 — a reading looks mistyped AND no meter compares to the last PM, so
// the PM itself cannot be checked (the typo may be hiding an overdue PM).
function fleetPmUncheckable(pmStatus) {
  const pm = pmStatus && pmStatus.pm;
  return !!(pmStatus && pmStatus.status === 'check_reading' && pm
    && !(pm.miles && pm.miles.known) && !(pm.hours && pm.hours.known));
}

// TASK-9898 — the card's PM line. Only the "can't be checked" case is reworded;
// every other status keeps the shared label (window.PmStatus).
function fleetPmHeadline(pmStatus) {
  return fleetPmUncheckable(pmStatus) ? "PM can't be checked — fix the reading" : pmStatus.text;
}

// TASK-9898 — Needs Attention: hold / critical defect / dispatched with no DVIR /
// the PM rule. A reading that only looks mistyped is NOT attention; it is listed
// in the readings strip instead. Plain open tasks are not attention (TASK-9766).
function fleetNeedsAttention({ activeHold, hasSafetyIssue, pmStatus, missingDvirToday }) {
  return !!(activeHold || hasSafetyIssue || missingDvirToday || fleetPmAttention(pmStatus));
}

// TASK-9898 follow-up (Chris 10/16) — the PM part is the SHARED rule
// (pmStatus `review`, the same answer the connector gives): overdue / due soon /
// PM can't be checked / overdue if a believable suspect reading is right / a
// suspect reading nobody has dealt with in 7 days.
function fleetPmAttention(pmStatus) {
  const review = pmStatus && pmStatus.pm && pmStatus.pm.review;
  if (review) return !!review.attention;
  const st = pmStatus && pmStatus.status;
  return st === 'overdue' || st === 'due_soon' || fleetPmUncheckable(pmStatus);
}

// TASK-9898 — severity order of the Needs Attention cards (lower = first).
function fleetAttentionRank(t) {
  if (t.activeHold || t.hasSafetyIssue) return 0;
  if (t.missingDvirToday) return 1;
  const st = t.pmStatus && t.pmStatus.status;
  if (st === 'overdue' || t.pmStatus?.pm?.review?.why === 'overdue_if_right') return 2;
  if (st === 'due_soon') return 3;
  return 4;
}

const _METER_SOURCE_LABEL = {
  dvir: 'DVIR',
  pretrip: 'Pre-trip',
  manual: 'Meter update',
  manual_override: 'Meter correction',
  pm_service: 'PM',
  maintenance_task: 'Work order',
  driver_pm: 'Driver PM',
  fuel_expense: 'Fuel receipt'
};

// TASK-9898 — every reading that looks mistyped, fleet-wide, built from each
// vehicle's `pm.suspect` (NOT from status === 'check_reading'), so an overdue
// truck's bad reading still gets its fix button.
function fleetSuspectReadings(trucks, metersById) {
  const out = [];
  (trucks || []).forEach(truck => {
    const pm = metersById.get(truck.id)?.pm;
    (pm?.suspect || []).forEach((sx, i) => {
      const row = sx.rowId ? MaintenanceState.metersLog.find(m => m.id === sx.rowId) : null;
      out.push({ truck, sx, row, rv: pm?.review?.readings?.[i] || null });
    });
  });
  const unitOf = (t) => String((window.getUnitDisplay ? window.getUnitDisplay(t) : t.unit) || '');
  return out.sort((a, b) => unitOf(a.truck).localeCompare(unitOf(b.truck), undefined, { numeric: true }));
}

function _suspectReadingButtons(truckId, sx) {
  const fixBtn = sx.where === 'last_pm'
    ? `<button type="button" class="btn btn-sm btn-secondary" onclick="event.stopPropagation(); window.MaintenanceModule.fixSuspectReading('${escJsAttr(truckId)}','relogpm')">Re-log PM</button>`
    : `<button type="button" class="btn btn-sm btn-secondary" onclick="event.stopPropagation(); window.MaintenanceModule.fixSuspectReading('${escJsAttr(truckId)}','fix')">Fix</button>`;
  // A reading past what any vehicle reads can never be "right" (the PM math ignores a confirm on it).
  const rightBtn = sx.rowId && sx.kind !== 'ceiling'
    ? `<button type="button" class="btn btn-sm btn-secondary" onclick="event.stopPropagation(); window.MaintenanceModule.fixSuspectReading('${escJsAttr(truckId)}','right','${escJsAttr(sx.rowId)}')">It's right</button>`
    : '';
  return fixBtn + rightBtn;
}

// TASK-9898 — "N readings look mistyped · Review": one line above Needs
// Attention; Review opens a plain list (one dated line per reading, actions on
// the right) on the Work Order History pattern.
function renderFleetReadingsStrip(trucks, metersById) {
  const el = document.getElementById('fleet-readings-strip');
  if (!el) return;
  const list = fleetSuspectReadings(trucks, metersById);
  if (!list.length) { el.innerHTML = ''; el.style.display = 'none'; return; }
  el.style.display = '';
  const open = !!MaintenanceState.readingsStripOpen;
  const n = list.length;
  const dateOf = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');
  const rows = open ? list.map(({ truck, sx, row, rv }) => {
    const unitWord = sx.meter === 'miles' ? 'mi' : 'hrs';
    const meterWord = sx.meter === 'miles' ? 'Miles' : 'Hours';
    const src = row ? (_METER_SOURCE_LABEL[row.source] || (row.type === 'pm' ? 'PM' : (row.source || ''))) : '';
    const meta = [
      dateOf(sx.atMs),
      src,
      sx.where === 'last_pm' ? 'logged at the last PM' : '',
      sx.against && sx.against.value != null ? `last believable ${formatNumber(sx.against.value)} ${unitWord}` : ''
    ].filter(Boolean).join(' · ');
    const unit = (window.getUnitDisplay ? window.getUnitDisplay(truck) : truck.unit) || '?';
    // TASK-9898 follow-up — "overdue if this reading is right" / "not reviewed in N
    // days" / "back in Needs Attention <date> if not reviewed" (shared wording).
    const reviewLine = rv && window.PmStatus?.pmReviewLine
      ? window.PmStatus.pmReviewLine(rv, (k) => new Date(`${k}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))
      : '';
    return `
      <div class="fleet-reading-row" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 12px;padding:10px 12px;border-top:1px solid var(--border-light);">
        <div style="flex:1 1 200px;min-width:0;">
          <div style="font-weight:600;color:var(--text-primary);overflow-wrap:anywhere;">${escapeHtml(`${unit} · ${meterWord} ${formatNumber(sx.value)}`)}</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);overflow-wrap:anywhere;">${escapeHtml(meta)}</div>
          ${reviewLine ? `<div class="fleet-reading-review" style="font-size:0.8rem;overflow-wrap:anywhere;color:var(--text-primary);${rv.attentionNow ? 'font-weight:600;' : ''}">${escapeHtml(reviewLine)}</div>` : ''}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:6px;">${_suspectReadingButtons(truck.id, sx)}</div>
      </div>`;
  }).join('') : '';
  el.innerHTML = `
    <div class="fleet-readings-strip" style="margin:0 0 var(--spacing-md, 12px);border:1px solid var(--border-light);border-radius:10px;background:var(--bg-secondary);overflow:hidden;">
      <button type="button" class="fleet-readings-toggle" aria-expanded="${open ? 'true' : 'false'}" onclick="window.MaintenanceModule.toggleReadingsStrip()"
        style="display:flex;align-items:center;gap:8px;width:100%;min-height:44px;padding:8px 12px;background:none;border:0;cursor:pointer;font-size:0.9rem;color:var(--text-primary);text-align:left;">
        <span class="material-icons" style="font-size:18px;color:var(--warning, #F59E0B);">error_outline</span>
        <span style="flex:1;min-width:0;">${n} reading${n === 1 ? '' : 's'} look${n === 1 ? 's' : ''} mistyped</span>
        <span style="text-decoration:underline;white-space:nowrap;">${open ? 'Hide' : 'Review'}</span>
        <span class="material-icons" style="font-size:20px;color:var(--text-secondary);">${open ? 'expand_less' : 'expand_more'}</span>
      </button>
      ${rows}
    </div>`;
}

function toggleReadingsStrip() {
  MaintenanceState.readingsStripOpen = !MaintenanceState.readingsStripOpen;
  renderFleetGrid();
}

// The chip on a card: open the strip and bring it into view.
function openReadingsStrip() {
  MaintenanceState.readingsStripOpen = true;
  renderFleetGrid();
  setTimeout(() => document.getElementById('fleet-readings-strip')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
}

// TASK-9898 — ONE fleet-level line instead of a "using the default interval"
// line on every card: "N vehicles use a default PM interval (Type a, Type b)".
function renderFleetDefaultIntervalLine(trucks, metersById) {
  const el = document.getElementById('fleet-default-interval-line');
  if (!el) return;
  const riding = (trucks || []).filter(t => {
    const pm = metersById.get(t.id)?.pm;
    return pm && pm.milesIntervalSource === 'default' && pm.reason !== 'no meter readings recorded';
  });
  if (!riding.length) { el.innerHTML = ''; el.style.display = 'none'; return; }
  el.style.display = '';
  const types = [...new Set(riding.map(t => String((window.getTypeLabel ? window.getTypeLabel(t.type, 'truckTypes') : t.type) || t.type || 'Truck')))];
  const n = riding.length;
  el.innerHTML = `
    <div class="fleet-default-interval-line" style="display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;margin:0 0 var(--spacing-md, 12px);font-size:0.85rem;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:16px;">tune</span>
      <span style="min-width:0;overflow-wrap:anywhere;">${escapeHtml(`${n} vehicle${n === 1 ? ' uses' : 's use'} a default PM interval (${types.join(', ')})`)}</span>
      <button type="button" class="btn btn-sm btn-secondary" style="min-height:36px;" onclick="window.MaintenanceModule.showPMSettings()">Set intervals</button>
    </div>`;
}

// TASK-9898 — one line on a card with a reading that looks mistyped. The reading
// itself (value, date, fix buttons) is said once, in the readings strip.
function renderPMNotes(truck, pmStatus) {
  const pm = pmStatus && pmStatus.pm;
  const n = (pm && pm.suspect && pm.suspect.length) || 0;
  if (!n) return '';
  // The headline already says a reading looks mistyped / the PM can't be checked.
  const said = pmStatus.status === 'check_reading';
  // TASK-9898 follow-up — a truck back in Needs Attention because of the reading
  // says why on its card: "Overdue if this reading is right" / "Not reviewed in 9 days".
  const rv = pm.review;
  const hot = rv && (rv.why === 'overdue_if_right' || rv.why === 'unreviewed')
    ? (rv.readings || []).find(r => r.attentionNow) : null;
  const hotLine = hot && window.PmStatus?.pmReviewLine ? window.PmStatus.pmReviewLine(hot) : '';
  const text = hotLine ? hotLine.charAt(0).toUpperCase() + hotLine.slice(1)
    : (said ? 'Fix the reading' : (n === 1 ? '1 reading looks mistyped' : `${n} readings look mistyped`));
  return `
      <button type="button" class="fleet-card-pm-note" onclick="event.stopPropagation(); window.MaintenanceModule.openReadingsStrip()"
        style="display:flex;align-items:center;gap:6px;width:100%;min-height:36px;margin-top:6px;padding:6px 10px;border:0;border-radius:8px;background:var(--bg-secondary);font-size:0.8rem;color:var(--text-primary);cursor:pointer;text-align:left;">
        <span class="material-icons" style="font-size:16px;color:var(--warning, #F59E0B);">error_outline</span>
        <span style="flex:1;">${escapeHtml(text)}</span>
        <span style="text-decoration:underline;">Review</span>
      </button>`;
}

// Persist the fleet sort choice (TASK-485 localStorage pattern). Per-tenant key.
function fleetSortStorageKey() {
  const tid = MaintenanceState.tenantId || window.AppState?.tenant || 'default';
  return `dd_maint_fleet_sort_${tid}`;
}
function getStoredFleetSort() {
  try {
    const v = localStorage.getItem(fleetSortStorageKey());
    return v === 'pmdue' ? 'pmdue' : 'unit';
  } catch (_) {
    return 'unit';
  }
}
function setStoredFleetSort(sort) {
  try {
    localStorage.setItem(fleetSortStorageKey(), sort === 'pmdue' ? 'pmdue' : 'unit');
  } catch (_) { /* ignore (private mode / quota) */ }
}

// ============================================
// METERS OPERATIONS
// ============================================
function showUpdateMeters(truckId) {
  const truck = MaintenanceState.trucks.find(t => t.id === truckId);
  if (!truck) return;

  const meters = getLatestMeters(truckId);

  document.getElementById('meters-truck-id').value = truckId;
  document.getElementById('modal-meters-title').textContent = `Update Meters - ${window.getUnitDisplay ? window.getUnitDisplay(truck) : truck.unit}`;

  document.getElementById('meters-current').innerHTML = `
    <div class="current-meters">
      <span>Current: ${meters.miles !== null ? formatNumber(meters.miles) + ' mi' : 'No miles'} / ${meters.hours !== null ? formatNumber(meters.hours) + ' hrs' : 'No hours'}</span>
    </div>
  `;

  document.getElementById('meters-miles').value = meters.miles || '';
  document.getElementById('meters-hours').value = meters.hours || '';

  document.getElementById('modal-meters').classList.remove('hidden');
}

async function saveMeters(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const truckId = document.getElementById('meters-truck-id').value;
  const miles = parseFloat(document.getElementById('meters-miles').value) || null;
  const hours = parseFloat(document.getElementById('meters-hours').value) || null;

  if (miles === null && hours === null) {
    window.showToast?.('Enter at least miles or hours', 'error');
    return;
  }

  // TASK-9659 — a reading that does not fit this vehicle's history asks first.
  const meterCheck = await confirmPlausibleMeter(truckId, miles, hours);
  if (!meterCheck.proceed) return;

  // Show spinner on submit button
  const submitBtn = e.target.querySelector('button[type="submit"]');
  const originalContent = submitBtn?.innerHTML;
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="material-icons rotating">sync</span> Saving...';
  }

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    const truck = MaintenanceState.trucks.find(t => t.id === truckId);

    const meterEntry = {
      truckId,
      truckUnit: truck?.unit || '',
      type: 'reading',
      miles,
      hours,
      timestamp: Timestamp.now(),
      source: 'manual',
      ...(meterCheck.confirmed ? { meterConfirmed: true } : {})
    };
    const docRef = await addMeterRow(tenant, meterEntry);

    // Context-aware success message
    const updatedItems = [];
    if (miles !== null) updatedItems.push('miles');
    if (hours !== null) updatedItems.push('hours');
    const itemsText = updatedItems.join(' and ');

    // Optimistic update: add entry to local state immediately
    const optimisticEntry = {
      id: docRef.id,
      truckId,
      truckUnit: truck?.unit || '',
      type: 'reading',
      miles,
      hours,
      timestamp: { toDate: () => new Date() },
      createdAt: meterEntry.createdAt, // TASK-9898 follow-up: review clock (addMeterRow)
      source: 'manual',
      ...(meterEntry.meterConfirmed ? { meterConfirmed: true } : {})
    };
    MaintenanceState.metersLog.unshift(optimisticEntry);

    closeModal('meters');
    renderFleetGrid();
    window.showToast?.(`${itemsText.charAt(0).toUpperCase() + itemsText.slice(1)} updated`, 'success');
  } catch (error) {
    console.error('[Maintenance] Error saving reading:', error);
    window.showToast?.('Error saving reading', 'error');
  } finally {
    // Restore button
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalContent;
    }
  }
}

// ============================================
// PM SERVICE OPERATIONS
// ============================================
function showLogPM(truckId) {
  const truck = MaintenanceState.trucks.find(t => t.id === truckId);
  if (!truck) return;

  const meters = getLatestMeters(truckId);

  document.getElementById('pm-truck-id').value = truckId;
  document.getElementById('modal-pm-title').textContent = `Log PM Service - ${window.getUnitDisplay ? window.getUnitDisplay(truck) : truck.unit}`;

  document.getElementById('pm-info').innerHTML = `
    <div class="pm-info-content">
      <strong>${escapeHtml(window.getUnitDisplay ? window.getUnitDisplay(truck) : truck.unit)}</strong> - ${escapeHtml((window.getTypeLabel ? window.getTypeLabel(truck.type, 'truckTypes') : truck.type) || 'Truck')}
      <br>
      <span class="text-muted">Current: ${meters.miles ? formatNumber(meters.miles) + ' mi' : '--'} / ${meters.hours ? formatNumber(meters.hours) + ' hrs' : '--'}</span>
    </div>
  `;

  document.getElementById('pm-miles').value = meters.miles || '';
  document.getElementById('pm-hours').value = meters.hours || '';

  // Render PM checklist items (custom or defaults)
  const checklistItems = MaintenanceState.pmSettings.pmChecklist || DEFAULT_PM_CHECKLIST;
  document.getElementById('pm-checklist').innerHTML = checklistItems.map(item =>
    `<label class="pm-check-item"><input type="checkbox" name="pm-item" value="${escapeHtml(item)}"> ${escapeHtml(item)}</label>`
  ).join('');
  document.getElementById('pm-notes').value = '';

  document.getElementById('modal-pm').classList.remove('hidden');
}

async function savePMService(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const truckId = document.getElementById('pm-truck-id').value;
  const miles = parseFloat(document.getElementById('pm-miles').value) || null;
  const hours = parseFloat(document.getElementById('pm-hours').value) || null;
  const notes = document.getElementById('pm-notes').value.trim();

  // Get checked items
  const items = [];
  document.querySelectorAll('#pm-checklist input:checked').forEach(cb => {
    items.push(cb.value);
  });

  if (items.length === 0) {
    window.showToast?.('Select at least one PM item', 'error');
    return;
  }

  // TASK-9659 — the PM's meter becomes the baseline every due date counts from:
  // one that does not fit this vehicle's history asks first.
  const meterCheck = await confirmPlausibleMeter(truckId, miles, hours);
  if (!meterCheck.proceed) return;

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    const truck = MaintenanceState.trucks.find(t => t.id === truckId);

    // Log PM as a meters entry with type 'pm'
    const pmEntry = {
      truckId,
      truckUnit: truck?.unit || '',
      type: 'pm',
      miles,
      hours,
      pmItems: items,
      notes,
      timestamp: Timestamp.now(),
      source: 'pm_service',
      ...(meterCheck.confirmed ? { meterConfirmed: true } : {})
    };
    const pmRef = await addMeterRow(tenant, pmEntry);
    // Optimistic: the card's badge flips now, not on the next reload.
    MaintenanceState.metersLog.unshift({ ...pmEntry, id: pmRef.id, timestamp: { toDate: () => new Date() } });

    closeModal('pm');
    renderFleetGrid();
    window.showToast?.('PM service logged', 'success');
  } catch (error) {
    console.error('[Maintenance] Error logging PM:', error);
    window.showToast?.('Error logging PM service', 'error');
  }
}

function showPMSettings() {
  const pm = MaintenanceState.pmSettings.pmIntervals;

  // TASK-4733 — snapshot what is actually SAVED before the modal starts editing
  // MaintenanceState in place. addPMTypeOverride/removePMTypeOverride and the
  // checklist add/remove helpers all write straight into state so their lists can
  // re-render, which means an abandoned edit would otherwise linger as a "saved"
  // value. closePMSettings() reverts to this snapshot on Cancel/X and repaints.
  // (Two things make the draft safe while the modal is open: 'modal-pm-settings'
  // is now in isMaintenanceModalOpen(), so a subscription callback cannot repaint
  // the grid mid-edit — COD 2026-09-08 caught that it was missing.) A FAILED save
  // deliberately KEEPS the draft: the modal stays open and a retry must send what
  // the user is still looking at.
  MaintenanceState.pmSettings._draftBackup = {
    pmIntervals: { ...pm },
    pmChecklist: Array.isArray(MaintenanceState.pmSettings.pmChecklist)
      ? [...MaintenanceState.pmSettings.pmChecklist]
      : MaintenanceState.pmSettings.pmChecklist || null,
  };

  // Set default and hours values
  document.getElementById('pm-miles-default').value = pm._default || 5000;
  document.getElementById('pm-hours-setting').value = pm._hours || 250;

  // Build list of types that have overrides (non-default values)
  _renderPMTypeList();

  // Reset dropdown state
  document.getElementById('pm-type-dropdown-row').style.display = 'none';
  document.getElementById('btn-pm-add-type').style.display = '';

  // Render PM checklist items
  _renderPMChecklistSettings();

  document.getElementById('modal-pm-settings').classList.remove('hidden');
}

function _renderPMChecklistSettings() {
  const items = MaintenanceState.pmSettings.pmChecklist || [...DEFAULT_PM_CHECKLIST];
  const container = document.getElementById('pm-checklist-settings');
  container.innerHTML = items.map((item, i) => `
    <div style="display:flex;align-items:center;gap:8px;padding:6px 12px;background:var(--bg-secondary);border-radius:8px;">
      <span class="material-icons" style="font-size:16px;color:var(--text-secondary);cursor:grab;">drag_indicator</span>
      <span style="flex:1;font-size:0.875rem;color:var(--text-primary);">${escapeHtml(item)}</span>
      <button type="button" onclick="window.MaintenanceModule.removePMChecklistItem(${i})" style="background:none;border:none;cursor:pointer;padding:4px;color:var(--text-secondary);display:flex;" title="Remove">
        <span class="material-icons" style="font-size:18px;">close</span>
      </button>
    </div>
  `).join('');
}

function addPMChecklistItem() {
  const input = document.getElementById('pm-checklist-new-item');
  const name = input.value.trim();
  if (!name) return;

  // Initialize from defaults if no custom list yet
  if (!MaintenanceState.pmSettings.pmChecklist) {
    MaintenanceState.pmSettings.pmChecklist = [...DEFAULT_PM_CHECKLIST];
  }

  // Prevent duplicates
  if (MaintenanceState.pmSettings.pmChecklist.some(item => item.toLowerCase() === name.toLowerCase())) {
    window.showToast?.('Item already exists', 'error');
    return;
  }

  MaintenanceState.pmSettings.pmChecklist.push(name);
  input.value = '';
  _renderPMChecklistSettings();
}

function removePMChecklistItem(index) {
  // Initialize from defaults if no custom list yet
  if (!MaintenanceState.pmSettings.pmChecklist) {
    MaintenanceState.pmSettings.pmChecklist = [...DEFAULT_PM_CHECKLIST];
  }

  MaintenanceState.pmSettings.pmChecklist.splice(index, 1);
  _renderPMChecklistSettings();
}

// TASK-9339 — the PM interval lookup (calculatePMStatus / calculatePMUrgency and the
// functions twin) keys on `(truck.type || '').toLowerCase()`. The override choices
// therefore come from the registered list PLUS every type the fleet actually carries,
// keyed lower-case — the registered list alone never looked at the vehicles, so a
// `hydrovac` / `trucking` truck could never get a type-specific interval.
function _pmTypeChoices() {
  if (window.getTypeListWithInUse) {
    return window.getTypeListWithInUse('truckTypes', MaintenanceState.trucks, { caseInsensitive: true });
  }
  return (window.getTypeList ? window.getTypeList('truckTypes') : []).map(t => ({ ...t, slug: String(t.slug).toLowerCase() }));
}

// A type override key the modal must never offer: the reserved baseline keys, and
// Firestore's reserved `__name__` form (a map key like that is rejected on write).
function _isReservedPMKey(k) {
  return k === '_default' || k === '_hours' || /^__.*__$/.test(k);
}

// Keys holding a real per-type interval. `null` = "no override" (a removed built-in).
function _pmOverrideSlugs(pm) {
  return Object.keys(pm).filter(k => !_isReservedPMKey(k) && k !== 'other' && pm[k] != null);
}

// A built-in default the tenant has never saved — shown as a default, not a setting.
function _isUnsavedBuiltinPM(slug) {
  return !_isReservedPMKey(slug)
    && Object.prototype.hasOwnProperty.call(BUILTIN_PM_INTERVALS, slug)
    && !(MaintenanceState.pmSettings.savedIntervalKeys || []).includes(slug);
}

function _pmVehicleCountText(n) {
  return `${n} vehicle${n === 1 ? '' : 's'}`;
}

function _renderPMTypeList() {
  const pm = MaintenanceState.pmSettings.pmIntervals;
  const typeList = _pmTypeChoices();
  const typeMap = {};
  typeList.forEach(t => { typeMap[t.slug] = t; });

  // Get slugs that have overrides (skip _default, _hours, "other", and removed built-ins)
  const overrideSlugs = _pmOverrideSlugs(pm);

  const container = document.getElementById('pm-type-list');

  // TASK-9659 — every type the fleet carries that has no interval of its own
  // rides the every-type default. Say so, with a one-tap way to set it, so a
  // custom type (e.g. "trucking") is never silently on 5,000 mi.
  const overrideSet = new Set(overrideSlugs);
  const riding = typeList.filter(t => (t.count || 0) > 0 && t.slug !== 'other' && !_isReservedPMKey(t.slug) && !overrideSet.has(t.slug));
  const ridingHtml = riding.map(t => `
      <div class="pm-type-row pm-type-row-default" style="display:flex;align-items:center;gap:10px;padding:8px 12px;border:1px dashed var(--border-light);border-radius:10px;">
        <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">local_shipping</span>
        <span style="flex:1;display:flex;flex-direction:column;min-width:0;">
          <span style="font-size:0.9rem;font-weight:500;color:var(--text-primary);">${escapeHtml(t.label || t.slug)}</span>
          <span class="pm-type-row-note" style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(`${_pmVehicleCountText(t.count || 0)} · using the default ${formatNumber(pm._default || 5000)} mi`)}</span>
        </span>
        <button type="button" class="btn btn-sm btn-secondary" onclick="window.MaintenanceModule.setPMTypeFromDefault('${escJsAttr(t.slug)}')">Set it</button>
      </div>`).join('');

  if (overrideSlugs.length === 0) {
    container.innerHTML = `<p style="color:var(--text-secondary);font-size:0.85rem;margin:0;">No per-type overrides. All vehicles use the default baseline.</p>${ridingHtml}`;
    return;
  }

  container.innerHTML = ridingHtml + overrideSlugs.map(slug => {
    const t = typeMap[slug];
    const label = t?.label || (window.getTypeLabel ? window.getTypeLabel(slug, 'truckTypes') : slug);
    const count = t?.count || 0;
    const notes = [_pmVehicleCountText(count)];
    if (t?.unregistered) notes.push('not on type list');
    if (_isUnsavedBuiltinPM(slug)) notes.push('built-in default');
    return `
      <div class="pm-type-row" style="display:flex;align-items:center;gap:10px;padding:8px 12px;background:var(--bg-secondary);border-radius:10px;">
        <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">local_shipping</span>
        <span style="flex:1;display:flex;flex-direction:column;min-width:0;">
          <span style="font-size:0.9rem;font-weight:500;color:var(--text-primary);">${escapeHtml(label)}</span>
          <span class="pm-type-row-note" style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(notes.join(' · '))}</span>
        </span>
        <input type="number" data-type-slug="${escapeHtml(slug)}" min="1000" step="500" value="${escapeHtml(String(pm[slug] ?? ''))}" style="width:90px;padding:6px 10px;border:1px solid var(--border-light);border-radius:8px;font-size:0.85rem;text-align:right;">
        <span style="font-size:0.75rem;color:var(--text-secondary);">mi</span>
        <button type="button" onclick="window.MaintenanceModule.removePMTypeOverride('${escJsAttr(slug)}')" style="background:none;border:none;cursor:pointer;padding:4px;color:var(--text-secondary);display:flex;" title="Remove override">
          <span class="material-icons" style="font-size:18px;">close</span>
        </button>
      </div>`;
  }).join('');
}

// TASK-9659 — "Set it" on a type riding the default: give it its own row
// (starting at the default) to edit, then Save as usual.
function setPMTypeFromDefault(slug) {
  if (!slug || _isReservedPMKey(slug)) return;
  const pm = MaintenanceState.pmSettings.pmIntervals;
  pm[slug] = pm._default || 5000;
  _renderPMTypeList();
  document.querySelector(`#pm-type-list input[data-type-slug="${CSS.escape(slug)}"]`)?.focus();
}

function showPMTypeDropdown() {
  const pm = MaintenanceState.pmSettings.pmIntervals;
  const typeList = _pmTypeChoices();
  // Only show types that don't already have overrides (a removed built-in, stored as
  // null, is offered again)
  const existing = new Set(_pmOverrideSlugs(pm));
  const available = typeList.filter(t => t.slug !== 'other' && !_isReservedPMKey(t.slug) && !existing.has(t.slug));

  if (available.length === 0) {
    window.showToast?.('All vehicle types already have overrides', 'info');
    return;
  }

  const select = document.getElementById('pm-type-selector');
  // TASK-9339 — slug is tenant data (an in-use type can be anything): escape the value too.
  // In-use counts and "not on type list" are shown so an unregistered type is visible as such.
  select.innerHTML = available.map(t => {
    const bits = [_pmVehicleCountText(t.count || 0)];
    if (t.unregistered) bits.push('not on type list');
    return `<option value="${escapeHtml(t.slug)}">${escapeHtml(`${t.label} (${bits.join(', ')})`)}</option>`;
  }).join('');

  document.getElementById('pm-type-dropdown-row').style.display = 'flex';
  document.getElementById('btn-pm-add-type').style.display = 'none';
}

function addPMTypeOverride() {
  const select = document.getElementById('pm-type-selector');
  const slug = select.value;
  if (!slug) return;

  const pm = MaintenanceState.pmSettings.pmIntervals;
  pm[slug] = pm._default || 5000;

  _renderPMTypeList();

  // Hide dropdown, show button
  document.getElementById('pm-type-dropdown-row').style.display = 'none';
  document.getElementById('btn-pm-add-type').style.display = '';
}

function removePMTypeOverride(slug) {
  // TASK-9339 — a built-in (e.g. truck) is merged back in on every load, so deleting the
  // key never stuck. null = "no override" to both lookups (`??` here, `!= null` in the
  // functions twin) and survives the merge.
  if (Object.prototype.hasOwnProperty.call(BUILTIN_PM_INTERVALS, slug) && !_isReservedPMKey(slug)) {
    MaintenanceState.pmSettings.pmIntervals[slug] = null;
  } else {
    delete MaintenanceState.pmSettings.pmIntervals[slug];
  }
  _renderPMTypeList();

  // Re-show add button in case it was hidden
  document.getElementById('btn-pm-add-type').style.display = '';
}

// TASK-4733 — abandoning the PM settings modal must not leave the in-place edits
// behind as if they were saved. Reverts to the snapshot taken in showPMSettings().
function closePMSettings() {
  const backup = MaintenanceState.pmSettings._draftBackup;
  if (backup) {
    MaintenanceState.pmSettings.pmIntervals = backup.pmIntervals;
    MaintenanceState.pmSettings.pmChecklist = backup.pmChecklist;
    MaintenanceState.pmSettings._draftBackup = null;
  }
  closeModal('pm-settings');
  _repaintAfterPMSettings();
}

// TASK-4733 — every view that guards on isMaintenanceModalOpen() drops its
// repaint while a modal is open, so an onSnapshot that fired mid-edit leaves the
// DOM stale. Same post-close recovery the task modal already does
// (saveTask → closeModal + renderTasksList/updateCounts/renderFleetGrid). Must
// run AFTER the modal is hidden or the guard swallows these too.
function _repaintAfterPMSettings() {
  renderFleetGrid();
  renderTrailerGrid();
  renderAssetGrid();
  renderTasksList(MaintenanceState.currentFilter);
  updateCounts();
}

// TASK-4733 — plain English for the failures a dispatcher can actually act on;
// the raw SDK error still goes to console.error for us. Never dump a bare
// Firestore code ("permission-denied", "unavailable") into a toast.
function _pmSaveErrorText(error) {
  const code = error?.code || '';
  if (code === 'permission-denied') {
    return 'you do not have permission to change PM settings — ask an account admin.';
  }
  if (code === 'unavailable' || code === 'deadline-exceeded' ||
      (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    return 'you appear to be offline. Check your connection and try again.';
  }
  return error?.message || 'unknown error.';
}

// TASK-4733 — resolve the tenant for a WRITE, or refuse. `|| 'demo'` on a write
// path is never right: with both sources falsy it silently lands a real
// carrier's data on the SHARED demo tenant root doc, and nothing in the UI says
// so. Returns null when nothing resolves — every caller must treat null as
// "refuse and tell the user", never as "pick something". ('demo-tenant' is
// normalised to 'demo' for parity with subscribeToData/loadPMSettings, which
// read from the mapped id — a write to the unmapped one would be invisible.)
function _resolveTenantForWrite() {
  const raw = MaintenanceState.tenantId || window.AppState?.tenant || null;
  if (!raw || typeof raw !== 'string' || !raw.trim()) return null;
  const tenant = raw.trim();
  return tenant === 'demo-tenant' ? 'demo' : tenant;
}

async function savePMSettings(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  // TASK-4733 — resolve the target BEFORE reading the form so a refusal can't
  // half-apply. No fallback: refuse rather than write to the shared demo tenant.
  const tenant = _resolveTenantForWrite();
  if (!tenant) {
    console.error('[Maintenance] Refusing to save PM settings: no tenant resolved');
    window.showToast?.('Could not save — your account session was lost. Log out and back in, then try again.', 'error');
    return; // modal stays open; entered values are preserved
  }

  // Read all type override inputs from the list
  const pmIntervals = {};
  const seenSlugs = new Set();
  document.querySelectorAll('#pm-type-list input[data-type-slug]').forEach(input => {
    const slug = input.dataset.typeSlug;
    seenSlugs.add(slug);
    const val = parseInt(input.value);
    if (!(val > 0)) return;
    // TASK-9339 — an untouched built-in default stays a default: don't write it, so it
    // keeps reading "built-in default" and never masquerades as a tenant setting.
    if (_isUnsavedBuiltinPM(slug) && val === BUILTIN_PM_INTERVALS[slug]) return;
    pmIntervals[slug] = val;
  });
  // A built-in type the user removed must be saved as null, or the load merge re-adds it.
  Object.keys(BUILTIN_PM_INTERVALS).forEach(slug => {
    if (_isReservedPMKey(slug) || seenSlugs.has(slug)) return;
    if (MaintenanceState.pmSettings.pmIntervals[slug] === null) pmIntervals[slug] = null;
  });
  pmIntervals._default = parseInt(document.getElementById('pm-miles-default').value) || 5000;
  pmIntervals._hours = parseInt(document.getElementById('pm-hours-setting').value) || 250;

  // Build checklist to save (null = using defaults, array = custom)
  const checklistToSave = MaintenanceState.pmSettings.pmChecklist || null;

  // Save to Firestore tenant settings.
  // TASK-4733 — the success trio (state mutation, closeModal, renderFleetGrid,
  // toast) lives INSIDE the try, AFTER the await. Previously it ran
  // unconditionally, so a rejected write looked identical to a saved one — and
  // because MaintenanceState was assigned before the await, the re-rendered grid
  // affirmatively confirmed a save that only existed in local state until the
  // next reload. Fail loud, keep the modal open, leave state alone.
  try {
    const updateData = { 'settings.pmIntervals': pmIntervals };
    if (checklistToSave) {
      updateData['settings.pmChecklist'] = checklistToSave;
    }
    await updateDoc(doc(db, 'tenants', tenant), updateData);

    // TASK-9339 — state = built-ins merged with what was written, exactly what the
    // next load produces (an unsaved built-in that was skipped above keeps applying).
    MaintenanceState.pmSettings.pmIntervals = { ...BUILTIN_PM_INTERVALS, ...pmIntervals };
    MaintenanceState.pmSettings.savedIntervalKeys = Object.keys(pmIntervals);
    MaintenanceState.pmSettings._draftBackup = null; // committed — nothing to revert to
    closeModal('pm-settings');
    _repaintAfterPMSettings(); // re-render with new settings + recover suppressed repaints
    window.showToast?.('PM settings updated', 'success');
  } catch (error) {
    console.error('[Maintenance] Error saving PM settings:', error);
    // Keep the modal open so the entered values are not lost, and do NOT commit
    // pmIntervals — the grid must keep showing what is actually saved. The draft
    // stays in state on purpose so a retry sends what the user is still looking
    // at; closePMSettings() reverts it if they give up instead.
    window.showToast?.(`Could not save PM settings — ${_pmSaveErrorText(error)}`, 'error');
  }
}

// ============================================
// VEHICLE HOLDS
// ============================================
function showNewHold() {
  document.getElementById('modal-hold-title').textContent = 'New Vehicle Hold';
  document.getElementById('form-hold').reset();
  document.getElementById('hold-id').value = '';

  // Set default dates — LOCAL day (TASK-9657 S0: the UTC date made an evening
  // hold start TOMORROW, so the truck read as free tonight everywhere).
  const today = _holdTodayLocal();
  document.getElementById('hold-start').value = today;

  // Default end date to 7 days from now
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 7);
  document.getElementById('hold-end').value = _holdLocalKey(endDate);

  updateHoldAssetDropdown();
  document.getElementById('modal-hold').classList.remove('hidden');
}

function showNewHoldForVehicle(assetId, assetUnit, assetType) {
  document.getElementById('modal-hold-title').textContent = 'New Vehicle Hold';
  document.getElementById('form-hold').reset();
  document.getElementById('hold-id').value = '';

  // Set default dates — LOCAL day (TASK-9657 S0: the UTC date made an evening
  // hold start TOMORROW, so the truck read as free tonight everywhere).
  const today = _holdTodayLocal();
  document.getElementById('hold-start').value = today;

  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 7);
  document.getElementById('hold-end').value = _holdLocalKey(endDate);

  // Pre-select the asset type and populate dropdown
  document.getElementById('hold-asset-type').value = assetType;
  updateHoldAssetDropdown();

  // Select the matching asset
  document.getElementById('hold-asset').value = assetId;

  document.getElementById('modal-hold').classList.remove('hidden');
}

function updateHoldAssetDropdown() {
  const assetType = document.getElementById('hold-asset-type').value;
  const select = document.getElementById('hold-asset');

  if (assetType === 'truck') {
    select.innerHTML = '<option value="">Select vehicle</option>' +
      MaintenanceState.trucks.map(t =>
        `<option value="${t.id}" data-unit="${escapeHtml(t.unit)}">${escapeHtml(t.unit)}</option>`
      ).join('');
  } else {
    select.innerHTML = '<option value="">Select trailer</option>' +
      MaintenanceState.trailers
        .filter(t => t.active !== false)
        .map(t =>
          `<option value="${t.id}" data-unit="${escapeHtml(t.unit || t.name)}">${escapeHtml(t.unit || t.name)}</option>`
        ).join('');
  }
}

function updateTaskAssetDropdown() {
  const assetType = document.getElementById('task-asset-type').value;
  const select = document.getElementById('task-asset');

  if (assetType === 'truck') {
    select.innerHTML = '<option value="">Select vehicle</option>' +
      MaintenanceState.trucks.map(t =>
        `<option value="${t.id}" data-unit="${escapeHtml(t.unit)}">${escapeHtml(t.unit)}${t.nickname ? ' — ' + escapeHtml(t.nickname) : ''}</option>`
      ).join('');
  } else if (assetType === 'trailer') {
    select.innerHTML = '<option value="">Select trailer</option>' +
      MaintenanceState.trailers
        .filter(t => t.active !== false)
        .map(t =>
          `<option value="${t.id}" data-unit="${escapeHtml(t.unit || t.name)}">${escapeHtml(t.unit || t.name)}</option>`
        ).join('');
  } else if (assetType === 'asset') {
    select.innerHTML = '<option value="">Select asset</option>' +
      MaintenanceState.assets.map(a =>
        `<option value="${a.id}" data-unit="${escapeHtml(a.name)}">${escapeHtml(a.name)}${a.partNumber ? ' (' + escapeHtml(a.partNumber) + ')' : ''}</option>`
      ).join('');
  }

  // Show meters row for trucks/trailers, hide for assets.
  // EXCEPTION: PM Service always needs a meter baseline (incl. hours-tracked assets),
  // so onTaskCategoryChange() forces it visible — re-evaluate that here too.
  // Edit mode never shows it (saveTask writes meters on CREATE only).
  const metersRow = document.getElementById('task-meters-row');
  if (metersRow) {
    const isEdit = !!document.getElementById('task-id')?.value;
    const isPM = document.getElementById('task-category')?.value === 'PM Service';
    metersRow.style.display = (!isEdit && (isPM || assetType === 'truck' || assetType === 'trailer')) ? '' : 'none';
  }

  // Wire up asset selection to pre-fill current meters
  select.onchange = () => updateTaskMetersHint();
  // Clear meters when asset type changes
  const milesInput = document.getElementById('task-miles');
  const hoursInput = document.getElementById('task-hours');
  if (milesInput) milesInput.value = '';
  if (hoursInput) hoursInput.value = '';
}

// PM Service needs a meter reading as its baseline (the "since last PM" counter is
// derived from the newest metersLog type:'pm' entry, or hoursTracking for assets).
// When the category is PM Service, force the meters row visible — even for
// hours-tracked assets, which normally hide it. Any other category restores the
// default asset-type-based visibility.
// EDIT MODE: saveTask only writes a meter on CREATE, so a meter typed while editing
// would be silently discarded — hide the row entirely when editing an existing task.
function onTaskCategoryChange() {
  const metersRow = document.getElementById('task-meters-row');
  if (!metersRow) return;
  const isEdit = !!document.getElementById('task-id')?.value;
  if (isEdit) {
    metersRow.style.display = 'none';
    return;
  }
  const isPM = document.getElementById('task-category')?.value === 'PM Service';
  const assetType = document.getElementById('task-asset-type')?.value;
  metersRow.style.display = (isPM || assetType === 'truck' || assetType === 'trailer') ? '' : 'none';
}

function updateTaskMetersHint() {
  const assetType = document.getElementById('task-asset-type').value;
  const assetId = document.getElementById('task-asset').value;
  const milesInput = document.getElementById('task-miles');
  const hoursInput = document.getElementById('task-hours');
  if (!assetId || !milesInput || !hoursInput) return;

  // Pre-fill with current values as placeholders
  const meters = getLatestMeters(assetId);
  milesInput.placeholder = meters.miles ? `Current: ${Math.round(meters.miles).toLocaleString()}` : 'e.g. 125000';
  hoursInput.placeholder = meters.hours ? `Current: ${parseFloat(meters.hours).toLocaleString()}` : 'e.g. 4500';
}

async function saveHold(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const holdId = document.getElementById('hold-id').value;
  const assetType = document.getElementById('hold-asset-type').value;
  const assetSelect = document.getElementById('hold-asset');
  const assetId = assetSelect.value;
  const assetUnit = assetSelect.options[assetSelect.selectedIndex]?.dataset?.unit || '';

  const startDate = document.getElementById('hold-start').value;
  const endDate = document.getElementById('hold-end').value;
  const reason = document.getElementById('hold-reason').value;
  const notes = document.getElementById('hold-notes').value.trim();

  if (!assetId) {
    window.showToast?.('Please select a vehicle', 'error');
    return;
  }

  if (new Date(endDate) < new Date(startDate)) {
    window.showToast?.('End date must be after start date', 'error');
    return;
  }

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;

    const holdData = {
      assetType,
      assetId,
      assetUnit,
      startDate,
      endDate,
      reason,
      notes,
      status: 'active',
      updatedAt: Timestamp.now()
    };

    if (holdId) {
      await updateDoc(doc(db, 'tenants', tenant, 'vehicleHolds', holdId), holdData);
      window.showToast?.('Hold updated', 'success');
    } else {
      holdData.createdAt = Timestamp.now();
      await addDoc(collection(db, 'tenants', tenant, 'vehicleHolds'), holdData);
      window.showToast?.('Vehicle hold created', 'success');
    }

    closeModal('hold');
  } catch (error) {
    console.error('[Maintenance] Error saving hold:', error);
    window.showToast?.('Error saving hold', 'error');
  }
}

async function releaseHold(holdId) {
  if (!confirm('Release this hold early?')) return;

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;

    await updateDoc(doc(db, 'tenants', tenant, 'vehicleHolds', holdId), {
      status: 'released',
      releasedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    window.showToast?.('Hold released', 'success');
  } catch (error) {
    console.error('[Maintenance] Error releasing hold:', error);
    window.showToast?.('Error releasing hold', 'error');
  }
}

// TASK-9657 S0 — the hold day is the LOCAL day. These used toISOString (UTC):
// after ~7pm Central a hold ending today dropped off the list, the tab count
// and the vehicle's "On Hold" state while the dispatch board still showed it.
function _holdLocalKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function _holdTodayLocal() {
  return _holdLocalKey(new Date());
}

function renderHoldsList() {
  const container = document.getElementById('holds-list');
  if (!container) return;

  // Filter active holds only
  const today = _holdTodayLocal();
  const activeHolds = MaintenanceState.vehicleHolds.filter(h =>
    h.status === 'active' && h.endDate >= today
  );

  if (activeHolds.length === 0) {
    if (!MaintenanceState._holdsLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading holds...</div>
        </div>
      `;
      return;
    }
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">✓</div>
        <div class="empty-state-title">No active holds</div>
        <div class="empty-state-text">All vehicles are available for dispatch.</div>
      </div>
    `;
    return;
  }

  container.innerHTML = activeHolds.map(hold => {
    const startDate = new Date(hold.startDate);
    const endDate = new Date(hold.endDate);
    const isCurrentlyOnHold = today >= hold.startDate && today <= hold.endDate;

    return `
      <div class="hold-card ${isCurrentlyOnHold ? 'hold-active' : 'hold-upcoming'}">
        <div class="hold-card-icon">
          <span class="material-icons">${hold.assetType === 'truck' ? 'local_shipping' : 'rv_hookup'}</span>
        </div>
        <div class="hold-card-info">
          <div class="hold-card-header">
            <span class="hold-card-unit">${escapeHtml(hold.assetUnit)}</span>
            <span class="badge ${isCurrentlyOnHold ? 'badge-high' : 'badge-normal'}">
              ${isCurrentlyOnHold ? 'On Hold' : 'Upcoming'}
            </span>
          </div>
          <div class="hold-card-dates">
            <span class="material-icons">event</span>
            ${formatDateShort(startDate)} - ${formatDateShort(endDate)}
          </div>
          <div class="hold-card-reason">${escapeHtml(hold.reason)}</div>
          ${hold.notes ? `<div class="hold-card-notes">${escapeHtml(hold.notes)}</div>` : ''}
        </div>
        <div class="hold-card-actions">
          <button class="btn btn-sm btn-secondary" onclick="window.MaintenanceModule.releaseHold('${escJsAttr(hold.id)}')">
            <span class="material-icons">check</span> Release
          </button>
        </div>
      </div>
    `;
  }).join('');
}

function updateHoldsCount() {
  const today = _holdTodayLocal();
  const activeCount = MaintenanceState.vehicleHolds.filter(h =>
    h.status === 'active' && h.endDate >= today
  ).length;

  if (window.setCountBadge) {
    window.setCountBadge('tab-count-holds', activeCount);
  } else {
    const countEl = document.getElementById('tab-count-holds');
    if (countEl) countEl.textContent = activeCount;
  }
}

// Twin: functions/src/services/maintenanceAssets.js _maintActiveHoldFor. The day
// test is the ONE rule (window.SafetyHold, TASK-9657 S0).
function getActiveHoldForAsset(assetId, assetType = 'truck') {
  const today = _holdTodayLocal();
  return MaintenanceState.vehicleHolds.find(h =>
    h.assetId === assetId &&
    h.assetType === assetType &&
    window.SafetyHold.vehicleHoldCoversDay(h, today)
  );
}

function formatDateShort(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ============================================
// RENDER FUNCTIONS (Tasks)
// ============================================
function isMaintenanceModalOpen() {
  // TASK-4733 — 'modal-pm-settings' was MISSING here ('modal-pm' is the separate
  // log-a-PM modal; getElementById does not prefix-match). Without it a live
  // subscription callback could repaint the fleet grid mid-edit using the
  // in-place PM interval draft, i.e. show unsaved values as if they were saved.
  return ['modal-task', 'modal-task-detail', 'modal-meters', 'modal-pm', 'modal-pm-settings',
    'modal-hold', 'modal-costs',
    'modal-dvir-history', 'modal-work-history', 'modal-vehicle-info',
    'modal-asset-hours', 'modal-quick-edit-asset', 'modal-asset-history'
  ].some(id => { const m = document.getElementById(id); return m && !m.classList.contains('hidden'); });
}

// ============================================
// TASK-9897 — WORK ORDER FILTERS (office view only; renderDriverTasksList untouched)
// ============================================
// Every helper here is a FUNCTION DECLARATION, never a top-level const:
// tests/task9287-maintenance-card-xss.spec.js lifts renderTasksList and these
// helpers out of this file BY NAME with acorn, and a const would not come along.
//
// Persistence (COD changed Chris's ask): the SORT persists per tenant+user in
// localStorage; FILTERS persist for this browser session only (sessionStorage,
// tenant+user key) because a filter that sticks across days can hide a new
// critical DVIR defect on another truck. The status tab is never restored.
function woDefaultFilter() {
  return {
    search: '', vehicles: [], type: '', priority: '',
    createdFrom: '', createdTo: '', completedFrom: '', completedTo: '',
    reportedBy: '', completedBy: '', assignedTo: '', cost: ''
  };
}

function _woFilter() {
  const f = MaintenanceState.woFilter;
  return f && typeof f === 'object' && Array.isArray(f.vehicles) ? f : woDefaultFilter();
}

function _woNorm(s) {
  return String(s ?? '').trim().toLowerCase();
}

function woTabLabel(tab) {
  return ({ open: 'open', in_progress: 'in progress', pending_review: 'needs review', completed: 'completed' })[tab] || String(tab || '');
}

function woValidSort(v) {
  return ['newest', 'oldest', 'cost', 'vehicle'].includes(v) ? v : 'newest';
}

// Tenant + USER: two people on one shop PC must not inherit each other's filters.
function woStorageKey(kind) {
  const tid = MaintenanceState.tenantId || window.AppState?.tenant || 'default';
  let uid = 'anon';
  try { uid = auth?.currentUser?.uid || 'anon'; } catch (_) { /* auth not ready */ }
  return `dd_maint_wo_${kind}_${tid}_${uid}`;
}

function woLoadState() {
  MaintenanceState.woFilter = woDefaultFilter();
  MaintenanceState.woSort = 'newest';
  MaintenanceState._woOptsSig = null;
  try { MaintenanceState.woSort = woValidSort(localStorage.getItem(woStorageKey('sort'))); } catch (_) { /* private mode */ }
  try {
    const raw = sessionStorage.getItem(woStorageKey('filters'));
    if (raw) {
      const saved = JSON.parse(raw) || {};
      const f = woDefaultFilter();
      for (const k of Object.keys(f)) {
        if (k === 'vehicles') {
          if (Array.isArray(saved.vehicles)) f.vehicles = saved.vehicles.filter(v => typeof v === 'string').slice(0, 200);
        } else if (typeof saved[k] === 'string') {
          f[k] = saved[k].slice(0, 200);
        }
      }
      MaintenanceState.woFilter = f;
    }
  } catch (_) { /* corrupt / blocked storage = no saved filters */ }
  // "More filters" opens itself when a filter inside it is already on.
  MaintenanceState.woMoreOpen = woActiveFilters(MaintenanceState.woFilter, 'completed').some(a => a.more);
}

function woSaveFilters() {
  try { sessionStorage.setItem(woStorageKey('filters'), JSON.stringify(_woFilter())); } catch (_) { /* ignore */ }
}

function woSaveSort() {
  try { localStorage.setItem(woStorageKey('sort'), woValidSort(MaintenanceState.woSort)); } catch (_) { /* ignore */ }
}

// wo-legacy-orders — the old app's imported work orders (status Open / Closed /
// Done, kind/text, assetName + its own row number in assetId) read in the
// current shape. ONE rule, window.LegacyWorkOrder, generated from
// functions/src/shared/legacyWorkOrder.js (the partner maintenance summary runs
// the same file). Closed = the shared vocabulary, window.MaintStatus (bi.js twin
// of utils/maintenanceStatus) — injected, never a second status map here.
// READ-side only: nothing is written back.
function _woIsClosed(status) {
  return window.MaintStatus
    ? window.MaintStatus.isMaintClosed(status)
    : String(status ?? '').trim().toLowerCase() === 'completed';
}

// COD 2026-10-16: an imported order resolves to a unit only once trucks,
// trailers AND equipment have all loaded — a name that is unique in a
// half-loaded fleet could otherwise attach (and prefill the edit form) wrongly.
function _woNormalize(task) {
  const L = window.LegacyWorkOrder;
  if (!L) return task;
  const ready = MaintenanceState._trucksLoaded && MaintenanceState._trailersLoaded && MaintenanceState._assetsLoaded;
  return L.normalizeWorkOrder(task, ready ? {
    trucks: MaintenanceState.trucks || [],
    trailers: MaintenanceState.trailers || [],
    assets: MaintenanceState._woAllAssets || MaintenanceState.assets || []
  } : null, _woIsClosed);
}

function _woRenormalizeTasks() {
  MaintenanceState.tasks = (MaintenanceState._rawTasks || []).map(_woNormalize);
}

// The Work Orders tab an order belongs to (open | in_progress | pending_review |
// completed). Reads the raw status, so a current-shape order whose status is a
// variant (e.g. "Closed") still lands in a tab instead of in none.
function woTab(task) {
  const L = window.LegacyWorkOrder;
  return L ? L.workOrderTab(task, _woIsClosed) : String(task?.status || '');
}

function woUnitLabel(obj) {
  if (!obj) return '';
  return String((window.getUnitDisplay ? window.getUnitDisplay(obj) : '') || obj.unit || obj.nickname || obj.name || obj.id || '');
}

// The vehicle a work order is on, as { key, label, group }. Id first; fall back to
// the unit text only WITHIN the same asset type (today's drill OR'd truckUnit with
// no type check). Pre-split trailer orders kept the TRAILER id in truckId
// (pretrip.js), so a truckId is also tried against trailers. A vehicle that is no
// longer in the fleet stays its own option ("not in fleet") instead of vanishing.
function woTaskVehicle(task) {
  const trucks = MaintenanceState.trucks || [];
  const trailers = MaintenanceState.trailers || [];
  const assets = MaintenanceState.assets || [];
  const isAsset = task.assetType === 'asset' || !!task.assetId;
  const isTrailer = !isAsset && (task.assetType === 'trailer' || !!task.trailerId);
  const gone = (key, unit, fallback) => ({ key, label: `${unit || fallback} (not in fleet)`, group: 'removed' });
  // COD 2026-10-16: a STORED id that no longer resolves is a removed vehicle — it
  // never falls back to the unit text, or a retired unit's history would attach to
  // the replacement that reused its number. Unit/name text is only for orders that
  // carry no id at all (legacy).
  if (isAsset) {
    const a = task.assetId ? assets.find(x => x.id === task.assetId)
      : (task.assetName && assets.find(x => _woNorm(x.name) === _woNorm(task.assetName)));
    if (a) return { key: 'asset:' + a.id, label: String(a.name || woUnitLabel(a)), group: 'asset' };
    return gone('asset?:' + (task.assetId || task.assetName || '?'), task.assetName, 'Unknown asset');
  }
  if (isTrailer) {
    const tr = task.trailerId ? trailers.find(x => x.id === task.trailerId)
      : (task.trailerUnit && trailers.find(x => _woNorm(x.unit) === _woNorm(task.trailerUnit)));
    if (tr) return { key: 'trailer:' + tr.id, label: woUnitLabel(tr), group: 'trailer' };
    return gone('trailer?:' + (task.trailerId || task.trailerUnit || '?'), task.trailerUnit, 'Unknown trailer');
  }
  if (task.truckId) {
    // Before TASK-9372 every DVIR writer put the trailer / asset id in truckId.
    const t = trucks.find(x => x.id === task.truckId);
    if (t) return { key: 'truck:' + t.id, label: woUnitLabel(t), group: 'truck' };
    const tr = trailers.find(x => x.id === task.truckId);
    if (tr) return { key: 'trailer:' + tr.id, label: woUnitLabel(tr), group: 'trailer' };
    const a = assets.find(x => x.id === task.truckId);
    if (a) return { key: 'asset:' + a.id, label: String(a.name || woUnitLabel(a)), group: 'asset' };
    return gone('truck?:' + task.truckId, task.truckUnit, 'Unknown vehicle');
  }
  if (task.truckUnit) {
    const t = trucks.find(x => _woNorm(x.unit) === _woNorm(task.truckUnit));
    if (t) return { key: 'truck:' + t.id, label: woUnitLabel(t), group: 'truck' };
  }
  if (task.truckId || task.truckUnit) return gone('truck?:' + (task.truckId || task.truckUnit), task.truckUnit, 'Unknown vehicle');
  return { key: 'none', label: 'No vehicle', group: 'removed' };
}

function woVehicleKeyLabel(key) {
  const i = String(key).indexOf(':');
  const kind = i < 0 ? key : key.slice(0, i);
  const ref = i < 0 ? '' : key.slice(i + 1);
  if (kind === 'truck') { const t = (MaintenanceState.trucks || []).find(x => x.id === ref); if (t) return woUnitLabel(t); }
  if (kind === 'trailer') { const t = (MaintenanceState.trailers || []).find(x => x.id === ref); if (t) return woUnitLabel(t); }
  if (kind === 'asset') { const a = (MaintenanceState.assets || []).find(x => x.id === ref); if (a) return String(a.name || woUnitLabel(a)); }
  if (key === 'none') return 'No vehicle';
  const task = (MaintenanceState.tasks || []).find(t => woTaskVehicle(t).key === key);
  return task ? woTaskVehicle(task).label : (ref || String(key));
}

// Reporter NAME. createdBy is a free-text name (DVIR writes driverName with no
// id); prefer createdByPersonId when set. Office-created orders have no reporter.
function woReporter(task) {
  if (task.createdByPersonId) {
    const p = (MaintenanceState.people || []).find(x => x.id === task.createdByPersonId);
    if (p && p.name) return String(p.name).trim();
  }
  return typeof task.createdBy === 'string' ? task.createdBy.trim() : '';
}

function woCompleter(task) {
  return typeof task.completedBy === 'string' ? task.completedBy.trim() : '';
}

function woSearchText(task) {
  return [task.description, task.notes, task.completionNotes, task.truckUnit, task.trailerUnit,
    task.assetName, task.vendor, task.category, woTaskVehicle(task).label]
    .map(x => (typeof x === 'string' ? x : '')).join(' \u0001 ').toLowerCase();
}

function woToDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') { try { return v.toDate(); } catch (_) { return null; } }
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  if (typeof v === 'string' || typeof v === 'number') { const d = new Date(v); return isNaN(d.getTime()) ? null : d; }
  return null;
}

// 'YYYY-MM-DD' -> LOCAL day start, or local 23:59:59.999 for the end (inclusive).
function woDayBound(str, end) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str || '');
  if (!m) return null;
  return end ? new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59, 999) : new Date(+m[1], +m[2] - 1, +m[3]);
}

function woInRange(d, fromStr, toStr) {
  const from = woDayBound(fromStr, false);
  const to = woDayBound(toStr, true);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

// "No cost entered" is NOT $0: totalCost > 0 is the same test as the card's badge.
function woHasCost(task) {
  return maintMoney(task.costs?.totalCost) > 0; // TASK-10269: "$1,250.00" has a cost
}

function woTypeLabel(type) {
  if (!type) return '';
  if (type.startsWith('src:')) return woSourceLabel(type.slice(4));
  return type.startsWith('cat:') ? type.slice(4) : type;
}

function woSourceLabel(src) {
  return ({ pretrip: 'From a DVIR', driver: 'Driver-reported', manual: 'Office-entered' })[src] || `Source: ${src}`;
}

// THE predicate. Everything except the status tab. `tab` only matters for the
// Completed date range, which applies on the Completed tab alone.
function woMatches(task, f, tab) {
  if (!f) return true;
  const q = _woNorm(f.search);
  if (q && !woSearchText(task).includes(q)) return false;
  if (f.vehicles && f.vehicles.length && !f.vehicles.includes(woTaskVehicle(task).key)) return false;
  if (f.type) {
    if (f.type.startsWith('cat:') && String(task.category || 'General') !== f.type.slice(4)) return false;
    if (f.type.startsWith('src:') && String(task.source || '') !== f.type.slice(4)) return false;
  }
  if (f.priority && _woNorm(task.priority || 'normal') !== f.priority) return false;
  if (f.createdFrom || f.createdTo) {
    // A just-written order has a pending (null) createdAt: it is "now", as on the card.
    const d = woToDate(task.createdAt) || new Date();
    if (!woInRange(d, f.createdFrom, f.createdTo)) return false;
  }
  if (tab === 'completed' && (f.completedFrom || f.completedTo)) {
    const d = woToDate(task.completedAt);
    if (!d || !woInRange(d, f.completedFrom, f.completedTo)) return false;
  }
  if (f.reportedBy && _woNorm(woReporter(task)) !== f.reportedBy) return false;
  if (f.completedBy && _woNorm(woCompleter(task)) !== f.completedBy) return false;
  if (f.assignedTo) {
    if (f.assignedTo === '__none') { if (task.assignedTo) return false; } else if (task.assignedTo !== f.assignedTo) return false;
  }
  if (f.cost === 'has' && !woHasCost(task)) return false;
  if (f.cost === 'none' && woHasCost(task)) return false;
  return true;
}

function woSortTasks(tasks, sort) {
  const s = woValidSort(sort);
  const ts = (t) => (woToDate(t.createdAt) || new Date(8.64e15)).getTime(); // pending write = newest
  const arr = tasks.slice();
  if (s === 'oldest') arr.sort((a, b) => ts(a) - ts(b));
  else if (s === 'cost') arr.sort((a, b) => (maintMoney(b.costs?.totalCost) - maintMoney(a.costs?.totalCost)) || (ts(b) - ts(a)));
  else if (s === 'vehicle') {
    arr.sort((a, b) => woTaskVehicle(a).label.localeCompare(woTaskVehicle(b).label, undefined, { numeric: true, sensitivity: 'base' }) || (ts(b) - ts(a)));
  } else arr.sort((a, b) => ts(b) - ts(a));
  return arr;
}

function woFindName(fn, normVal) {
  const hit = (MaintenanceState.tasks || []).find(t => _woNorm(fn(t)) === normVal);
  return hit ? fn(hit) : normVal;
}

function woRangeLabel(from, to) {
  if (from && to) return `${from} – ${to}`;
  return from ? `from ${from}` : `through ${to}`;
}

// The active filters as chips. `more: true` = lives under "More filters".
function woActiveFilters(f, tab) {
  const out = [];
  if (!f) return out;
  if (f.search && f.search.trim()) out.push({ kind: 'search', val: '', label: `Search: ${f.search.trim()}` });
  (f.vehicles || []).forEach(k => out.push({ kind: 'vehicle', val: k, label: woVehicleKeyLabel(k) }));
  if (f.type) out.push({ kind: 'type', val: '', label: woTypeLabel(f.type) });
  if (f.priority) out.push({ kind: 'priority', val: '', label: `Priority: ${f.priority}` });
  if (f.createdFrom || f.createdTo) out.push({ kind: 'created', val: '', label: `Created ${woRangeLabel(f.createdFrom, f.createdTo)}`, more: true });
  if (tab === 'completed' && (f.completedFrom || f.completedTo)) {
    out.push({ kind: 'completed', val: '', label: `Completed ${woRangeLabel(f.completedFrom, f.completedTo)}`, more: true });
  }
  if (f.reportedBy) out.push({ kind: 'reportedBy', val: '', label: `Reported by ${woFindName(woReporter, f.reportedBy)}`, more: true });
  if (f.completedBy) out.push({ kind: 'completedBy', val: '', label: `Completed by ${woFindName(woCompleter, f.completedBy)}`, more: true });
  if (f.assignedTo) {
    out.push({ kind: 'assignedTo', val: '', label: f.assignedTo === '__none' ? 'Unassigned' : `Assigned to ${getTaskAssigneeName({ assignedTo: f.assignedTo })}`, more: true });
  }
  if (f.cost) out.push({ kind: 'cost', val: '', label: f.cost === 'has' ? 'Has cost' : 'No cost entered', more: true });
  return out;
}

function woOptionsHtml(pairs, anyLabel) {
  return `<option value="">${escapeHtml(anyLabel)}</option>` +
    pairs.map(([v, label]) => `<option value="${escapeHtml(v)}">${escapeHtml(label)}</option>`).join('');
}

// Paints the bar's option lists (only when they changed, so an open <select> is
// not rebuilt under the user by a live snapshot), syncs control values to the
// state, and paints chips + filtered-state line + hidden-critical warning.
function woRenderFilterChrome(tab, statusTotal, shown) {
  const bar = document.getElementById('wo-filter-bar');
  if (!bar) return;
  const f = _woFilter();
  const tasks = MaintenanceState.tasks || [];
  const byLabel = (a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { numeric: true, sensitivity: 'base' });

  const vehicles = new Map();
  const cats = new Set();
  const srcs = new Set();
  const prios = new Set(['critical', 'high', 'normal']);
  const reporters = new Map();
  const completers = new Map();
  const assignees = new Map();
  for (const t of tasks) {
    const v = woTaskVehicle(t);
    if (!vehicles.has(v.key)) vehicles.set(v.key, v);
    cats.add(String(t.category || 'General'));
    if (t.source) srcs.add(String(t.source));
    prios.add(_woNorm(t.priority || 'normal'));
    const r = woReporter(t); if (r) reporters.set(_woNorm(r), r);
    const c = woCompleter(t); if (c) completers.set(_woNorm(c), c);
    if (t.assignedTo) assignees.set(String(t.assignedTo), getTaskAssigneeName(t)); // dual id/name lookup (legacy GAS refs)
  }
  // Whatever is selected stays an option, even if no doc carries it any more.
  f.vehicles.forEach(k => { if (!vehicles.has(k)) vehicles.set(k, { key: k, label: woVehicleKeyLabel(k), group: 'removed' }); });
  if (f.reportedBy && !reporters.has(f.reportedBy)) reporters.set(f.reportedBy, f.reportedBy);
  if (f.completedBy && !completers.has(f.completedBy)) completers.set(f.completedBy, f.completedBy);
  if (f.assignedTo && f.assignedTo !== '__none' && !assignees.has(f.assignedTo)) assignees.set(f.assignedTo, getTaskAssigneeName({ assignedTo: f.assignedTo }));
  if (f.priority) prios.add(f.priority);
  if (f.type.startsWith('cat:')) cats.add(f.type.slice(4));
  if (f.type.startsWith('src:')) srcs.add(f.type.slice(4));

  const groups = [['truck', 'Vehicles'], ['trailer', 'Trailers'], ['asset', 'Assets'], ['removed', 'Not in fleet']];
  const vehicleList = [...vehicles.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  const catList = [...cats].sort((a, b) => a.localeCompare(b));
  const srcList = [...srcs].sort();
  const prioOrder = ['critical', 'high', 'normal'];
  const prioList = [...prios].sort((a, b) => {
    const ia = prioOrder.indexOf(a), ib = prioOrder.indexOf(b);
    return (ia < 0 ? 9 : ia) - (ib < 0 ? 9 : ib) || a.localeCompare(b);
  });
  const repList = [...reporters.entries()].sort(byLabel);
  const compList = [...completers.entries()].sort(byLabel);
  const asgList = [...assignees.entries()].sort(byLabel);

  const sig = JSON.stringify([vehicleList.map(v => [v.key, v.label, v.group]), catList, srcList, prioList, repList, compList, asgList]);
  if (sig !== MaintenanceState._woOptsSig) {
    MaintenanceState._woOptsSig = sig;
    const list = document.getElementById('wo-vehicle-list');
    if (list) {
      const html = groups.map(([g, title]) => {
        const rows = vehicleList.filter(v => v.group === g);
        if (!rows.length) return '';
        return `<div class="wo-vehicle-group">${escapeHtml(title)}</div>` + rows.map(v => `
          <label class="wo-vehicle-option"><input type="checkbox" value="${escapeHtml(v.key)}"
            onchange="window.MaintenanceModule.woToggleVehicle(this.value, this.checked)"><span>${escapeHtml(v.label)}</span></label>`).join('');
      }).join('');
      list.innerHTML = html || '<div class="wo-vehicle-empty">No work orders yet</div>';
    }
    const typeSel = document.getElementById('wo-type');
    if (typeSel) {
      typeSel.innerHTML = '<option value="">All types</option>' +
        (catList.length ? `<optgroup label="Category">${catList.map(c => `<option value="${escapeHtml('cat:' + c)}">${escapeHtml(c)}</option>`).join('')}</optgroup>` : '') +
        (srcList.length ? `<optgroup label="Source">${srcList.map(s => `<option value="${escapeHtml('src:' + s)}">${escapeHtml(woSourceLabel(s))}</option>`).join('')}</optgroup>` : '');
    }
    const prioSel = document.getElementById('wo-priority');
    if (prioSel) prioSel.innerHTML = woOptionsHtml(prioList.map(p => [p, p.charAt(0).toUpperCase() + p.slice(1)]), 'All priorities');
    const repSel = document.getElementById('wo-reported-by');
    if (repSel) repSel.innerHTML = woOptionsHtml(repList, 'Anyone');
    const compSel = document.getElementById('wo-completed-by');
    if (compSel) compSel.innerHTML = woOptionsHtml(compList, 'Anyone');
    const asgSel = document.getElementById('wo-assigned-to');
    if (asgSel) asgSel.innerHTML = woOptionsHtml([['__none', 'Unassigned'], ...asgList], 'Anyone');
  }

  // Sync control values to the state (never rewrite the field being typed in).
  const setVal = (id, v) => {
    const el = document.getElementById(id);
    if (el && el.value !== v && document.activeElement !== el) el.value = v;
  };
  setVal('wo-search', f.search);
  setVal('wo-type', f.type);
  setVal('wo-priority', f.priority);
  setVal('wo-sort', woValidSort(MaintenanceState.woSort));
  setVal('wo-created-from', f.createdFrom);
  setVal('wo-created-to', f.createdTo);
  setVal('wo-completed-from', f.completedFrom);
  setVal('wo-completed-to', f.completedTo);
  setVal('wo-reported-by', f.reportedBy);
  setVal('wo-completed-by', f.completedBy);
  setVal('wo-assigned-to', f.assignedTo);
  setVal('wo-cost', f.cost);
  document.querySelectorAll('#wo-vehicle-list input[type="checkbox"]').forEach(cb => { cb.checked = f.vehicles.includes(cb.value); });
  const summary = document.getElementById('wo-vehicle-summary');
  if (summary) {
    summary.textContent = !f.vehicles.length ? 'All vehicles'
      : f.vehicles.length === 1 ? woVehicleKeyLabel(f.vehicles[0]) : `${f.vehicles.length} vehicles`;
  }
  const more = document.getElementById('wo-more');
  if (more) more.classList.toggle('open', !!MaintenanceState.woMoreOpen);
  document.getElementById('wo-more-toggle')?.setAttribute('aria-expanded', MaintenanceState.woMoreOpen ? 'true' : 'false');
  bar.querySelectorAll('.wo-completed-only').forEach(el => el.classList.toggle('hidden', tab !== 'completed'));

  const active = woActiveFilters(f, tab);
  const doneBtn = document.getElementById('wo-sheet-done');
  if (doneBtn) doneBtn.textContent = `Show ${Number(shown)} result${shown === 1 ? '' : 's'}`;
  const toggleLabel = document.getElementById('wo-filters-toggle-label');
  const nonSearch = active.filter(a => a.kind !== 'search').length;
  if (toggleLabel) toggleLabel.textContent = nonSearch ? `Filters (${nonSearch})` : 'Filters';

  const region = document.getElementById('wo-active-filters');
  if (!region) return;
  if (!active.length) {
    region.classList.add('hidden');
    region.innerHTML = '';
    return;
  }
  // A critical defect not yet completed (open, in progress OR waiting on review —
  // none is certified repaired, 49 CFR 396.11) is never hidden silently. COD
  // widened this from 'open' only.
  const critHidden = tasks.filter(t => ['open', 'in_progress', 'pending_review'].includes(t.status)
    && _woNorm(t.priority) === 'critical' && !woMatches(t, f, t.status)).length;
  region.classList.remove('hidden');
  region.innerHTML = `
    <div class="wo-chips">
      ${active.map(a => `<button type="button" class="wo-chip" data-kind="${escapeHtml(a.kind)}" data-val="${escapeHtml(a.val || '')}"
        title="Remove this filter" onclick="window.MaintenanceModule.woRemoveFilter(this.dataset.kind, this.dataset.val)">
        <span class="wo-chip-text">${escapeHtml(a.label)}</span><span class="material-icons" aria-hidden="true">close</span></button>`).join('')}
      <button type="button" class="wo-clear-link" onclick="window.MaintenanceModule.woClearFilters()">Clear filters</button>
    </div>
    <div class="wo-filter-status">Filters on — ${Number(shown)} of ${Number(statusTotal)} ${escapeHtml(woTabLabel(tab))} shown</div>
    ${critHidden ? `<div class="wo-critical-hidden" role="status"><span class="material-icons">warning</span>
      <span>${critHidden} critical defect${critHidden === 1 ? '' : 's'} hidden by filters</span>
      <button type="button" class="btn btn-sm btn-secondary" onclick="window.MaintenanceModule.woClearFilters()">Show all</button></div>` : ''}
  `;
}

function woRerender() {
  renderTasksList(MaintenanceState.currentFilter || 'open');
}

function woSetFilter(key, value) {
  const base = woDefaultFilter();
  if (!(key in base) || key === 'vehicles') return;
  const f = { ..._woFilter(), vehicles: [..._woFilter().vehicles] };
  f[key] = typeof value === 'string' ? value.slice(0, 200) : '';
  if (key === 'search') f.search = value ? String(value).slice(0, 200) : '';
  MaintenanceState.woFilter = f;
  woSaveFilters();
  woRerender();
}

function woToggleVehicle(key, checked) {
  if (typeof key !== 'string' || !key) return;
  const f = { ..._woFilter() };
  const set = new Set(f.vehicles);
  if (checked) set.add(key); else set.delete(key);
  f.vehicles = [...set];
  MaintenanceState.woFilter = f;
  woSaveFilters();
  woRerender();
}

function woRemoveFilter(kind, val) {
  const f = { ..._woFilter(), vehicles: [..._woFilter().vehicles] };
  switch (kind) {
    case 'search': f.search = ''; break;
    case 'vehicle': f.vehicles = f.vehicles.filter(k => k !== val); break;
    case 'type': f.type = ''; break;
    case 'priority': f.priority = ''; break;
    case 'created': f.createdFrom = ''; f.createdTo = ''; break;
    case 'completed': f.completedFrom = ''; f.completedTo = ''; break;
    case 'reportedBy': f.reportedBy = ''; break;
    case 'completedBy': f.completedBy = ''; break;
    case 'assignedTo': f.assignedTo = ''; break;
    case 'cost': f.cost = ''; break;
    default: return;
  }
  MaintenanceState.woFilter = f;
  woSaveFilters();
  woRerender();
}

// Clears FILTERS. Sort is a preference, not a filter — it stays.
function woClearFilters() {
  MaintenanceState.woFilter = woDefaultFilter();
  woSaveFilters();
  const picker = document.getElementById('wo-vehicle-picker');
  if (picker) picker.open = false;
  woRerender();
}

function woSetSort(value) {
  MaintenanceState.woSort = woValidSort(value);
  woSaveSort();
  woRerender();
}

function woToggleMore() {
  MaintenanceState.woMoreOpen = !MaintenanceState.woMoreOpen;
  woRerender();
}

// Phone: #wo-filter-panel becomes a bottom sheet (styles.css). Same DOM as desktop.
function woOpenSheet() {
  document.getElementById('wo-filter-panel')?.classList.add('sheet-open');
  document.getElementById('wo-sheet-backdrop')?.classList.add('open');
}

function woCloseSheet() {
  document.getElementById('wo-filter-panel')?.classList.remove('sheet-open');
  document.getElementById('wo-sheet-backdrop')?.classList.remove('open');
  const picker = document.getElementById('wo-vehicle-picker');
  if (picker) picker.open = false;
}

// Desktop vehicle dropdown closes on an outside click (bound once per page load).
function woOnDocClick(e) {
  const picker = document.getElementById('wo-vehicle-picker');
  if (picker && picker.open && !picker.contains(e.target)) picker.open = false;
}

// A fleet-card "show tasks" REPLACES the vehicle filter (and the old parallel
// truck/trailer/asset drills are cleared), instead of AND-ing with a stale one.
// Other filters stay and are visible as chips.
function woDrillToVehicle(key) {
  const f = { ..._woFilter(), vehicles: [key] };
  MaintenanceState.woFilter = f;
  woSaveFilters();
  MaintenanceState.truckFilter = null;
  if (MaintenanceState.trailerFilter) MaintenanceState.trailerFilter.id = null;
  if (MaintenanceState.assetFilter) MaintenanceState.assetFilter.filterId = null;
  MaintenanceState.currentFilter = 'open';
  switchTab('tasks');
  document.querySelectorAll('.maintenance-filter').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.filter === 'open');
  });
  renderTasksList('open');
}

function renderTasksList(filter = 'open') {
  const container = document.getElementById('maintenance-list');
  if (!container) return;
  if (isMaintenanceModalOpen()) return;

  // TASK-9897 — ONE predicate. The status tab picks the set; woMatches applies every
  // other filter (search, vehicles — the fleet-card drill lives here now, type,
  // priority, dates, people, cost). The old parallel truck/trailer/asset drill ANDs
  // are gone, so a stale drill can no longer silently AND with a new one.
  const statusTasks = MaintenanceState.tasks.filter(t => woTab(t) === filter); // wo-legacy-orders — the tab, not the raw value
  const woF = _woFilter();
  let tasks = woSortTasks(statusTasks.filter(t => woMatches(t, woF, filter)), MaintenanceState.woSort);
  woRenderFilterChrome(filter, statusTasks.length, tasks.length);

  if (tasks.length === 0) {
    const activeFilters = woActiveFilters(woF, filter);
    if (activeFilters.length && MaintenanceState._tasksLoaded) {
      // Filtered-empty is NOT "no tasks": name what is hiding them and offer Clear.
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">🔎</div>
          <div class="empty-state-title">No work orders match these filters</div>
          <div class="empty-state-text">${statusTasks.length} ${escapeHtml(woTabLabel(filter))} hidden by: ${activeFilters.map(a => escapeHtml(a.label)).join(' · ')}</div>
          <button type="button" class="btn btn-secondary" onclick="window.MaintenanceModule.woClearFilters()">
            <span class="material-icons">filter_alt_off</span> Clear filters
          </button>
        </div>
      `;
      return;
    }
    if (!MaintenanceState._tasksLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading tasks...</div>
        </div>
      `;
      return;
    }
    const filterLabel = { open: 'open', in_progress: 'in progress', pending_review: 'pending review', completed: 'completed' }[filter];
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🔧</div>
        <div class="empty-state-title">No ${filterLabel} tasks</div>
        <div class="empty-state-text">${filter === 'open' ? 'Create a task or complete a DVIR inspection with notes.' : 'Tasks will appear here as they are updated.'}</div>
      </div>
    `;
    return;
  }

  container.innerHTML = tasks.map(task => {
    const date = task.createdAt?.toDate?.() || new Date();
    const dateStr = formatDate(date);
    const priorityClass = task.priority || 'normal';
    const isFromDvir = task.source === 'pretrip';
    const hasCosts = woHasCost(task); // TASK-10269: "$1,250.00" text costs (same test as the Cost filter)
    const isAssetTask = task.assetType === 'asset' || !!task.assetId;
    const isTrailerTask = !isAssetTask && (task.assetType === 'trailer' || !!task.trailerId);
    const assetIcon = isAssetTask ? 'handyman' : isTrailerTask ? 'rv_hookup' : 'local_shipping';
    let assetUnit;
    if (isAssetTask) {
      assetUnit = task.assetName || '?';
    } else if (isTrailerTask) {
      const trailerObj = task.trailerId && MaintenanceState.trailers.find(t => t.id === task.trailerId);
      assetUnit = trailerObj && window.getUnitDisplay ? window.getUnitDisplay(trailerObj) : (task.trailerUnit || '?');
    } else {
      const truckObj = task.truckId && MaintenanceState.trucks.find(t => t.id === task.truckId);
      assetUnit = truckObj && window.getUnitDisplay ? window.getUnitDisplay(truckObj) : (task.truckUnit || '?');
    }

    return `
      ${/* TASK-9287 -- THREE POSITIONS, TWO ESCAPERS. 'priority' is a free-text field
             on the task document and a Firestore doc id is chosen by the caller on a
             setDoc, so both are attacker-controlled from a driver-PIN session.
             class= and data-id= are HTML ATTRIBUTE VALUES -> escapeHtml (this file's
             local wrapper delegates to window.escapeHtml, the regex escaper covering
             the full &<>"' set, so it IS attribute-safe here -- unlike app.js's
             DOM-based local variant, which leaves the quote unescaped).
             The onclick argument is a JS STRING INSIDE an attribute: the HTML parser
             decodes &#39; back to an apostrophe BEFORE the handler is compiled as
             JavaScript, so no html escaper protects it -- that position needs
             escJsAttr and only escJsAttr. Proof in tests/task9287-maintenance-card-xss.spec.js. */''}
      <div class="maintenance-card priority-${escapeHtml(priorityClass)}" data-id="${escapeHtml(task.id)}" onclick="window.MaintenanceModule.showTaskDetail('${escJsAttr(task.id)}')">
        <div class="maintenance-card-priority">
          <span class="priority-indicator"></span>
        </div>
        <div class="maintenance-card-info">
          <div class="maintenance-card-header">
            <span class="maintenance-card-truck"><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 2px;">${assetIcon}</span>${escapeHtml(assetUnit)}</span>
            <span class="maintenance-card-category">${escapeHtml(task.category || 'General')}</span>
            ${isFromDvir ? '<span class="source-badge">DVIR</span>' : ''}
            ${isFromDvir && _repeatsForTask(task).length ? '<span class="source-badge task-repeat-chip" title="This unit already has an open defect from an earlier DVIR" style="background:rgba(245,158,11,0.15);color:#b45309;">Possible repeat</span>' : ''}
            ${hasCosts ? `<span class="cost-badge">$${maintMoney(task.costs.totalCost).toFixed(2)}</span>` : ''}
          </div>
          <div class="maintenance-card-description">${escapeHtml(task.description || 'No description')}</div>
          <div class="maintenance-card-meta">
            <span><span class="material-icons">event</span> ${dateStr}</span>
            ${task.status === 'completed' && task.completedBy
              ? `<span class="completed-by"><span class="material-icons">check_circle</span> ${escapeHtml(task.completedBy)}</span>`
              : task.status === 'pending_review' && task.driverCompletedBy
                // TASK-4773 — "logged this work" means it was never an open work
                // order: the driver recorded finished routine work (greasing, a
                // PM). "marked done" means they closed one out. Same review queue,
                // different story for whoever is signing off.
                ? `<span class="completed-by"><span class="material-icons">person</span> ${escapeHtml(task.driverCompletedBy)} ${task.driverLoggedComplete ? 'logged this work' : 'marked done'}</span>`
                : task.assignedTo
                  ? `<span><span class="material-icons">person</span> ${escapeHtml(getPersonName(task.assignedTo))}</span>`
                  : task.createdBy
                    ? `<span class="reported-by"><span class="material-icons">person_outline</span> Reported by ${escapeHtml(task.createdBy)}</span>`
                    : '<span class="unassigned">Unassigned</span>'}
          </div>
        </div>
        <div class="maintenance-card-actions">
          ${task.status === 'open' ? `
            <button class="btn btn-sm btn-secondary" onclick="event.stopPropagation(); window.MaintenanceModule.updateStatus('${escJsAttr(task.id)}', 'in_progress')">
              Start
            </button>
          ` : ''}
          ${task.status === 'in_progress' ? `
            <button class="btn btn-sm btn-primary" onclick="event.stopPropagation(); window.MaintenanceModule.updateStatus('${escJsAttr(task.id)}', 'completed')">
              Finalize &amp; Add Costs
            </button>
          ` : ''}
          ${task.status === 'pending_review' ? `
            <button class="btn btn-sm btn-primary" onclick="event.stopPropagation(); window.MaintenanceModule.updateStatus('${escJsAttr(task.id)}', 'completed')">
              <span class="material-icons">check</span> Review &amp; Complete
            </button>
          ` : ''}
          ${task.status === 'completed' ? `
            <button class="btn btn-sm btn-secondary" onclick="event.stopPropagation(); window.MaintenanceModule.showAddCosts('${escJsAttr(task.id)}')">
              <span class="material-icons">attach_money</span> ${hasCosts ? 'Edit Costs' : 'Add Costs'}
            </button>
          ` : ''}
          <button class="btn btn-sm btn-danger-subtle" onclick="event.stopPropagation(); window.MaintenanceModule.deleteTask('${escJsAttr(task.id)}')" title="Delete task">
            <span class="material-icons">delete</span>
          </button>
        </div>
      </div>
    `;
  }).join('');
}

function updateCounts() {
  const open = MaintenanceState.tasks.filter(t => woTab(t) === 'open').length;
  const inProgress = MaintenanceState.tasks.filter(t => woTab(t) === 'in_progress').length;
  const pendingReview = MaintenanceState.tasks.filter(t => woTab(t) === 'pending_review').length;
  const completed = MaintenanceState.tasks.filter(t => woTab(t) === 'completed').length;

  const countOpen = document.getElementById('count-open');
  const countInProgress = document.getElementById('count-in-progress');
  const countPendingReview = document.getElementById('count-pending-review');
  const countCompleted = document.getElementById('count-completed');
  const tabCountTasks = document.getElementById('tab-count-tasks');

  if (window.setCountBadge) {
    window.setCountBadge(countOpen, open);
    window.setCountBadge(countInProgress, inProgress);
    window.setCountBadge(countPendingReview, pendingReview);
    window.setCountBadge(countCompleted, completed);
    window.setCountBadge(tabCountTasks, open + inProgress + pendingReview);
  } else {
    if (countOpen) countOpen.textContent = open;
    if (countInProgress) countInProgress.textContent = inProgress;
    if (countPendingReview) countPendingReview.textContent = pendingReview;
    if (countCompleted) countCompleted.textContent = completed;
    if (tabCountTasks) tabCountTasks.textContent = open + inProgress + pendingReview;
  }
}

function updateTruckDropdown() {
  const select = document.getElementById('task-truck');
  if (!select) return;

  select.innerHTML = '<option value="">Select vehicle</option>' +
    MaintenanceState.trucks.map(t => `<option value="${t.id}" data-unit="${escapeHtml(t.unit)}">${escapeHtml(t.unit)}</option>`).join('');
}

function updateTrailerDropdown() {
  const select = document.getElementById('task-trailer');
  if (!select) return;

  select.innerHTML = '<option value="">Select trailer</option>' +
    MaintenanceState.trailers
      .filter(t => t.active !== false)
      .map(t => `<option value="${t.id}" data-unit="${escapeHtml(t.unit || t.name)}">${escapeHtml(t.unit || t.name)}</option>`).join('');
}

function updateAssignedDropdown() {
  const select = document.getElementById('task-assigned');
  if (!select) return;

  // Show all active people (anyone can be assigned a maintenance task)
  const people = MaintenanceState.people.filter(p => p.active !== false);

  select.innerHTML = '<option value="">Unassigned</option>' +
    people.map(m => `<option value="${m.id}">${escapeHtml(m.name)}</option>`).join('');
}

function getPersonName(personId) {
  const person = MaintenanceState.people.find(p => p.id === personId);
  return person?.name || 'Unknown';
}

// ============================================
// TASK OPERATIONS
// ============================================
// TASK-9122 — `mode` is which of the two driver paths the tapped entry button
// means ('issue' | 'done'). Ignored outside driver-PIN mode: the admin "New Task"
// button (line ~722) calls this with no argument and its modal has no two-path
// concept at all. Order matters — setDriverTaskMode runs AFTER the reset below,
// because form.reset() restores the DOM defaults and would otherwise leave a
// 'done' open reading the 'issue' labels.
function showNewTask(mode) {
  // TASK-4773 — this reset is the partial-rename trap: it sets the title AGAIN at
  // open time, so renaming only the <h3> in the template leaves the modal saying
  // one thing and the button another.
  document.getElementById('modal-task-title').textContent = isDriverPinMode() ? 'Log Maintenance' : 'New Maintenance Task';
  document.getElementById('form-task').reset();
  clearTaskFormError(document.getElementById('form-task')); // form.reset() doesn't remove injected nodes
  document.querySelectorAll('#task-category option[data-legacy-cat]').forEach(o => o.remove()); // wo-legacy-orders: an edit's imported category is not a new-order choice
  document.getElementById('task-id').value = '';
  document.getElementById('task-asset-type').value = 'truck';
  updateTaskAssetDropdown();
  onTaskCategoryChange();
  // In driver mode, auto-assign to self
  if (isDriverPinMode()) {
    // Always set the mode explicitly — form.reset() restores the DOM defaults but
    // not MaintenanceState, so without this a driver's previous "work I did"
    // session would silently make their next problem report land as
    // pending_review. An unrecognised/absent mode falls back to 'issue' inside
    // setDriverTaskMode, which is the safe direction: a problem report that
    // opens a work order, never a silent self-signed completion.
    setDriverTaskMode(mode);
    const assignedEl = document.getElementById('task-assigned');
    if (assignedEl) assignedEl.value = getDriverPersonId() || '';
  }
  document.getElementById('modal-task').classList.remove('hidden');
  // TASK-9122 — after the modal is visible, so the .modal-content query hits.
  if (isDriverPinMode()) attachKeyboardInset();
}

function editTask(id) {
  const task = MaintenanceState.tasks.find(t => t.id === id);
  if (!task) return;

  document.getElementById('modal-task-title').textContent = 'Edit Task';
  clearTaskFormError(document.getElementById('form-task'));
  document.getElementById('task-id').value = id;

  // Determine asset type and ID
  const isAsset = !!task.assetId;
  const isTrailer = !isAsset && !!task.trailerId;
  document.getElementById('task-asset-type').value = isAsset ? 'asset' : isTrailer ? 'trailer' : 'truck';
  updateTaskAssetDropdown();
  document.getElementById('task-asset').value = isAsset ? task.assetId : isTrailer ? task.trailerId : (task.truckId || '');

  // wo-legacy-orders (COD) — an imported order's category ("PM", "Safety") is not
  // one of the form's options; offer it so Save keeps it instead of blanking it.
  const _catSel = document.getElementById('task-category');
  _catSel.querySelectorAll('option[data-legacy-cat]').forEach(o => o.remove());
  if (task.category && !Array.from(_catSel.options).some(o => o.value === task.category)) {
    const opt = document.createElement('option');
    opt.value = task.category;
    opt.textContent = task.category;
    opt.dataset.legacyCat = '1';
    _catSel.appendChild(opt);
  }
  _catSel.value = task.category || 'Other';
  onTaskCategoryChange();
  document.getElementById('task-description').value = task.description || '';
  document.getElementById('task-priority').value = task.priority || 'normal';
  document.getElementById('task-assigned').value = task.assignedTo || '';
  document.getElementById('task-notes').value = task.notes || '';
  document.getElementById('task-vendor').value = task.vendor || '';
  document.getElementById('modal-task').classList.remove('hidden');
}

// ── TASK-4779, second defect ────────────────────────────────────────────────
// window.validateRequiredFields() refuses the submit with a toast in
// #toast-container, which on mobile is `position:fixed; bottom:8px` — and on a
// .modal-fullscreen sheet that is the extreme bottom of the phone. It also
// deliberately skips .focus() (app.js:13369 — focusing opens the Android keyboard
// ON TOP of the toast, which is the "nothing happened" bug in a different costume).
// Net effect for a driver: the field gets a red border somewhere mid-form, and the
// only words explaining why Submit did nothing are pinned off in the corner.
//
// So mirror the refusal INLINE, in the two places the driver is actually looking:
//   1. under the offending field — validateRequiredFields already scrolled it to
//      the centre of the viewport, so that is where their eye lands;
//   2. inside the sticky .form-actions bar, right above Submit — where the thumb is.
// Still no .focus() anywhere, so the keyboard problem is not reintroduced.
const TASK_FORM_ERROR_CLASS = 'task-form-inline-error';

function _taskFieldLabel(el) {
  return el.closest('.form-group')?.querySelector('label')?.textContent?.replace(/\s*\*\s*$/, '').trim()
    || el.placeholder || el.id || 'This field';
}

// Mirrors window.validateRequiredFields' own skip logic so the message we render
// names the SAME field it red-bordered — never a different one.
function _firstInvalidTaskField(formEl) {
  if (!formEl) return null;
  for (const el of formEl.querySelectorAll('[required]')) {
    if (el.type === 'hidden') continue;
    if (el.offsetParent === null) continue;
    const val = (el.value || '').trim();
    if (!val) return { el, message: `${_taskFieldLabel(el)} is required.` };
    if (el.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      return { el, message: `${_taskFieldLabel(el)} must be a valid email address.` };
    }
  }
  return null;
}

// TASK-9122 — `.form-actions, .modal-form-footer`, both, in BOTH helpers. The
// driver form's action row was renamed to .modal-form-footer when it moved to the
// scrollable-form trio; the admin form still uses .form-actions. A single-selector
// lookup here fails OPEN and SILENT — the refusal still renders under the field,
// so nothing throws and the suite's "no page errors" check stays green, while the
// sticky copy TASK-4779 added (because on a 402px sheet the offending field has
// usually scrolled out of view) quietly stops appearing. That is the same
// invisible-refusal this whole area exists to prevent.
const TASK_FORM_ACTION_ROW = '.form-actions, .modal-form-footer';

function clearTaskFormError(formEl) {
  formEl = formEl || document.getElementById('form-task');
  if (!formEl) return;
  formEl.querySelectorAll('.' + TASK_FORM_ERROR_CLASS).forEach(n => n.remove());
  const actions = formEl.querySelector(TASK_FORM_ACTION_ROW);
  if (actions) actions.style.flexWrap = '';
}

function showTaskFormError(formEl, message, el) {
  if (!formEl) return;
  clearTaskFormError(formEl);
  // #b91c1c, not var(--text-muted): the action row is var(--bg-card) (light) on
  // mobile and a muted grey there is exactly the invisible-refusal we're fixing.
  const build = (extraCss) => {
    const d = document.createElement('div');
    d.className = TASK_FORM_ERROR_CLASS;
    d.setAttribute('role', 'alert');
    // textContent, never innerHTML — the label is DOM-sourced text.
    d.textContent = message;
    d.style.cssText = 'color:#b91c1c;font-size:0.8125rem;font-weight:600;line-height:1.35;' + (extraCss || '');
    return d;
  };
  const group = el?.closest('.form-group');
  if (group) group.appendChild(build('margin-top:6px;'));
  const actions = formEl.querySelector(TASK_FORM_ACTION_ROW);
  if (actions) {
    actions.style.flexWrap = 'wrap';
    const bar = build('flex:1 0 100%;width:100%;order:-1;margin:0 0 8px 0;text-align:left;');
    // COD UX Lead — on a 402px sheet the offending field can be scrolled away by
    // the time they read this. Tapping the sticky copy takes them back to it.
    // scrollIntoView only, never .focus() — see the note above.
    if (el) {
      bar.style.cursor = 'pointer';
      bar.addEventListener('click', () => el.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }
    actions.insertBefore(bar, actions.firstChild);
  }
  // Clear as soon as they fix it — same input/change contract the red border uses.
  if (el) {
    const clear = () => clearTaskFormError(formEl);
    el.addEventListener('input', clear, { once: true });
    el.addEventListener('change', clear, { once: true });
  }
}

async function saveTask(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) {
    const bad = _firstInvalidTaskField(e.target);
    showTaskFormError(e.target, bad?.message || 'Fill in the required fields to submit.', bad?.el);
    return;
  }
  clearTaskFormError(e.target);

  const assetType = document.getElementById('task-asset-type').value;
  const assetSelect = document.getElementById('task-asset');
  const assetId = assetSelect.value;
  const assetUnit = assetSelect.options[assetSelect.selectedIndex]?.dataset?.unit || '';

  const id = document.getElementById('task-id').value;
  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const data = {
    assetType,
    category: document.getElementById('task-category').value,
    description: _clamp(document.getElementById('task-description').value.trim(), 2000),
    priority: document.getElementById('task-priority').value,
    assignedTo: document.getElementById('task-assigned').value || null,
    // TASK-4779 — #task-vendor exists ONLY in the admin modal. The driver modal
    // (getDriverTaskModalHTML) is a REDUCED copy of this form and never had it, so
    // since d0f9b617 (2026-04-16, "Add Vendor / Service Company field") this line
    // threw a bare TypeError on EVERY driver "Report Issue" submit — before the try
    // block below, so nothing was written and the driver saw nothing at all.
    // Every read of a field the reduced form may not carry must be optional-chained.
    vendor: _clamp(document.getElementById('task-vendor')?.value?.trim() || '', 200) || null,
    notes: _clamp(document.getElementById('task-notes').value.trim(), 2000),
    updatedAt: Timestamp.now()
  };

  // wo-legacy-orders (COD) — editing an imported order replaces its typed unit
  // name and old row number with the vehicle picked here; keep both on the doc
  // (once) so the original record is never lost.
  const _editing = id ? MaintenanceState.tasks.find(t => t.id === id) : null;
  if (_editing && _editing.legacy && _editing.legacyAssetName !== undefined) {
    data.legacyAssetName = _editing.legacyAssetName || '';
    data.legacyAssetId = _editing.legacyAssetId ?? null;
  }

  // Set the appropriate ID field based on asset type
  if (assetType === 'truck') {
    data.truckId = assetId;
    data.truckUnit = assetUnit;
    data.trailerId = null;
    data.trailerUnit = null;
    data.assetId = null;
    data.assetName = null;
  } else if (assetType === 'trailer') {
    data.trailerId = assetId;
    data.trailerUnit = assetUnit;
    data.truckId = null;
    data.truckUnit = null;
    data.assetId = null;
    data.assetName = null;
  } else if (assetType === 'asset') {
    data.assetId = assetId;
    data.assetName = assetUnit;
    data.truckId = null;
    data.truckUnit = null;
    data.trailerId = null;
    data.trailerUnit = null;
  }

  // PM Service requires a meter reading as its baseline. The "miles/hours since
  // last PM" counter is DERIVED (trucks/trailers from the newest metersLog
  // type:'pm' entry via getLatestMeters(); assets from hoursTracking.lastPmHours).
  // A PM with no meter can't establish a baseline → block submit (CREATE only).
  // parseNum preserves a literal 0 (parseFlo(..)||null would treat 0 as empty).
  const parseNum = (v) => {
    if (v === undefined || v === null || String(v).trim() === '') return null;
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
  };
  const isPMService = data.category === 'PM Service';
  const taskMiles = parseNum(document.getElementById('task-miles')?.value);
  const taskHours = parseNum(document.getElementById('task-hours')?.value);
  // An asset only has a PM counter if it tracks hours (calculateAssetPMStatus
  // returns null otherwise) — and its reset lives on the asset doc, which
  // firestore.rules only let dispatcher+/roster-module (NOT a driver PIN) write.
  const pmAsset = assetType === 'asset'
    ? MaintenanceState.assets.find(x => x.id === assetId)
    : null;
  const assetTracksHours = !!pmAsset?.hoursTracking?.enabled;
  if (!id && isPMService) {
    // Driver PINs can't write the assets collection — don't pretend an asset PM
    // reset works for them (COD: no silent failures). Block with a clear message.
    if (assetType === 'asset' && isDriverPinMode()) {
      // TASK-4779 (COD Code Reviewer) — the other two refusals in this function
      // were still toast-only, i.e. the exact invisible-refusal the inline surface
      // above exists to end. Mirror them the same way.
      const msg = 'Asset PM reset isn’t available from the driver app yet — log it as a Repair, or ask your shop to record the PM.';
      window.showToast?.(msg, 'error');
      showTaskFormError(e.target, msg, document.getElementById('task-asset'));
      return;
    }
    // Hours-tracked assets reset off HOURS; trucks/trailers off either meter.
    // A non-hours-tracked asset has no PM counter — don't require a meter for it.
    const needsBaseline = assetType !== 'asset' || assetTracksHours;
    const hasBaseline = assetType === 'asset' ? taskHours !== null
                                              : (taskMiles !== null || taskHours !== null);
    if (needsBaseline && !hasBaseline) {
      const msg = assetType === 'asset'
        ? 'Enter current hours to log a PM — it sets the new PM baseline.'
        : 'Enter current mileage or hours to log a PM — it sets the new PM baseline.';
      window.showToast?.(msg, 'error');
      showTaskFormError(e.target, msg,
        document.getElementById(assetType === 'asset' ? 'task-hours' : 'task-miles'));
      return;
    }
  }

  // TASK-9659 — a truck / trailer meter on a new task that does not fit the
  // vehicle's history asks first (before the task is written, so Cancel leaves nothing).
  let taskMeterConfirmed = false;
  if (!id && (assetType === 'truck' || assetType === 'trailer') && (taskMiles !== null || taskHours !== null)) {
    const meterCheck = await confirmPlausibleMeter(assetId, taskMiles, taskHours);
    if (!meterCheck.proceed) return;
    taskMeterConfirmed = meterCheck.confirmed;
  }

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;

    if (id) {
      await updateDoc(doc(db, 'tenants', tenant, 'maintenance', id), data);
      // TASK-10146 — the picker rewrites truckId on EVERY edit; record it only
      // when the vehicle actually changed (a WO silently moved units on a live tenant).
      const _moved = taskVehicleChange(_editing, data);
      if (_moved) await auditTaskVehicleChange(_editing, _moved);
      window.showToast?.('Task updated', 'success');
    } else {
      data.status = 'open';
      data.createdAt = Timestamp.now();
      data.source = isDriverPinMode() ? 'driver' : 'manual';
      // Track who created the task (especially for driver-created tasks)
      if (isDriverPinMode()) {
        data.createdBy = getDriverPersonName();
        data.createdByPersonId = getDriverPersonId();
        // Auto-assign to self if not already set
        if (!data.assignedTo) {
          data.assignedTo = getDriverPersonId();
        }

        // ── TASK-4773 — "Log work I did" ────────────────────────────────────
        // Chris: "weird we say 'report issue' when someone is just greasing a
        // truck." Before this, EVERY driver create was status:'open', so routine
        // finished work minted a phantom work order for the office to close.
        //
        // Shape is deliberately the SAME one driverConfirmCompletion already
        // writes (status:'pending_review' + driverCompletedAt/By/ByPersonId +
        // completionNotes) — NOT a parallel completedBy. The two paths now agree
        // on identical outcome fields, and the office reviews both in the one
        // Pending Review tab that already exists.
        //
        // NEVER 'completed' from a driver session: firestore.rules:4029-4031 has
        // no field-level validation on `maintenance`, so a direct-to-completed
        // default would make forged sign-off the path of least resistance
        // (TASK-4778). 'pending_review' keeps a human in the loop.
        //
        // Identity comes from the PIN session, never a text input — mirroring
        // createdBy/createdByPersonId above. confirmCompletion() takes a typed
        // name today; that is pre-existing and deliberately NOT copied here.
        //
        // Priority is forced normal because a completed log must not become a
        // safety hold: dispatch.js:3736 and pretrip.js:2172/2209/3634 all gate on
        // `priority === 'critical' && status !== 'completed'`, and
        // compliance.js:1137 on `priority` in (safety|high). The field is hidden
        // in 'done' mode; this is the belt to that suspenders.
        if (MaintenanceState.driverTaskMode === 'done') {
          data.status = 'pending_review';
          data.priority = 'normal';
          data.driverCompletedAt = Timestamp.now();
          data.driverCompletedBy = getDriverPersonName();
          data.driverCompletedByPersonId = getDriverPersonId();
          data.completionNotes = data.description;
          // Provenance: this was logged already-done, it was never an open work
          // order. Lets the office tell routine self-logged work from a work
          // order a driver finished. Additive — no consumer branches on it.
          data.driverLoggedComplete = true;
        }
      }
      await addDoc(collection(db, 'tenants', tenant, 'maintenance'), data);

      // Log the meter reading so the PM/odometer state stays current.
      // - Trucks/trailers: write a metersLog entry. PM Service → type:'pm' so
      //   getLatestMeters() resets the "since last PM" counter (mirrors admin
      //   savePMService); every other category → type:'reading' (unchanged).
      //   Driver PINs ARE allowed to write metersLog (firestore.rules:555-558),
      //   so this works in both driver and admin mode.
      // - Assets: PM status comes from the asset doc's hoursTracking.{currentHours,
      //   lastPmHours} (calculateAssetPMStatus, saveAssetHours) — NOT metersLog —
      //   so an asset PM updates currentHours + lastPmHours on the asset doc to
      //   reset its counter (mirror saveAssetHours' markPM path). Only the office/
      //   admin path reaches here for assets: driver-PIN asset PMs were already
      //   blocked at the guard above (rules forbid driver asset writes), so there
      //   is no silent-failure swallow — a real error surfaces a toast.
      if (assetType === 'asset') {
        if (isPMService && assetTracksHours && taskHours !== null) {
          await updateDoc(doc(db, 'tenants', tenant, 'assets', assetId), {
            'hoursTracking.currentHours': taskHours,
            'hoursTracking.lastPmHours': taskHours,
            updatedAt: Timestamp.now()
          });
          // Optimistic update so the asset grid reflects the reset before reload.
          const a = MaintenanceState.assets.find(x => x.id === assetId);
          if (a) {
            a.hoursTracking = a.hoursTracking || {};
            a.hoursTracking.currentHours = taskHours;
            a.hoursTracking.lastPmHours = taskHours;
          }
        }
      } else if ((taskMiles !== null || taskHours !== null) &&
                 (assetType === 'truck' || assetType === 'trailer')) {
        const metersEntry = {
          truckId: assetId,
          truckUnit: assetUnit,
          type: isPMService ? 'pm' : 'reading',
          miles: taskMiles,
          hours: taskHours,
          timestamp: Timestamp.now(),
          source: isPMService
            ? (isDriverPinMode() ? 'driver_pm' : 'pm_service')
            : 'maintenance_task'
        };
        // PM entries carry a pmItems array (admin history/reporting expects it).
        // Driver PMs skip the itemized checklist — default a single proof-of-work item.
        if (isPMService) {
          metersEntry.pmItems = ['Driver Reported'];
        }
        if (taskMeterConfirmed) metersEntry.meterConfirmed = true;
        await addMeterRow(tenant, metersEntry);
        // Optimistic update
        MaintenanceState.metersLog.unshift({
          ...metersEntry,
          timestamp: { toDate: () => new Date() }
        });
      }

      // TASK-4773 — say which of the two things just happened. A driver who logs
      // finished work and gets "Task created" has no way to tell it did not open
      // a work order on them, which is the whole point of the second path.
      window.showToast?.(
        (isDriverPinMode() && MaintenanceState.driverTaskMode === 'done')
          ? 'Logged — sent to the office'
          : 'Task created',
        'success');
    }

    closeModal('task');
    // onSnapshot fires before closeModal, but renderTasksList() short-circuits
    // while a maintenance modal is open (line 4050). Re-render now that it's
    // closed so the newly-created/updated task appears without a page refresh.
    //
    // TASK-4779 — but pick the renderer that matches the DOM we are standing in.
    // Unbranched, a driver's successful save painted the ADMIN cards into the
    // driver's #maintenance-list: every tenant's tasks instead of just theirs, a
    // "Delete Work Order" affordance, and an onclick to showTaskDetail — which
    // ends at `getElementById('modal-task-detail').innerHTML` for a modal the
    // driver template never renders. Probed 2026-09-08 on a real PIN session:
    // tapping your own just-created card threw "Cannot set properties of null
    // (setting 'innerHTML')" and did nothing. Same reduced-copy trap as the
    // vendor field, one line further down the very flow this task repairs.
    if (isDriverPinMode()) {
      // TASK-4773 — a 'done' create lands in pending_review, but the driver's
      // default tab is Open. Without this they submit, the modal closes, and the
      // list is empty: the exact "nothing happened" the 4779 fix just ended, in a
      // new costume. Mirrors driverConfirmCompletion, which already does this.
      if (!id && MaintenanceState.driverTaskMode === 'done') {
        filterTasks('pending_review');
        updateDriverCounts();
      } else {
        renderDriverTasksList(MaintenanceState.currentFilter);
        updateDriverCounts();
      }
    } else {
      renderTasksList(MaintenanceState.currentFilter);
      updateCounts();
      renderFleetGrid();
    }
  } catch (error) {
    console.error('[Maintenance] Error saving task:', error);
    window.showToast?.('Error saving task', 'error');
  }
}

// ============================================
// TASK-9766 (c) — every work-order status change writes an auditLog row
// ============================================
// Start, complete, submit-for-review, reopen and the DVIR-delete cascade all
// change a task's status; before this only DVIR sign-offs were logged, so a
// safety defect could be closed with nothing in the trail (seen on a live
// tenant 10/9). Written through window.logAudit (the one client audit writer;
// the rules pin `user` to the caller), AFTER the status write succeeded, so a
// failed write never leaves a row claiming a change that did not happen.
// NOTE: never put `category` / `user` / `action` keys in the payload —
// logAudit spreads the payload over its own fields. The task category goes in
// `taskCategory`. The signature image is never copied, only whether one exists.
function taskStatusAuditPayload(task, fromStatus, toStatus, change, extra = {}) {
  const t = task || {};
  const clip = (v, n) => (typeof v === 'string' ? v.slice(0, n) : (v ?? null));
  return {
    entityType: 'maintenanceTask',
    entityId: t.id || null,
    taskId: t.id || null,
    change: change || 'status',
    fromStatus: fromStatus || null,
    toStatus: toStatus || null,
    priority: t.priority || null,
    taskCategory: clip(t.category, 80),
    description: clip(t.description, 200),
    source: t.source || null,
    sourceId: t.sourceId || null,
    assetType: t.assetType || null,
    truckId: t.truckId || null,
    truckUnit: t.truckUnit || null,
    trailerId: t.trailerId || null,
    trailerUnit: t.trailerUnit || null,
    assetId: t.assetId || null,
    assetName: t.assetName || null,
    signOffName: clip(extra.signOffName, 120) || null,
    safetySignOff: extra.safetySignOff === true,
    mechanicSigned: extra.mechanicSigned === true
  };
}

// Returns true when the row landed. A failure does not undo the status change
// (the task doc still carries completedBy / completedAt / the signature) but it
// is never silent: the person who made the change is told, and the console
// names the task.
async function auditTaskStatus(task, fromStatus, toStatus, change, extra = {}) {
  if (!task?.id || fromStatus === toStatus) return true;
  let ok = false;
  try {
    ok = (await window.logAudit?.('maintenance', 'MAINT_TASK_STATUS',
      taskStatusAuditPayload(task, fromStatus, toStatus, change, extra))) === true;
  } catch (_) { ok = false; }
  if (!ok) {
    console.error(`[Maintenance] audit row NOT written for task ${task.id} ${fromStatus} -> ${toStatus}`);
    window.showToast?.('Saved, but the change was not recorded in the audit log. Tell your admin.', 'warning');
  }
  return ok;
}

// ============================================
// TASK-10146 — a work order moved to another vehicle writes an auditLog row
// ============================================
// Which vehicle a task is on = assetType + the matching id. Returns null when
// nothing changed (or the old task is unknown), else { from, to }. Pure.
function taskVehicleRef(t) {
  const x = t || {};
  const type = x.assetType || (x.truckId ? 'truck' : x.trailerId ? 'trailer' : x.assetId ? 'asset' : null);
  const id = type === 'truck' ? x.truckId : type === 'trailer' ? x.trailerId : type === 'asset' ? x.assetId : null;
  const unit = type === 'truck' ? x.truckUnit : type === 'trailer' ? x.trailerUnit : type === 'asset' ? x.assetName : null;
  return { type: type || null, id: id || null, unit: unit || null };
}
function taskVehicleChange(before, after) {
  if (!before || !after) return null;
  const from = taskVehicleRef(before);
  const to = taskVehicleRef(after);
  if (from.type === to.type && from.id === to.id) return null;
  return { from, to };
}
// Same contract as auditTaskStatus: written after the save, never silent on failure.
async function auditTaskVehicleChange(task, change) {
  let ok = false;
  try {
    ok = (await window.logAudit?.('maintenance', 'MAINT_TASK_VEHICLE_CHANGED', {
      entityType: 'maintenanceTask',
      entityId: task.id || null,
      taskId: task.id || null,
      source: task.source || null,
      sourceId: task.sourceId || null,
      fromAssetType: change.from.type, fromVehicleId: change.from.id, fromVehicleUnit: change.from.unit,
      toAssetType: change.to.type, toVehicleId: change.to.id, toVehicleUnit: change.to.unit
    })) === true;
  } catch (_) { ok = false; }
  if (!ok) {
    console.error(`[Maintenance] audit row NOT written for task ${task.id} vehicle change`);
    window.showToast?.('Saved, but the vehicle change was not recorded in the audit log. Tell your admin.', 'warning');
  }
  return ok;
}

// ============================================
// TASK-9766 (d) — possible repeat defect
// ============================================
// A DVIR task on a unit that already has an OPEN defect from an EARLIER DVIR is
// flagged for the mechanic (Hooty #11: "Odometer inop" 9/24 and "Miles counter
// does not work" 9/25 — one problem, two work orders). Read-time only: no
// driver-side read or write, never blocks a DVIR, nothing is merged, and the
// flag goes away when the earlier defect closes.
// "Open defect" is NOT redefined here: an earlier DVIR counts when
// window.dvirDefectClosure (pretrip.js, TASK-9405/9706 — the one predicate the
// Defects tab, Compliance and the DVIR history read) says it is open, fed the
// same linked-task counts renderDVIRList uses. An earlier open pretrip task
// whose DVIR doc is not loaded counts too (an open repair = an open defect).
// It is a hint, so while tasks are loading or failed it shows nothing.
// COD: only earlier DVIRs within REPEAT_WINDOW_DAYS of this one count. Old
// never-signed defect DVIRs read open forever under the predicate, and a chip
// that fires on every DVIR for a unit gets ignored. The Defects tab still
// lists every open defect; this window only limits the hint.
const REPEAT_WINDOW_DAYS = 90;
function _repeatTime(x) {
  const v = x?.createdAt;
  const d = v?.toDate?.() || (typeof v?.seconds === 'number' ? new Date(v.seconds * 1000) : (v ? new Date(v) : null));
  return d && !isNaN(d.getTime()) ? d.getTime() : null;
}
function _taskAssetKey(t) {
  return t?.truckId || t?.trailerId || t?.assetId || null;
}
function _dvirShortSummary(p) {
  const notes = typeof p?.defectNotes === 'string' ? p.defectNotes.trim() : '';
  if (notes) return notes;
  const items = Array.isArray(p?.defects) ? p.defects : [];
  return items.map(x => (typeof x === 'string' ? x : (x?.label || x?.name || x?.description || ''))).filter(Boolean).join(', ');
}
let _repeatIndexMemo = { tasks: null, pretrips: null, idx: null };
function buildRepeatIndex(tasks, pretrips) {
  const openByDvir = new Map();
  (tasks || []).forEach(t => {
    if (t?.source !== 'pretrip' || !t.sourceId || t.status === 'completed') return;
    if (!openByDvir.has(t.sourceId)) openByDvir.set(t.sourceId, []);
    openByDvir.get(t.sourceId).push(t);
  });
  const dvirsByAsset = new Map();
  const dvirIds = new Set();
  (pretrips || []).forEach(p => {
    if (!p?.id) return;
    dvirIds.add(p.id);
    if (!p.truckId) return;
    if (!dvirsByAsset.has(p.truckId)) dvirsByAsset.set(p.truckId, []);
    dvirsByAsset.get(p.truckId).push(p);
  });
  return { openByDvir, dvirsByAsset, dvirIds, pretrips: pretrips || [] };
}
function possibleRepeatDefects(task, idx, closureFn) {
  if (!task || task.source !== 'pretrip' || !task.sourceId || task.status === 'completed') return [];
  if (!idx || typeof closureFn !== 'function') return [];
  const key = _taskAssetKey(task);
  if (!key) return [];
  const own = idx.pretrips.find(p => p.id === task.sourceId);
  const cutoff = _repeatTime(own) ?? _repeatTime(task);
  if (cutoff == null) return [];
  const floor = cutoff - REPEAT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const out = [];
  (idx.dvirsByAsset.get(key) || []).forEach(p => {
    if (p.id === task.sourceId) return;
    const at = _repeatTime(p);
    if (at == null || at >= cutoff || at < floor) return;
    const openTasks = idx.openByDvir.get(p.id) || [];
    const c = closureFn(p, { state: 'ok', open: openTasks.length });
    if (!c || c.open !== true) return;
    out.push({
      dvirId: p.id,
      at,
      stage: c.stage || null,
      openTaskIds: openTasks.map(t => t.id),
      label: openTasks[0]?.description || _dvirShortSummary(p) || 'Defect'
    });
  });
  idx.openByDvir.forEach((list, dvirId) => {
    if (dvirId === task.sourceId || idx.dvirIds.has(dvirId)) return; // a loaded DVIR was judged above
    const earlier = list.filter(t => t.id !== task.id && _taskAssetKey(t) === key
      && _repeatTime(t) != null && _repeatTime(t) < cutoff && _repeatTime(t) >= floor);
    if (!earlier.length) return;
    out.push({
      dvirId,
      at: Math.min(...earlier.map(_repeatTime)),
      stage: 'repair-open',
      openTaskIds: earlier.map(t => t.id),
      label: earlier[0].description || 'Defect'
    });
  });
  return out.sort((a, b) => a.at - b.at);
}
function _repeatsForTask(task) {
  if (MaintenanceState._tasksError || !MaintenanceState._tasksLoaded) return [];
  const tasks = MaintenanceState.tasks;
  const pretrips = MaintenanceState.pretrips;
  if (_repeatIndexMemo.tasks !== tasks || _repeatIndexMemo.pretrips !== pretrips) {
    _repeatIndexMemo = { tasks, pretrips, idx: buildRepeatIndex(tasks, pretrips) };
  }
  return possibleRepeatDefects(task, _repeatIndexMemo.idx, window.dvirDefectClosure);
}
function _repeatWarningHtml(task, repeats) {
  if (!repeats?.length) return '';
  const unit = getTaskAssetLabel(task);
  const rows = repeats.slice(0, 3).map(r => {
    const when = formatDate(new Date(r.at));
    const link = r.openTaskIds.length
      ? `<button type="button" class="btn btn-sm btn-secondary" style="min-height:44px;" onclick="window.MaintenanceModule.showTaskDetail('${escJsAttr(r.openTaskIds[0])}')">Open task</button>`
      : `<button type="button" class="btn btn-sm btn-secondary" style="min-height:44px;" onclick="window.MaintenanceModule.viewSourceDvir('${escJsAttr(r.dvirId)}')">View DVIR</button>`;
    return `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
        <span style="flex:1 1 180px;min-width:0;overflow-wrap:anywhere;">${escapeHtml(when)} · ${escapeHtml(r.label)}</span>${link}
      </div>`;
  }).join('');
  const more = repeats.length > 3 ? `<div style="margin-top:4px;font-size:0.8rem;">+${repeats.length - 3} more</div>` : '';
  return `
      <div class="task-repeat-warning" data-repeat-count="${repeats.length}" style="margin:var(--spacing-sm) 0;padding:10px 12px;border-radius:8px;background:rgba(245,158,11,0.12);border:1px solid rgba(245,158,11,0.45);color:var(--text-primary);font-size:0.875rem;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:600;"><span class="material-icons" style="font-size:18px;color:#b45309;">content_copy</span> Possible repeat</div>
        <div style="color:var(--text-secondary);margin-top:2px;">${escapeHtml(unit)} already has an open defect from an earlier DVIR. Check it before starting a second repair.</div>
        ${rows}${more}
      </div>`;
}

async function updateStatus(id, newStatus) {
  // If completing, show the completion modal for sign-off
  if (newStatus === 'completed') {
    if (isDriverPinMode()) {
      showDriverCompletionModal(id);
    } else {
      showCompletionModal(id);
    }
    return;
  }

  // Pre-switch the filter so onSnapshot re-render shows the right tab
  if (newStatus === 'in_progress') {
    filterTasks('in_progress');
  }

  // TASK-9766 (c) — the before-state, read before the write (the local write
  // updates MaintenanceState.tasks before updateDoc resolves).
  const _auditBefore = { ...(MaintenanceState.tasks.find(t => t.id === id) || { id }) };

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    const updateData = {
      status: newStatus,
      updatedAt: Timestamp.now()
    };

    // Track when work started
    if (newStatus === 'in_progress') {
      updateData.startedAt = Timestamp.now();
    }

    await updateDoc(doc(db, 'tenants', tenant, 'maintenance', id), updateData);
    window.showToast?.('Task started', 'success');
    await auditTaskStatus(_auditBefore, _auditBefore.status || null, newStatus,
      newStatus === 'in_progress' ? 'start' : 'status');
    return true;
  } catch (error) {
    console.error('[Maintenance] Error updating status:', error);
    window.showToast?.('Error updating task — check permissions', 'error');
    // Revert filter back to open since the update failed
    filterTasks('open');
    return false;
  }
}

// TASK-9766 (a) — Start Work from the task-detail modal keeps the modal open and
// re-renders it in the in-progress state, so the next step (Finalize & Add
// Costs, Edit for notes) is right there instead of a toast and an empty screen.
async function startWorkFromDetail(id) {
  const before = MaintenanceState.tasks.find(x => x.id === id)?.status;
  const ok = await updateStatus(id, 'in_progress');
  if (!ok) return;
  const modal = document.getElementById('modal-task-detail');
  const body = document.getElementById('task-detail-body');
  if (!modal || modal.classList.contains('hidden') || body?.dataset.taskId !== id) return;
  // The snapshot normally lands before updateDoc resolves (local write); if it
  // has not, mirror the write locally so the re-render shows the new state.
  // Only over the PRE-write copy (COD): a status that moved on meanwhile (a
  // completion from another screen) is newer than ours and is shown as is.
  // The next snapshot replaces this copy.
  const t = MaintenanceState.tasks.find(x => x.id === id);
  if (t && t.status === before && before !== 'in_progress') {
    t.status = 'in_progress';
    if (!t.startedAt) t.startedAt = Timestamp.now();
  }
  showTaskDetail(id);
}

async function deleteTask(id) {
  const task = MaintenanceState.tasks.find(t => t.id === id);

  // TASK-773 — DVIR-sourced SAFETY defects are REOPEN-ONLY, never hard-deleted.
  // A DVIR-originated critical work order (source==='pretrip' && priority==='critical'
  // — the signature-required safety class, matching the completion flow at :4663/4793)
  // must be SIGNED OFF as repaired to satisfy DOT/FMCSA recordkeeping; deleting it
  // silently orphaned the safety defect (it vanished from the Open queue with no
  // reopen, no re-flag, no trace). So the trash on those becomes a "Reopen to Open"
  // action via the existing status update — no deleteDoc, and NO pretrips write-back
  // (a client-side pretrips stamp was an exploitable safety-record-suppression hole:
  // any tenant member could fabricate a fake task pointing at someone else's real
  // pretrip and mark it "handled"). Non-critical pretrip notes (pretrip.js:3180 — NOT
  // safety defects) and all other tasks keep the normal plain delete below.
  const isDvirSafetyDefect = task?.source === 'pretrip' && task?.priority === 'critical';

  if (isDvirSafetyDefect) {
    // Already open? Nothing to reopen — explain why it can't be deleted.
    if (task.status === 'open') {
      window.showToast?.('DVIR safety defects can’t be deleted — they must be repaired and signed off to satisfy DOT. This one is already in the Open queue.', 'info');
      return;
    }
    const assetUnit = task.truckUnit || task.trailerUnit || '';
    const defectLabel = task.description ? `“${task.description}”` : 'this defect';
    const ok = await window.showConfirm?.(
      'Reopen DVIR safety defect?',
      `${defectLabel}${assetUnit ? ` (Unit ${assetUnit})` : ''} came from a DVIR inspection and must be repaired and signed off to satisfy DOT — it can’t be deleted. Reopening moves it back to the Open queue so the defect stays tracked until it’s completed.`,
      { confirmText: 'Reopen to Open', cancelText: 'Cancel', danger: false }
    );
    if (!ok) return;
    // Reuse the existing status path. Pre-switch the filter so the snapshot
    // re-render lands on the Open tab where the reopened defect now lives.
    filterTasks('open');
    const _auditBefore = { ...task };
    try {
      const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
      await updateDoc(doc(db, 'tenants', tenant, 'maintenance', id), {
        status: 'open',
        updatedAt: Timestamp.now()
      });
      window.showToast?.('Safety defect reopened', 'success');
      await auditTaskStatus(_auditBefore, _auditBefore.status || null, 'open', 'reopen'); // TASK-9766 (c)
    } catch (error) {
      console.error('[Maintenance] Error reopening DVIR safety defect:', error);
      window.showToast?.('Error reopening defect — check permissions', 'error');
    }
    return;
  }

  if (!confirm('Delete this work order? This cannot be undone.')) return;
  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    await deleteDoc(doc(db, 'tenants', tenant, 'maintenance', id));
    window.showToast?.('Work order deleted', 'success');
  } catch (error) {
    console.error('[Maintenance] Error deleting task:', error);
    window.showToast?.('Error deleting work order', 'error');
  }
}

function showCompletionModal(taskId) {
  document.getElementById('complete-task-id').value = taskId;
  document.getElementById('completion-notes').value = '';

  // Check if this is a DVIR safety issue (only DVIR-originated critical tasks need signature)
  const task = MaintenanceState.tasks.find(t => t.id === taskId);
  const isSafetyIssue = task?.priority === 'critical' && task?.source === 'pretrip';

  // Populate the completed-by dropdown
  const select = document.getElementById('completed-by-name');
  let eligiblePeople;

  // Any active person can sign off — DOT requires the signature, not mechanic certification
  eligiblePeople = MaintenanceState.people.filter(p => p.active !== false);

  select.innerHTML = '<option value="">Select person...</option>' +
    eligiblePeople.map(p => {
      const roleLabel = window.getTypeLabel?.(p.crewType || p.role, 'crewTypes') || p.crewType || p.role || ''; // TASK-9766 — args were reversed (slug, kind): every person read "(CrewTypes)"
      return `<option value="${escapeHtml(p.name)}" data-person-id="${escapeHtml(p.id)}">${escapeHtml(p.name)}${roleLabel ? ` (${escapeHtml(roleLabel)})` : ''}</option>`;
    }).join('');

  // Update modal header/hint for safety issues
  const hintEl = document.querySelector('#modal-complete-task .form-hint');
  if (hintEl) {
    if (isSafetyIssue) {
      hintEl.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;color:var(--warning);">draw</span> <strong>Safety Issue:</strong> Signature required for safety sign-off.';
      hintEl.style.color = 'var(--warning)';
    } else {
      hintEl.textContent = 'Please sign off on the completed work.';
      hintEl.style.color = '';
    }
  }

  // Show/hide mechanic signature section for safety issues
  const sigSection = document.getElementById('mechanic-sig-section');
  if (sigSection) {
    if (isSafetyIssue) {
      sigSection.classList.remove('hidden');
      setTimeout(() => initMechanicSigCanvas(), 50);
    } else {
      sigSection.classList.add('hidden');
    }
  }

  document.getElementById('modal-complete-task').classList.remove('hidden');
}

function initMechanicSigCanvas() {
  const origCanvas = document.getElementById('mechanic-sig-canvas');
  if (!origCanvas) return;

  // Clone to remove any old event listeners
  origCanvas.replaceWith(origCanvas.cloneNode(true));
  const canvas = document.getElementById('mechanic-sig-canvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');

  // Set canvas resolution for crisp drawing
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * 2;
  canvas.height = rect.height * 2;
  ctx.scale(2, 2);
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#1e293b';

  // Clear with white
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, rect.width, rect.height);

  MaintenanceState.mechSigCanvas = canvas;
  MaintenanceState.mechSigCtx = ctx;
  MaintenanceState.mechSigDrawing = false;
  MaintenanceState.mechSigHasDrawn = false;

  function getPos(e) {
    const r = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  }

  function start(e) {
    e.preventDefault();
    MaintenanceState.mechSigDrawing = true;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  function move(e) {
    if (!MaintenanceState.mechSigDrawing) return;
    e.preventDefault();
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    MaintenanceState.mechSigHasDrawn = true;
  }

  function end() { MaintenanceState.mechSigDrawing = false; }

  canvas.addEventListener('mousedown', start);
  canvas.addEventListener('mousemove', move);
  canvas.addEventListener('mouseup', end);
  canvas.addEventListener('mouseleave', end);
  canvas.addEventListener('touchstart', start, { passive: false });
  canvas.addEventListener('touchmove', move, { passive: false });
  canvas.addEventListener('touchend', end);
}

function clearMechanicSig() {
  const canvas = MaintenanceState.mechSigCanvas;
  const ctx = MaintenanceState.mechSigCtx;
  if (!canvas || !ctx) return;
  const rect = canvas.getBoundingClientRect();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, rect.width, rect.height);
  MaintenanceState.mechSigHasDrawn = false;
}

async function confirmCompletion(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const taskId = document.getElementById('complete-task-id').value;
  const completedBy = document.getElementById('completed-by-name').value.trim();
  const completionNotes = document.getElementById('completion-notes').value.trim();

  if (!completedBy) {
    window.showToast?.('Please select who completed the work', 'error');
    return;
  }

  // Check if this is a DVIR safety issue (only DVIR-originated critical tasks need signature)
  const task = MaintenanceState.tasks.find(t => t.id === taskId);
  const isSafetyIssue = task?.priority === 'critical' && task?.source === 'pretrip';

  if (isSafetyIssue) {
    // Require signature for safety sign-off (all accounts)
    if (!MaintenanceState.mechSigHasDrawn) {
      window.showToast?.('Signature is required for safety sign-off', 'error');
      return;
    }
  }

  try {
    const tenant = MaintenanceState.tenantId === 'demo-tenant' ? 'demo' : MaintenanceState.tenantId;
    const updateData = {
      status: 'completed',
      completedAt: Timestamp.now(),
      completedBy: completedBy,
      updatedAt: Timestamp.now()
    };

    if (completionNotes) {
      updateData.completionNotes = completionNotes;
    }

    // Store whether this was a safety issue sign-off for audit trail
    if (isSafetyIssue) {
      updateData.safetySignOff = true;

      // Capture mechanic signature as base64
      const sigCanvas = MaintenanceState.mechSigCanvas;
      if (sigCanvas) {
        updateData.mechanicSignature = sigCanvas.toDataURL('image/png');
        updateData.mechanicSignedBy = completedBy;
        updateData.mechanicSignedAt = Timestamp.now();
      }
    }

    const _auditBefore = { ...(task || { id: taskId }) };
    await updateDoc(doc(db, 'tenants', tenant, 'maintenance', taskId), updateData);

    // If linked to a DVIR, update the pretrip doc with mechanic signature.
    // TASK-9691 — this used to swallow every failure into a console.warn, so a
    // mechanic was told "Work order completed" while the DVIR never got its
    // repair certification. Two cases are now told apart:
    //   • the DVIR is ALREADY mechanic-signed (a second critical task on a
    //     multi-defect DVIR, or the DVIR was signed first) — the rules freeze
    //     the signature, so the write would only ever be denied. Not a failure:
    //     the certification is on file; skip the write.
    //   • the write genuinely failed — say so, by name, in the same toast.
    let dvirSigWarning = '';
    if (isSafetyIssue && task?.sourceId && updateData.mechanicSignature) {
      try {
        const pretripRef = doc(db, 'tenants', tenant, 'pretrips', task.sourceId);
        const pretripSnap = await getDoc(pretripRef);
        const pre = pretripSnap.exists() ? (pretripSnap.data() || {}) : null;
        // integ/2095 COD: only `mechanicSignature` certifies (closure + screens read
        // it). A DVIR with just mechanicSignedBy/At is NOT certified and the rules
        // freeze those keys, so the write cannot land — say so instead of skipping
        // quietly. A missing linked DVIR is reported the same way.
        if (!pre) {
          dvirSigWarning = ' — but the linked DVIR was not found, so it was not signed off.';
        } else if (pre.mechanicSignature) {
          // already certified (a sibling repair signed it) — nothing to write
        } else if (pre.mechanicSignedBy || pre.mechanicSignedAt) {
          dvirSigWarning = ' — but the DVIR could not be signed off (it carries a sign-off name with no signature). Open the DVIR (View DVIR) and check it.';
        } else {
          await setDoc(pretripRef, {
            mechanicSignature: updateData.mechanicSignature,
            mechanicSignedBy: completedBy,
            mechanicSignedAt: Timestamp.now(),
            pdfUrl: null // Clear so next download regenerates with signature
          }, { merge: true });
          console.log('[Maintenance] Updated linked DVIR with mechanic signature');
        }
      } catch (pretripErr) {
        console.error('[Maintenance] Could not update linked DVIR:', pretripErr);
        dvirSigWarning = ' — but the DVIR sign-off was NOT saved. Open the DVIR (View DVIR) and sign it off.';
      }
    }

    // TASK-9766 (c) — who (logAudit stamps the signed-in user), which task and
    // unit, old -> new status, and the name signed in the modal. After the DVIR
    // write-back (COD) so a slow audit write never delays the DVIR sign-off.
    await auditTaskStatus(_auditBefore, _auditBefore.status || null, 'completed', 'complete', {
      signOffName: completedBy,
      safetySignOff: isSafetyIssue,
      mechanicSigned: !!updateData.mechanicSignature
    });

    closeModal('complete-task');
    if (dvirSigWarning) window.showToast?.(`Work order completed${dvirSigWarning}`, 'warning');
    else window.showToast?.('Work order completed', 'success');

    // Auto-open costs modal so user can add labor, parts, receipts
    setTimeout(() => showAddCosts(taskId), 400);
  } catch (error) {
    console.error('[Maintenance] Error completing task:', error);
    window.showToast?.('Error completing task', 'error');
  }
}

// TASK-9691 — "Open work order" from a DVIR (pretrip.js window.DvirRepair.openWorkOrder).
// Module-level, not MaintenanceState, so it survives initMaintenance re-running
// when the router lands on the view. Opened once the task subscription has it.
let _pendingOpenTaskId = null;
function openTaskById(id) {
  if (!id) return;
  const onView = window.AppState?.currentView === 'maintenance'
    && !!document.getElementById('modal-task-detail')
    && MaintenanceState._tasksLoaded;
  if (onView && MaintenanceState.tasks.some(t => t.id === id)) {
    showTaskDetail(id);
    return;
  }
  _pendingOpenTaskId = id;
  if (window.AppState?.currentView !== 'maintenance' && typeof window.navigateToView === 'function') {
    window.navigateToView('maintenance');
  } else {
    _maybeOpenPendingTask();
  }
}
function _maybeOpenPendingTask() {
  const id = _pendingOpenTaskId;
  if (!id || !MaintenanceState._tasksLoaded || !document.getElementById('modal-task-detail')) return;
  _pendingOpenTaskId = null;
  if (MaintenanceState.tasks.some(t => t.id === id)) showTaskDetail(id);
  else window.showToast?.('That work order could not be found.', 'error');
}

function showTaskDetail(id) {
  const task = MaintenanceState.tasks.find(t => t.id === id);
  if (!task) return;

  const date = task.createdAt?.toDate?.() || new Date();
  const startedDate = task.startedAt?.toDate?.();
  const completedDate = task.completedAt?.toDate?.();

  // Build workflow history timeline
  let workflowHtml = `
    <div class="workflow-history">
      <h4>Workflow History</h4>
      <div class="workflow-timeline">
        <div class="workflow-item">
          <span class="material-icons workflow-icon">add_circle</span>
          <div class="workflow-content">
            <span class="workflow-action">Created</span>
            <span class="workflow-date">${formatDate(date)}</span>
            ${task.createdBy ? `<span class="workflow-by">by ${escapeHtml(task.createdBy)}</span>` : ''}
            ${task.source === 'pretrip' ? '<span class="workflow-source">(from DVIR)</span>' : ''}
          </div>
        </div>
  `;

  if (startedDate || task.status === 'in_progress' || task.status === 'completed') {
    workflowHtml += `
        <div class="workflow-item">
          <span class="material-icons workflow-icon">build</span>
          <div class="workflow-content">
            <span class="workflow-action">Started</span>
            ${startedDate ? `<span class="workflow-date">${formatDate(startedDate)}</span>` : '<span class="workflow-date">--</span>'}
          </div>
        </div>
    `;
  }

  if (completedDate) {
    workflowHtml += `
        <div class="workflow-item completed">
          <span class="material-icons workflow-icon">check_circle</span>
          <div class="workflow-content">
            <span class="workflow-action">Completed</span>
            <span class="workflow-date">${formatDate(completedDate)}</span>
            ${task.completedBy ? `<span class="workflow-by">by ${escapeHtml(task.completedBy)}</span>` : ''}
          </div>
        </div>
    `;
  }

  workflowHtml += `
      </div>
    </div>
  `;

  const body = document.getElementById('task-detail-body');
  body.dataset.taskId = task.id; // TASK-9766 (a) — which task the open modal shows
  body.innerHTML = `
    <div class="task-detail">
      <div class="task-detail-header">
        <span class="task-detail-truck"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">${task.assetId ? 'handyman' : task.trailerId ? 'rv_hookup' : 'local_shipping'}</span>${escapeHtml(getTaskAssetLabel(task))}</span>
        <span class="badge badge-${escapeHtml(task.priority || 'normal')}">${escapeHtml(capitalizeFirst(task.priority || 'normal'))}</span>
        ${task.source === 'pretrip' && task.sourceId ? `<button class="source-badge-btn" onclick="event.stopPropagation();window.MaintenanceModule.viewSourceDvir('${escJsAttr(task.sourceId)}')"><span class="material-icons" style="font-size:14px;">assignment</span> View DVIR</button>` : task.source === 'pretrip' ? '<span class="source-badge">From DVIR</span>' : ''}
      </div>

      <div class="task-detail-category">${escapeHtml(task.category || 'General')}</div>

      <div class="task-detail-description">${escapeHtml(task.description || 'No description')}</div>

      ${_repeatWarningHtml(task, _repeatsForTask(task))}

      <div class="task-detail-meta">
        <div class="meta-item">
          <span class="material-icons">info</span>
          <span>Status: ${escapeHtml(capitalizeFirst(task.status?.replace('_', ' ') || 'open'))}</span>
        </div>
        <div class="meta-item">
          <span class="material-icons">person</span>
          <span>Assigned: ${task.assignedTo ? escapeHtml(getPersonName(task.assignedTo)) : 'Unassigned'}</span>
        </div>
        ${task.completedBy ? `
          <div class="meta-item">
            <span class="material-icons">check_circle</span>
            <span>Completed by: ${escapeHtml(task.completedBy)}</span>
          </div>
        ` : ''}
        ${task.vendor ? `
          <div class="meta-item">
            <span class="material-icons">business</span>
            <span>Vendor: ${escapeHtml(task.vendor)}</span>
          </div>
        ` : ''}
      </div>

      ${workflowHtml}

      ${task.notes ? `
        <div class="task-detail-notes">
          <h4>Notes</h4>
          <p>${escapeHtml(task.notes)}</p>
        </div>
      ` : ''}

      ${task.completionNotes ? `
        <div class="task-detail-notes">
          <h4>Completion Notes</h4>
          <p>${escapeHtml(task.completionNotes)}</p>
        </div>
      ` : ''}

      ${task.costs?.totalCost ? `
        <div class="task-cost-summary">
          <h4><span class="material-icons">paid</span> Cost Summary</h4>
          <div class="cost-summary-rows">
            ${task.costs.isThirdPartyVendor ? `
              <div class="cost-summary-row">
                <span>3rd Party Vendor</span>
                <span>${escapeHtml(task.costs.vendorName || task.vendor || '—')}</span>
              </div>
            ` : ''}
            ${task.costs.laborHours ? `
              <div class="cost-summary-row">
                <span>Labor: ${escapeHtml(String(task.costs.laborHours ?? ''))}h @ $${maintMoney(task.costs.laborRate).toFixed(2)}/hr</span>
                <span>$${(maintMoney(task.costs.laborHours) * maintMoney(task.costs.laborRate)).toFixed(2)}</span>
              </div>
            ` : ''}
            ${(task.costs.partsPreTax || task.costs.partsTotal) ? `
              <div class="cost-summary-row">
                <span>Parts${task.costs.partsTaxRate > 0 ? ` + ${escapeHtml(String(task.costs.partsTaxRate ?? ''))}% tax` : ''}</span>
                <span>$${maintMoney(task.costs.partsTotal).toFixed(2)}</span>
              </div>
            ` : ''}
            ${task.costs.shopPreTax || task.costs.shopTotal ? `
              <div class="cost-summary-row">
                <span>Shop${task.costs.shopTaxRate > 0 ? ` + ${escapeHtml(String(task.costs.shopTaxRate ?? ''))}% tax` : task.costs.shopEntry === 'total' ? ` (tax $${(+task.costs.shopTaxAmount || 0).toFixed(2)}, card fee $${(+task.costs.shopFeeAmount || 0).toFixed(2)})` : ''}</span>
                <span>$${maintMoney(task.costs.shopTotal).toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="cost-summary-total">
              <span>Total</span>
              <span>$${maintMoney(task.costs.totalCost).toFixed(2)}</span>
            </div>
          </div>
        </div>
      ` : ''}

      ${task.costs?.receipts?.length ? `
        <div class="task-receipts-section">
          <h4>
            <span class="material-icons">receipt_long</span>
            Receipts (${task.costs.receipts.length})
            <button type="button" class="btn-print-receipts" onclick="window.MaintenanceModule.printReceipts('${escJsAttr(task.id)}')" style="margin-left:auto;">
              <span class="material-icons">print</span> Print All
            </button>
          </h4>
          <div class="task-receipts-grid">
            ${task.costs.receipts.map(r => {
              const isPdf = r.fileName?.toLowerCase().endsWith('.pdf');
              return `
                <a class="task-receipt-thumb" ${window.StorageFile.linkAttrs(r)} style="display:block;color:inherit;text-decoration:none;">
                  ${isPdf
                    ? `<div class="pdf-placeholder"><span class="material-icons">picture_as_pdf</span><span>PDF</span></div>`
                    : `<img ${window.StorageFile.imgAttrs(r)} alt="${escapeHtml(r.fileName || 'Receipt')}" loading="lazy">`
                  }
                </a>
              `;
            }).join('')}
          </div>
        </div>
      ` : ''}

      <div class="task-detail-actions">
        ${task.status === 'open' ? `
          <button class="btn btn-secondary" onclick="window.MaintenanceModule.startWorkFromDetail('${escJsAttr(task.id)}')">
            <span class="material-icons">build</span> Start Work
          </button>
        ` : ''}
        ${task.status === 'in_progress' ? `
          <button class="btn btn-primary" onclick="window.MaintenanceModule.updateStatus('${escJsAttr(task.id)}', 'completed'); window.MaintenanceModule.closeModal('task-detail');">
            <span class="material-icons">paid</span> Finalize & Add Costs
          </button>
        ` : ''}
        ${task.status === 'completed' ? `
          <button class="btn btn-primary" onclick="window.MaintenanceModule.showAddCosts('${escJsAttr(task.id)}'); window.MaintenanceModule.closeModal('task-detail');">
            <span class="material-icons">attach_money</span> ${task.costs?.totalCost ? 'Edit Costs' : 'Add Costs'}
          </button>
        ` : ''}
        <button class="btn btn-secondary" onclick="window.MaintenanceModule.editTask('${escJsAttr(task.id)}'); window.MaintenanceModule.closeModal('task-detail');">
          <span class="material-icons">edit</span> Edit
        </button>
        ${/* TASK-9361 -- every status; the modal stays open because the sheet opens
               in its own tab and the user usually comes straight back. */''}
        <button class="btn btn-secondary" onclick="window.MaintenanceModule.printWorkOrder('${escJsAttr(task.id)}')">
          <span class="material-icons">print</span> Print Work Order
        </button>
        <div class="task-detail-actions-secondary">
          <button class="btn-delete-link" onclick="window.MaintenanceModule.deleteTask('${escJsAttr(task.id)}'); window.MaintenanceModule.closeModal('task-detail');">
            <span class="material-icons">delete</span> Delete Work Order
          </button>
        </div>
      </div>
    </div>
  `;

  document.getElementById('modal-task-detail').classList.remove('hidden');
}

// TASK-10065 (bundle headroom) — the deferred leg of every lazy-Maintenance shim (the
// _loadMtXxx blocks below). A failed chunk load has already told the user (the
// loader's toast) and is retried on the next tap, so it is marked handled here:
// onclick callers ignore the returned promise and must not leave an unhandled
// rejection. Any other error a moved function throws on that first call is logged.
// Awaiting callers still see the promise reject exactly as before. Same pattern as
// hr.js's _hrLazyCall (TASK-9938).
function _maintLazyCall(load, name, args) {
  const p = load().then((m) => m[name](...args));
  p.catch((e) => { if (!(e && e._maintLazyLoadFailed)) console.error('[Maintenance] ' + name + ' failed:', e); });
  return p;
}

// =============================================================================
// TASK-10065 (bundle headroom) — DVIR History (day list, delete, PDF download), DVIR photos, bulk PDF generation
// live in the LAZY views/maintDvirHistory.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps the name window.MaintenanceModule and maintenance.js
// call, loads the module on first use and, once loaded, calls straight through
// synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _mtDvirMod = null;
let _mtDvirLoad = null;
function _mtDvirDeps() {
  return {
    MaintenanceState, Timestamp, _resolveTenantForWrite, auditTaskStatus, auth, collection, db,
    deleteDoc, doc, escJsAttr, escapeHtml, getDocs, query, safeHttpUrl, updateDoc, where
  };
}
function _loadMtDvir() {
  if (_mtDvirMod) return Promise.resolve(_mtDvirMod);
  if (!_mtDvirLoad) {
    _mtDvirLoad = __ddChunkWrap(import('./maintDvirHistory.js?v=1006506')).then((m) => { m._bind(_mtDvirDeps()); _mtDvirMod = m; return m; }).catch((e) => {
      _mtDvirLoad = null; // the next click tries again
      console.error('[Maintenance] maintDvirHistory load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _maintLazyLoadFailed: true });
    });
  }
  return _mtDvirLoad;
}
function showDVIRHistory(...a) { return _mtDvirMod ? _mtDvirMod.showDVIRHistory(...a) : _maintLazyCall(_loadMtDvir, 'showDVIRHistory', a); }
function selectDVIRDay(...a) { return _mtDvirMod ? _mtDvirMod.selectDVIRDay(...a) : _maintLazyCall(_loadMtDvir, 'selectDVIRDay', a); }
function applyDVIRDateRange(...a) { return _mtDvirMod ? _mtDvirMod.applyDVIRDateRange(...a) : _maintLazyCall(_loadMtDvir, 'applyDVIRDateRange', a); }
function deleteDVIR(...a) { return _mtDvirMod ? _mtDvirMod.deleteDVIR(...a) : _maintLazyCall(_loadMtDvir, 'deleteDVIR', a); }
function downloadDVIRPdf(...a) { return _mtDvirMod ? _mtDvirMod.downloadDVIRPdf(...a) : _maintLazyCall(_loadMtDvir, 'downloadDVIRPdf', a); }
function showDVIRPhotos(...a) { return _mtDvirMod ? _mtDvirMod.showDVIRPhotos(...a) : _maintLazyCall(_loadMtDvir, 'showDVIRPhotos', a); }
function openFullPhoto(...a) { return _mtDvirMod ? _mtDvirMod.openFullPhoto(...a) : _maintLazyCall(_loadMtDvir, 'openFullPhoto', a); }
function showBulkPdfModal(...a) { return _mtDvirMod ? _mtDvirMod.showBulkPdfModal(...a) : _maintLazyCall(_loadMtDvir, 'showBulkPdfModal', a); }
function generateBulkPdfs(...a) { return _mtDvirMod ? _mtDvirMod.generateBulkPdfs(...a) : _maintLazyCall(_loadMtDvir, 'generateBulkPdfs', a); }

function filterTasks(filter) {
  MaintenanceState.currentFilter = filter;

  // Update active filter chips (works for both admin .maintenance-filter and driver .filter-chip)
  document.querySelectorAll('.maintenance-filter, .filter-chip').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.filter === filter);
  });

  if (isDriverPinMode()) {
    renderDriverTasksList(filter);
  } else {
    renderTasksList(filter);
  }
}

// TASK-9897 — the fleet-card drill is now the Vehicle filter (one predicate); the
// old "Showing tasks for X" indicator is replaced by the vehicle chip.
function showTruckTasks(truckId) {
  woDrillToVehicle('truck:' + truckId);
}

function clearTruckFilter() {
  MaintenanceState.truckFilter = null;
  const f = { ..._woFilter(), vehicles: [] };
  MaintenanceState.woFilter = f;
  woSaveFilters();
  renderTasksList(MaintenanceState.currentFilter);
}

function closeModal(type) {
  // TASK-4779 — don't leave a stale inline refusal behind for the next open.
  if (type === 'task') { clearTaskFormError(document.getElementById('form-task')); detachKeyboardInset(); }
  document.getElementById(`modal-${type}`).classList.add('hidden');
  // TASK-9898 S3 — a modal opened from the Vehicle panel closed: repaint the
  // panel under it (its totals / hold / PM line may have changed).
  if (type === 'vehicle-panel') _vehiclePanel = null;
  else refreshVehiclePanel();
  // TASK-9766 (a) — the list, counts and fleet grid skip repaints while the
  // detail modal is open (isMaintenanceModalOpen). Now that Start Work keeps it
  // open, repaint on close so the list behind it is not left stale.
  if (type === 'task-detail' && !isDriverPinMode()) {
    renderTasksList(MaintenanceState.currentFilter);
    updateCounts();
    renderFleetGrid();
  }
}

// ── TASK-9122 / TASK-9274 — the software-keyboard half of the unreachable-Submit bug ──
// The scrollable-form trio above fixes the CLIPPING (provable in a headless
// browser). It does NOT fix the keyboard: on iOS the keyboard shrinks only the
// VISUAL viewport, so a footer pinned to the bottom of a full-height fixed
// overlay ends up UNDERNEATH it, and .modal is overflow:hidden, so nothing
// scrolls it back.
//
// TASK-9274 — the first version of this (TASK-9122) padded .modal-content by
// `innerHeight − (vv.height + vv.offsetTop)`. Measured on Chris's iPhone in a
// Safari tab (2026-09-22): innerHeight − vv.height = 21px (WebKit reports
// innerHeight off the VISUAL viewport) and Safari had panned the page so
// offsetTop = 326px — the term went negative and clamped to 0, the padding was
// never applied. And padding could not have rescued it anyway: the sheet was
// sized 100dvh, which by design does NOT track the keyboard.
//
// So this no longer pads anything and NEVER reads innerHeight. It moves the
// OVERLAY itself onto the visual viewport — top = vv.offsetTop, height =
// vv.height — the two numbers visualViewport defines relative to the layout
// viewport that `position:fixed` is laid out against (same move as
// assistantMobileFix.js, minus its innerHeight-based keyboard gate, which the
// 21px reading says would not fire on that phone). `.modal-kb-aware` +
// `.kb-open` in styles.css make the sheet 100% of the overlay instead of 100dvh
// / 90vh, so the pinned footer follows the overlay's bottom edge up to the
// keyboard's top edge.
//
// Gate uses SIZES only, never mixed coordinate systems: act when the visual
// viewport is panned (offsetTop > 0) or shorter than the overlay's natural
// height. Otherwise (desktop, Android/iOS with no keyboard) every inline style
// is removed and the modal is exactly what TASK-9122 shipped. Pinch-zoom
// (scale ≠ 1) also stands down.
//
// Never calls scrollIntoView: that can pan the window, which fires the visual-
// viewport scroll event that calls this again (COD round 1 — a jitter loop).
// The focused field is revealed by moving the form's OWN scroller, which does
// not pan the viewport.
//
// Chromium AND WebKit-in-Playwright have no software keyboard. The spec for this
// (tests/task9274-keyboard-visual-viewport.spec.js) FAKES visualViewport, so it
// proves the arithmetic and the CSS, never iOS's behaviour. The only real check
// is an iPhone in a Safari tab.
let _kbInsetCleanup = null;

const _KB_OVERLAY_PROPS = ['top', 'bottom', 'height', 'max-height'];

function attachKeyboardInset() {
  detachKeyboardInset();
  const vv = window.visualViewport;
  const overlay = document.getElementById('modal-task');
  if (!vv || !overlay || !overlay.classList.contains('modal-kb-aware')) return;
  const scroller = overlay.querySelector('.modal-form-scroll');

  let raf = 0;
  let applied = null;        // { top, height } currently written, or null
  let wantReveal = false;    // set by focus changes; consumed by the next apply
  const timers = new Set();
  const clear = () => {
    if (!applied) return;    // no writes when nothing is applied — no forced layout
    _KB_OVERLAY_PROPS.forEach(p => overlay.style.removeProperty(p));
    overlay.classList.remove('kb-open');
    applied = null;
  };
  const revealFocused = () => {
    const active = document.activeElement;
    if (!scroller || !active || !scroller.contains(active)) return;
    const s = scroller.getBoundingClientRect();
    const f = active.getBoundingClientRect();
    if (f.bottom > s.bottom) scroller.scrollTop += Math.ceil(f.bottom - s.bottom) + 8;
    else if (f.top < s.top) scroller.scrollTop -= Math.ceil(s.top - f.top) + 8;
  };
  // COD round 2: never clear-then-measure. Clearing expands the sheet to full
  // height for a forced layout on every visualViewport scroll, which resets the
  // form scroller's scrollTop and can itself make Safari re-pan (re-entry). The
  // overlay's natural height is the layout viewport it is `inset:0` against —
  // documentElement.clientHeight, which the keyboard does not change — so it is
  // read without touching the overlay. Styles are written only when the target
  // geometry actually changes.
  const apply = () => {
    raf = 0;
    const reveal = wantReveal;
    wantReveal = false;
    if (overlay.classList.contains('hidden')) { clear(); return; }
    if (Math.abs((vv.scale || 1) - 1) > 0.01) { clear(); return; }
    const top = Math.round(vv.offsetTop);
    const height = Math.round(vv.height);
    const layoutH = document.documentElement.clientHeight;
    if (top <= 0 && height >= layoutH - 1) { clear(); return; }
    if (!applied || applied.top !== top || applied.height !== height) {
      // Inline !important: .modal carries `inset:0` / `height:100dvh` !important.
      overlay.style.setProperty('top', `${top}px`, 'important');
      overlay.style.setProperty('bottom', 'auto', 'important');
      overlay.style.setProperty('height', `${height}px`, 'important');
      overlay.style.setProperty('max-height', `${height}px`, 'important');
      overlay.classList.add('kb-open');
      applied = { top, height };
    }
    // Only a focus change moves the form scroller — a plain viewport scroll
    // (the driver dragging the page) never fights the driver's own scrolling.
    if (reveal) revealFocused();
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(apply); };
  // The keyboard animates in/out over ~300ms and iOS can lag its resize events
  // behind focus — re-check a few times around focus changes (the same cadence
  // assistantMobileFix.js settled on).
  const settle = () => {
    wantReveal = true;
    schedule();
    [50, 300, 650].forEach(ms => {
      const t = setTimeout(() => { timers.delete(t); wantReveal = true; schedule(); }, ms);
      timers.add(t);
    });
  };

  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
  overlay.addEventListener('focusin', settle);
  overlay.addEventListener('focusout', settle);
  apply();
  _kbInsetCleanup = () => {
    if (raf) cancelAnimationFrame(raf);
    timers.forEach(t => clearTimeout(t));
    timers.clear();
    vv.removeEventListener('resize', schedule);
    vv.removeEventListener('scroll', schedule);
    overlay.removeEventListener('focusin', settle);
    overlay.removeEventListener('focusout', settle);
    clear();
  };
}

function detachKeyboardInset() {
  if (_kbInsetCleanup) { _kbInsetCleanup(); _kbInsetCleanup = null; }
}

// ============================================
// UTILITIES
// ============================================
// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js). Escapes the
// identical set (&<>"', '->&#39;) for every string/falsy input; the shared helper
// also String()-coerces a non-string truthy input (former `str.replace` threw) —
// strictly safer, never a rendering regression. Hoisted wrapper, lookup at call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

// TASK-4838 — delegating wrapper for the shared JS-STRING-IN-AN-ATTRIBUTE escaper
// (sharedFormat.js). Use it for `onclick="window.Mod.fn('${…}')"`, never escapeHtml:
// the HTML parser decodes `&#39;` back to an apostrophe BEFORE the handler is
// compiled as JavaScript, so escapeHtml/escAttr give that position no protection
// at all. Hoisted wrapper, window lookup deferred to call time (Pitfall #1).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

// TASK-9287 -- NAVIGATION-SINK SCHEME ALLOWLIST. escJsAttr closes a string
// BREAKOUT; it does nothing about a stored url whose SCHEME is executable, and
// window.open('javascript:...') / <a href="javascript:..."> both run it. Same
// allowlist COD's adversary lane put on the shop-cost receipt in TASK-570
// (search "COD adversary, TASK-570") -- reused, not reinvented. Returns '' for
// anything that is not http(s) -- including a leading-space "  javascript:",
// which a browser trims and runs -- so the thumb/link renders inert rather than
// dangerous. NOT for <img src>: an image is not a script sink, and DVIR
// signatures are legitimately data: urls -- gating those would blank a real one.
// TASK-9287 -- 🔴 A DOCUMENT'S `id` IS NOT ITS DOCUMENT ID. Every subscription
// in this file builds rows as `{ id: doc.id, ...doc.data() }` (:487, :2133,
// :2166, and friends) and THE SPREAD COMES SECOND, so a stored `id` FIELD in
// the document body overrides the real Firestore id. Two consequences, both
// counter-intuitive, and both of which killed an earlier draft of this fix:
//   - "it is an addDoc auto-id, nobody can choose it" is FALSE of what the
//     renderer interpolates. Anyone who can write the document can choose it.
//     That argument was used here to defer 31 fleet-asset sites. It was wrong,
//     so they are fixed rather than deferred.
//   - firestore.rules' TASK-4778 `taskId.matches('^[A-Za-z0-9_-]{1,64}$')`
//     constrains the PATH. It does not constrain an `id` field in the body.
//
// MEASURED, not asserted (2026-09-22, this build lane): the override-able
// `{ id: x.id, ...x.data() }` form appears 370 times across public/src +
// functions/src; the safe spread-first form appears 4 times, all in
// functions/src/index.js (~:7052-7121). This is the codebase's default shape,
// not a local slip. A live read across 25 tenants found 11 documents carrying
// an `id` field and every one matched its own doc id — nothing is corrupt
// today. So it is a LATENT hazard, not an active exploit, and the answer is to
// escape at the render boundary regardless of provenance — NOT to go and
// rewrite 370 construction sites. (COD round 3.)
function safeHttpUrl(u) {
  return (typeof u === 'string' && /^https?:\/\//i.test(u)) ? u : '';
}

function formatDate(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatNumber(num) {
  if (num === null || num === undefined) return '--';
  return num.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

// =============================================================================
// TASK-10065 (bundle headroom) — Work-order Add Costs, standalone Shop Cost + Shop Cost History, receipt upload / AI scan
// live in the LAZY views/maintCosts.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps the name window.MaintenanceModule and maintenance.js
// call, loads the module on first use and, once loaded, calls straight through
// synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _mtCostMod = null;
let _mtCostLoad = null;
// The P&L third-party rule for the tie-out; same ?v= as bi.js.
function loadPnlStatement() { return window.PnlStatement || __ddChunkWrap(import('../shared/pnl-statement.js?v=10342131')); }
function _mtCostDeps() {
  return {
    MaintenanceState, Timestamp, addDoc, auth, closeModal, collection, db, deleteDoc, doc,
    escapeHtml, functions, getDocs, httpsCallable, prepareUploadFile, query, ref, runTransaction, setDoc, storage,
    updateDoc, uploadBytes, where, loadPnlStatement
  };
}
function _loadMtCost() {
  if (_mtCostMod) return Promise.resolve(_mtCostMod);
  if (!_mtCostLoad) {
    _mtCostLoad = __ddChunkWrap(import('./maintCosts.js?v=1139855')).then((m) => { m._bind(_mtCostDeps()); _mtCostMod = m; return m; }).catch((e) => {
      _mtCostLoad = null; // the next click tries again
      console.error('[Maintenance] maintCosts load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _maintLazyLoadFailed: true });
    });
  }
  return _mtCostLoad;
}
function showAddCosts(...a) { return _mtCostMod ? _mtCostMod.showAddCosts(...a) : _maintLazyCall(_loadMtCost, 'showAddCosts', a); }
function calculateCostTotals(...a) { return _mtCostMod ? _mtCostMod.calculateCostTotals(...a) : _maintLazyCall(_loadMtCost, 'calculateCostTotals', a); }
function togglePartsTax(...a) { return _mtCostMod ? _mtCostMod.togglePartsTax(...a) : _maintLazyCall(_loadMtCost, 'togglePartsTax', a); }
function toggleShopTax(...a) { return _mtCostMod ? _mtCostMod.toggleShopTax(...a) : _maintLazyCall(_loadMtCost, 'toggleShopTax', a); }
function toggleVendorField(...a) { return _mtCostMod ? _mtCostMod.toggleVendorField(...a) : _maintLazyCall(_loadMtCost, 'toggleVendorField', a); }
function toggleCostSection(...a) { return _mtCostMod ? _mtCostMod.toggleCostSection(...a) : _maintLazyCall(_loadMtCost, 'toggleCostSection', a); }
// The form's onsubmit passes the event: stop the native submit NOW, before the
// module lands (saveCosts calls preventDefault itself once it runs).
function saveCosts(...a) {
  if (_mtCostMod) return _mtCostMod.saveCosts(...a);
  if (a[0] && typeof a[0].preventDefault === 'function') a[0].preventDefault();
  return _maintLazyCall(_loadMtCost, 'saveCosts', a);
}
function showShopCost(...a) { return _mtCostMod ? _mtCostMod.showShopCost(...a) : _maintLazyCall(_loadMtCost, 'showShopCost', a); }
function toggleStandaloneShopTax(...a) { return _mtCostMod ? _mtCostMod.toggleStandaloneShopTax(...a) : _maintLazyCall(_loadMtCost, 'toggleStandaloneShopTax', a); }
function updateStandaloneShopTotal(...a) { return _mtCostMod ? _mtCostMod.updateStandaloneShopTotal(...a) : _maintLazyCall(_loadMtCost, 'updateStandaloneShopTotal', a); }
// The form's onsubmit passes the event: stop the native submit NOW, before the
// module lands (saveShopCost calls preventDefault itself once it runs).
function saveShopCost(...a) {
  if (_mtCostMod) return _mtCostMod.saveShopCost(...a);
  if (a[0] && typeof a[0].preventDefault === 'function') a[0].preventDefault();
  return _maintLazyCall(_loadMtCost, 'saveShopCost', a);
}
function handleShopReceipt(...a) { return _mtCostMod ? _mtCostMod.handleShopReceipt(...a) : _maintLazyCall(_loadMtCost, 'handleShopReceipt', a); }
function captureShopReceipt(...a) { return _mtCostMod ? _mtCostMod.captureShopReceipt(...a) : _maintLazyCall(_loadMtCost, 'captureShopReceipt', a); }
function removeShopReceipt(...a) { return _mtCostMod ? _mtCostMod.removeShopReceipt(...a) : _maintLazyCall(_loadMtCost, 'removeShopReceipt', a); }
function showShopCostHistory(...a) { return _mtCostMod ? _mtCostMod.showShopCostHistory(...a) : _maintLazyCall(_loadMtCost, 'showShopCostHistory', a); }
function addShopCostFromHistory(...a) { return _mtCostMod ? _mtCostMod.addShopCostFromHistory(...a) : _maintLazyCall(_loadMtCost, 'addShopCostFromHistory', a); }
function deleteShopCost(...a) { return _mtCostMod ? _mtCostMod.deleteShopCost(...a) : _maintLazyCall(_loadMtCost, 'deleteShopCost', a); }
function removeReceipt(...a) { return _mtCostMod ? _mtCostMod.removeReceipt(...a) : _maintLazyCall(_loadMtCost, 'removeReceipt', a); }
function handleReceiptUpload(...a) { return _mtCostMod ? _mtCostMod.handleReceiptUpload(...a) : _maintLazyCall(_loadMtCost, 'handleReceiptUpload', a); }
function captureReceipt(...a) { return _mtCostMod ? _mtCostMod.captureReceipt(...a) : _maintLazyCall(_loadMtCost, 'captureReceipt', a); }
function aiScanReceipt(...a) { return _mtCostMod ? _mtCostMod.aiScanReceipt(...a) : _maintLazyCall(_loadMtCost, 'aiScanReceipt', a); }

async function printReceipts(taskId) {
  const task = MaintenanceState.tasks.find(t => t.id === taskId);
  if (!task?.costs?.receipts?.length) {
    window.showToast?.('No receipts to print', 'error');
    return;
  }

  // Create a print-friendly window with all receipts
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    window.showToast?.('Please allow popups to print receipts', 'error');
    return;
  }

  const receipts = task.costs.receipts;
  // TASK-9855 — image receipts load through the Storage SDK (rules-checked) as
  // object URLs; the about:blank print window shares this origin, so they load.
  const receiptSrcs = await Promise.all(receipts.map(r => (
    r.fileName?.toLowerCase().endsWith('.pdf')
      ? Promise.resolve('')
      : window.StorageFile.blobUrl(r).catch(() => '')
  )));
  const taskDate = task.completedAt?.toDate?.() || task.createdAt?.toDate?.() || new Date();
  const dateStr = taskDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  let receiptHtml = receipts.map((r, idx) => {
    const isPdf = r.fileName?.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      return `
        <div class="receipt-page">
          <div class="receipt-header">Receipt ${idx + 1}: ${escapeHtml(r.fileName)}</div>
          <p style="text-align:center;color:#666;">PDF receipts cannot be previewed. Open it from the task in DispatchDeck.</p>
        </div>
      `;
    }
    return `
      <div class="receipt-page">
        <div class="receipt-header">Receipt ${idx + 1}: ${escapeHtml(r.fileName || 'Receipt')}</div>
        ${receiptSrcs[idx] ? `<img src="${escapeHtml(receiptSrcs[idx])}" alt="Receipt ${idx + 1}" style="max-width:100%;height:auto;">` : '<p style="text-align:center;color:#666;">Receipt could not be loaded.</p>'}
      </div>
    `;
  }).join('');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Receipts - ${escapeHtml(task.truckUnit)} - ${escapeHtml(task.description || 'Work Order')}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
        .header h1 { margin: 0 0 5px 0; font-size: 1.5rem; }
        .header p { margin: 0; color: #666; }
        .receipt-page { page-break-after: always; margin-bottom: 40px; }
        .receipt-page:last-child { page-break-after: avoid; }
        .receipt-header { font-weight: bold; margin-bottom: 10px; padding: 8px; background: #f5f5f5; }
        img { max-width: 100%; height: auto; border: 1px solid #ddd; }
        @media print {
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${escapeHtml(task.truckUnit)} - ${escapeHtml(task.description || 'Work Order')}</h1>
        <p>${escapeHtml(task.category || 'Maintenance')} | Completed: ${dateStr}</p>
        ${task.costs?.totalCost ? `<p>Total Cost: $${maintMoney(task.costs.totalCost).toFixed(2)}</p>` : ''}
      </div>
      <button class="no-print" onclick="window.print()" style="margin-bottom:20px;padding:10px 20px;cursor:pointer;">Print Receipts</button>
      ${receiptHtml}
    </body>
    </html>
  `);
  printWindow.document.close();
}

// ============================================
// TASK-9361 — PRINT A SINGLE WORK ORDER
// ============================================
// The asset label the task detail header shows. Equipment carries its own
// `assetName`; a truck/trailer task carries only an id + a unit string, so the
// fleet object is looked up and handed to window.getUnitDisplay (nickname +
// unit). 🔴 Never pass the TASK to getUnitDisplay — it reads `.unit`, which a
// task does not have, and prints blank. Falls back to the unit string the task
// was saved with when the vehicle has since been removed from the fleet.
function getTaskAssetLabel(task) {
  if (task.assetId) return task.assetName || '?';
  if (task.trailerId) {
    const trailer = MaintenanceState.trailers.find(t => t.id === task.trailerId);
    return trailer && window.getUnitDisplay ? window.getUnitDisplay(trailer) : (task.trailerUnit || '?');
  }
  const truck = MaintenanceState.trucks.find(t => t.id === task.truckId);
  return truck && window.getUnitDisplay ? window.getUnitDisplay(truck) : (task.truckUnit || '?');
}

// Pitfall #5 — GAS-imported tasks carry the assignee's NAME in `assignedTo`,
// not a person id, so getPersonName (id-only) prints "Unknown" for them. Match
// either; when nobody matches (person since deleted), fall back to the name the
// task was saved with, then to the raw ref — which for a legacy row IS the name.
function getTaskAssigneeName(task) {
  const ref = task.assignedTo;
  if (!ref) return '';
  const person = MaintenanceState.people.find(p => p.id === ref || p.name === ref);
  return person?.name || task.assignedToName || String(ref);
}

// Pure: task + options in, full HTML document out. Split from printWorkOrder so
// the escaping contract can be unit-tested without a window (tests/task9361-*).
// 🔴 EVERY task field is driver-PIN writable (TASK-9287), so every interpolation
// below goes through escapeHtml — including <title> and the company name, which
// a tenant admin types free-form. `driverMode` omits costs + vendor: the driver
// card never shows them, and a print sheet must not widen what the UI displays.
function buildWorkOrderSheetHtml(task, { driverMode = false, companyName = '' } = {}) {
  const money = (n) => '$' + maintMoney(n).toFixed(2); // TASK-10269: "$1,250.00" text costs
  const created = task.createdAt?.toDate?.() || new Date();
  const shortId = String(task.id || '').slice(0, 8);
  const company = (companyName || '').trim();
  const statusLabel = capitalizeFirst((task.status || 'open').replace(/_/g, ' '));
  const priorityLabel = capitalizeFirst(task.priority || 'normal');
  const assetLabel = getTaskAssetLabel(task);
  const assignee = getTaskAssigneeName(task);
  const fromDvir = task.source === 'pretrip';
  const costs = task.costs || {};
  const hasCosts = !driverMode && maintMoney(costs.totalCost) > 0;

  const row = (label, value) => `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`;
  const blank = (label, cls = '') => `<div class="fill${cls ? ' ' + cls : ''}"><span class="fill-label">${escapeHtml(label)}</span><span class="fill-line"></span></div>`;

  const costRows = hasCosts ? `
      <h2>Cost Summary</h2>
      <table class="facts">
        ${costs.isThirdPartyVendor ? row('3rd Party Vendor', costs.vendorName || task.vendor || '—') : ''}
        ${costs.laborHours ? row(`Labor (${escapeHtml(String(costs.laborHours))}h @ ${money(costs.laborRate)}/hr)`, money(maintMoney(costs.laborHours) * maintMoney(costs.laborRate))) : ''}
        ${(costs.partsPreTax || costs.partsTotal) ? row(`Parts${Number(costs.partsTaxRate) > 0 ? ` + ${escapeHtml(String(costs.partsTaxRate))}% tax` : ''}`, money(costs.partsTotal)) : ''}
        ${costs.shopPreTax || costs.shopTotal ? row(`Shop${Number(costs.shopTaxRate) > 0 ? ` + ${escapeHtml(String(costs.shopTaxRate))}% tax` : costs.shopEntry === 'total' ? ` (tax $${(+costs.shopTaxAmount || 0).toFixed(2)}, card fee $${(+costs.shopFeeAmount || 0).toFixed(2)})` : ''}`, money(costs.shopTotal)) : ''}
        <tr class="total"><th>Total</th><td>${money(costs.totalCost)}</td></tr>
      </table>` : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Work Order ${escapeHtml(shortId)} - ${escapeHtml(assetLabel)}${company ? ` - ${escapeHtml(company)}` : ''}</title>
  <style>
    @page { size: letter; margin: 0.5in; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; margin: 0; padding: 24px; font-size: 12pt; line-height: 1.35; }
    .sheet { max-width: 7.5in; margin: 0 auto; }
    .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
    .head h1 { margin: 0; font-size: 18pt; }
    .head .wo { text-align: right; }
    .head .wo strong { display: block; font-size: 14pt; letter-spacing: 0.5px; }
    .head .wo span { font-size: 10pt; }
    h2 { font-size: 11pt; text-transform: uppercase; letter-spacing: 0.5px; margin: 18px 0 6px; border-bottom: 1px solid #000; padding-bottom: 2px; }
    table.facts { width: 100%; border-collapse: collapse; }
    table.facts th { text-align: left; width: 32%; padding: 5px 8px 5px 0; vertical-align: top; font-weight: 600; }
    table.facts td { padding: 5px 0; vertical-align: top; white-space: pre-wrap; word-break: break-word; }
    table.facts tr.total th, table.facts tr.total td { border-top: 1px solid #000; font-weight: 700; }
    .desc { white-space: pre-wrap; word-break: break-word; border: 1px solid #000; padding: 8px; min-height: 48px; }
    .fill { display: flex; align-items: flex-end; gap: 8px; margin: 16px 0; }
    .fill-label { white-space: nowrap; font-weight: 600; }
    .fill-line { flex: 1; border-bottom: 1px solid #000; height: 1.4em; }
    .fill.tall .fill-line { height: 4.5em; }
    .sig { display: flex; gap: 24px; }
    .sig .fill { flex: 1; }
    .badge { display: inline-block; border: 1px solid #000; padding: 1px 6px; font-size: 10pt; margin-left: 6px; }
    .no-print { margin: 0 0 16px; padding: 10px 20px; cursor: pointer; font-size: 12pt; }
    @media print { .no-print { display: none; } body { padding: 0; } }
  </style>
</head>
<body>
  <div class="sheet">
    <button class="no-print" onclick="window.print()">Print</button>
    <div class="head">
      <h1>${company ? escapeHtml(company) : 'Work Order'}</h1>
      <div class="wo">
        <strong>WORK ORDER #${escapeHtml(shortId)}</strong>
        <span>Created ${escapeHtml(formatDate(created))}</span>
      </div>
    </div>

    <table class="facts">
      ${row('Status', statusLabel)}
      ${row('Priority', priorityLabel)}
      <tr><th>Asset</th><td>${escapeHtml(assetLabel)}${fromDvir ? '<span class="badge">From DVIR</span>' : ''}</td></tr>
      ${row('Category', task.category || 'General')}
      ${!driverMode ? row('Assigned To', assignee || 'Unassigned') : ''}
      ${!driverMode && task.vendor ? row('Vendor', task.vendor) : ''}
    </table>

    <h2>Description</h2>
    <div class="desc">${escapeHtml(task.description || 'No description')}</div>

    ${!driverMode && task.notes ? `<h2>Notes</h2><div class="desc">${escapeHtml(task.notes)}</div>` : ''}
    ${costRows}

    <h2>Shop Use</h2>
    ${blank('Work performed', 'tall')}
    ${blank('Parts used')}
    ${blank('Labor hrs')}
    <div class="sig">
      ${blank('Mechanic signature / date')}
      ${blank('Supervisor signature / date')}
    </div>
  </div>
</body>
</html>`;
}

// Modeled on printReceipts. 🔴 window.open runs FIRST and this function stays
// synchronous end to end — iOS Safari only honours a popup opened inside the
// click's own tick, so there is no await/timeout anywhere before it. Nothing is
// read from or written to Firestore: the sheet is built from the task already
// in MaintenanceState.
function printWorkOrder(taskId) {
  const task = MaintenanceState.tasks.find(t => t.id === taskId);
  if (!task) {
    window.showToast?.('Work order not found', 'error');
    return;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    window.showToast?.('Please allow popups to print the work order', 'error');
    return;
  }

  const html = buildWorkOrderSheetHtml(task, {
    driverMode: isDriverPinMode(),
    companyName: window.AppState?.tenantData?.companyName || ''
  });
  printWindow.document.write(html);
  printWindow.document.close();
}

// =============================================================================
// TASK-10065 (bundle headroom) — Vehicle Info, Fluids log, Work Order History (+ email summary / CSV), Trailer Info, Correct Meters
// live in the LAZY views/maintVehicleInfo.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps the name window.MaintenanceModule and maintenance.js
// call, loads the module on first use and, once loaded, calls straight through
// synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _mtInfoMod = null;
let _mtInfoLoad = null;
function _mtInfoDeps() {
  return {
    MaintenanceState, Timestamp, _regExpiryOf, addDoc, auth, capitalizeFirst, closeModal,
    collection, db, doc, escJsAttr, escapeHtml, formatDate, formatExpiryDate, functions,
    getExpiryStatus, httpsCallable, onSnapshot, orderBy, query, renderFleetGrid, setDoc, where,
    maintHistoryDay, maintMoney
  };
}
function _loadMtInfo() {
  if (_mtInfoMod) return Promise.resolve(_mtInfoMod);
  if (!_mtInfoLoad) {
    _mtInfoLoad = __ddChunkWrap(import('./maintVehicleInfo.js?v=1102692')).then((m) => { m._bind(_mtInfoDeps()); _mtInfoMod = m; return m; }).catch((e) => {
      _mtInfoLoad = null; // the next click tries again
      console.error('[Maintenance] maintVehicleInfo load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _maintLazyLoadFailed: true });
    });
  }
  return _mtInfoLoad;
}
function showVehicleInfo(...a) { return _mtInfoMod ? _mtInfoMod.showVehicleInfo(...a) : _maintLazyCall(_loadMtInfo, 'showVehicleInfo', a); }
function showFluidsLog(...a) { return _mtInfoMod ? _mtInfoMod.showFluidsLog(...a) : _maintLazyCall(_loadMtInfo, 'showFluidsLog', a); }
function toggleFluidsAutoPurge(...a) { return _mtInfoMod ? _mtInfoMod.toggleFluidsAutoPurge(...a) : _maintLazyCall(_loadMtInfo, 'toggleFluidsAutoPurge', a); }
function saveFluidsRetention(...a) { return _mtInfoMod ? _mtInfoMod.saveFluidsRetention(...a) : _maintLazyCall(_loadMtInfo, 'saveFluidsRetention', a); }
function toggleOilFields(...a) { return _mtInfoMod ? _mtInfoMod.toggleOilFields(...a) : _maintLazyCall(_loadMtInfo, 'toggleOilFields', a); }
// The form's onsubmit passes the event: stop the native submit NOW, before the
// module lands (addFluidEntry calls preventDefault itself once it runs).
function addFluidEntry(...a) {
  if (_mtInfoMod) return _mtInfoMod.addFluidEntry(...a);
  if (a[0] && typeof a[0].preventDefault === 'function') a[0].preventDefault();
  return _maintLazyCall(_loadMtInfo, 'addFluidEntry', a);
}
function showWorkHistory(...a) { return _mtInfoMod ? _mtInfoMod.showWorkHistory(...a) : _maintLazyCall(_loadMtInfo, 'showWorkHistory', a); }
function filterHistory(...a) { return _mtInfoMod ? _mtInfoMod.filterHistory(...a) : _maintLazyCall(_loadMtInfo, 'filterHistory', a); }
function showEmailSummary(...a) { return _mtInfoMod ? _mtInfoMod.showEmailSummary(...a) : _maintLazyCall(_loadMtInfo, 'showEmailSummary', a); }
function updateEmailPeriod(...a) { return _mtInfoMod ? _mtInfoMod.updateEmailPeriod(...a) : _maintLazyCall(_loadMtInfo, 'updateEmailPeriod', a); }
// The form's onsubmit passes the event: stop the native submit NOW, before the
// module lands (sendEmailSummary calls preventDefault itself once it runs).
function sendEmailSummary(...a) {
  if (_mtInfoMod) return _mtInfoMod.sendEmailSummary(...a);
  if (a[0] && typeof a[0].preventDefault === 'function') a[0].preventDefault();
  return _maintLazyCall(_loadMtInfo, 'sendEmailSummary', a);
}
function exportHistory(...a) { return _mtInfoMod ? _mtInfoMod.exportHistory(...a) : _maintLazyCall(_loadMtInfo, 'exportHistory', a); }
function showTrailerInfo(...a) { return _mtInfoMod ? _mtInfoMod.showTrailerInfo(...a) : _maintLazyCall(_loadMtInfo, 'showTrailerInfo', a); }
function showTrailerHistory(...a) { return _mtInfoMod ? _mtInfoMod.showTrailerHistory(...a) : _maintLazyCall(_loadMtInfo, 'showTrailerHistory', a); }
function showCorrectMeters(...a) { return _mtInfoMod ? _mtInfoMod.showCorrectMeters(...a) : _maintLazyCall(_loadMtInfo, 'showCorrectMeters', a); }
function saveCorrectMeters(...a) { return _mtInfoMod ? _mtInfoMod.saveCorrectMeters(...a) : _maintLazyCall(_loadMtInfo, 'saveCorrectMeters', a); }

// =============================================================================
// TASK-10065 (bundle headroom) — Vehicle Inventory modal (count / purchase / history / review queue) + the fleet-wide Inventory view
// live in the LAZY views/maintInventory.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps the name window.MaintenanceModule and maintenance.js
// call, loads the module on first use and, once loaded, calls straight through
// synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _mtInvMod = null;
let _mtInvLoad = null;
function _mtInvDeps() {
  return {
    MaintenanceState, Timestamp, addDoc, auth, buildFleetInventoryItems, collection, db,
    deleteDoc, doc, escapeHtml, fleetInventoryPeople, fleetInventoryUnitLabel,
    inventoryHistoryRowHtml, limit, setDoc, updateDoc
  };
}
function _loadMtInv() {
  if (_mtInvMod) return Promise.resolve(_mtInvMod);
  if (!_mtInvLoad) {
    _mtInvLoad = __ddChunkWrap(import('./maintInventory.js?v=1006504')).then((m) => { m._bind(_mtInvDeps()); _mtInvMod = m; return m; }).catch((e) => {
      _mtInvLoad = null; // the next click tries again
      console.error('[Maintenance] maintInventory load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _maintLazyLoadFailed: true });
    });
  }
  return _mtInvLoad;
}
function showInventory(...a) { return _mtInvMod ? _mtInvMod.showInventory(...a) : _maintLazyCall(_loadMtInv, 'showInventory', a); }
function switchInventoryTab(...a) { return _mtInvMod ? _mtInvMod.switchInventoryTab(...a) : _maintLazyCall(_loadMtInv, 'switchInventoryTab', a); }
function submitInventory(...a) { return _mtInvMod ? _mtInvMod.submitInventory(...a) : _maintLazyCall(_loadMtInv, 'submitInventory', a); }
// The form's onsubmit passes the event: stop the native submit NOW, before the
// module lands (addInventoryPurchase calls preventDefault itself once it runs).
function addInventoryPurchase(...a) {
  if (_mtInvMod) return _mtInvMod.addInventoryPurchase(...a);
  if (a[0] && typeof a[0].preventDefault === 'function') a[0].preventDefault();
  return _maintLazyCall(_loadMtInv, 'addInventoryPurchase', a);
}
function renderInventoryHistory(...a) { return _mtInvMod ? _mtInvMod.renderInventoryHistory(...a) : _maintLazyCall(_loadMtInv, 'renderInventoryHistory', a); }
function approveInventoryPurchase(...a) { return _mtInvMod ? _mtInvMod.approveInventoryPurchase(...a) : _maintLazyCall(_loadMtInv, 'approveInventoryPurchase', a); }
function rejectInventoryPurchase(...a) { return _mtInvMod ? _mtInvMod.rejectInventoryPurchase(...a) : _maintLazyCall(_loadMtInv, 'rejectInventoryPurchase', a); }
function openEditInventoryPurchase(...a) { return _mtInvMod ? _mtInvMod.openEditInventoryPurchase(...a) : _maintLazyCall(_loadMtInv, 'openEditInventoryPurchase', a); }
function updateEditPurchaseTotal(...a) { return _mtInvMod ? _mtInvMod.updateEditPurchaseTotal(...a) : _maintLazyCall(_loadMtInv, 'updateEditPurchaseTotal', a); }
// The form's onsubmit passes the event: stop the native submit NOW, before the
// module lands (saveInventoryPurchaseEdit calls preventDefault itself once it runs).
function saveInventoryPurchaseEdit(...a) {
  if (_mtInvMod) return _mtInvMod.saveInventoryPurchaseEdit(...a);
  if (a[0] && typeof a[0].preventDefault === 'function') a[0].preventDefault();
  return _maintLazyCall(_loadMtInv, 'saveInventoryPurchaseEdit', a);
}
function showPendingPurchases(...a) { return _mtInvMod ? _mtInvMod.showPendingPurchases(...a) : _maintLazyCall(_loadMtInv, 'showPendingPurchases', a); }
function filterInventoryHistory(...a) { return _mtInvMod ? _mtInvMod.filterInventoryHistory(...a) : _maintLazyCall(_loadMtInv, 'filterInventoryHistory', a); }
function toggleInventoryDetails(...a) { return _mtInvMod ? _mtInvMod.toggleInventoryDetails(...a) : _maintLazyCall(_loadMtInv, 'toggleInventoryDetails', a); }
function showFleetInventory(...a) { return _mtInvMod ? _mtInvMod.showFleetInventory(...a) : _maintLazyCall(_loadMtInv, 'showFleetInventory', a); }
function renderFleetInventory(...a) { return _mtInvMod ? _mtInvMod.renderFleetInventory(...a) : _maintLazyCall(_loadMtInv, 'renderFleetInventory', a); }
function fleetInventoryShowMore(...a) { return _mtInvMod ? _mtInvMod.fleetInventoryShowMore(...a) : _maintLazyCall(_loadMtInv, 'fleetInventoryShowMore', a); }
function fleetInventoryShowPending(...a) { return _mtInvMod ? _mtInvMod.fleetInventoryShowPending(...a) : _maintLazyCall(_loadMtInv, 'fleetInventoryShowPending', a); }

// ============================================
// TASK-9335 — FLEET-WIDE INVENTORY (every truck + trailer)
// ============================================
// Past counts used to be reachable one vehicle at a time, and only while that
// vehicle's inventory checkbox was on. This is the fleet view: every
// inventoryLogs count + inventoryPurchases row, newest first, from data the
// Maintenance load already live-subscribes (no new query).
//
// The block between the markers is pure (no DOM, no Firestore) and is lifted
// verbatim by tests/task9335-fleet-inventory.unit.cjs.
// >>> TASK-9335-FLEET-INVENTORY (extracted verbatim by tests/task9335-fleet-inventory.unit.cjs) >>>

// Show the header button when ANY vehicle has inventory on OR any history
// exists. There is no tenant-level switch (isVehicleInventoryEnabled() is
// hard-true); "turned off for all trucks" = every per-vehicle box unchecked,
// and the history clause keeps those past counts reachable.
function fleetInventoryHasData(trucks, trailers, logs, purchases) {
  const on = (list) => (list || []).some(v => v && v.inventoryEnabled === true);
  return on(trucks) || on(trailers) || (logs || []).length > 0 || (purchases || []).length > 0;
}

// Timestamp | Date | ISO string | {seconds} → Date, or null when undated/invalid.
// (The per-vehicle view falls back to `new Date()`, which labels an undated
// row as TODAY — the fleet view sorts those last as "No date" instead.)
function _fleetInvDate(ts) {
  if (!ts) return null;
  let d = null;
  if (typeof ts.toDate === 'function') d = ts.toDate();
  else if (typeof ts.seconds === 'number') d = new Date(ts.seconds * 1000);
  else if (typeof ts === 'string' || typeof ts === 'number' || typeof ts.getTime === 'function') d = new Date(ts);
  return d && typeof d.getTime === 'function' && Number.isFinite(d.getTime()) ? d : null;
}

// Trailer ids are stored in `truckId` too, and two of the three writers omit
// vehicleType — so resolve against BOTH lists, then the stored label.
function fleetInventoryUnitLabel(vehicleId, storedUnit, trucks, trailers) {
  const v = (trucks || []).find(t => t && t.id === vehicleId) || (trailers || []).find(t => t && t.id === vehicleId);
  const label = v ? (v.unit || v.nickname || v.name || storedUnit) : storedUnit;
  return label ? String(label) : 'Deleted vehicle';
}

// Counts are written by submittedBy; purchases by purchasedBy.
function _fleetInvPerson(item) {
  const d = item.data || {};
  return String((item.type === 'count' ? d.submittedBy : d.purchasedBy) || '');
}

// A qty is user/driver-entered and inventoryLogs create has no schema: compare
// numerically when both parse, else fall back to the per-vehicle view's rule.
function _invIsDiscrepancy(i) {
  const a = Number(i && i.actualQty);
  const e = Number(i && i.expectedQty);
  if (Number.isFinite(a) && Number.isFinite(e)) return a !== e;
  return (i && i.actualQty) !== (i && i.expectedQty);
}

// filters: { type:'all'|'counts'|'purchases'|'pending', vehicleId, person,
//            from:'YYYY-MM-DD', to:'YYYY-MM-DD', discrepanciesOnly }
function buildFleetInventoryItems(logs, purchases, filters) {
  const f = filters || {};
  const type = f.type || 'all';
  const items = [];
  if (type === 'all' || type === 'counts') {
    for (const l of logs || []) items.push({ type: 'count', date: _fleetInvDate(l.submittedAt), data: l });
  }
  if (type === 'all' || type === 'purchases' || type === 'pending') {
    for (const p of purchases || []) {
      if (type === 'pending' && p.status !== 'pending') continue;
      items.push({ type: 'purchase', date: _fleetInvDate(p.purchasedAt), data: p });
    }
  }
  const fromMs = f.from ? new Date(`${f.from}T00:00:00`).getTime() : null;
  const toMs = f.to ? new Date(`${f.to}T23:59:59.999`).getTime() : null;
  const out = items.filter(item => {
    if (f.vehicleId && item.data.truckId !== f.vehicleId) return false;
    if (f.person && _fleetInvPerson(item) !== f.person) return false;
    if (fromMs != null || toMs != null) {
      if (!item.date) return false;                       // undated can't be in a range
      const t = item.date.getTime();
      if (fromMs != null && Number.isFinite(fromMs) && t < fromMs) return false;
      if (toMs != null && Number.isFinite(toMs) && t > toMs) return false;
    }
    // Discrepancies are a property of COUNTS. In the Pending / Purchases views
    // the checkbox is incompatible, so it is ignored there rather than
    // emptying the review queue (COD, TASK-9335 follow-up).
    if (f.discrepanciesOnly && (type === 'all' || type === 'counts')) {
      if (item.type !== 'count') return false;
      // One malformed doc (items not an array) must not throw the whole view.
      if (!(Array.isArray(item.data.items) ? item.data.items : []).some(_invIsDiscrepancy)) return false;
    }
    return true;
  });
  // Newest first; undated rows last.
  out.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date - a.date;
  });
  return out;
}

// Distinct people who counted or purchased, for the Person filter.
function fleetInventoryPeople(logs, purchases) {
  const set = new Set();
  for (const l of logs || []) if (l && l.submittedBy) set.add(String(l.submittedBy));
  for (const p of purchases || []) if (p && p.purchasedBy) set.add(String(p.purchasedBy));
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function _invQty(v) {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '—';
}

// ONE row renderer for both the per-vehicle History tab and the fleet view.
// opts.idPrefix — row-detail id prefix ('inv-' per vehicle, 'fleetinv-' fleet),
//   so a hidden per-vehicle modal's leftover rows can never collide.
// opts.scopeId  — container the expand toggle searches INSIDE (fleet only).
// opts.unitLabel — shown in the fleet view so you can tell the trucks apart.
// Every user-entered string is escapeHtml'd, every onclick arg escJsAttr'd,
// every qty/cost Number()-coerced (TASK-9287 pattern).
function inventoryHistoryRowHtml(item, opts) {
  const o = opts || {};
  const prefix = o.idPrefix || 'inv-';
  const unit = o.unitLabel ? ` · ${escapeHtml(o.unitLabel)}` : '';
  const dateStr = item.date
    ? item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'No date';
  if (item.type === 'count') {
    const log = item.data || {};
    const timeStr = item.date ? ` at ${item.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : '';
    const rows = Array.isArray(log.items) ? log.items : [];
    const discrepancies = rows.filter(_invIsDiscrepancy).length;
    const discrepancyLabel = discrepancies > 0
      ? `<span class="inventory-discrepancy-badge">${discrepancies} discrepanc${discrepancies === 1 ? 'y' : 'ies'}</span>`
      : '<span class="inventory-ok-badge">All OK</span>';
    const itemId = `${prefix}${log.id || Date.now()}`;
    return `
        <div class="inventory-history-item inventory-count">
          <div class="inventory-history-header inventory-expandable" onclick="window.MaintenanceModule.toggleInventoryDetails('${escJsAttr(itemId)}'${o.scopeId ? `, '${escJsAttr(o.scopeId)}'` : ''})">
            <div class="inventory-history-icon"><span class="material-icons">checklist</span></div>
            <div class="inventory-history-info">
              <div class="inventory-history-title">Inventory Count${unit}</div>
              <div class="inventory-history-meta">${dateStr}${timeStr} by ${escapeHtml(log.submittedBy)}</div>
            </div>
            <div class="inventory-summary">
              <span class="inventory-item-count">${rows.length} items</span>
              ${discrepancyLabel}
              <span class="material-icons inventory-expand-icon">expand_more</span>
            </div>
          </div>
          ${log.notes ? `<div class="inventory-history-notes">${escapeHtml(log.notes)}</div>` : ''}
          <div class="inventory-history-items collapsed" id="${escapeHtml(itemId)}">
            ${rows.map(i => `
              <div class="inventory-history-item-row ${_invIsDiscrepancy(i) ? 'discrepancy' : ''}">
                <span class="item-name">${escapeHtml(i && i.itemName)}</span>
                <span class="item-qty">${_invQty(i && i.actualQty)}/${_invQty(i && i.expectedQty)}</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;
  }
  const purchase = item.data || {};
  const status = purchase.status || 'approved';
  const statusBadge = status === 'pending'
    ? '<span class="inventory-status-badge inventory-status-pending">Pending Review</span>'
    : status === 'rejected'
      ? '<span class="inventory-status-badge inventory-status-rejected">Rejected</span>'
      : '';
  const sourceLabel = purchase.enteredBy === 'driver' ? `<span class="inventory-source-tag">via Driver PIN</span>` : '';
  // TASK-9287 -- encodeURIComponent is NOT a handler sanitiser (it leaves the
  // apostrophe alone). The encoding stays because the callees
  // decodeURIComponent() their argument; escJsAttr is layered on top.
  const safeId = encodeURIComponent(purchase.id || '');
  const actions = status === 'pending' ? `
        <div class="inventory-purchase-actions">
          <button class="btn btn-sm btn-success" onclick="window.MaintenanceModule.approveInventoryPurchase('${escJsAttr(safeId)}')">
            <span class="material-icons">check</span> Approve
          </button>
          <button class="btn btn-sm btn-secondary" onclick="window.MaintenanceModule.openEditInventoryPurchase('${escJsAttr(safeId)}')">
            <span class="material-icons">edit</span> Edit
          </button>
          <button class="btn btn-sm btn-danger" onclick="window.MaintenanceModule.rejectInventoryPurchase('${escJsAttr(safeId)}')">
            <span class="material-icons">close</span> Reject
          </button>
        </div>
      ` : `
        <div class="inventory-purchase-actions">
          <button class="btn-icon btn-sm" title="Edit purchase" onclick="window.MaintenanceModule.openEditInventoryPurchase('${escJsAttr(safeId)}')">
            <span class="material-icons">edit</span>
          </button>
        </div>
      `;
  const cost = maintMoney(purchase.totalCost); // TASK-10269: "$1,250.00" text costs
  return `
        <div class="inventory-history-item inventory-purchase ${status === 'rejected' ? 'inventory-rejected' : ''}">
          <div class="inventory-history-header">
            <div class="inventory-history-icon purchase"><span class="material-icons">shopping_cart</span></div>
            <div class="inventory-history-info">
              <div class="inventory-history-title">${escapeHtml(purchase.itemName)} × ${_invQty(purchase.quantity)}${unit} ${statusBadge}</div>
              <div class="inventory-history-meta">${dateStr} by ${escapeHtml(purchase.purchasedBy)} ${sourceLabel}</div>
            </div>
            <div class="inventory-history-cost">$${cost.toFixed(2)}</div>
          </div>
          ${purchase.notes ? `<div class="inventory-history-notes">${escapeHtml(purchase.notes)}</div>` : ''}
          ${actions}
        </div>
      `;
}
// <<< TASK-9335-FLEET-INVENTORY <<<


function _fleetInvModalOpen() {
  const m = document.getElementById('modal-fleet-inventory');
  return !!m && !m.classList.contains('hidden');
}

function updateFleetInventoryButton() {
  const btn = document.getElementById('btn-fleet-inventory');
  if (!btn) return;
  btn.classList.toggle('hidden', !fleetInventoryHasData(
    MaintenanceState.trucks, MaintenanceState.trailers,
    MaintenanceState.inventoryLogs, MaintenanceState.inventoryPurchases));
}

// Called from all FOUR snapshots (trucks, trailers, inventoryLogs,
// inventoryPurchases): the header is static HTML, so nothing else re-evaluates
// the button, and an open fleet modal must pick up a driver's PIN count live.
function onFleetInventoryDataChanged() {
  updateFleetInventoryButton();
  if (_fleetInvModalOpen()) renderFleetInventory();
}


// ============================================
// FLEET FILTER FUNCTION
// ============================================
function filterFleet() {
  const searchEl = document.getElementById('search-fleet');
  const typeEl = document.getElementById('filter-fleet-type');
  const statusEl = document.getElementById('filter-fleet-status');
  const expiryEl = document.getElementById('filter-fleet-expiry');
  const sortEl = document.getElementById('sort-fleet');
  MaintenanceState.fleetFilter.search = searchEl?.value || '';
  MaintenanceState.fleetFilter.type = typeEl?.value || 'all';
  // TASK-9898 S4 — the Status select became count tiles (setFleetStatusTile);
  // only read a select if one is on the page.
  if (statusEl) MaintenanceState.fleetFilter.status = statusEl.value || 'all';
  MaintenanceState.fleetFilter.expiry = expiryEl?.value || 'all';
  if (sortEl) {
    const sort = sortEl.value === 'pmdue' ? 'pmdue' : 'unit';
    MaintenanceState.fleetFilter.sort = sort;
    setStoredFleetSort(sort);
  }
  renderFleetGrid();
}

// ============================================
// TASK-9898 S4 — FLEET STATUS COUNT TILES + FLEET TOOLS MENU
// ============================================
// Tiles replace the Status select. Counts are the WHOLE fleet (search, type and
// document filters never change them); tap = show only those trucks, tap the
// lit tile again = all. "Open tasks" keeps TASK-9766's "Has open task" filter.
const FLEET_STATUS_TILES = [
  { key: 'hold', label: 'On hold', match: t => !!t.activeHold },
  { key: 'overdue', label: 'PM overdue', match: t => t.pmStatus?.status === 'overdue' },
  { key: 'duesoon', label: 'Due soon', match: t => t.pmStatus?.status === 'due_soon' },
  { key: 'nodvir', label: 'No DVIR today', match: t => !!t.missingDvirToday },
  { key: 'readings', label: 'Readings to check', match: t => !!(t.pmStatus?.pm?.suspect?.length) },
  { key: 'opentask', label: 'Open tasks', match: t => t.openTasks > 0 }
];

function fleetTileMatch(key) {
  return FLEET_STATUS_TILES.find(x => x.key === key)?.match || null;
}

function fleetStatusTileCounts(rows) {
  const out = {};
  FLEET_STATUS_TILES.forEach(tile => { out[tile.key] = (rows || []).filter(tile.match).length; });
  return out;
}

function renderFleetStatusTiles(counts) {
  const el = document.getElementById('fleet-status-tiles');
  if (!el) return;
  if (!counts) { el.innerHTML = ''; return; }
  const active = MaintenanceState.fleetFilter.status;
  el.innerHTML = FLEET_STATUS_TILES.map(tile => {
    const n = counts[tile.key] || 0;
    const on = active === tile.key;
    const hot = n > 0 && tile.key !== 'opentask';
    return `
      <button type="button" class="fleet-status-tile${on ? ' active' : ''}" data-tile="${tile.key}" aria-pressed="${on ? 'true' : 'false'}"
        onclick="window.MaintenanceModule.setFleetStatusTile('${escJsAttr(tile.key)}')"
        style="display:flex;flex-direction:column;align-items:flex-start;gap:2px;min-height:56px;padding:8px 10px;border-radius:10px;cursor:pointer;text-align:left;
          border:${on ? '2px solid var(--brand-blue, #3B82F6)' : '1px solid var(--border-light, #E2E8F0)'};
          background:${on ? 'rgba(59,130,246,0.08)' : 'var(--bg-primary, #fff)'};color:var(--text-primary);">
        <span style="font-size:1.25rem;font-weight:700;line-height:1.1;color:${hot ? 'var(--warning, #B45309)' : 'var(--text-primary)'};">${n}</span>
        <span style="font-size:0.78rem;color:var(--text-secondary);">${escapeHtml(tile.label)}</span>
      </button>`;
  }).join('');
}

function setFleetStatusTile(key) {
  const cur = MaintenanceState.fleetFilter.status;
  MaintenanceState.fleetFilter.status = (!fleetTileMatch(key) || cur === key) ? 'all' : key;
  // "Readings to check" also opens the strip that lists each reading's fix.
  if (MaintenanceState.fleetFilter.status === 'readings') MaintenanceState.readingsStripOpen = true;
  renderFleetGrid();
}

function _fleetToolsEls() {
  return { btn: document.getElementById('btn-fleet-tools'), menu: document.getElementById('fleet-tools-menu') };
}

function closeFleetTools() {
  const { btn, menu } = _fleetToolsEls();
  if (menu) menu.hidden = true;
  if (btn) btn.setAttribute('aria-expanded', 'false');
  document.removeEventListener('click', _fleetToolsOutside, true);
  document.removeEventListener('keydown', _fleetToolsOutside, true);
}

// Outside tap or Escape closes the menu. Taps on the menu run their item (which
// closes it); a tap on the toggle is left to toggleFleetTools.
function _fleetToolsOutside(e) {
  const { btn, menu } = _fleetToolsEls();
  if (e.type === 'keydown') {
    if (e.key !== 'Escape') return;
    closeFleetTools();
    btn?.focus();
    return;
  }
  if ((menu && menu.contains(e.target)) || (btn && btn.contains(e.target))) return;
  closeFleetTools();
}

function toggleFleetTools() {
  const { btn, menu } = _fleetToolsEls();
  if (!menu) return;
  if (!menu.hidden) { closeFleetTools(); return; }
  menu.hidden = false;
  if (btn) btn.setAttribute('aria-expanded', 'true');
  // Capture phase: a card's own stopPropagation cannot keep the menu open.
  document.addEventListener('click', _fleetToolsOutside, true);
  document.addEventListener('keydown', _fleetToolsOutside, true);
  menu.querySelector('[role="menuitem"]:not(.hidden)')?.focus();
}

// ============================================
// TRAILER FILTER FUNCTION
// ============================================
function filterTrailers() {
  const searchEl = document.getElementById('search-trailers');
  const typeEl = document.getElementById('filter-trailer-type');
  const statusEl = document.getElementById('filter-trailer-status');
  MaintenanceState.trailerFilter.search = searchEl?.value || '';
  MaintenanceState.trailerFilter.type = typeEl?.value || 'all';
  MaintenanceState.trailerFilter.status = statusEl?.value || 'all';
  renderTrailerGrid();
}


function showTrailerTasks(trailerId) {
  // TASK-9897 — one Vehicle filter; replaces any earlier drill instead of AND-ing.
  woDrillToVehicle('trailer:' + trailerId);
}


function clearAssetFilter() {
  MaintenanceState.truckFilter = null;
  MaintenanceState.trailerFilter.id = null;
  MaintenanceState.assetFilter.filterId = null;
  clearTruckFilter();
}

// =============================================================================
// TASK-10065 (bundle headroom) — View Source DVIR (from a work order) + the mechanic DVIR repair sign-off
// live in the LAZY views/maintSourceDvir.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps the name window.MaintenanceModule and maintenance.js
// call, loads the module on first use and, once loaded, calls straight through
// synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _mtSrcMod = null;
let _mtSrcLoad = null;
function _mtSrcDeps() {
  return {
    MaintenanceState, Timestamp, _resolveTenantForWrite, db, doc, escJsAttr, escapeHtml, getDoc,
    safeHttpUrl, updateDoc
  };
}
function _loadMtSrc() {
  if (_mtSrcMod) return Promise.resolve(_mtSrcMod);
  if (!_mtSrcLoad) {
    _mtSrcLoad = __ddChunkWrap(import('./maintSourceDvir.js?v=1033102')).then((m) => { m._bind(_mtSrcDeps()); _mtSrcMod = m; return m; }).catch((e) => {
      _mtSrcLoad = null; // the next click tries again
      console.error('[Maintenance] maintSourceDvir load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _maintLazyLoadFailed: true });
    });
  }
  return _mtSrcLoad;
}
function viewSourceDvir(...a) { return _mtSrcMod ? _mtSrcMod.viewSourceDvir(...a) : _maintLazyCall(_loadMtSrc, 'viewSourceDvir', a); }
function clearMaintDvirSignoff(...a) { return _mtSrcMod ? _mtSrcMod.clearMaintDvirSignoff(...a) : _maintLazyCall(_loadMtSrc, 'clearMaintDvirSignoff', a); }
function submitMaintDvirSignoff(...a) { return _mtSrcMod ? _mtSrcMod.submitMaintDvirSignoff(...a) : _maintLazyCall(_loadMtSrc, 'submitMaintDvirSignoff', a); }

// ============================================
// EXPOSE TO WINDOW
// ============================================
window.MaintenanceModule = {
  showNewTask,
  // TASK-9897 — Work Orders filter bar
  woSetFilter,
  woToggleVehicle,
  woRemoveFilter,
  woClearFilters,
  woSetSort,
  woToggleMore,
  woOpenSheet,
  woCloseSheet,
  setDriverTaskMode, // TASK-4773 — the driver modal's two onclick handlers
  editTask,
  saveTask,
  updateStatus,
  startWorkFromDetail, // TASK-9766 (a)
  auditTaskStatus, // TASK-9766 (c) — pretrip.js clearSafetyHoldsForDvir writes the same row
  deleteTask,
  confirmCompletion,
  showTaskDetail,
  openTaskById, // TASK-9691 — "Open work order" link on a DVIR
  filterTasks,
  showTruckTasks,
  clearTruckFilter,
  closeModal,
  switchTab,
  switchAssetType,
  showUpdateMeters,
  saveMeters,
  showCorrectMeters,
  saveCorrectMeters,
  showLogPM,
  fixSuspectReading,
  toggleReadingsStrip,
  openReadingsStrip,
  setPMTypeFromDefault,
  savePMService,
  showPMSettings,
  savePMSettings,
  closePMSettings,
  showPMTypeDropdown,
  addPMTypeOverride,
  removePMTypeOverride,
  addPMChecklistItem,
  removePMChecklistItem,
  showNewHold,
  showNewHoldForVehicle,
  updateHoldAssetDropdown,
  saveHold,
  releaseHold,
  showDVIRHistory,
  selectDVIRDay,
  applyDVIRDateRange,
  deleteDVIR,
  downloadDVIRPdf,
  showDVIRPhotos,
  openFullPhoto,
  showBulkPdfModal,
  generateBulkPdfs,
  // Cost tracking
  showAddCosts,
  calculateCostTotals,
  saveCosts,
  togglePartsTax,
  toggleShopTax,
  toggleVendorField,
  toggleCostSection,
  // Standalone shop cost
  showShopCost,
  saveShopCost,
  toggleStandaloneShopTax,
  updateStandaloneShopTotal,
  handleShopReceipt,
  captureShopReceipt,
  removeShopReceipt,
  showShopCostHistory,
  addShopCostFromHistory, // TASK-9335
  deleteShopCost,
  handleReceiptUpload,
  captureReceipt,
  aiScanReceipt,
  removeReceipt,
  printReceipts,
  printWorkOrder, // TASK-9361 — admin detail modal + driver card
  // Vehicle info
  showVehicleInfo,
  // Fluid tracking
  showFluidsLog,
  addFluidEntry,
  toggleOilFields,
  toggleFluidsAutoPurge,
  saveFluidsRetention,
  // Work history
  showWorkHistory,
  // TASK-9898 S3 — Vehicle panel
  showVehiclePanel,
  vehiclePanelGo,
  filterHistory,
  showEmailSummary,
  updateEmailPeriod,
  sendEmailSummary,
  exportHistory,
  // Fleet filter
  filterFleet,
  setFleetStatusTile, // TASK-9898 S4
  toggleFleetTools, // TASK-9898 S4
  closeFleetTools, // TASK-9898 S4
  // Trailer functions
  filterTrailers,
  showTrailerInfo,
  showTrailerTasks,
  showTrailerHistory,
  clearAssetFilter,
  updateTaskAssetDropdown,
  onTaskCategoryChange,
  // Vehicle Inventory
  showInventory,
  switchInventoryTab,
  submitInventory,
  addInventoryPurchase,
  filterInventoryHistory,
  toggleInventoryDetails,
  // TASK-9335 — fleet-wide inventory
  showFleetInventory,
  renderFleetInventory,
  fleetInventoryShowMore,
  fleetInventoryShowPending,
  approveInventoryPurchase,
  rejectInventoryPurchase,
  openEditInventoryPurchase,
  updateEditPurchaseTotal,
  saveInventoryPurchaseEdit,
  showPendingPurchases,
  // Assets
  filterAssets,
  showAssetTasks,
  showAssetHistory,
  showLogAssetHours,
  saveAssetHours,
  showQuickEditAsset,
  saveQuickEditAsset,
  showAssetLocationComingSoon,
  dismissReturnInspection,
  // Mechanic signature
  clearMechanicSig,
  // Source DVIR
  viewSourceDvir,
  // DVIR Sign-Off
  clearMaintDvirSignoff,
  submitMaintDvirSignoff,
  // Compliance Report
  emailComplianceReport,
  // Citations
  loadCitations,
  updateCitationRepairStatus,
  updateCitationCert,
  toggleCitationDetail,
  renderCitationLinks,
  linkCitationDvir,
  unlinkCitationDvir,
  // Driver mode
  driverConfirmCompletion,
  addWoReceipt, // TASK-9775
  editWoReceipt // TASK-9775
};

// ============================================
// VEHICLE COMPLIANCE REPORT (EMAIL)
// ============================================

// =============================================================================
// TASK-10065 (bundle headroom) — Vehicle Compliance Report (email) + the Citations / Vehicle Exam Reports tab
// live in the LAZY views/maintCitations.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps the name window.MaintenanceModule and maintenance.js
// call, loads the module on first use and, once loaded, calls straight through
// synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _mtCitMod = null;
let _mtCitLoad = null;
function _mtCitDeps() {
  return {
    MaintenanceState, Timestamp, arrayRemove, arrayUnion, collection, db, doc, escJsAttr,
    escapeHtml, functions, getDoc, getDocs, httpsCallable, limit, orderBy, query, safeHttpUrl,
    updateDoc, where
  };
}
function _loadMtCit() {
  if (_mtCitMod) return Promise.resolve(_mtCitMod);
  if (!_mtCitLoad) {
    _mtCitLoad = __ddChunkWrap(import('./maintCitations.js?v=1027146')).then((m) => { m._bind(_mtCitDeps()); _mtCitMod = m; return m; }).catch((e) => {
      _mtCitLoad = null; // the next click tries again
      console.error('[Maintenance] maintCitations load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _maintLazyLoadFailed: true });
    });
  }
  return _mtCitLoad;
}
function emailComplianceReport(...a) { return _mtCitMod ? _mtCitMod.emailComplianceReport(...a) : _maintLazyCall(_loadMtCit, 'emailComplianceReport', a); }
function loadCitations(...a) { return _mtCitMod ? _mtCitMod.loadCitations(...a) : _maintLazyCall(_loadMtCit, 'loadCitations', a); }
function toggleCitationDetail(...a) { return _mtCitMod ? _mtCitMod.toggleCitationDetail(...a) : _maintLazyCall(_loadMtCit, 'toggleCitationDetail', a); }
function renderCitationLinks(...a) { return _mtCitMod ? _mtCitMod.renderCitationLinks(...a) : _maintLazyCall(_loadMtCit, 'renderCitationLinks', a); }
function linkCitationDvir(...a) { return _mtCitMod ? _mtCitMod.linkCitationDvir(...a) : _maintLazyCall(_loadMtCit, 'linkCitationDvir', a); }
function unlinkCitationDvir(...a) { return _mtCitMod ? _mtCitMod.unlinkCitationDvir(...a) : _maintLazyCall(_loadMtCit, 'unlinkCitationDvir', a); }
function updateCitationRepairStatus(...a) { return _mtCitMod ? _mtCitMod.updateCitationRepairStatus(...a) : _maintLazyCall(_loadMtCit, 'updateCitationRepairStatus', a); }
function updateCitationCert(...a) { return _mtCitMod ? _mtCitMod.updateCitationCert(...a) : _maintLazyCall(_loadMtCit, 'updateCitationCert', a); }

// TASK-10148 — Downtime Audit tab: LAZY views/maintDowntimeAudit.js (deps handed over, pitfall #1).
let _mtDtaLoad = null;
function _openDowntimeAudit() {
  if (!_mtDtaLoad) {
    _mtDtaLoad = __ddChunkWrap(import('./maintDowntimeAudit.js?v=10148')).catch((e) => {
      _mtDtaLoad = null;
      console.error('[Maintenance] maintDowntimeAudit load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      return null;
    });
  }
  _mtDtaLoad.then((m) => m && m.mount(document.getElementById('downtime-audit-host'), { functions, httpsCallable, escapeHtml, tenantId: MaintenanceState.tenantId }));
}

export { initMaintenance };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
